import { useState, useEffect, useLayoutEffect, useCallback, useMemo, useRef, Fragment } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Film,
  Download,
  Play,
  Pause,
  Square,
  SkipBack,
  SkipForward,
  Volume2,
  VolumeX,
  Music,
  Image as ImageIcon,
  Scissors,
  Trash2,
  Plus,
  Minus,
  Loader2,
  ChevronRight,
  ChevronLeft,
  ChevronDown,
  ChevronUp,
  ZoomIn,
  ZoomOut,
  Upload,
  Save,
  ChevronsDownUp,
  ChevronsUpDown,
  Pencil,
  X,
  FolderInput,
  FolderOpen,
  RefreshCw,
  Languages,
  Youtube,
  Instagram,
  Mic,
  AlignJustify,
  Captions,
  Newspaper,
  Maximize2,
  Minimize2,
  SlidersHorizontal,
  Undo2,
  Redo2,
  BookOpen,
  Folder,
  Camera,
  Wand2,
  Layers,
  Type,
  PlusSquare,
  Palette,
  FileText,
  Compass,
  Radio,
  Clapperboard,
  Map as MapIcon,
  FolderMinus,
  PlayCircle,
  Activity,
  Maximize,
  Video as VideoIcon,
  Waves,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { EditorTopNav } from '@/components/editorViewTabs/EditorTopNav';
import { CreateAgentBackButton } from '@/components/createPlan/CreateAgentBackButton';
import { Slider } from '@/components/ui/slider';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { toast } from 'sonner';
import {
  Panel,
  PanelGroup,
  PanelResizeHandle,
} from 'react-resizable-panels';
import { VideoClipEditor } from '@/components/VideoClipEditor';
import { VideoFilterOverlayStack } from '@/components/VideoFilterOverlayStack';
import { TimelineClipVerticalVolume } from '@/components/TimelineClipVerticalVolume';
import { MediaLibrary } from '@/components/MediaLibrary';
import { useSequentialPlayback } from '@/hooks/useSequentialPlayback';
import { useVideoEditorUndoRedo } from '@/hooks/useVideoEditorUndoRedo';
import { applyMainClipPreviewFrame, applyTransitionPreviewFrame } from '@/hooks/usePreviewMotionLayer';
import { PlaybackChrome } from '@/components/videoEditor/PlaybackChrome';
import { PreviewTimeBadge } from '@/components/videoEditor/PreviewTimeBadge';
import { TimelineTransitionGate } from '@/components/videoEditor/TimelineTransitionGate';
import { DualTransitionPreview } from '@/components/videoEditor/DualTransitionPreview';
import { TransitionPickerDialog } from '@/components/videoEditor/TransitionPickerDialog';
import { useVideoExport } from '@/hooks/useVideoExport';
import api from '@/lib/api';
import {
  mergeTimelineClipFrozenFields,
} from '@/lib/videoEditorSceneMerge';
import {
  clearActiveExportJob,
  exportJobStorageKey,
  loadActiveExportJob,
  pollExportJob,
  saveActiveExportJob,
} from '@/lib/exportJobTracker';
import { OnboardingJourney } from '@/components/onboarding/OnboardingJourney';
import { GuideButton } from '@/components/onboarding/GuideButton';
import { videoEditorOnboardingSteps } from '@/data/onboardingSteps';
import { useOnboardingJourney } from '@/hooks/useOnboardingJourney';
import { JOURNEY_IDS } from '@/lib/onboardingJourney';
import { formatApiError } from '@/lib/formatApiError';
import { leftTopArrowGradient, rightBottomArrowGradient } from '@/assets/svgs/gradientIcons';
import { useInsufficientCredits, handleInsufficientCreditsError } from '@/contexts/InsufficientCreditsContext';
import {
  buildVideoProjectDocument,
  normalizeVideoSubtitleStyle,
  parseVideoProjectDocument,
  setPendingVideoProjectNavigation,
  readAndClearPendingVideoProject,
  formatVideoProjectLanguageLabel,
  resolveVideoProjectScriptLanguage,
} from '@/lib/videoEditorProjectFile';
import {
  AUDIO_TRANSLATION_LANGUAGES,
  audioTranslationLanguageLabel,
  audioLanePlaybackMask,
  buildTranslationAudioLaneFromScenes,
  findPairedAudioClipForVideoOnActiveLanes,
  localizedAudioForScene,
  mergeAudioTrackMeta,
  normalizeAudioLaneActiveFlags,
  resolveMagicEditReferenceAudioLane,
  resolveAnimationReferenceAudioLane,
  sceneAudioBlockFromLane,
  sceneAudioDurationFromLane,
  audioLaneChoiceLabel,
} from '@/lib/videoEditorAudioTranslation';
import {
  padSubtitleTracksToAudioLanes,
  subtitleLaneLabel,
  subtitleLanePlaybackMask,
  normalizeSubtitleLaneActiveFlags,
} from '@/lib/videoEditorSubtitleLanes';
import {
  saveProjectToLinkedOrNewFolder,
  getPersistedProjectFolder,
  readLinkedRootProjectFile,
} from '@/lib/videoEditorProjectStorage';
import {
  patchVideoEditorSnapshotToCloud,
  cloudSnapshotPayloadFromDoc,
  saveScriptLanguageDraftToCloud,
  scriptLanguageDraftFromProject,
  fetchNarrationEditorShell,
  fetchNarrationEditorScenes,
  fetchNarrationEditorTimelines,
  CLOUD_EDITOR_AUTOSAVE_INTERVAL_MS,
} from '@/lib/videoEditorCloudSync';
import { captureVideoEditorSnapshot } from '@/lib/videoEditorHistory';
import {
  readVideoEditorAutoSegmentSyncEnabled,
  segmentSyncThrottleElapsed,
  VIDEO_EDITOR_SEGMENT_SYNC_INTERVAL_MS,
  writeVideoEditorAutoSegmentSyncEnabled,
} from '@/lib/videoEditorSegmentSync';
import { finalizeNarrationSegmentAudioRegeneration } from '@/lib/narrationSegmentAudioRegen';
import { YouTubePublishDialog } from '@/components/YouTubePublishDialog';
import { InstagramPublishDialog } from '@/components/InstagramPublishDialog';
import { NarrationAudioSettingsPanel } from '@/components/NarrationAudioSettingsPanel';
import { NarrationVoicePickerDialog } from '@/components/NarrationVoicePickerDialog';
import { narrationAudioSettingsFromInput, buildNarrationAudioApiPayload } from '@/lib/narrationAudioSettings';
import { ExportFramingPreview, resetExportFraming } from '@/components/ExportFramingPreview';
import {
  centerExportFraming,
  ensureExportFramingCentered,
  exportFramingToApiPayload,
  exportTargetSizeForAspect,
  normalizeVideoExportFraming,
} from '@/lib/videoExportFraming';
import { MovieDirectorPanel } from '@/components/MovieDirectorPanel';
import { useMovieDirector } from '@/contexts/MovieDirectorContext';
import { fetchVideoGenerationPrompt } from '@/lib/videoGenerationPrompt';
import {
  MIN_CLIP_PLAYBACK_SEC,
  clipVideoTimelineDuration,
  clipVideoExportPlaybackSeconds,
  clipVideoSnippetDrawDuration,
  imageLaneClipDrawDuration,
  clipVideoTimelineOccupancy,
  clipVideoTimelineLaneDuration,
  clipVideoSourceTrimEndSeconds,
  clipPreviewMediaTime,
  clipVideoMaxSourceSeconds,
  clipVideoMediaCapSeconds,
  clipHasUserVideoTrim,
  clampVideoClipTrimToMediaCap,
  syncVideoClipTimelineSlot,
  getVideoExtendGapForClip,
  clipVideoPlaybackSpeed,
  clipAudioTimelineDuration,
  clipStoryBackgroundMusicDrawDuration,
  isStoryBackgroundMusicClip,
  storyBackgroundMusicMaxDuration,
  clipAudioPlaybackSpeed,
  effectiveClipAudioPlaybackSpeed,
  playbackSpeedToSliderPercent,
  playbackSpeedFromSliderPercent,
  clipSourcePlaybackDuration,
  isAudioTimelineClip,
  clampPlaybackSpeed,
  MIN_PLAYBACK_SPEED,
  MAX_PLAYBACK_SPEED,
  PLAYBACK_SPEED_PRESETS,
  effectiveVideoClipPreviewMuted,
  effectiveVideoClipPreviewVolume,
  normalizeVideoClipEmbeddedAudioMute,
  stripLegacyVideoClipEmbeddedMute,
  DEFAULT_VIDEO_EMBEDDED_AUDIO_VOLUME,
  probeVideoFileDurationSeconds,
} from '@/lib/videoEditorPlayback';
import {
  isMusicLanePlaybackEnabled,
  isEngineSfxStemLane,
  isSceneSfxLane,
  maskMusicTracksForPlayback,
  musicTracksNeedStoryMusic,
  placeSceneSfxOnOwnLane,
  sceneSfxSeconds,
  sceneSlotsFromClips,
  withStoryMusicLane,
} from '@/lib/storySoundTracks';
import {
  applyEngineSfxPlayback,
  engineSfxPlaybackState,
  ensureVisibleEngineSfxLane,
  isEngineSfxStemClip,
  mixLayoutRows,
  orderedMusicLanesForTimeline,
  shouldTriggerEngineRemix,
} from '@/lib/storySfxStem';
import {
  BG_MUSIC_GENERATION_OPTIONS,
  DEFAULT_BG_MUSIC_PROVIDER,
} from '@/lib/bgMusicProviders';
import {
  clipMatchesSceneSource,
  clipSceneSourceId,
  imageUrlForTimelineClip,
  primaryVideoClipForScene,
  remapTimelineClipToCurrentScene,
  resolveNarrationSegmentId,
  sceneForTimelineClip,
  sceneDocumentMediaPlayback,
  sceneDocumentVideoUrlChanged,
  sceneDocumentImageUrlChanged,
  resolveClipVideoUrlAfterImageChange,
  resolveSceneVideoUrlForTimelineClip,
  sceneTimelineColors,
  segmentNumberFromTimelineClip,
  stampSegmentImagesOntoVideoTracks,
  timelineClipSceneIndex,
} from '@/lib/videoEditorSceneTimelinePack';
import {
  coerceTimelineTracks,
  mediaUrlForPlayback,
  mediaUrlForSegmentField,
  mediaUrlIdentityKey,
  mediaUrlWithCacheBust,
  normalizeEditorTracksForPlayback,
  resolveMediaPlaybackUrl,
} from '@/lib/mediaUrls';
import {
  editorOverlaysToCutPatch,
  padReelsOverlayTracks,
  reelsOverlayLaneMeta,
  reelsShortsProjectToEditorState,
} from '@/lib/reelsShortsEditor';
import { isSlidingCardCarouselClip } from '@/lib/reelsShortsBroll';
import { CardCarousel } from '@/components/CardCarouselOverlay';
import {
  buildSongMasterAudioLane,
  buildSongMasterAudioLaneFromScenes,
  buildSongStanzaSubtitleTracks,
  expandSongSceneToTimelineClips,
  isSongBasedEditorScenes,
  isSongBasedNarrationProject,
  mergeSongMagicEditVideoTracks,
  songClipLocalWordTimestamps,
  songMasterAudioUrl,
  songSceneHasAlignTiming,
  songSegmentWindow,
  songStanzaSubtitleText,
  songTimelineDuration,
  subtitleCueEditorOverlay,
  applySubtitleCueOverlayTiming,
} from '@/lib/songBasedVideoEditor';
import { expandTrackWithVisualBeats, visualBeatsWithImages } from '@/lib/visualBeatTimeline';
import {
  isPitchRecordingStory,
  isPitchTimelineClip,
  loadPitchStoryEditorScenes,
  mergePitchTimelineClipWithScene,
  pitchVideoClipAfterProbe,
  syncPitchClipTimelineMetadata,
  tagPitchRecordingVideoTracks,
} from '@/lib/pitchBasedVideoEditor';
import {
  songAnimationSettingsFromInput,
  songAnimationSettingsToApiPayload,
  clampSongAnimationSpeed,
} from '@/lib/songAnimationSettings';
import {
  EDITOR_ANIMATION_SPEED_DEFAULT,
  clampEditorAnimationSpeed,
  editorAnimationSpeedFromSnapshot,
} from '@/lib/editorAnimationSettings';
import {
  applyMultishotSelectionPatch,
  multishotSelectionPayloadFromScene,
  scenesHaveMultishotTimeline,
} from '@/lib/songMultishotSelection';
import {
  exportPayloadHasLocalMedia,
  stageExportPayloadForCloud,
} from '@/lib/exportMediaStaging';
import {
  exportTimelineLocally,
  localExportSupported,
  pickLocalExportFile,
} from '@/lib/localTimelineExport';
import {
  applyBookendsToExportPayload,
  bookendAudioExportClips,
  bookendClipsForMainEnd,
  bookendTimelineClipStyle,
  DEFAULT_VIDEO_BOOKENDS,
  isBookendTimelineClip,
  mergeLaneClipsWithBookends,
  mergeVideoBookendsPreservingLocal,
  normalizeVideoBookends,
  offsetTimelineClipForBookendIntro,
  stretchBackgroundMusicForBookends,
  withBrandOutroOnTimeline,
} from '@/lib/videoEditorBookends';
import BookendTitleOverlay from '@/components/BookendTitleOverlay';
import { VideoEditorTimelineBookendsControl } from '@/components/VideoEditorTimelineBookendsControl';
import {
  normalizeTransitionOut,
  transitionShortLabel,
} from '@/lib/videoTransitions';
import {
  clipFilterPreviewStyle,
  clipFiltersFromClip,
  cssPreviewForFilters,
  filterShortLabel,
  normalizeClipFilters,
} from '@/lib/videoFilters';
import { clipMotionPreviewStyle } from '@/lib/videoMotionPreview';
import {
  dialogueShotsExportRows,
  focusPreviewStyle,
  sceneCameraRectAt,
  shotsBySceneId,
  usesDialogueCamera,
} from '@/lib/dialogueShots';
import { clipHasAudioMotion, segmentToClipMotionFields } from '@/lib/songAudioMotion';
import {
  motionPresetLabel,
  normalizeMotionPreset,
  pickRandomMotionPreset,
} from '@/lib/videoMotionPresets';
import { AnimationTimelineTrack } from '@/components/AnimationTimelineTrack';
import { mergePreviewLayerStyles, applyPreviewLayerStylesToElement } from '@/lib/videoTransitionPreview';
import { computeTransitionPreviewBlend } from '@/lib/videoTransitionPreviewBlend';
import {
  narrationSegmentToScene,
  narrationSegmentsFromProject,
} from '@/lib/sceneGridNarrationAdapter';
import {
  narrationAspectRatioLabel,
  resolveNarrationProjectAspectRatio,
} from '@/lib/narrationAspectRatio';
import { sceneSkipsNarration } from '@/lib/narrationSceneScripts';
import {
  characterVoicesPayload,
  resolveSceneAudioScriptForTts,
  spokenSubtitleEntries,
  spokenSubtitleTextForScene,
} from '@/lib/sceneScript';
import {
  SUBTITLE_ANIMATION_OPTIONS,
  SUBTITLE_COLOR_PRESETS,
  SUBTITLE_FONT_OPTIONS,
  subtitleStyleToExportPayload,
} from '@/lib/subtitles/subtitleStyleConfig';
import {
  alignSpokenWords,
  clampSpokenWordLimit,
  expandSpokenWordCues,
  spokenWordWindowAt,
} from '@/lib/subtitles/spokenWordWindow';

/** Synced from editor state so timeline pack helpers respect master audio speed. */
const masterAudioPlaybackSpeedRef = { current: 1 };
import { grabVideoFrameDataUrl } from '@/lib/videoFrameGrab';
import {
  computeObjectContainContentRect,
  exportAspectRatioCss,
  SUBTITLE_MAX_FONT_SIZE,
  SUBTITLE_MAX_OUTLINE_WIDTH,
  SUBTITLE_MIN_FONT_SIZE,
} from '@/lib/videoSubtitlePreview';
import { buildSubtitleTimelineDocument } from '@/lib/captions/subtitleTimeline';
import { computeSubtitleLayerTextLayout } from '@/lib/subtitles/subtitleEditorLayout';
import {
  stretchSubtitleLayerToAspectMax,
  subtitleLayerFromLegacyAnchors,
  subtitleLayerToExportAnchors,
} from '@/lib/subtitles/subtitleLayer';
import SubtitleLookControls from '@/components/subtitles/SubtitleLookControls';
import SubtitlePreviewOverlay from '@/components/subtitles/SubtitlePreviewOverlay';
import { VfxPreviewOverlay } from '@/components/vfx/VfxPreviewOverlay';
import { EffectsTimelineTrack } from '@/components/vfx/EffectsTimelineTrack';
import { EffectInspectorPanel } from '@/components/vfx/EffectInspectorPanel';
import { buildVfxExportPayload, deserializeEffectTracks } from '@/vfx/serialization';
import {
  addEffectToTrack,
  beginEffectClipDrag,
  beginEffectClipResize,
  clearAllEffectClips,
  duplicateEffectClip,
  loadEffectTracksFromEditor,
  normalizeEffectTracks,
  patchEffectClip,
  removeEffectClip,
} from '@/lib/vfxTimelineHelpers';
import {
  beginHostOverlayClipDrag,
  beginHostOverlayClipResize,
  duplicateHostOverlayClip,
  patchHostOverlayClip,
  removeHostOverlayClip,
} from '@/lib/hostOverlayTimelineHelpers';
import {
  loadHostOverlayTracksFromEditor,
  buildHostOverlayExportPayload,
  hostOverlaysForPreview,
  createHostOverlayClip,
  createBackgroundTemplateClip,
  isBackgroundOverlayClip,
  isLayoutBrollClip,
  DEFAULT_HOST_OVERLAY_DURATION,
  resolveHostOverlayClipForPlayback,
} from '@/lib/videoEditorHostOverlays';
import {
  grabFrameFromVideoElement,
  localFillCropFromCanvas,
  talkingHeadPaneFromLayoutClip,
  talkPaneScreenRect,
  videoSourceCropStyle,
} from '@/lib/talkingHeadFocus';
import { HostOverlayTimelineTrack } from '@/components/hostOverlay/HostOverlayTimelineTrack';
import { TimelineLaneMinimizeButton } from '@/components/videoEditor/TimelineLaneMinimizeButton';
import { OverlayClipDetailsDialog } from '@/components/reelsShorts/OverlayClipDetailsDialog';
import { overlayClipDisplayTextPatch } from '@/lib/videoEditorOverlayDetails';
import {
  timelineLaneKey,
  areAllTimelineLanesCollapsed,
  collapseTimelineLanes,
  isTimelineLaneCollapsed,
  toggleAllTimelineLanes,
  toggleTimelineLaneCollapsed,
  timelineLaneClipAreaStyle,
  timelineLaneRowSizeStyle,
} from '@/lib/videoEditorTimelineLanes';
import {
  clipsInTimelineViewport,
  timeMarksInTimelineViewport,
  timelineViewportTimeRange,
} from '@/lib/timelineViewportClips';
import {
  clipRevealSeekTime,
  laneIndexOfClip,
  timelineRevealScrollLeft,
} from '@/lib/videoEditorTimelineReveal';
import HostOverlayPreview from '@/components/hostOverlay/HostOverlayPreview';
import {
  applyParamsToGroup,
  buildCarouselClips,
  isCardOverlayClip,
  parseCarouselCreatePrompt,
  patchCarouselClip,
} from '@/lib/cardCarousel';
import { CardCarouselInspector } from '@/components/cardCarousel/CardCarouselInspector';
import { ReelsLayoutInspector } from '@/components/reelsShorts/ReelsLayoutInspector';
import demoVfxTimeline from '@/vfx/demo/demoTimeline.json';
import environmentalDemoTimeline from '@/vfx/demo/environmentalDemoTimeline.json';

import { TwinneeLogo } from '@/components/TwinneeLogo';
const TIMELINE_CLIP_VOLUME_STEP = 0.05;

function musicTimelineEmptyOrUnreadable(tracks) {
  const flat = (tracks || []).flat();
  if (!flat.length) return true;
  return flat.every((c) => !c?.url || String(c.url).trim() === '');
}

const clampTimelineClipVolume = (v) => {
  const n = Number(v);
  if (!Number.isFinite(n)) return 0.8;
  return Math.min(1, Math.max(0, n));
};

const PX_PER_SEC = 80; // pixels per second at zoom=1
const MIN_TIMELINE_ZOOM = 0.05;
const MAX_TIMELINE_ZOOM = 4;
/** Must match timeline row label column (`w-36` = 9rem). */
const TIMELINE_LABEL_WIDTH_PX = 144;
const TIMELINE_LABEL_Z_INDEX = 100;
const TIMELINE_LABEL_STICKY_CLASS = 'w-36 shrink-0 sticky left-0 border-r shadow-[4px_0_10px_-2px_rgba(0,0,0,0.45)]';
const TIMELINE_RULER_CORNER_CLASS = 'w-36 shrink-0 sticky top-0 left-0 border-r shadow-[4px_0_10px_-2px_rgba(0,0,0,0.45)]';
const TIMELINE_LABEL_PANEL_STYLE = {
  background: 'var(--bg-secondary)',
  borderColor: 'var(--glass-border)',
  zIndex: TIMELINE_LABEL_Z_INDEX,
};
const DEFAULT_VIDEO_TRIM_START = 0.0;
const MIN_TIMELINE_GAP_SEC = 0.1;
const REGENERATION_JOB_POLL_MS = 5000;
const REGENERATION_JOB_MAX_POLLS = 360;
const EXPORT_ASPECT_RATIO_OPTIONS = [
  { value: '16:9', label: '16:9', detail: 'YouTube' },
  { value: '9:16', label: '9:16', detail: 'Reels / Shorts' },
  { value: '1:1', label: '1:1', detail: 'Instagram' },
  { value: '4:5', label: '4:5', detail: 'Portrait feed' },
  { value: '21:9', label: '21:9', detail: 'Cinematic' },
  { value: '4:3', label: '4:3', detail: 'Classic' },
];
const EXPORT_QUALITY_OPTIONS = [
  { value: 'best', label: 'Best', detail: '1080p high quality (default)' },
  { value: 'low', label: 'Low', detail: 'Smaller file, more compression' },
  { value: 'lossless', label: 'Lossless', detail: 'Near-transparent (CRF 12), largest file' },
];

function normalizeExportQuality(value) {
  const v = String(value || 'best').trim().toLowerCase();
  return EXPORT_QUALITY_OPTIONS.some((o) => o.value === v) ? v : 'best';
}
const IMAGE_PROVIDER_OPTIONS = [
  { value: 'nano_banana', label: 'Gemini Nano Banana' },
  { value: 'veo3', label: 'Veo 3 / Gemini Images' },
  { value: 'minimax', label: 'MiniMax Hailuo' },
  { value: 'runway', label: 'Runway Gen4 (text / image refs)' },
  { value: 'inhouse', label: 'Inhouse' },
];
const VIDEO_PROVIDER_OPTIONS = [
  { value: 'nano_banana', label: 'Nano Banana slideshow' },
  { value: 'minimax', label: 'MiniMax Hailuo AI Video' },
  { value: 'veo3', label: 'Google Veo' },
  { value: 'runway', label: 'Runway ML' },
  { value: 'seedance', label: 'Seedance 2 (Runway)' },
  { value: 'seedance_2_5', label: 'Seedance 2.5 (Runway)' },
  { value: 'chatgpt_astra', label: 'ChatGPT Astra (OpenAI Video)' },
  { value: 'fable_5_1', label: 'Fable 5.1 (via Seedance 2.5)' },
  { value: 'inhouse', label: 'Inhouse' },
];
const NARRATION_VIDEO_PROVIDER_OPTIONS = [
  { value: 'narration_slideshow', label: 'Narration slideshow' },
  { value: 'minimax', label: 'MiniMax Hailuo AI Video' },
  { value: 'veo3', label: 'Google Veo' },
  { value: 'runway', label: 'Runway ML' },
  { value: 'seedance', label: 'Seedance 2 (Runway)' },
  { value: 'seedance_2_5', label: 'Seedance 2.5 (Runway)' },
  { value: 'chatgpt_astra', label: 'ChatGPT Astra (OpenAI Video)' },
  { value: 'fable_5_1', label: 'Fable 5.1 (via Seedance 2.5)' },
  { value: 'inhouse', label: 'Inhouse' },
];
const LANGUAGE_OPTIONS = [
  { value: 'en', label: 'English' },
  { value: 'hi', label: 'Hindi' },
  { value: 'kn', label: 'Kannada' },
  { value: 'mr', label: 'Marathi' },
  { value: 'ta', label: 'Tamil' },
  { value: 'de', label: 'German' },
];
const CAMERA_MOTION_OPTIONS = [
  { value: 'auto', label: 'Auto' },
  { value: 'static', label: 'Static' },
  { value: 'pan_left', label: 'Pan left' },
  { value: 'pan_right', label: 'Pan right' },
  { value: 'tilt_up', label: 'Tilt up' },
  { value: 'tilt_down', label: 'Tilt down' },
  { value: 'zoom_in', label: 'Zoom in' },
  { value: 'zoom_out', label: 'Zoom out' },
  { value: 'dolly_in', label: 'Dolly in' },
  { value: 'dolly_out', label: 'Dolly out' },
];

const defaultReplacementOverrides = (story, isNarrationEditor = false) => {
  const input = story?.input || {};
  const audio = narrationAudioSettingsFromInput({
    voice_style: input.voice_style || story?.voice_style,
    tts_provider: input.tts_provider || story?.tts_provider,
    language: input.language || story?.language,
    narrator_elevenlabs_voice_id: input.narrator_elevenlabs_voice_id,
    narrator_murf_voice_id: input.narrator_murf_voice_id,
    narrator_murf_locale: input.narrator_murf_locale,
    narrator_minimax_voice_id: input.narrator_minimax_voice_id,
  });
  return {
    image_provider: story?.image_provider || input.image_provider || 'nano_banana',
    image_aspect_ratio: story?.image_aspect_ratio || input.image_aspect_ratio || '16:9',
    video_provider: isNarrationEditor
      ? (input.video_provider || 'narration_slideshow')
      : (story?.default_scene_video_provider || 'nano_banana'),
    camera_motion: 'auto',
    ...audio,
    extend_scene_video_to_audio_duration: false,
    lip_sync_scene_video_with_audio: false,
  };
};

function storySceneAudioGenerationPayload(defs, story, scene, scriptText) {
  const script = String(scriptText || '').trim();
  const audioScript = resolveSceneAudioScriptForTts({
    scene,
    scriptText: script,
    characters: story?.characters || [],
  });
  const payload = {
    audio_script: audioScript.length ? audioScript : [{ kind: 'narration', text: script }],
    voice_style: defs?.voice_style,
    language: defs?.language,
    narrator: {
      elevenlabs_voice_id: defs?.narrator_elevenlabs_voice_id || undefined,
      murf_voice_id: defs?.narrator_murf_voice_id || undefined,
      murf_locale: defs?.narrator_murf_locale || undefined,
      minimax_voice_id: defs?.narrator_minimax_voice_id || undefined,
    },
  };
  if (!audioScript.some((seg) => String(seg?.kind || '').toLowerCase() === 'dialogue')) {
    payload.narration_text = script;
  }
  if ((defs?.tts_provider || 'auto') !== 'auto') {
    payload.narrator_tts_provider = defs.tts_provider;
  }
  const voices = characterVoicesPayload(story?.characters || []);
  if (Object.keys(voices).length) payload.character_voices = voices;
  return payload;
}

const EXTEND_VIDEO_PROVIDER_IDS = new Set([
  'minimax',
  'ltx',
  'veo3',
  'runway',
  'seedance',
  'seedance_2_5',
  'chatgpt_astra',
  'fable_5_1',
  'inhouse',
]);

function probeVideoDurationSeconds(url) {
  return probeVideoFileDurationSeconds(url);
}

function buildExtendedTimelineClip(item, { playableVideo, audioSec, videoFileSec }) {
  const fileSec = Math.max(Number(videoFileSec) || 0, Number(audioSec) || 0, MIN_CLIP_PLAYBACK_SEC);
  const next = clampVideoClipTrimToMediaCap({
    ...item,
    videoUrl: playableVideo || item.videoUrl,
    duration: audioSec,
    trimStart: 0,
    trimEnd: fileSec,
    videoPlaybackSpeed: 1,
    videoMediaDurationSeconds: fileSec,
    videoSourceDurationMax: fileSec,
  });
  return { ...next, playbackDuration: clipPlaybackFromTrim(next) };
}

/** Sync timeline-visible length with source trim window (video only). */
function clipPlaybackFromTrim(clip) {
  if (!clip.videoUrl) {
    return clip.playbackDuration ?? clip.duration ?? 5;
  }
  const dur = clip.duration ?? 5;
  const te = clip.trimEnd != null ? clip.trimEnd : dur;
  const ts = Math.max(0, Math.min(clip.trimStart ?? 0, te - MIN_CLIP_PLAYBACK_SEC));
  return Math.max(MIN_CLIP_PLAYBACK_SEC, te - ts);
}

function clipEndTime(clip, fileDur = 0) {
  const master = masterAudioPlaybackSpeedRef.current;
  const start = Number(clip?.startTime) || 0;
  if (isStoryBackgroundMusicClip(clip)) {
    return start + clipStoryBackgroundMusicDrawDuration(clip, fileDur, master);
  }
  if (isAudioTimelineClip(clip)) {
    return start + clipAudioTimelineDuration(clip, fileDur, master);
  }
  if (clip?.videoUrl || clip?.imageUrl) {
    return start + clipVideoTimelineLaneDuration(clip);
  }
  return start + clipVideoTimelineOccupancy(clip);
}

function clampVideoTracksForMediaCap(videoTracks) {
  return (videoTracks || []).map((track) =>
    (Array.isArray(track) ? track : []).map((c) => {
      const next = c.videoUrl ? clampVideoClipTrimToMediaCap(c) : c;
      const norm = normalizeVideoClipEmbeddedAudioMute(next);
      const vol = norm.volume != null ? clampTimelineClipVolume(norm.volume) : DEFAULT_VIDEO_EMBEDDED_AUDIO_VOLUME;
      return { ...norm, volume: vol };
    }),
  );
}

/** Match backend `narration_app._estimate_word_timestamps` for scenes without stored timestamps. */
function estimateWordTimestamps(text, durationSeconds) {
  const duration = Math.max(MIN_CLIP_PLAYBACK_SEC, Number(durationSeconds) || 0);
  const words = (text || '').split(/\s+/).filter((w) => w.trim());
  if (!words.length) return [];
  const totalChars = words.reduce((sum, w) => sum + Math.max(1, w.length), 0);
  let cursor = 0;
  const out = [];
  for (const word of words) {
    const share = Math.max(1, word.length) / totalChars;
    const span = Math.max(0.08, duration * share);
    const start = cursor;
    const end = Math.min(duration, cursor + span);
    out.push({
      word,
      start: Math.round(start * 1000) / 1000,
      end: Math.round(end * 1000) / 1000,
    });
    cursor = end;
  }
  if (out.length) {
    out[out.length - 1] = { ...out[out.length - 1], end: Math.round(duration * 1000) / 1000 };
  }
  return out;
}

/** Stored TTS word timestamps can lag script edits (e.g. language preview); ignore them if they no longer match the clip script. */
function wordTimestampsMatchNarrationText(wordTimestamps, text) {
  if (!wordTimestamps?.length || !(text || '').trim()) return false;
  const fromTs = wordTimestamps
    .map((w) => (w && w.word != null ? String(w.word) : ''))
    .join(' ')
    .replace(/\s+/g, ' ')
    .trim();
  const fromNarr = String(text)
    .replace(/\s+/g, ' ')
    .trim();
  return fromTs === fromNarr;
}

function resolveClipWordTimestamps(clip) {
  const text = clip.narrationText || clip.sceneText || '';
  const srcCap = clipSourcePlaybackDuration(clip);
  if (
    clip.wordTimestamps?.length
    && wordTimestampsMatchNarrationText(clip.wordTimestamps, text)
  ) {
    return clip.wordTimestamps;
  }
  return estimateWordTimestamps(text, srcCap);
}

function scenePrimaryScriptText(scene) {
  const script = Array.isArray(scene?.audio_script) ? scene.audio_script : [];
  if (script.length) {
    const lines = script
      .map((seg) => {
        if (!seg || typeof seg !== 'object') return '';
        const text = String(seg.text || '').trim();
        if (!text) return '';
        const kind = String(seg.kind || '').toLowerCase();
        if (kind === 'action') return '';
        if (kind === 'dialogue') {
          const speaker = String(seg.speaker || '').trim();
          return speaker ? `${speaker}: ${text}` : text;
        }
        return text;
      })
      .filter(Boolean);
    if (lines.length) return lines.join('\n');
  }
  return String(
    scene?.text || scene?.narration_text || scene?.scene_text || scene?.transcription_text || '',
  ).trim();
}

function sceneSubtitleScriptText(scene) {
  if (isSongBasedEditorScenes([scene])) {
    const songText = songStanzaSubtitleText(scene);
    if (songText) return songText;
  }
  if (sceneSkipsNarration(scene)) return '';
  return spokenSubtitleTextForScene(scene);
}

/** Caption for one audio clip: the words being spoken (no speaker labels / scene overview). */
function subtitleTextForAudioClip(ac, scene) {
  if (!scene && !ac) return '';
  const dub = String(ac?.dubText || '').trim();
  if (dub) return dub;
  const lang = ac?.languageCode ? String(ac.languageCode).toLowerCase() : '';
  if (lang && scene) {
    const loc = localizedAudioForScene(scene, lang);
    const locText = String(loc?.text || '').trim();
    if (locText) return locText;
  }
  if (!scene) return '';
  const id = String(ac?.id || '');
  const m = id.match(/^seg-audio-(.+)-seg-(\d+)$/);
  if (m) {
    const segIdx = Number(m[2]);
    const spoken = spokenSubtitleEntries(scene);
    const hit = spoken.find((t) => Number(t.index) === segIdx) || spoken[segIdx];
    if (hit?.text) return hit.text;
  }
  return sceneSubtitleScriptText(scene);
}

/** Subtitle burn text: spoken line for this cue; keep lane edits only when marked manual. */
function resolveSubtitleCueText(cue, audioClip, scene, { preferManual = true } = {}) {
  const cueText = String(cue?.text || '').trim();
  // Word-level / kinetic cues (REEL&SHORTS) must keep their timed token text.
  if (cue?.wordIndex != null && cueText) return cueText;
  if (String(cue?.id || '').startsWith('subtitle-word-') && cueText) return cueText;
  if (String(cue?.id || '').startsWith('subtitle-group-') && cueText) return cueText;
  if (preferManual && cue?.manualEdit && cueText) return cueText;
  const lineMatch = String(cue?.id || '').match(/-line-(\d+)$/);
  if (scene && lineMatch) {
    const spoken = spokenSubtitleEntries(scene);
    const hit = spoken.find((e) => Number(e.index) === Number(lineMatch[1]));
    if (hit?.text) return hit.text;
  }
  const fromScript = audioClip
    ? subtitleTextForAudioClip(audioClip, scene)
    : sceneSubtitleScriptText(scene);
  const script = String(fromScript || '').trim();
  if (cueText && script && cueText !== script && script.includes(cueText)) return cueText;
  return script || cueText;
}

function findVideoClipForScene(videoTracks, sceneId, videoClipId = null) {
  if (videoClipId) {
    for (const track of videoTracks || []) {
      const hit = (track || []).find((c) => c.id === videoClipId);
      if (hit) return hit;
    }
  }
  const norm = normalizeEditorSceneId(sceneId);
  if (!norm) return null;
  for (const track of videoTracks || []) {
    for (const vc of track || []) {
      if (normalizeEditorSceneId(vc.sceneId || vc.id) === norm) return vc;
    }
  }
  return null;
}

/** Resolve burn-in text for one timeline cue (subtitle lane, scene row, or video clip). */
function resolveSubtitleExportText(cue, audioClip, scene, videoClip) {
  const resolved = String(resolveSubtitleDisplayText(cue, audioClip, scene, videoClip) || '').trim();
  if (resolved) return resolved;
  return String(sceneSubtitleScriptText(scene) || '').trim();
}

function subtitleExportEntriesFromVideoLane(videoTracks, scenesSnapshot) {
  const sceneById = Object.fromEntries((scenesSnapshot || []).map((s) => [String(s.id), s]));
  return ((videoTracks || [])[0] || [])
    .filter((vc) => !isBookendTimelineClip(vc))
    .map((vc) => {
      const sid = normalizeEditorSceneId(vc.sceneId || vc.id);
      const scene = sid ? sceneById[sid] : null;
      const text = resolveSubtitleExportText(null, null, scene, vc);
      if (!text) return null;
      const startTime = Number(vc.startTime) || 0;
      const playbackSeconds = Math.max(0.01, clipVideoTimelineDuration(vc));
      return {
        cue: {
          id: `subtitle-export-${vc.id}`,
          sceneId: sid,
          videoClipId: vc.id,
          startTime,
          duration: playbackSeconds,
          text,
          muted: false,
        },
        text,
        ac: null,
        videoClip: vc,
        startTime,
        playbackSeconds,
      };
    })
    .filter(Boolean);
}

/** Preview + export text: scene script, manual cue, then video-clip narration fallback. */
function resolveSubtitleDisplayText(cue, audioClip, scene, videoClip) {
  const fromScript = resolveSubtitleCueText(cue, audioClip, scene);
  if (String(fromScript || '').trim()) return String(fromScript).trim();
  const spoken = String(sceneSubtitleScriptText(scene) || '').trim();
  if (spoken) return spoken;
  if (videoClip) {
    const fromClip = String(videoClip.narrationText || '').trim();
    if (fromClip) return fromClip;
  }
  return '';
}

function normalizeEditorSceneId(sid) {
  return String(sid ?? '')
    .replace(/^audio-/, '')
    .replace(/^seg-audio-/, '')
    .split('-seg-')[0];
}

const SUBTITLE_CUE_END_EPSILON_SEC = 0.05;

const DEFAULT_TIMELINE_ACTIVE = {
  image: true,
  video: true,
  audio: true,
  music: true,
  sfx: true,
  subtitle: true,
  subtitleLook: true,
  // Keep captions visible on the preview by default; user can minimize from Look panel.
  subtitleLookMinimized: false,
  effects: true,
  animation: true,
  hostOverlay: true,
};

/** Embedded video soundtrack on the Video 1 lane is muted until the user unmutes. */
const DEFAULT_VIDEO_MASTER_MUTED = true;

function restoredVideoBookends(saved, { defaultTitle = '', aspectRatio = '16:9' } = {}) {
  return normalizeVideoBookends(saved || DEFAULT_VIDEO_BOOKENDS, { defaultTitle, aspectRatio });
}

function mergeRestoredVideoBookends(localBookends, savedBookends, { defaultTitle = '', aspectRatio = '16:9' } = {}) {
  return restoredVideoBookends(
    mergeVideoBookendsPreservingLocal(localBookends, savedBookends, { defaultTitle, aspectRatio }),
    { defaultTitle, aspectRatio },
  );
}

function mergeTimelineActiveForEditorLoad(saved = {}, laneCount = 1) {
  const n = Math.max(1, Number(laneCount) || 1);
  const merged = {
    ...DEFAULT_TIMELINE_ACTIVE,
    ...saved,
  };
  // Never force-minimize captions on load — that hid the preview overlay even when Subs was on.
  if (saved?.subtitleLookMinimized == null) {
    merged.subtitleLookMinimized = false;
  }
  const { lanes: audioLanes } = normalizeAudioLaneActiveFlags(merged, n);
  const { lanes: subtitleLanes } = normalizeSubtitleLaneActiveFlags(merged, n);
  return {
    ...merged,
    audioLanes,
    subtitleLanes,
  };
}

/** Match playhead to clip span; small epsilon so last frame still resolves a cue. */
function timelineTimeMatchesClipRange(t, startTime, duration) {
  const st = Number(startTime) || 0;
  const dur = Math.max(0.01, Number(duration) || 0.01);
  const time = Number(t) || 0;
  return time >= st && time <= st + dur + SUBTITLE_CUE_END_EPSILON_SEC;
}

/** Subtitle cues are stored on main timeline time; playback includes intro bookend offset. */
function subtitleCuePlaybackStart(cue, introDur) {
  return (Number(cue?.startTime) || 0) + Math.max(0, Number(introDur) || 0);
}

function isPlayheadBeforeIntroBookend(introDur, t) {
  const intro = Math.max(0, Number(introDur) || 0);
  return intro > MIN_TIMELINE_GAP_SEC && (Number(t) || 0) < intro - 0.001;
}

function findActiveSubtitleCueAtTime(cues, t, introDur) {
  if (isPlayheadBeforeIntroBookend(introDur, t)) return null;
  const row = cues || [];
  for (let i = 0; i < row.length; i += 1) {
    const c = row[i];
    if (c.muted) continue;
    const st = subtitleCuePlaybackStart(c, introDur);
    if (timelineTimeMatchesClipRange(t, st, c.duration)) return c;
  }
  return null;
}

function findActiveSubtitleCueOnActiveLanes(subtitleTracks, timelineActive, t, introDur) {
  const tracks = subtitleTracks || [];
  const mask = subtitleLanePlaybackMask(timelineActive, tracks.length);
  for (let i = tracks.length - 1; i >= 0; i -= 1) {
    if (!mask[i]) continue;
    const cue = findActiveSubtitleCueAtTime(tracks[i], t, introDur);
    if (cue) return { cue, laneIndex: i };
  }
  return null;
}

function subtitleCueSpokenIndex(cue) {
  const line = String(cue?.id || '').match(/-line-(\d+)$/);
  if (line) return Number(line[1]);
  const seg = String(cue?.audioClipId || cue?.id || '').match(/-seg-(\d+)$/);
  if (seg) return Number(seg[1]);
  return 0;
}

/** When audio lane cues are missing, derive subtitle rows from spoken dialogue timed to the video clip. */
function appendVideoFallbackSubtitleCues(next, videoTracks, scenesSnapshot, overlay) {
  const sceneById = Object.fromEntries((scenesSnapshot || []).map((s) => [String(s.id), s]));
  const covered = new Set(
    (next || [])
      .map((c) => {
        const sid = normalizeEditorSceneId(c.sceneId);
        return sid ? `${sid}:${subtitleCueSpokenIndex(c)}` : '';
      })
      .filter(Boolean),
  );
  const videoLane0 = (videoTracks || [])[0] || [];
  const out = [...next];
  videoLane0.forEach((vc) => {
    const sid = normalizeEditorSceneId(vc.sceneId || vc.id);
    if (!sid) return;
    const scene = sceneById[sid];
    const syntheticAudioId = `audio-${sid}`;
    const clipStart = Number(vc.startTime) || 0;
    const clipDur = Math.max(0.01, clipVideoTimelineDuration(vc));
    const entries = spokenSubtitleEntries(scene);
    const lines = entries.length
      ? entries
      : [{ index: 0, text: sceneSubtitleScriptText(scene), startSeconds: 0, durationSeconds: clipDur }];
    const usable = lines.filter((e) => String(e.text || '').trim());
    if (!usable.length) return;
    const timed = usable.length > 1 && usable.every((e) => e.durationSeconds > 0 && e.startSeconds != null);
    usable.forEach((entry, i) => {
      const idx = Number.isFinite(Number(entry.index)) ? Number(entry.index) : i;
      if (covered.has(`${sid}:${idx}`)) return;
      const cueId = usable.length > 1
        ? `subtitle-${syntheticAudioId}-line-${idx}`
        : `subtitle-${syntheticAudioId}`;
      const o = overlay[cueId] || (usable.length === 1 ? overlay[syntheticAudioId] : null);
      const hasOverlay = Boolean(o);
      const startTime = timed ? clipStart + Number(entry.startSeconds || 0) : clipStart + (clipDur * i) / usable.length;
      const duration = timed
        ? Math.max(0.01, Number(entry.durationSeconds) || 0.01)
        : Math.max(0.01, clipDur / usable.length);
      const timing = applySubtitleCueOverlayTiming({ startTime, duration }, hasOverlay ? o : null);
      out.push({
        id: cueId,
        audioClipId: syntheticAudioId,
        sceneId: sid,
        videoClipId: vc.id,
        startTime: timing.startTime,
        duration: timing.duration,
        text: (hasOverlay && o.manualEdit && o.text) ? o.text : String(entry.text).trim(),
        muted: hasOverlay ? Boolean(o.muted) : false,
        manualEdit: Boolean(hasOverlay && o.manualEdit),
        manualTiming: timing.manualTiming,
      });
      covered.add(`${sid}:${idx}`);
    });
  });
  return out.sort((a, b) => (Number(a.startTime) || 0) - (Number(b.startTime) || 0));
}

/**
 * Rebuild subtitle lane 1 after script/transcript save so timeline cues match saved text.
 * Forces `text` on every audio-lane clip for the saved scene (including segment clips).
 */
function rebuildSubtitlesAfterSceneScriptSave(audioTracks, scenesSnapshot, sceneId, text, savedVideoClip = null) {
  const norm = normalizeEditorSceneId(sceneId);
  const trimmed = String(text || '').trim();
  if (!norm || !trimmed) return (prevRows) => prevRows;

  if (isSongBasedEditorScenes(scenesSnapshot)) {
    return (prevRows) => {
      const prev = (prevRows || [])[0] || [];
      const cueId = `subtitle-song-stanza-${norm}`;
      let hit = false;
      const next = prev.map((c) => {
        if (normalizeEditorSceneId(c.sceneId) !== norm && c.id !== cueId) return c;
        hit = true;
        return { ...c, text: trimmed, manualEdit: false };
      });
      if (!hit && savedVideoClip) {
        const scene = (scenesSnapshot || []).find((s) => String(s.id) === norm);
        const window = scene ? songSegmentWindow(scene) : { start: savedVideoClip.startTime || 0, duration: savedVideoClip.duration || 5 };
        next.push({
          id: cueId,
          audioClipId: 'song-master-audio',
          sceneId: norm,
          videoClipId: savedVideoClip.id,
          startTime: (window.start ?? Number(savedVideoClip.startTime)) || 0,
          duration: Math.max(0.01, (window.duration ?? Number(savedVideoClip.duration)) || 5),
          text: trimmed,
          muted: false,
          manualEdit: false,
        });
      }
      return [next.sort((a, b) => (Number(a.startTime) || 0) - (Number(b.startTime) || 0)), ...(prevRows || []).slice(1)];
    };
  }

  const lane0 = (audioTracks || [])[0] || [];
  const sceneById = Object.fromEntries((scenesSnapshot || []).map((s) => [String(s.id), s]));

  return (prevRows) => {
    const prev = (prevRows || [])[0] || [];
    const overlay = Object.fromEntries(
      prev.filter((c) => c && c.audioClipId != null).map((c) => [c.audioClipId, { text: c.text, muted: c.muted }]),
    );

    const next = lane0.map((ac) => {
      const sid = normalizeEditorSceneId(ac.sceneId);
      const scene = sid ? sceneById[sid] : null;
      const o = overlay[ac.id];
      const hasOverlay = Object.prototype.hasOwnProperty.call(overlay, ac.id);
      const isSavedScene = sid === norm;
      const defaultText = isSavedScene
        ? trimmed
        : subtitleTextForAudioClip(ac, scene);
      return {
        id: `subtitle-${ac.id}`,
        audioClipId: ac.id,
        sceneId: ac.sceneId,
        startTime: Number(ac.startTime) || 0,
        duration: clipAudioTimelineDuration(ac, 0, masterAudioPlaybackSpeedRef.current),
        text: isSavedScene ? trimmed : (hasOverlay && o.manualEdit ? o.text : defaultText),
        muted: hasOverlay ? Boolean(o.muted) : false,
        manualEdit: isSavedScene ? false : Boolean(hasOverlay && o.manualEdit),
      };
    });

    const laneAudioIds = new Set(lane0.map((ac) => ac.id));
    const hasSavedSceneCue = next.some((c) => normalizeEditorSceneId(c.sceneId) === norm);

    if (!hasSavedSceneCue) {
      const prevForScene = prev.filter((c) => normalizeEditorSceneId(c.sceneId) === norm);
      if (prevForScene.length) {
        prevForScene.forEach((sub) => {
          if (sub.audioClipId && laneAudioIds.has(sub.audioClipId)) return;
          next.push({ ...sub, text: trimmed });
        });
      } else if (savedVideoClip) {
        const audioClipId = `audio-${norm}`;
        next.push({
          id: `subtitle-${audioClipId}`,
          audioClipId,
          sceneId: savedVideoClip.sceneId || norm,
          startTime: Number(savedVideoClip.startTime) || 0,
          duration: Math.max(
            0.01,
            Number(savedVideoClip.playbackDuration ?? savedVideoClip.duration) || 5,
          ),
          text: trimmed,
          muted: false,
        });
      }
    }

    return [next, ...(prevRows || []).slice(1)];
  };
}

function shortenScriptToTargetDuration(text, currentSeconds, targetSeconds) {
  const raw = String(text || '').replace(/\s+/g, ' ').trim();
  if (!raw) return '';
  const cur = Number(currentSeconds) || 0;
  const tgt = Number(targetSeconds) || 0;
  if (!(cur > 0) || !(tgt > 0) || tgt >= cur * 0.98) return raw;
  const ratio = Math.max(0.2, Math.min(0.95, tgt / cur));
  const words = raw.split(' ').filter(Boolean);
  if (words.length <= 6) {
    const keep = Math.max(3, Math.floor(words.length * ratio));
    return words.slice(0, keep).join(' ');
  }
  const targetWords = Math.max(6, Math.floor(words.length * ratio));
  const sentences = raw.match(/[^.!?]+[.!?]?/g)?.map((s) => s.trim()).filter(Boolean) || [raw];
  const out = [];
  let count = 0;
  for (const s of sentences) {
    const c = s.split(/\s+/).filter(Boolean).length;
    if (count > 0 && count + c > targetWords) break;
    out.push(s);
    count += c;
    if (count >= targetWords) break;
  }
  if (!out.length) return words.slice(0, targetWords).join(' ');
  return out.join(' ').trim();
}

/** Map playhead time within timeline clip window → word timestamp clock (source timebase). */
function wordHighlightClock(localTimeInClip, clip) {
  const cap = clipVideoTimelineDuration(clip);
  const spd = clipVideoPlaybackSpeed(clip);
  const words = resolveClipWordTimestamps(clip);
  if (!words.length || cap <= 0) return 0;
  const t = Math.max(0, Math.min(cap, Number(localTimeInClip) || 0));
  const srcTime = t * spd;
  const tsEnd = Math.max(words[words.length - 1].end || 0, 1e-6);
  const srcSpan = clipSourcePlaybackDuration(clip);
  if (Math.abs(tsEnd - srcSpan) < 0.02) return srcTime;
  return (srcTime / srcSpan) * tsEnd;
}

function RollingScriptHighlightedWords({ wordTimestamps, clock }) {
  if (!wordTimestamps?.length) return null;
  let activeIdx = wordTimestamps.findIndex((w) => clock >= w.start && clock < w.end);
  if (activeIdx < 0 && wordTimestamps.length) {
    const last = wordTimestamps[wordTimestamps.length - 1];
    if (clock >= last.start) activeIdx = wordTimestamps.length - 1;
  }
  return (
    <p className="leading-relaxed">
      {wordTimestamps.map((w, i) => (
        <span key={`${i}-${w.start}-${w.word}`}>
          {i > 0 ? ' ' : ''}
          <span
            className="rounded px-0.5 transition-colors duration-75"
            style={{
              background: i === activeIdx ? 'var(--primary)' : 'transparent',
              color: i === activeIdx ? 'var(--text-primary)' : 'inherit',
              fontWeight: i === activeIdx ? 600 : 400,
            }}
          >
            {w.word}
          </span>
        </span>
      ))}
    </p>
  );
}

const sleep = (ms) => new Promise(resolve => setTimeout(resolve, ms));

function AudioWaveform({ url, waveformUrl, barCount = 48 }) {
  const [peaks, setPeaks] = useState([]);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    let cancelled = false;
    if (!url && !waveformUrl) {
      setPeaks([]);
      setFailed(false);
      return undefined;
    }

    const loadWaveform = async () => {
      const decodePeaksFromUrl = async () => {
        if (!url) return false;
        const playback = mediaUrlForPlayback(url);
        let arrayBuffer;
        if (playback && (playback.startsWith('/api/') || playback.includes('/api/media/'))) {
          const apiPath = playback.startsWith('/')
            ? playback
            : playback.replace(/^https?:\/\/[^/]+/, '');
          const { data } = await api.get(apiPath, { responseType: 'arraybuffer' });
          arrayBuffer = data;
        } else {
          const response = await fetch(playback, { credentials: 'include' });
          if (!response.ok) throw new Error(`fetch ${response.status}`);
          arrayBuffer = await response.arrayBuffer();
        }
        const AudioContextClass = window.AudioContext || window.webkitAudioContext;
        if (!AudioContextClass) throw new Error('AudioContext unavailable');
        const context = new AudioContextClass();
        const buffer = await context.decodeAudioData(arrayBuffer.slice(0));
        await context.close?.();
        const channel = buffer.getChannelData(0);
        const samplesPerBar = Math.max(1, Math.floor(channel.length / barCount));
        const nextPeaks = Array.from({ length: barCount }, (_, barIndex) => {
          const start = barIndex * samplesPerBar;
          const end = Math.min(channel.length, start + samplesPerBar);
          let peak = 0;
          for (let idx = start; idx < end; idx += 1) {
            peak = Math.max(peak, Math.abs(channel[idx]));
          }
          return Math.max(0.02, Math.min(1, peak));
        });
        if (!cancelled) {
          setPeaks(nextPeaks);
          setFailed(false);
        }
        return true;
      };

      try {
        if (waveformUrl) {
          try {
            const { data } = await api.get(waveformUrl, { params: { peaks: barCount } });
            const nextPeaks = Array.isArray(data.peaks) ? data.peaks : [];
            if (!nextPeaks.length) {
              if (await decodePeaksFromUrl()) return;
              const flat = Array.from({ length: barCount }, () => 0.06);
              if (!cancelled) {
                setPeaks(flat);
                setFailed(false);
              }
              return;
            }
            if (!cancelled) {
              setPeaks(nextPeaks);
              setFailed(false);
            }
            return;
          } catch {
            if (await decodePeaksFromUrl()) return;
            throw new Error('waveform api failed');
          }
        }

        if (await decodePeaksFromUrl()) return;
      } catch {
        if (!cancelled) {
          setPeaks([]);
          setFailed(true);
        }
      }
    };

    loadWaveform();
    return () => {
      cancelled = true;
    };
  }, [url, waveformUrl, barCount]);

  if (failed) {
    return (
      <div className="absolute inset-x-7 bottom-1.5 top-6 flex items-center justify-center pointer-events-none">
        <span className="text-[9px] font-semibold text-[#ec4899]/70">waveform unavailable</span>
      </div>
    );
  }

  const displayPeaks = peaks.length ? peaks : [];
  return (
    <div className="absolute inset-x-7 bottom-1.5 top-6 flex items-center gap-px opacity-75 pointer-events-none">
      {displayPeaks.map((peak, index) => (
        <span
          // eslint-disable-next-line react/no-array-index-key
          key={index}
          className="flex-1 rounded-full bg-[#ec4899]"
          style={{ height: `${Math.max(3, peak * 26)}px` }}
        />
      ))}
    </div>
  );
}

function normalizeTrackNoOverlap(track) {
  let cursor = 0;
  return [...track]
    .sort((a, b) => (a.startTime || 0) - (b.startTime || 0))
    .map((clip) => {
      const nextStart = Math.max(0, Number(clip.startTime) || 0, cursor);
      const nextClip = { ...clip, startTime: nextStart };
      cursor = clipEndTime(nextClip);
      return nextClip;
    });
}

/** Pack clips back-to-back on one lane; keeps the first clip’s start time (leading space unchanged). */
function removeGapsFromTrack(track) {
  if (!Array.isArray(track) || !track.length) return Array.isArray(track) ? track : [];
  const sorted = [...track].sort((a, b) => (Number(a.startTime) || 0) - (Number(b.startTime) || 0));
  let cursor = Number(sorted[0].startTime) || 0;
  return sorted.map((clip) => {
    const packed = { ...clip, startTime: cursor };
    cursor = clipEndTime(packed);
    return packed;
  });
}

/** Pack clips back-to-back starting at t=0 (removes leading gaps and all inter-clip gaps/overlaps). */
function packTrackFromZero(track) {
  if (!Array.isArray(track) || !track.length) return Array.isArray(track) ? track : [];
  const sorted = [...track].sort((a, b) => (Number(a.startTime) || 0) - (Number(b.startTime) || 0));
  let cursor = 0;
  return sorted.map((clip) => {
    const packed = { ...clip, startTime: cursor };
    cursor = clipEndTime(packed);
    return packed;
  });
}

function rippleResizeTrack(track, clipId, originalEnd, updatedClip) {
  const nextEnd = clipEndTime(updatedClip);
  const delta = nextEnd - originalEnd;
  const shifted = track.map((clip) => {
    if (clip.id === clipId) return updatedClip;
    if ((Number(clip.startTime) || 0) >= originalEnd - 0.001) {
      return { ...clip, startTime: Math.max(0, (Number(clip.startTime) || 0) + delta) };
    }
    return clip;
  });
  return normalizeTrackNoOverlap(shifted);
}

function shiftTrackAfter(track, originalEnd, delta, excludeClipId = null) {
  if (Math.abs(delta) < 0.001) return normalizeTrackNoOverlap(track);
  const shifted = track.map((clip) => {
    if (clip.id === excludeClipId) return clip;
    if ((Number(clip.startTime) || 0) >= originalEnd - 0.001) {
      return { ...clip, startTime: Math.max(0, (Number(clip.startTime) || 0) + delta) };
    }
    return clip;
  });
  return normalizeTrackNoOverlap(shifted);
}

function rippleResizeTracks(tracks, trackIdx, clipId, originalEnd, updatedClip) {
  const delta = clipEndTime(updatedClip) - originalEnd;
  return tracks.map((track, idx) => {
    if (idx === trackIdx) {
      return rippleResizeTrack(track, clipId, originalEnd, updatedClip);
    }
    return shiftTrackAfter(track, originalEnd, delta);
  });
}

function trackTimelineGaps(track) {
  const gaps = [];
  let cursor = 0;
  [...track]
    .sort((a, b) => (Number(a.startTime) || 0) - (Number(b.startTime) || 0))
    .forEach((clip) => {
      const start = Number(clip.startTime) || 0;
      if (start - cursor > MIN_TIMELINE_GAP_SEC) {
        gaps.push({
          id: `gap-${cursor.toFixed(3)}-${start.toFixed(3)}`,
          startTime: cursor,
          duration: start - cursor,
          endTime: start,
        });
      }
      cursor = Math.max(cursor, clipEndTime(clip));
    });
  return gaps;
}

const formatTime = (s) => {
  if (!isFinite(s) || s < 0) return '0:00';
  const m = Math.floor(s / 60);
  const sec = Math.floor(s % 60);
  return `${m}:${String(sec).padStart(2, '0')}`;
};

const timelineEndFromVideoClips = (clips) => {
  if (!clips?.length) return 0;
  return clips.reduce(
    (m, c) =>
      Math.max(m, (Number(c.startTime) || 0) + clipVideoTimelineDuration(c)),
    0,
  );
};

const buildMusicTracksFromStoryUrl = (musicUrl, totalSeconds, opts = {}) => {
  const dur = Number(totalSeconds);
  const narrationPid = opts.narrationProjectId || null;
  if (!musicUrl || !(dur > 0)) return [[]];
  return [[{
    id: 'story-background-music',
    name: 'Background music',
    url: mediaUrlForPlayback(musicUrl),
    source: 'background_music',
    ...(narrationPid
      ? { waveformUrl: `/narration/projects/${narrationPid}/story-background-music/waveform` }
      : {}),
    startTime: 0,
    duration: dur,
    sourceDuration: dur,
    volume: 0.22,
    muted: false,
  }]];
};

const buildSceneSfxMusicClips = (scenes, videoClips, opts = {}) => {
  const slots = sceneSlotsFromClips(videoClips);
  return (scenes || [])
    .filter((s) => s && s.id && s.sound_effect_url)
    .map((s, idx) => {
      const sid = String(s.id);
      const slot = slots.get(sid);
      const startTime = slot ? slot.start : 0;
      const dur = sceneSfxSeconds(
        positiveDuration(s.sound_effect_duration_seconds, s.duration_seconds, s.audio_duration_seconds, 5),
        slot,
      );
      return {
        id: `scene-sfx-${sid}`,
        name: `SFX Scene ${s.scene_number || idx + 1}`,
        sceneId: sid,
        url: mediaUrlForPlayback(s.sound_effect_url),
        startTime,
        duration: dur,
        sourceDuration: dur,
        trimStart: 0,
        volume: 0.28,
        muted: false,
        sourceType: 'scene_sfx',
      };
    });
};

const mergeSceneSfxIntoMusicTracks = (musicTracks, scenes, videoClips, opts = {}) => (
  placeSceneSfxOnOwnLane(musicTracks, buildSceneSfxMusicClips(scenes, videoClips, opts))
);

const positiveDuration = (...values) => {
  for (const value of values) {
    const numeric = Number(value);
    if (Number.isFinite(numeric) && numeric > 0) return numeric;
  }
  return 5;
};

const sceneAudioDurationSeconds = (scene) => (
  scene?.audio_url
    ? positiveDuration(scene.audio_duration_seconds, scene.audio_duration, scene.duration_seconds, scene.duration)
    : 0
);

const sceneVisualDurationSeconds = (scene) => (
  positiveDuration(scene?.video_duration_seconds, scene?.duration_seconds, scene?.duration)
);

const sceneSlotDurationSeconds = (scene) => (
  Math.max(sceneVisualDurationSeconds(scene), sceneAudioDurationSeconds(scene) || 0, MIN_CLIP_PLAYBACK_SEC)
);

/** Scene block length = longest narration, segment audio span, or video file on the segment. */
const sceneBlockDurationSeconds = (scene) => {
  let segEnd = 0;
  (Array.isArray(scene?.audio_timeline) ? scene.audio_timeline : []).forEach((t) => {
    if (!t?.segment_audio_url) return;
    segEnd = Math.max(segEnd, (Number(t.start_seconds) || 0) + (Number(t.duration_seconds) || 0));
  });
  return Math.max(MIN_CLIP_PLAYBACK_SEC, sceneSlotDurationSeconds(scene), segEnd);
};

/** Probed or server-stored mp4 length only — never the narration audio slot. */
const sceneVideoFileDurationSeconds = (scene) => {
  const n = Number(scene?.video_duration_seconds);
  return Number.isFinite(n) && n > 0 ? n : 0;
};

const mediaLabelForScene = (scene, kind, source, index) => {
  const base = scene?.title || `Scene ${scene?.scene_number || index + 1}`;
  if (source === 'current') return `${base} ${kind}`;
  return `${base} ${kind} (${source || 'history'})`;
};

const addMediaLibraryItem = (items, seen, item) => {
  if (!item?.url) return;
  const key = `${item.type}:${item.url}`;
  if (seen.has(key)) return;
  seen.add(key);
  items.push(item);
};

const sceneMediaToLibraryItems = (scene, index) => {
  const items = [];
  const seen = new Set();
  ['image', 'video', 'audio'].forEach((kind) => {
    const currentUrl = scene?.[`${kind}_url`];
    const duration = Number(scene?.audio_duration || scene?.duration_seconds || scene?.duration) || 5;
    addMediaLibraryItem(items, seen, {
      id: `${scene.id || index}-${kind}-current`,
      name: mediaLabelForScene(scene, kind, 'current', index),
      url: currentUrl,
      type: kind,
      thumbnail: kind === 'image' ? currentUrl : null,
      duration,
      source: 'current',
      sceneId: scene?.id,
    });

    (scene?.[`${kind}_history`] || []).forEach((entry, historyIndex) => {
      const url = typeof entry === 'string' ? entry : entry?.url;
      const source = typeof entry === 'string' ? 'history' : entry?.source || 'history';
      addMediaLibraryItem(items, seen, {
        id: `${scene.id || index}-${kind}-history-${historyIndex}`,
        name: mediaLabelForScene(scene, kind, source, index),
        url,
        type: kind,
        thumbnail: kind === 'image' ? url : null,
        duration,
        source,
        sceneId: scene?.id,
      });
    });
  });
  return items;
};

const EXTEND_MATCH_EPSILON_SEC = 0.12;

/** Resolve trim/cap fields for a narration/story video clip from server segment + optional saved clip. */
function resolveVideoClipMediaTiming({ scene, slotDur, existingClip = null, forceUrlChanged = false }) {
  const audioSlot = Math.max(
    MIN_CLIP_PLAYBACK_SEC,
    Number(slotDur) || sceneAudioDurationSeconds(scene) || 5,
  );
  const hasVideo = Boolean(scene?.video_url || existingClip?.videoUrl);
  const sceneVideoDur = sceneVideoFileDurationSeconds(scene);
  const sceneTrimEnd = Number(scene?.trim_end_seconds);
  const sceneTrimStart = Math.max(0, Number(scene?.trim_start_seconds) || 0);

  const prevUrl = existingClip?.videoUrl ? mediaUrlForPlayback(existingClip.videoUrl) : '';
  const nextUrl = scene?.video_url ? mediaUrlForSegmentField(scene, 'video_url') : '';
  const sceneUpdated = scene?.updated_at && scene.updated_at !== existingClip?.sceneMediaUpdatedAt;
  const urlChanged = Boolean(
    forceUrlChanged
    || sceneUpdated
    || (nextUrl && (!prevUrl || mediaUrlIdentityKey(prevUrl) !== mediaUrlIdentityKey(nextUrl))),
  );

  let videoFileDur = Math.max(
    sceneVideoDur,
    sceneTrimEnd > 0 ? sceneTrimEnd : 0,
  );
  if (!videoFileDur && existingClip && !urlChanged) {
    videoFileDur = Math.max(
      Number(existingClip.videoMediaDurationSeconds) || 0,
      Number(existingClip.videoSourceDurationMax) || 0,
    );
  }

  const videoCoversAudio = hasVideo && (
    (videoFileDur > 0 && videoFileDur >= audioSlot - EXTEND_MATCH_EPSILON_SEC)
    || (sceneTrimEnd > 0 && sceneTrimEnd >= audioSlot - EXTEND_MATCH_EPSILON_SEC)
  );

  const hasUserTrim = Boolean(
    existingClip?.videoUrl
    && !urlChanged
    && !forceUrlChanged
    && clipHasUserVideoTrim(existingClip, videoFileDur || audioSlot),
  );

  let trimStart = 0;
  let trimEnd = audioSlot;
  let playbackSpeed = Number(existingClip?.videoPlaybackSpeed) || 1;

  if (hasVideo) {
    if (hasUserTrim) {
      const cap = Math.max(videoFileDur, clipVideoMediaCapSeconds(existingClip));
      trimStart = Math.max(0, Number(existingClip.trimStart) || 0);
      trimEnd = Math.min(
        cap,
        existingClip.trimEnd != null ? Number(existingClip.trimEnd) : cap,
      );
      trimStart = Math.max(0, Math.min(trimStart, trimEnd - MIN_CLIP_PLAYBACK_SEC));
      playbackSpeed = Number(existingClip.videoPlaybackSpeed) || 1;
    } else if (videoCoversAudio || urlChanged) {
      trimStart = 0;
      trimEnd = Math.max(videoFileDur || audioSlot, audioSlot);
      videoFileDur = Math.max(videoFileDur, trimEnd);
      playbackSpeed = 1;
    } else if (videoFileDur > 0) {
      trimStart = Math.max(DEFAULT_VIDEO_TRIM_START, sceneTrimStart || Number(existingClip?.trimStart) || 0);
      trimEnd = Math.min(audioSlot, Math.max(sceneTrimEnd || videoFileDur, videoFileDur));
      trimStart = Math.max(0, Math.min(trimStart, trimEnd - MIN_CLIP_PLAYBACK_SEC));
    } else {
      trimStart = 0;
      trimEnd = audioSlot;
    }
  }

  return {
    duration: audioSlot,
    trimStart,
    trimEnd,
    videoMediaDurationSeconds: videoFileDur > 0 ? videoFileDur : undefined,
    videoSourceDurationMax: videoFileDur > 0 ? videoFileDur : 0,
    videoPlaybackSpeed: playbackSpeed,
    playbackDuration: hasVideo
      ? Math.max(MIN_CLIP_PLAYBACK_SEC, trimEnd - trimStart)
      : audioSlot,
  };
}

// Convert a scene document to a VideoClip timeline object
const sceneToClip = (scene, startTime) => {
  const slotDur = sceneSlotDurationSeconds(scene);
  const timing = resolveVideoClipMediaTiming({ scene, slotDur });
  const script = scenePrimaryScriptText(scene);
  const motionFields = segmentToClipMotionFields(scene);
  const sceneMedia = sceneDocumentMediaPlayback(scene);
  const clip = clampVideoClipTrimToMediaCap({
    id: scene.id,
    name: scene.title || `Scene ${scene.scene_number || ''}`,
    sceneId: scene.id,
    videoUrl: sceneMedia.videoUrl || null,
    imageUrl: sceneMedia.imageUrl || null,
    audioUrl: sceneMedia.audioUrl || null,
    startTime,
    duration: timing.duration,
    videoSourceDurationMax: timing.videoSourceDurationMax,
    videoMediaDurationSeconds: timing.videoMediaDurationSeconds,
    videoPlaybackSpeed: timing.videoPlaybackSpeed,
    playbackDuration: timing.playbackDuration,
    trimStart: timing.trimStart,
    trimEnd: timing.trimEnd,
    sceneText: scene.scene_text || '',
    narrationText: script,
    wordTimestamps: Array.isArray(scene.word_timestamps) && scene.word_timestamps.length
      ? scene.word_timestamps
      : estimateWordTimestamps(script, timing.playbackDuration),
    muted: false,
    volume: DEFAULT_VIDEO_EMBEDDED_AUDIO_VOLUME,
    motionPreset: motionFields.motionPreset || 'ken_burns',
    motionStrength: motionFields.motionStrength || 0.12,
    audioMotionTimeline: motionFields.audioMotionTimeline,
    beatSyncMetadata: motionFields.beatSyncMetadata,
  });
  return clip;
};

/** Preserve timeline placement from a saved local overlay but refresh script/media from latest scene docs. */
function mergeTimelineClipWithLatestScene(clip, scene, { freezeMedia = false } = {}) {
  if (!clip || !scene) return clip;
  const linkedId = timelineClipLinkedSceneId(clip);
  if (String(linkedId) !== String(scene.id || '')) return clip;

  if (clip.visualBeat != null) {
    const beats = visualBeatsWithImages(scene);
    const beat = beats[Number(clip.visualBeat)] || null;
    const hold = Number(beat?.duration) || Number(clip.duration) || 0;
    const sceneImage = sceneDocumentMediaPlayback(scene).imageUrl || null;
    return {
      ...clip,
      imageUrl: beat?.imageUrl || sceneImage || clip.imageUrl || null,
      image_s3_url: null,
      image_local_url: null,
      videoUrl: null,
      video_s3_url: null,
      video_local_url: null,
      duration: hold,
      playbackDuration: hold,
      timelineSlotDuration: hold,
      trimStart: 0,
      trimEnd: hold,
      audioUrl: clip.audioUrl || mediaUrlForSegmentField(scene, 'audio_url') || null,
      sceneMediaUpdatedAt: scene.updated_at || clip.sceneMediaUpdatedAt,
    };
  }

  if (freezeMedia) {
    const script = scenePrimaryScriptText(scene);
    const frozen = mergeTimelineClipFrozenFields(clip, scene, {
      freezeMedia: true,
      script,
      name: scene.title || scene.scene_title || clip.name,
      resolveSceneMediaUrl: (row, field) => (
        row ? mediaUrlForSegmentField(row, field) : null
      ),
    });
    return clampVideoClipTrimToMediaCap({
      ...frozen,
      wordTimestamps:
        Array.isArray(scene.word_timestamps) && scene.word_timestamps.length
          ? scene.word_timestamps
          : clip.wordTimestamps,
    });
  }

  if (scene.pitch_recording_segment && (clip.videoUrl || scene.video_url)) {
    const script = scenePrimaryScriptText(scene);
    const sceneImage = sceneDocumentMediaPlayback(scene).imageUrl || null;
    const sceneAudio = scene.audio_url ? mediaUrlForSegmentField(scene, 'audio_url') : null;
    const videoUrl = resolveSceneVideoUrlForTimelineClip(scene, clip);
    const merged = mergePitchTimelineClipWithScene(clip, scene, {
      name: scene.title || scene.scene_title || clip.name,
      sceneText: scene.scene_text || scene.narration_text || '',
      narrationText: script,
      videoUrl,
      imageUrl: sceneImage,
      audioUrl: sceneAudio,
      sceneMediaUpdatedAt: scene.updated_at || clip.sceneMediaUpdatedAt || null,
    });
    return clampVideoClipTrimToMediaCap(merged);
  }

  // Multishot slices keep their own still; don't replace it with the scene master image.
  if (clip.parentSceneId != null && clip.multishotPart != null) {
    const script = scenePrimaryScriptText(scene);
    return {
      ...clip,
      name: scene.title || scene.scene_title || clip.name,
      sceneText: scene.scene_text || scene.narration_text || '',
      narrationText: script,
      wordTimestamps:
        Array.isArray(scene.word_timestamps) && scene.word_timestamps.length
          ? scene.word_timestamps
          : clip.wordTimestamps,
      ...segmentToClipMotionFields(scene),
    };
  }

  const slotDur = sceneSlotDurationSeconds(scene);
  const forceUrlChanged = Boolean(
    scene.updated_at && scene.updated_at !== clip.sceneMediaUpdatedAt,
  ) || sceneDocumentVideoUrlChanged(clip, scene) || sceneDocumentImageUrlChanged(clip, scene);
  const timing = resolveVideoClipMediaTiming({
    scene, slotDur, existingClip: clip, forceUrlChanged,
  });
  const script = scenePrimaryScriptText(scene);
  const sceneImage = sceneDocumentMediaPlayback(scene).imageUrl || null;
  const sceneAudio = scene.audio_url ? mediaUrlForSegmentField(scene, 'audio_url') : null;
  const videoUrl = resolveSceneVideoUrlForTimelineClip(scene, clip);
  return clampVideoClipTrimToMediaCap({
    ...clip,
    name: scene.title || scene.scene_title || clip.name,
    sceneText: scene.scene_text || scene.narration_text || '',
    narrationText: script,
    videoUrl,
    imageUrl: sceneImage || clip.imageUrl || null,
    image_s3_url: sceneImage ? null : (clip.image_s3_url || null),
    image_local_url: sceneImage ? null : (clip.image_local_url || null),
    audioUrl: sceneAudio,
    sceneMediaUpdatedAt: scene.updated_at || clip.sceneMediaUpdatedAt || null,
    duration: timing.duration,
    videoSourceDurationMax: forceUrlChanged
      ? (timing.videoSourceDurationMax || 0)
      : (timing.videoSourceDurationMax || (Number(clip.videoSourceDurationMax) || 0)),
    videoMediaDurationSeconds: forceUrlChanged
      ? timing.videoMediaDurationSeconds
      : (timing.videoMediaDurationSeconds ?? clip.videoMediaDurationSeconds),
    videoPlaybackSpeed: timing.videoPlaybackSpeed,
    playbackDuration: timing.playbackDuration,
    trimStart: timing.trimStart,
    trimEnd: timing.trimEnd,
    wordTimestamps:
      Array.isArray(scene.word_timestamps) && scene.word_timestamps.length
        ? scene.word_timestamps
        : estimateWordTimestamps(script, timing.playbackDuration),
    ...segmentToClipMotionFields(scene),
  });
}

function mergeAudioLaneClipWithLatestScene(ac, sceneById, { freezeMedia = false } = {}) {
  const rawSid = String(ac.sceneId ?? '').replace(/^audio-/, '').replace(/^seg-audio-/, '');
  const sid = rawSid.split('-seg-')[0];
  const scene = sid ? sceneById[sid] : null;
  if (!scene) return ac;
  if (freezeMedia) return ac;

  const lang = ac.languageCode ? String(ac.languageCode).toLowerCase() : '';
  if (lang) {
    const loc = localizedAudioForScene(scene, lang);
    const dubUrl = loc?.audio_url ? mediaUrlForPlayback(loc.audio_url) : null;
    const dubDur = Number(loc?.audio_duration) || 0;
    if (dubUrl) {
      return {
        ...ac,
        url: dubUrl,
        duration: dubDur > 0 ? dubDur : ac.duration,
        sourceDuration: dubDur > 0 ? dubDur : ac.sourceDuration,
        dubText: loc?.text || ac.dubText || '',
      };
    }
    return ac;
  }

  // Segment clips: seg-audio-${sceneId}-seg-${index}
  if (String(ac.id || '').startsWith(`seg-audio-${sid}-seg-`)) {
    const suffix = String(ac.id || '').slice(`seg-audio-${sid}-seg-`.length);
    const idx = Number(suffix);
    const timeline = Array.isArray(scene.audio_timeline) ? scene.audio_timeline : [];
    const seg = Number.isFinite(idx) ? timeline.find((t) => Number(t?.index) === idx) : null;
    const url = seg?.segment_audio_url ? mediaUrlForPlayback(seg.segment_audio_url) : null;
    const dur = Number(seg?.duration_seconds) || 0;
    return {
      ...ac,
      url: url || ac.url,
      duration: dur > 0 ? dur : ac.duration,
      sourceDuration: dur > 0 ? dur : ac.sourceDuration,
    };
  }

  // Default scene mix clip: audio-${sceneId}
  const fromScene = scene.audio_url ? mediaUrlForSegmentField(scene, 'audio_url') : null;
  const dur = sceneAudioDurationSeconds(scene);
  return {
    ...ac,
    url: fromScene || ac.url,
    duration: dur > 0 ? dur : ac.duration,
    sourceDuration: dur > 0 ? dur : ac.sourceDuration,
  };
}

function segmentWaveformUrl(narrationProjectId, sceneId) {
  if (!narrationProjectId || !sceneId) return null;
  return `/narration/projects/${narrationProjectId}/segments/${sceneId}/waveform`;
}

function bgMusicWaveformUrl(narrationProjectId) {
  if (!narrationProjectId) return null;
  return `/narration/projects/${narrationProjectId}/story-background-music/waveform`;
}

function ensureAudioClipWaveformUrl(clip, narrationProjectId) {
  if (!clip || clip.waveformUrl || !narrationProjectId) return clip;
  const sid = timelineClipLinkedSceneId(clip);
  if (!sid || sid.startsWith('media-') || sid.startsWith('audio-upload') || sid.startsWith('audio-drop')) {
    return clip;
  }
  const waveformUrl = segmentWaveformUrl(narrationProjectId, sid);
  return waveformUrl ? { ...clip, waveformUrl } : clip;
}

function ensureMusicClipWaveformUrl(clip, narrationProjectId) {
  if (!clip || clip.waveformUrl || !narrationProjectId) return clip;
  if (clip.id === 'story-background-music' || clip.source === 'background_music') {
    const waveformUrl = bgMusicWaveformUrl(narrationProjectId);
    return waveformUrl ? { ...clip, waveformUrl } : clip;
  }
  return clip;
}

function ensureAudioTracksWaveformUrls(tracks, narrationProjectId) {
  if (!narrationProjectId) return tracks;
  return (tracks || [[]]).map((tr) =>
    (tr || []).map((c) => ensureAudioClipWaveformUrl(c, narrationProjectId)),
  );
}

function ensureMusicTracksWaveformUrls(tracks, narrationProjectId) {
  if (!narrationProjectId) return tracks;
  return (tracks || [[]]).map((tr) =>
    (tr || []).map((c) => ensureMusicClipWaveformUrl(c, narrationProjectId)),
  );
}

function buildSegmentAudioLaneClipsForScenes(scenes, videoClips, narrationProjectId = null) {
  const bySceneStart = {};
  (videoClips || []).forEach((c) => {
    const sid = String(timelineClipLinkedSceneId(c) || c.sceneId || c.id || '');
    if (!sid) return;
    const t = Number(c.startTime) || 0;
    if (bySceneStart[sid] == null || t < bySceneStart[sid]) bySceneStart[sid] = t;
  });
  const out = [];
  (scenes || []).forEach((scene) => {
    const sid = scene?.id != null ? String(scene.id) : '';
    if (!sid) return;
    const timeline = Array.isArray(scene.audio_timeline) ? scene.audio_timeline : [];
    const baseStart = Number(bySceneStart[sid] ?? 0);
    timeline
      .filter((t) => t && t.segment_audio_url)
      .sort((a, b) => (Number(a.index) || 0) - (Number(b.index) || 0))
      .forEach((t) => {
        const idx = Number(t.index) || 0;
        const speaker = String(t.speaker || '').trim();
        const kind = String(t.kind || '').toLowerCase();
        const label = kind === 'narration'
          ? 'Narrator'
          : (speaker || 'Dialogue');
        const startOffset = Number(t.start_seconds) || 0;
        const dur = Number(t.duration_seconds) || 0;
        out.push({
          id: `seg-audio-${sid}-seg-${idx}`,
          name: `Scene ${scene.scene_number || ''} · ${label}`.trim(),
          sceneId: sid,
          url: mediaUrlForPlayback(t.segment_audio_url),
          startTime: baseStart + startOffset,
          duration: dur > 0 ? dur : 1,
          sourceDuration: dur > 0 ? dur : 1,
          volume: 0.8,
          muted: false,
          sourceType: 'scene_segment',
          ...(narrationProjectId ? { waveformUrl: segmentWaveformUrl(narrationProjectId, sid) } : {}),
        });
      });
  });
  return out;
}

/** Restore narration audio lanes / clip audioUrl from scene snapshots when loading a saved version. */
function reconcileLoadedProjectDocSceneAudio(doc, urlForPlayback) {
  const raw = JSON.parse(JSON.stringify(doc));
  const scenes = Array.isArray(raw.scenes) ? raw.scenes : [];
  let vTracks = raw.editor?.videoTracks || [[]];
  let aTracks = raw.editor?.audioTracks || [[]];
  const narrationPid =
    typeof raw.storyId === 'string' && raw.storyId.startsWith('narration-')
      ? raw.storyId.slice('narration-'.length)
      : null;

  scenes.forEach((scene) => {
    const sid = scene.id != null ? String(scene.id) : '';
    const storedAudio = scene.audio_url;
    if (!sid || !storedAudio) return;
    const playable = urlForPlayback(storedAudio);
    const dur =
      Number(scene.audio_duration || scene.audio_duration_seconds || scene.duration_seconds) || 5;

    vTracks = vTracks.map((tr) =>
      tr.map((c) =>
        String(c.sceneId || '') === sid ? { ...c, audioUrl: c.audioUrl || playable } : c,
      ),
    );

    let laneHit = null;
    aTracks.forEach((tr, ti) => {
      tr.forEach((a, ai) => {
        if (String(a.sceneId ?? '').replace(/^audio-/, '') === sid) laneHit = { ti, ai };
      });
    });

    if (laneHit != null) {
      const tr = [...aTracks[laneHit.ti]];
      const a = tr[laneHit.ai];
      tr[laneHit.ai] = {
        ...a,
        url: a.url || playable,
        duration: Number(a.duration) > 0 ? a.duration : dur,
        sourceDuration: Number(a.sourceDuration) > 0 ? a.sourceDuration : dur,
      };
      aTracks = aTracks.map((t, i) => (i === laneHit.ti ? tr : [...t]));
    } else {
      const vFlat = vTracks.flat();
      const vc = vFlat.find((c) => String(c.sceneId || '') === sid);
      const startTime = Number(vc?.startTime ?? 0);
      const newClip = {
        id: `audio-${sid}`,
        name: vc?.name || 'Narration',
        sceneId: sid,
        url: playable,
        startTime,
        duration: dur,
        sourceDuration: dur,
        volume: 0.8,
        muted: false,
      };
      if (narrationPid) {
        newClip.waveformUrl = `/narration/projects/${narrationPid}/segments/${sid}/waveform`;
      }
      if (!aTracks.length) aTracks = [[]];
      aTracks = aTracks.map((tr, i) => (i === 0 ? [...tr, newClip] : [...tr]));
    }
  });

  raw.editor = { ...raw.editor, videoTracks: vTracks, audioTracks: aTracks };
  return raw;
}

/** Append timeline clips for server scenes that are not yet on the saved video tracks (e.g. after Extend story). */
function appendMissingScenesToVideoTracks(videoTracks, sortedScenes) {
  const tracks = JSON.parse(JSON.stringify(videoTracks?.length ? videoTracks : [[]]));
  const flat = tracks.flat();
  const onTimeline = new Set(
    flat.map((c) => timelineClipLinkedSceneId(c)).filter(Boolean),
  );
  const missing = [...(sortedScenes || [])]
    .filter((s) => s?.id && !onTimeline.has(String(s.id)))
    .sort((a, b) => (Number(a.scene_number) || 0) - (Number(b.scene_number) || 0));
  if (!missing.length) {
    return { videoTracks: tracks, appendedCount: 0 };
  }

  let startTime = timelineEndFromVideoClips(flat);
  const newClips = [];
  for (const scene of missing) {
    const clip = sceneToClip(scene, startTime);
    newClips.push(clip);
    startTime = timelineEndFromVideoClips([...flat, ...newClips]);
  }
  tracks[0] = [...(tracks[0] || []), ...newClips];
  return { videoTracks: tracks, appendedCount: missing.length };
}

/** Narration projects: one mix clip per segment with `audio_url`, aligned to video lane start times. */
function buildNarrationMixAudioLane(scenes, videoClips, narrationProjectId) {
  const byStart = Object.fromEntries(
    (videoClips || []).map((c) => [String(c.sceneId || c.id), Number(c.startTime) || 0]),
  );
  const out = [];
  (scenes || []).forEach((scene) => {
    if (!scene?.id || !scene.audio_url) return;
    const sid = String(scene.id);
    const dur = sceneAudioDurationSeconds(scene) || sceneSlotDurationSeconds(scene) || 5;
    const clip = {
      id: `audio-${sid}`,
      name: scene.title || `Segment ${scene.scene_number || ''}`.trim(),
      sceneId: sid,
      url: mediaUrlForSegmentField(scene, 'audio_url'),
      startTime: byStart[sid] ?? 0,
      duration: dur,
      sourceDuration: dur,
      volume: 0.8,
      muted: false,
    };
    if (narrationProjectId) {
      clip.waveformUrl = segmentWaveformUrl(narrationProjectId, sid);
    }
    out.push(clip);
  });
  return out.sort((a, b) => (Number(a.startTime) || 0) - (Number(b.startTime) || 0));
}

/** Per-line clips when they exist; otherwise one mix clip per scene that has generated speech. */
function buildStorySpeechAudioLane(scenes, videoClips, narrationProjectId = null) {
  const segments = buildSegmentAudioLaneClipsForScenes(scenes, videoClips, narrationProjectId);
  if (segments.length) return segments;
  return buildNarrationMixAudioLane(scenes, videoClips, narrationProjectId);
}

/** Keep a saved audio lane. Fill it from generated scene audio when that lane is empty. */
function audioTracksWithStorySpeech(savedAudioTracks, scenes, videoClips, narrationProjectId = null) {
  const saved = savedAudioTracks?.length ? savedAudioTracks : [[]];
  const speech = buildStorySpeechAudioLane(scenes, videoClips, narrationProjectId);
  if (speech.some((clip) => clip?.sourceType === 'scene_segment')) {
    return [speech, ...saved.slice(1)];
  }
  const savedPrimaryHasAudio = (saved[0] || []).some((clip) => clip?.url);
  if (!savedPrimaryHasAudio && speech.length) return [speech, ...saved.slice(1)];
  return saved;
}

/** Keep translation / extra audio lanes when rebuilding the primary narration lane. */
function resolveNarrationEditorAudioTracks(scenes, flatVideo, projectId, savedAudioTracks, savedMeta) {
  if (isSongBasedEditorScenes(scenes)) {
    const master = buildSongMasterAudioLaneFromScenes(
      scenes,
      mediaUrlForPlayback,
      projectId,
      scenes?.[0]?.song_timeline_duration,
    );
    if (master.length) {
      return ensureAudioTracksWaveformUrls(
        appendSavedExtraAudioLanes(master, savedAudioTracks || [[]], savedMeta, scenes, projectId),
        projectId,
      );
    }
  }

  const saved = savedAudioTracks || [[]];
  const segLane = buildSegmentAudioLaneClipsForScenes(scenes, flatVideo, projectId);
  let primary = saved[0] || [];
  const hasSavedClips = primary.some((c) => c?.url);

  if (!hasSavedClips) {
    if (segLane.length) primary = segLane;
    else if (projectId) {
      const mixLane = buildNarrationMixAudioLane(scenes, flatVideo, projectId);
      if (mixLane.length) primary = mixLane;
    }
  } else if (segLane.length) {
    const segById = Object.fromEntries(segLane.map((c) => [String(c.id), c]));
    primary = primary.map((c) => {
      const seg = segById[String(c.id)];
      const merged = !seg
        ? c
        : {
          ...c,
          url: seg.url || c.url,
          startTime: seg.startTime ?? c.startTime,
          duration: Number(seg.duration) > 0 ? seg.duration : c.duration,
          sourceDuration: Number(seg.sourceDuration) > 0 ? seg.sourceDuration : c.sourceDuration,
          waveformUrl: c.waveformUrl || seg.waveformUrl,
        };
      return ensureAudioClipWaveformUrl(merged, projectId);
    });
    const coveredIds = new Set(primary.map((c) => String(c.id || '')));
    const extras = segLane.filter((c) => c?.id && !coveredIds.has(String(c.id)));
    if (extras.length) {
      primary = [...primary, ...extras].sort(
        (a, b) => (Number(a.startTime) || 0) - (Number(b.startTime) || 0),
      );
    }
  } else {
    primary = primary.map((c) => ensureAudioClipWaveformUrl(c, projectId));
  }

  if (!primary.length && saved.length <= 1) {
    return ensureAudioTracksWaveformUrls(saved.length ? saved : [[]], projectId);
  }
  return ensureAudioTracksWaveformUrls(
    appendSavedExtraAudioLanes(primary, saved, savedMeta, scenes, projectId),
    projectId,
  );
}

function appendSavedExtraAudioLanes(primaryLane, savedAudioTracks, savedMeta, scenes, narrationProjectId) {
  const saved = savedAudioTracks || [[]];
  if (saved.length <= 1) return primaryLane.length ? [primaryLane] : [[]];
  const meta = mergeAudioTrackMeta(savedMeta || [], saved);
  const extras = [];
  for (let i = 1; i < saved.length; i += 1) {
    const m = meta[i];
    if (m?.isTranslation && m?.languageCode) {
      const lane = buildTranslationAudioLaneFromScenes(
        scenes,
        primaryLane,
        m.languageCode,
        narrationProjectId,
      );
      extras.push(lane.length ? lane : (saved[i] || []));
    } else {
      extras.push(saved[i] || []);
    }
  }
  return primaryLane.length ? [primaryLane, ...extras] : extras.length ? [primaryLane, ...extras] : [[]];
}

/** Fill clip media gaps from the latest segment/scene row (saved timeline may predate image gen). */
function fillMissingTimelineClipMediaFromScene(clip, scene) {
  if (!clip || !scene) return clip;
  if (clip.visualBeat != null) return clip;
  const next = { ...clip };
  const media = sceneDocumentMediaPlayback(scene);
  if (!next.imageUrl && media.imageUrl) {
    next.imageUrl = media.imageUrl;
  }
  if (!next.videoUrl && media.videoUrl) {
    next.videoUrl = media.videoUrl;
  }
  if (!next.audioUrl && media.audioUrl) {
    next.audioUrl = media.audioUrl;
  }
  if (!next.sceneMediaUpdatedAt && scene.updated_at) {
    next.sceneMediaUpdatedAt = scene.updated_at;
  }
  return next;
}

/** Image URL for the image lane — narration_segments row, same source as the edit page. */
function clipImageUrlForTimelineDisplay(clip, sceneById, scenes) {
  const rows = Array.isArray(scenes) && scenes.length
    ? scenes
    : Object.values(sceneById || {});
  return imageUrlForTimelineClip(clip, rows) || null;
}

/** Resolve segment/scene id from a timeline clip (video, audio, or subtitle). */
function timelineClipLinkedSceneId(clip) {
  if (!clip || typeof clip !== 'object') return '';
  if (clip.parentSceneId != null) return String(clip.parentSceneId);
  let raw = String(clip.sceneId || clip.id || '');
  raw = raw.replace(/^audio-/, '').replace(/^seg-audio-/, '').replace(/__image$/, '');
  if (raw.includes('__ms')) raw = raw.split('__ms')[0];
  if (raw.includes('__beat')) raw = raw.split('__beat')[0];
  if (raw.includes('-seg-')) raw = raw.split('-seg-')[0];
  return raw;
}

function timelineSelectionKey(sel) {
  if (!sel) return '';
  if (sel.type === 'gap') return `gap:${sel.trackType}:${sel.trackIdx}:${sel.id}`;
  if (sel.type === 'bookend') return `bookend:${sel.id}:${sel.role || ''}`;
  return `${sel.type}:${sel.id}`;
}

function timelineSelectionEquals(a, b) {
  return timelineSelectionKey(a) === timelineSelectionKey(b);
}

function isClipInTimelineSelection(sel, clips) {
  return (clips || []).some((c) => timelineSelectionEquals(c, sel));
}

function resolveVideoClipIdFromTimelineSelection(sel, videoTracks) {
  if (!sel?.id) return '';
  const rawId = String(sel.id).replace(/__image$/, '');
  for (const tr of videoTracks || []) {
    const found = tr.find((c) => c.id === rawId || c.id === sel.id || `${c.id}__image` === String(sel.id));
    if (found) return found.id;
  }
  return rawId;
}

/** True when clip is tied to a project segment/scene that still exists on the server. */
function timelineClipMatchesExistingScene(clip, validSceneIds) {
  const sid = timelineClipLinkedSceneId(clip);
  if (!sid) return true;
  if (sid.startsWith('media-') || sid.startsWith('audio-upload') || sid.startsWith('audio-drop')) {
    return true;
  }
  return validSceneIds.has(sid);
}

/** First packed start time per scene on the primary video lane. */
function sceneStartTimesFromVideoClips(flatVideo) {
  const map = {};
  [...(flatVideo || [])]
    .sort((a, b) => (Number(a.startTime) || 0) - (Number(b.startTime) || 0))
    .forEach((clip) => {
      const sid = timelineClipLinkedSceneId(clip);
      if (sid && map[sid] == null) map[sid] = Number(clip.startTime) || 0;
    });
  return map;
}

function alignAudioClipToSceneStarts(ac, sceneStarts, sceneById) {
  const sid = timelineClipLinkedSceneId(ac);
  if (!sid || sceneStarts[sid] == null) return ac;
  const base = sceneStarts[sid];
  const id = String(ac.id || '');
  const m = id.match(/^seg-audio-.+-seg-(\d+)$/);
  if (m) {
    const idx = Number(m[1]);
    const scene = sceneById[sid];
    const timeline = Array.isArray(scene?.audio_timeline) ? scene.audio_timeline : [];
    const seg = timeline.find((t) => Number(t?.index) === idx);
    const offset = Number(seg?.start_seconds) || 0;
    return { ...ac, startTime: base + offset };
  }
  return { ...ac, startTime: base };
}

function subtitleCuesForSpokenAudioClip(ac, scene, overlayById, overlayByAudio) {
  const clipStart = Number(ac.startTime) || 0;
  const clipDur = Math.max(
    0.01,
    clipAudioTimelineDuration(ac, 0, masterAudioPlaybackSpeedRef.current),
  );
  const segMatch = String(ac?.id || '').match(/^seg-audio-.+-seg-(\d+)$/);
  let entries = spokenSubtitleEntries(scene);
  if (segMatch) {
    const segIdx = Number(segMatch[1]);
    const hit = entries.find((e) => Number(e.index) === segIdx) || entries[segIdx];
    entries = hit ? [hit] : [];
  }
  const lines = entries.length
    ? entries
    : [{ index: 0, text: subtitleTextForAudioClip(ac, scene), startSeconds: 0, durationSeconds: clipDur }];
  const usable = lines.filter((e) => String(e.text || '').trim());
  if (!usable.length) return [];
  const timed = !segMatch && usable.length > 1 && usable.every((e) => e.durationSeconds > 0 && e.startSeconds != null);
  return usable.map((entry, i) => {
    const cueId = (!segMatch && usable.length > 1)
      ? `subtitle-${ac.id}-line-${entry.index ?? i}`
      : `subtitle-${ac.id}`;
    const o = overlayById[cueId] || (usable.length === 1 ? overlayByAudio[ac.id] : null);
    const hasOverlay = Boolean(o);
    const prevCue = hasOverlay && o.manualEdit ? { text: o.text, manualEdit: true } : null;
    const startTime = timed ? clipStart + Number(entry.startSeconds || 0) : clipStart + (clipDur * i) / usable.length;
    const duration = timed
      ? Math.max(0.01, Number(entry.durationSeconds) || 0.01)
      : Math.max(0.01, clipDur / usable.length);
    const timing = applySubtitleCueOverlayTiming({ startTime, duration }, hasOverlay ? o : null);
    const spoken = String(entry.text || '').trim();
    return {
      id: cueId,
      audioClipId: ac.id,
      sceneId: ac.sceneId,
      languageCode: ac.languageCode || null,
      startTime: timing.startTime,
      duration: timing.duration,
      text: prevCue?.text || spoken,
      muted: hasOverlay ? Boolean(o.muted) : false,
      manualEdit: Boolean(hasOverlay && o.manualEdit),
      manualTiming: timing.manualTiming,
    };
  });
}

/** Build one subtitle lane from paired audio lane clips. */
function rebuildSingleSubtitleLaneFromAudio(
  audioLane,
  scenesSnapshot,
  prevLane,
  videoTracks,
  { allowVideoFallback = false } = {},
) {
  const prev = prevLane || [];
  const overlayById = Object.fromEntries(
    prev.filter((c) => c?.id).map((c) => [c.id, subtitleCueEditorOverlay(c)]),
  );
  const overlayByAudio = Object.fromEntries(
    prev
      .filter((c) => c?.audioClipId != null)
      .map((c) => [c.audioClipId, subtitleCueEditorOverlay(c)]),
  );
  const sceneById = Object.fromEntries((scenesSnapshot || []).map((s) => [String(s.id), s]));
  let next = (audioLane || []).flatMap((ac) => {
    const sid = normalizeEditorSceneId(ac.sceneId);
    const scene = sid ? sceneById[sid] : null;
    return subtitleCuesForSpokenAudioClip(ac, scene, overlayById, overlayByAudio);
  });
  if (allowVideoFallback) {
    next = appendVideoFallbackSubtitleCues(
      next,
      videoTracks,
      scenesSnapshot,
      { ...overlayByAudio, ...overlayById },
    );
  }
  return next;
}

/** Rebuild subtitle lane(s) from paired audio lane(s). */
function rebuildSubtitleTracksFromAudio(audioTracks, scenesSnapshot, prevSubtitleTracks, videoTracks, opts = {}) {
  if (isSongBasedEditorScenes(scenesSnapshot)) {
    return buildSongStanzaSubtitleTracks(scenesSnapshot, videoTracks, prevSubtitleTracks);
  }

  const tracks = audioTracks || [[]];
  const prevAll = Array.isArray(prevSubtitleTracks) ? prevSubtitleTracks : [[]];
  const laneCount = Math.max(tracks.length, prevAll.length, 1);
  const onlyIdx = Number.isFinite(opts.sourceLaneIndex) ? opts.sourceLaneIndex : null;
  const out = [];

  for (let i = 0; i < laneCount; i += 1) {
    if (onlyIdx != null && i !== onlyIdx) {
      out.push(prevAll[i] || []);
      continue;
    }
    const audioLane = tracks[i] || [];
    if (!audioLane.length && !(prevAll[i] || []).length) {
      out.push([]);
      continue;
    }
    out.push(rebuildSingleSubtitleLaneFromAudio(
      audioLane,
      scenesSnapshot,
      prevAll[i],
      videoTracks,
      { allowVideoFallback: i === 0 },
    ));
  }
  return out;
}

/**
 * Pack gaps on video lanes, then align scene images/videos and narration/segment audio to those starts.
 * Scene SFX on the music lane follow packed video positions; background music is left unchanged.
 */
function sortPrimaryTrackBySceneNumber(track, scenes) {
  const sceneOrder = Object.fromEntries(
    (scenes || []).map((scene, index) => [
      String(scene.id),
      Number(scene.scene_number) || Number(scene.segment_number) || index,
    ]),
  );
  return [...(track || [])].sort((a, b) => {
    const orderA = sceneOrder[String(a.sceneId || a.id)] ?? 99999;
    const orderB = sceneOrder[String(b.sceneId || b.id)] ?? 99999;
    if (orderA !== orderB) return orderA - orderB;
    return (Number(a.startTime) || 0) - (Number(b.startTime) || 0);
  });
}

function ensureClipTimelineSlotOnVideoClip(clip, scene) {
  if (Number(clip?.timelineSlotDuration) > 0) return clip;
  const slot = scene
    ? sceneBlockDurationSeconds(scene)
    : clipVideoTimelineOccupancy(clip);
  return { ...clip, timelineSlotDuration: slot };
}

function narrationTimelineLayoutNeedsRepair(videoTracks, scenes) {
  if (!Array.isArray(scenes) || !scenes.length || isSongBasedEditorScenes(scenes)) return false;
  const primary = [...((videoTracks || [])[0] || [])]
    .filter((clip) => !clip?.isBookend)
    .sort((a, b) => (Number(a.startTime) || 0) - (Number(b.startTime) || 0));
  if (!primary.length) return scenes.some((scene) => scene?.id);

  if (trackTimelineGaps(primary).length) return true;
  if ((Number(primary[0]?.startTime) || 0) > MIN_TIMELINE_GAP_SEC) return true;

  const onTimeline = new Set(
    primary.map((clip) => timelineClipLinkedSceneId(clip)).filter(Boolean),
  );
  const sortedSceneIds = [...scenes]
    .filter((scene) => scene?.id)
    .sort(
      (a, b) =>
        (Number(a.scene_number) || Number(a.segment_number) || 0)
        - (Number(b.scene_number) || Number(b.segment_number) || 0),
    )
    .map((scene) => String(scene.id));
  if (sortedSceneIds.some((sceneId) => !onTimeline.has(sceneId))) return true;

  let lastSceneOrder = -1;
  const sceneOrder = Object.fromEntries(
    sortedSceneIds.map((sceneId, index) => [sceneId, index]),
  );
  for (const clip of primary) {
    const sceneOrderIndex = sceneOrder[String(clip.sceneId || clip.id)];
    if (sceneOrderIndex == null) continue;
    if (sceneOrderIndex < lastSceneOrder) return true;
    lastSceneOrder = sceneOrderIndex;
  }

  return false;
}

/** Sort scene clips, fill slot metadata, pack from t=0, and realign narration audio. */
function reconcileNarrationTimelineLayout(videoTracks, audioTracks, musicTracks, scenes, opts = {}) {
  if (!narrationTimelineLayoutNeedsRepair(videoTracks, scenes)) return null;
  const sceneById = Object.fromEntries((scenes || []).map((scene) => [String(scene.id), scene]));
  const sortedPrimary = sortPrimaryTrackBySceneNumber((videoTracks || [])[0] || [], scenes)
    .map((clip) => {
      const sceneId = timelineClipLinkedSceneId(clip);
      return ensureClipTimelineSlotOnVideoClip(clip, sceneId ? sceneById[sceneId] : null);
    });
  const nextVideoTracks = [
    sortedPrimary,
    ...(videoTracks || []).slice(1),
  ];
  return packVideoTimelineAndAlignSceneMedia(
    nextVideoTracks,
    audioTracks,
    musicTracks,
    scenes,
    opts,
  );
}

function packVideoTimelineAndAlignSceneMedia(videoTracks, audioTracks, musicTracks, scenes, opts = {}) {
  const { narrationProjectId = null } = opts;
  if (isSongBasedEditorScenes(scenes)) {
    const master = buildSongMasterAudioLaneFromScenes(
      scenes,
      mediaUrlForPlayback,
      narrationProjectId,
      scenes?.[0]?.song_timeline_duration,
    );
    const extra = (audioTracks || []).slice(1);
    const nextAudio = master.length ? [master, ...extra] : (audioTracks || [[]]);
    const nextMusic = mergeSceneSfxIntoMusicTracks(
      JSON.parse(JSON.stringify(musicTracks || [[]])),
      scenes,
      (videoTracks || [])[0] || [],
    );
    return {
      videoTracks,
      audioTracks: ensureAudioTracksWaveformUrls(nextAudio, narrationProjectId),
      musicTracks: ensureMusicTracksWaveformUrls(nextMusic, narrationProjectId),
    };
  }

  const packedVideo = (videoTracks || []).map((track, idx) => (
    idx === 0 ? packTrackFromZero(track) : removeGapsFromTrack(track)
  ));
  const primaryFlat = packedVideo[0] || [];
  const sceneStarts = sceneStartTimesFromVideoClips(primaryFlat);
  const sceneById = Object.fromEntries((scenes || []).map((s) => [String(s.id), s]));

  const alignedVideo = packedVideo.map((track, idx) =>
    idx === 0
      ? track
      : track.map((c) => {
          const sid = timelineClipLinkedSceneId(c);
          if (sid && sceneStarts[sid] != null) return { ...c, startTime: sceneStarts[sid] };
          return c;
        }),
  );

  let nextAudio = JSON.parse(JSON.stringify(audioTracks || [[]]));
  const segLane = buildSegmentAudioLaneClipsForScenes(scenes, primaryFlat, narrationProjectId);
  if (segLane.length) {
    const extra = nextAudio.slice(1).map((t) =>
      removeGapsFromTrack(t.map((ac) => alignAudioClipToSceneStarts(ac, sceneStarts, sceneById))),
    );
    nextAudio = [segLane, ...extra];
  } else {
    const mixLane = buildNarrationMixAudioLane(scenes, primaryFlat, narrationProjectId);
    const savedPrimaryHasAudio = (nextAudio[0] || []).some((clip) => clip?.url);
    if (mixLane.length && (narrationProjectId || !savedPrimaryHasAudio)) {
      const extra = nextAudio.slice(1).map((t) =>
        removeGapsFromTrack(t.map((ac) => alignAudioClipToSceneStarts(ac, sceneStarts, sceneById))),
      );
      nextAudio = [mixLane, ...extra];
    } else {
      nextAudio = nextAudio.map((track) => {
        const aligned = track.map((ac) => alignAudioClipToSceneStarts(ac, sceneStarts, sceneById));
        const hasSegmentClips = aligned.some((ac) => String(ac.id || '').startsWith('seg-audio-'));
        return hasSegmentClips ? aligned : removeGapsFromTrack(aligned);
      });
    }
  }

  const nextMusic = mergeSceneSfxIntoMusicTracks(
    JSON.parse(JSON.stringify(musicTracks || [[]])),
    scenes,
    primaryFlat,
  );

  return {
    videoTracks: alignedVideo,
    audioTracks: ensureAudioTracksWaveformUrls(nextAudio, narrationProjectId),
    musicTracks: ensureMusicTracksWaveformUrls(nextMusic, narrationProjectId),
  };
}

function videoClipForPackedSceneBlock(scene, startTime, blockDur, sceneColorIndex, multishotOpts = null) {
  const block = Math.max(MIN_CLIP_PLAYBACK_SEC, Number(blockDur) || MIN_CLIP_PLAYBACK_SEC);
  const clip = sceneToClip(scene, startTime);
  const timing = resolveVideoClipMediaTiming({ scene, slotDur: block, existingClip: clip });
  let next = clampVideoClipTrimToMediaCap({
    ...clip,
    startTime,
    sceneColorIndex,
    duration: block,
    timelineSlotDuration: block,
    trimStart: timing.trimStart,
    trimEnd: timing.trimEnd,
    videoSourceDurationMax: timing.videoSourceDurationMax,
    videoMediaDurationSeconds: timing.videoMediaDurationSeconds,
    videoPlaybackSpeed: timing.videoPlaybackSpeed,
    playbackDuration: timing.playbackDuration,
  });
  if (!multishotOpts) return next;
  const overrideImage = String(multishotOpts.imageUrl || '').trim();
  if (overrideImage) {
    const forceStill = Boolean(multishotOpts.forceStill) || !mediaUrlForSegmentField(scene, 'video_url');
    if (forceStill) {
      const imageTiming = resolveVideoClipMediaTiming({
        scene: { ...scene, video_url: null, video_s3_url: null, video_local_url: null },
        slotDur: block,
        existingClip: null,
      });
      next = {
        ...next,
        imageUrl: mediaUrlForPlayback(overrideImage),
        videoUrl: null,
        duration: block,
        timelineSlotDuration: block,
        trimStart: imageTiming.trimStart,
        trimEnd: imageTiming.trimEnd,
        playbackDuration: imageTiming.playbackDuration,
        videoSourceDurationMax: 0,
        videoMediaDurationSeconds: undefined,
        videoPlaybackSpeed: 1,
      };
    } else {
      next = { ...next, imageUrl: mediaUrlForPlayback(overrideImage) };
    }
  }
  return {
    ...next,
    id: multishotOpts.clipId || next.id,
    sceneId: multishotOpts.parentSceneId || next.sceneId,
    parentSceneId: multishotOpts.parentSceneId || null,
    multishotPart: multishotOpts.multishotPart,
    multishotShotIndex: multishotOpts.multishotShotIndex,
    multishotIsMaster: multishotOpts.multishotIsMaster,
    multishotSegmentDuration: multishotOpts.multishotSegmentDuration,
    name: multishotOpts.nameSuffix ? `${next.name}${multishotOpts.nameSuffix}` : next.name,
  };
}

/**
 * Rebuild timeline: one block per scene (no overlap), block length = max(audio, video, segments).
 * Image / video / audio / subtitles stay on separate lanes; adjacent scenes use distinct colors.
 */
function rebuildTimelinePackedScenes(scenes, opts = {}) {
  const { narrationProjectId = null } = opts;
  const sorted = [...(scenes || [])]
    .filter((s) => s?.id)
    .sort((a, b) => (Number(a.scene_number) || 0) - (Number(b.scene_number) || 0));
  const isSong = isSongBasedEditorScenes(sorted);

  let cursor = 0;
  const videoClips = [];
  sorted.forEach((scene, sceneIndex) => {
    if (isSong) {
      const parts = expandSongSceneToTimelineClips(
        scene,
        sceneIndex,
        (scn, startTime, blockDur, colorIdx, opts) =>
          videoClipForPackedSceneBlock(scn, startTime, blockDur, colorIdx, opts),
      );
      videoClips.push(...parts);
      return;
    }
    const blockDur = sceneBlockDurationSeconds(scene);
    const startTime = cursor;
    const beats = visualBeatsWithImages(scene);
    if (beats.length >= 2 && !mediaUrlForSegmentField(scene, 'video_url')) {
      beats.forEach((beat, index) => {
        const clip = videoClipForPackedSceneBlock(
          scene,
          startTime + beat.start,
          beat.duration,
          sceneIndex,
          {
            imageUrl: beat.imageUrl,
            forceStill: true,
            clipId: `${scene.id}__beat${index}`,
            parentSceneId: scene.id,
            nameSuffix: ` · ${index + 1}`,
          },
        );
        videoClips.push({
          ...clip,
          visualBeat: index,
          muted: true,
          videoUrl: null,
          video_s3_url: null,
          video_local_url: null,
          sceneText: beat.sentence || clip.sceneText,
          narrationText: beat.sentence || clip.narrationText,
        });
      });
    } else {
      videoClips.push(videoClipForPackedSceneBlock(scene, startTime, blockDur, sceneIndex));
    }
    cursor += blockDur;
  });

  const tagSceneColor = (clip, sceneId) => {
    const idx = sorted.findIndex((s) => String(s.id) === String(sceneId));
    const sceneColorIndex = idx >= 0 ? idx : 0;
    return { ...clip, sceneColorIndex };
  };

  let audioTracks;
  if (isSong) {
    const master = buildSongMasterAudioLaneFromScenes(
      sorted,
      mediaUrlForPlayback,
      narrationProjectId,
      sorted[0]?.song_timeline_duration,
    );
    audioTracks = master.length ? [master] : [[]];
  } else {
    const speech = buildStorySpeechAudioLane(sorted, videoClips, narrationProjectId).map((ac) =>
      tagSceneColor(ac, timelineClipLinkedSceneId(ac)),
    );
    audioTracks = speech.length ? [speech] : [[]];
  }

  let subtitleTracks = rebuildSubtitleTracksFromAudio(audioTracks, sorted, [[]], [videoClips]);
  subtitleTracks = subtitleTracks.map((track) =>
    track.map((st) => tagSceneColor(st, timelineClipLinkedSceneId(st))),
  );

  const { preserveExtraAudioTracks = null, preserveAudioTrackMeta = null } = opts;
  if (preserveExtraAudioTracks?.length > 1) {
    const primary = (audioTracks || [])[0] || [];
    const sceneStarts = sceneStartTimesFromVideoClips(videoClips);
    const sceneById = Object.fromEntries(sorted.map((s) => [String(s.id), s]));
    const meta = mergeAudioTrackMeta(
      preserveAudioTrackMeta || [],
      preserveExtraAudioTracks,
    );
    const extras = [];
    for (let i = 1; i < preserveExtraAudioTracks.length; i += 1) {
      const m = meta[i];
      if (m?.isTranslation && m?.languageCode) {
        const lane = buildTranslationAudioLaneFromScenes(
          sorted,
          primary,
          m.languageCode,
          narrationProjectId,
        ).map((ac) => alignAudioClipToSceneStarts(ac, sceneStarts, sceneById));
        extras.push(lane.length ? lane : preserveExtraAudioTracks[i] || []);
      } else {
        const aligned = (preserveExtraAudioTracks[i] || []).map((ac) =>
          alignAudioClipToSceneStarts(ac, sceneStarts, sceneById),
        );
        extras.push(aligned);
      }
    }
    audioTracks = primary.length || extras.length ? [primary, ...extras] : [[]];
  }

  return { videoTracks: [videoClips], audioTracks, subtitleTracks };
}

/** Total narration/segment audio span for a scene on the reference audio lane. */
function sceneAudioDurationFromTimeline(sceneId, videoStartTime, audioTracks, referenceLaneIndex = 0) {
  return sceneAudioDurationFromLane(
    sceneId,
    videoStartTime,
    audioTracks,
    referenceLaneIndex,
    masterAudioPlaybackSpeedRef.current,
  );
}

/** Shorten a scene clip when its timeline duration exceeds audioSec (never extends). */
function trimVideoClipToMatchAudioDuration(clip, audioSec) {
  const videoSec = Math.max(0.01, clipVideoSnippetDrawDuration(clip));
  const target = Math.max(MIN_CLIP_PLAYBACK_SEC, Number(audioSec) || 0);
  if (!(target > 0) || target + EXTEND_MATCH_EPSILON_SEC >= videoSec) {
    return { clip, changed: false };
  }

  if (!clip.videoUrl) {
    return {
      clip: {
        ...clip,
        duration: target,
        timelineSlotDuration: target,
        playbackDuration: target,
        trimEnd: target,
      },
      changed: true,
    };
  }

  const spd = clipVideoPlaybackSpeed(clip);
  const srcDur = Math.max(
    MIN_CLIP_PLAYBACK_SEC * spd,
    clipVideoMediaCapSeconds(clip) * spd,
  );
  const ts = Math.max(0, Math.min(Number(clip.trimStart) || 0, srcDur - MIN_CLIP_PLAYBACK_SEC * spd));
  const desiredSrcSpan = Math.max(MIN_CLIP_PLAYBACK_SEC * spd, target * spd);
  const te = Math.min(srcDur, ts + desiredSrcSpan);
  const timelineDur = Math.max(MIN_CLIP_PLAYBACK_SEC, (te - ts) / spd);
  const next = clampVideoClipTrimToMediaCap({
    ...clip,
    trimStart: ts,
    trimEnd: te,
    duration: target,
    timelineSlotDuration: target,
    playbackDuration: timelineDur,
  });
  return { clip: { ...next, playbackDuration: clipPlaybackFromTrim(next) }, changed: true };
}

/** Extend a scene clip when audio exceeds visible video (hold last frame / still for the gap). */
function extendVideoClipToMatchAudioDuration(clip, audioSec) {
  const videoSec = Math.max(0.01, clipVideoSnippetDrawDuration(clip));
  const target = Math.max(MIN_CLIP_PLAYBACK_SEC, Number(audioSec) || 0);
  if (!(target > 0) || target <= videoSec + EXTEND_MATCH_EPSILON_SEC) {
    return { clip, changed: false };
  }

  if (!clip.videoUrl) {
    return {
      clip: {
        ...clip,
        duration: target,
        timelineSlotDuration: target,
        playbackDuration: target,
        trimEnd: target,
      },
      changed: true,
    };
  }

  const fileSec = Math.max(
    clipVideoMediaCapSeconds(clip),
    clipVideoSourceTrimEndSeconds(clip),
    MIN_CLIP_PLAYBACK_SEC,
  );
  const trimStart = Math.max(0, Number(clip.trimStart) || 0);
  const trimEnd = Math.max(trimStart + MIN_CLIP_PLAYBACK_SEC, fileSec);
  const next = clampVideoClipTrimToMediaCap({
    ...clip,
    duration: target,
    timelineSlotDuration: target,
    trimStart,
    trimEnd,
    videoMediaDurationSeconds: Math.max(fileSec, trimEnd),
    videoSourceDurationMax: Math.max(fileSec, trimEnd),
  });
  return { clip: { ...next, playbackDuration: clipPlaybackFromTrim(next) }, changed: true };
}

/**
 * Magic edit: per segment trim/extend video to narration audio, then pack all segments side-by-side
 * with no gaps or overlaps and align audio/subtitle lanes.
 * Song flow: place each stanza on aligned master-song timestamps with lyric subtitles.
 */
function applyMagicEditToTimeline(videoTracks, audioTracks, musicTracks, scenes, opts = {}) {
  const referenceAudioLaneIndex = Number.isFinite(opts.referenceAudioLaneIndex)
    ? opts.referenceAudioLaneIndex
    : 0;
  if (isSongBasedEditorScenes(scenes)) {
    const { narrationProjectId = null } = opts;
    const sorted = [...(scenes || [])]
      .filter((s) => s?.id)
      .sort((a, b) => (Number(a.scene_number ?? a.segment_number) || 0) - (Number(b.scene_number ?? b.segment_number) || 0));
    const alignedCount = sorted.filter((s) => songSceneHasAlignTiming(s)).length;

    const packed = rebuildTimelinePackedScenes(scenes, {
      narrationProjectId,
      preserveExtraAudioTracks: opts.preserveExtraAudioTracks ?? audioTracks,
      preserveAudioTrackMeta: opts.preserveAudioTrackMeta ?? null,
    });

    let mergedVideo = mergeSongMagicEditVideoTracks(videoTracks, packed.videoTracks);
    mergedVideo = mergedVideo.map((track, idx) => (
      idx === 0
        ? (track || []).map((clip) => {
            const target = Math.max(MIN_CLIP_PLAYBACK_SEC, Number(clip.duration) || 0);
            if (!(target > 0)) return clip;
            const videoSec = clipVideoSnippetDrawDuration(clip);
            if (target + EXTEND_MATCH_EPSILON_SEC < videoSec) {
              return trimVideoClipToMatchAudioDuration(clip, target).clip;
            }
            if (target > videoSec + EXTEND_MATCH_EPSILON_SEC) {
              return extendVideoClipToMatchAudioDuration(clip, target).clip;
            }
            return clip;
          })
        : track
    ));

    const nextMusic = mergeSceneSfxIntoMusicTracks(
      JSON.parse(JSON.stringify(musicTracks || [[]])),
      scenes,
      mergedVideo[0] || [],
    );

    return {
      videoTracks: mergedVideo,
      audioTracks: packed.audioTracks,
      musicTracks: nextMusic,
      subtitleTracks: packed.subtitleTracks,
      stats: {
        trimmed: 0,
        extended: 0,
        skipped: 0,
        realigned: alignedCount,
        totalScenes: sorted.length,
      },
    };
  }

  const workingVideo = JSON.parse(JSON.stringify(videoTracks || [[]]));
  const primary = workingVideo[0] || [];
  const audioSnap = audioTracks || [[]];
  const updates = new Map();
  let trimmed = 0;
  let extended = 0;
  let skipped = 0;

  const sceneClips = primary
    .filter((c) => c?.sceneId)
    .sort((a, b) => (Number(a.startTime) || 0) - (Number(b.startTime) || 0));

  for (const clip of sceneClips) {
    const sourceId = String(clip.sceneId || '');
    const audioSec = sceneAudioDurationFromTimeline(sourceId, clip.startTime, audioSnap, referenceAudioLaneIndex);
    if (!(audioSec > 0)) {
      skipped += 1;
      continue;
    }
    const videoSec = clipVideoSnippetDrawDuration(clip);
    if (audioSec + EXTEND_MATCH_EPSILON_SEC < videoSec) {
      const { clip: next, changed } = trimVideoClipToMatchAudioDuration(clip, audioSec);
      if (changed) {
        updates.set(clip.id, next);
        trimmed += 1;
      } else {
        skipped += 1;
      }
    } else if (audioSec > videoSec + EXTEND_MATCH_EPSILON_SEC) {
      const { clip: next, changed } = extendVideoClipToMatchAudioDuration(clip, audioSec);
      if (changed) {
        updates.set(clip.id, next);
        extended += 1;
      } else {
        skipped += 1;
      }
    } else {
      const normalized = {
        ...clip,
        duration: audioSec,
        timelineSlotDuration: audioSec,
      };
      if (
        Math.abs(Number(clip.duration) - audioSec) > EXTEND_MATCH_EPSILON_SEC
        || Math.abs(Number(clip.timelineSlotDuration) - audioSec) > EXTEND_MATCH_EPSILON_SEC
      ) {
        updates.set(clip.id, normalized);
      } else {
        skipped += 1;
      }
    }
  }

  const syncedVideo = workingVideo.map((track, idx) => (
    idx === 0
      ? track.map((c) => (updates.has(c.id) ? updates.get(c.id) : c))
      : track
  ));

  const packedPrimary = packTrackFromZero(syncedVideo[0] || []);
  const zeroPackedVideo = syncedVideo.map((track, idx) => (idx === 0 ? packedPrimary : track));

  const result = packVideoTimelineAndAlignSceneMedia(
    zeroPackedVideo,
    audioTracks,
    musicTracks,
    scenes,
    opts,
  );

  const subtitleTracks = rebuildSubtitleTracksFromAudio(
    result.audioTracks,
    scenes,
    opts.prevSubtitleTracks || [[]],
    result.videoTracks,
  );

  return {
    ...result,
    subtitleTracks,
    stats: { trimmed, extended, skipped },
  };
}

/** Rebuild aligned song stanza clips when multishot inclusion changes (master + alternates). */
function applySongMultishotTimelineRealign(videoTracks, audioTracks, musicTracks, scenes, opts = {}) {
  if (!isSongBasedEditorScenes(scenes) || !scenesHaveMultishotTimeline(scenes)) return null;
  return applyMagicEditToTimeline(videoTracks, audioTracks, musicTracks, scenes, opts);
}

/** @typedef {'live' | 'frozen' | 'working-base'} EditorSceneMediaPolicy */

function narrationSegmentApiTarget(sourceId, scenes, clip) {
  const segmentId = resolveNarrationSegmentId(sourceId, scenes, clip);
  const segmentNumber = segmentNumberFromTimelineClip(clip);
  return { segmentId, segmentNumber };
}

/** Merge saved timeline/lanes onto server scenes (keeps startTime; media policy controls URL refresh). */
function mergeFreshScenesWithSavedEditor(sortedScenes, savedEditor, options = {}) {
  if (!savedEditor) return null;
  const mediaPolicy = options.mediaPolicy || 'live';
  const freezeMedia = mediaPolicy === 'frozen' || mediaPolicy === 'working-base';
  const sceneById = Object.fromEntries(sortedScenes.map((s) => [String(s.id), s]));
  const validSceneIds = new Set(Object.keys(sceneById));
  const keepClip = (clip) => timelineClipMatchesExistingScene(clip, validSceneIds);

  let videoTracks = coerceTimelineTracks(
    JSON.parse(JSON.stringify(savedEditor.videoTracks || [[]])),
  ).map((track) =>
    track
      .map((c) => remapTimelineClipToCurrentScene(c, sortedScenes))
      .filter(keepClip)
      .map((c) => {
        const linkedId = timelineClipLinkedSceneId(c);
        const scene = sceneById[linkedId] || sceneForTimelineClip(c, sortedScenes);
        let clip = c;
        if (scene?.id && String(linkedId) !== String(scene.id)) {
          clip = {
            ...c,
            sceneId: String(scene.id),
            ...(c.parentSceneId != null ? { parentSceneId: String(scene.id) } : {}),
          };
        }
        const merged = mergeTimelineClipWithLatestScene(clip, scene, { freezeMedia });
        return fillMissingTimelineClipMediaFromScene(
          stripLegacyVideoClipEmbeddedMute(merged, scene),
          scene,
        );
      }),
  );
  const { videoTracks: withNewScenes, appendedCount } = appendMissingScenesToVideoTracks(
    videoTracks,
    sortedScenes,
  );
  videoTracks = withNewScenes.map((track) => expandTrackWithVisualBeats(track, sortedScenes));
  let audioTracks = coerceTimelineTracks(
    JSON.parse(JSON.stringify(savedEditor.audioTracks || [[]])),
  ).map((track) =>
    track
      .map((ac) => remapTimelineClipToCurrentScene(ac, sortedScenes))
      .filter(keepClip)
      .map((ac) => mergeAudioLaneClipWithLatestScene(ac, sceneById, { freezeMedia }))
      .filter((ac) => ac.url),
  );
  let musicTracks = coerceTimelineTracks(
    JSON.parse(JSON.stringify(savedEditor.musicTracks || [[]])),
  );
  const savedSubtitleTracks = coerceTimelineTracks(
    JSON.parse(JSON.stringify(savedEditor.subtitleTracks || [[]])),
  ).map((track) =>
    track
      .map((st) => remapTimelineClipToCurrentScene(st, sortedScenes))
      .filter(keepClip),
  );

  if (!freezeMedia) {
    videoTracks = stampSegmentImagesOntoVideoTracks(videoTracks, sortedScenes, audioTracks);
  }

  if (options.narrationProjectId && !isSongBasedEditorScenes(sortedScenes)) {
    const repaired = reconcileNarrationTimelineLayout(
      videoTracks,
      audioTracks,
      musicTracks,
      sortedScenes,
      { narrationProjectId: options.narrationProjectId },
    );
    if (repaired) {
      videoTracks = repaired.videoTracks;
      audioTracks = repaired.audioTracks;
      musicTracks = repaired.musicTracks;
    }
  }

  const subtitleTracks = rebuildSubtitleTracksFromAudio(
    audioTracks,
    sortedScenes,
    savedSubtitleTracks,
    videoTracks,
  );

  return {
    videoTracks,
    audioTracks,
    musicTracks,
    appendedSceneCount: appendedCount,
    subtitleStyle: normalizeVideoSubtitleStyle(savedEditor.subtitleStyle),
    timelineZoom: savedEditor.timelineZoom ?? 1,
    volume: savedEditor.volume ?? 0.8,
    masterAudioPlaybackSpeed: clampPlaybackSpeed(savedEditor.masterAudioPlaybackSpeed ?? 1),
    isMuted: Boolean(savedEditor.isMuted),
    videoMasterMuted: savedEditor.videoMasterMuted != null
      ? Boolean(savedEditor.videoMasterMuted)
      : DEFAULT_VIDEO_MASTER_MUTED,
    subtitleTracks,
    timelineActive: mergeTimelineActiveForEditorLoad(savedEditor.timelineActive, audioTracks.length),
    exportAspectRatio: savedEditor.exportAspectRatio || '16:9',
    exportQuality: normalizeExportQuality(savedEditor.exportQuality),
    exportFraming: normalizeVideoExportFraming(savedEditor.exportFraming),
    exportClipVideoToSubtitle: Boolean(savedEditor.exportClipVideoToSubtitle),
    audioTrackMeta: Array.isArray(savedEditor.audioTrackMeta) ? savedEditor.audioTrackMeta : [],
    videoBookends: savedEditor.videoBookends || null,
    overlayTracks: loadHostOverlayTracksFromEditor(
      savedEditor,
      savedEditor.exportAspectRatio || '16:9',
    ),
    effectTracks: loadEffectTracksFromEditor(savedEditor),
    editorAnimationSpeed: editorAnimationSpeedFromSnapshot(savedEditor),
  };
}

/** True if persisted editor snapshot has enough data to restore (tracks, cues, or subtitle rows). */
function videoEditorSnapshotHasClips(savedEditor) {
  if (!savedEditor || typeof savedEditor !== 'object') return false;
  const vn = (savedEditor.videoTracks || []).flat().filter(Boolean).length;
  const an = (savedEditor.audioTracks || []).flat().filter(Boolean).length;
  const mn = (savedEditor.musicTracks || []).flat().filter(Boolean).length;
  const sn = (savedEditor.subtitleTracks || []).flat().filter(Boolean).length;
  const on = (savedEditor.overlayTracks || []).flat().filter(Boolean).length;
  return vn + an + mn + sn + on > 0;
}

function narrationStoryExtrasFromProject(project) {
  const isNews = (project?.app || '').toLowerCase() === 'news_video';
  const isSong = isSongBasedNarrationProject(project);
  const np = project?.news_pack && typeof project.news_pack === 'object' ? { ...project.news_pack } : {};
  return {
    app: project?.app || null,
    story_type: isNews ? 'news_video_project' : (isSong ? 'song_based_project' : 'narration_project'),
    news_pack: np,
  };
}

function narrationProjectIdFromEditorStoryId(editorStoryId) {
  if (typeof editorStoryId !== 'string') return null;
  if (editorStoryId.startsWith('narration-')) return editorStoryId.slice('narration-'.length);
  if (editorStoryId.startsWith('news-')) return editorStoryId.slice('news-'.length);
  return null;
}

const narrationProjectToEditorState = (project, editorId) => {
  const duration = Number(project?.total_duration) || 5;
  if (!project?.segments?.length && project?.final_video_url) {
    const clip = {
      id: `narration-final-${project.id}`,
      name: `${project.title || 'Narration'} final video`,
      videoUrl: mediaUrlForPlayback(project.final_video_url),
      imageUrl: null,
      audioUrl: null,
      startTime: 0,
      duration,
      playbackDuration: duration,
      trimStart: 0,
      trimEnd: duration,
      sceneText: 'Final stitched narration video',
      narrationText: '',
      muted: false,
      volume: DEFAULT_VIDEO_EMBEDDED_AUDIO_VOLUME,
    };
    return {
      story: {
        id: editorId,
        title: project.title || 'Narration Project',
        tone: project.input?.tone,
        visual_style: project.input?.visual_style,
        input: project.input || {},
        language:
          resolveVideoProjectScriptLanguage({ input: project.input }, []) || 'en',
        final_video_url: project.final_video_url || null,
        total_duration: project.total_duration,
        music_url: project.music_url || null,
        music_mood: project.music_mood || null,
        music_prompt: project.music_prompt || null,
        music_history: project.music_history || [],
        music_target_duration: project.music_target_duration ?? null,
        ...narrationStoryExtrasFromProject(project),
      },
      scenes: [{
        id: clip.id,
        scene_number: 1,
        title: clip.name,
        scene_text: clip.sceneText,
        narration_text: '',
        video_url: project.final_video_url,
        image_url: null,
        audio_url: null,
        duration_seconds: duration,
      }],
      videoTracks: [[clip]],
      audioTracks: [[]],
      musicTracks: buildMusicTracksFromStoryUrl(project?.music_url, duration, {
        narrationProjectId: project.id,
      }),
    };
  }

  const isSong = isSongBasedNarrationProject(project);
  const masterSongUrl = isSong ? songMasterAudioUrl(project, project?.segments) : '';
  const songDuration = isSong ? songTimelineDuration(project, project?.segments) : 0;
  let startTime = 0;
  const clips = [];
  if (isSong) {
    const sortedSegments = [...(project?.segments || [])].sort(
      (a, b) => (Number(a.segment_number) || 0) - (Number(b.segment_number) || 0),
    );
    sortedSegments.forEach((segment, sceneIndex) => {
      const sceneDoc = narrationSegmentToScene(segment, project);
      const parts = expandSongSceneToTimelineClips(
        sceneDoc,
        sceneIndex,
        (scn, startTime, blockDur, colorIdx, opts) =>
          videoClipForPackedSceneBlock(scn, startTime, blockDur, colorIdx, opts),
      );
      clips.push(...parts);
    });
  } else {
    (project?.segments || []).forEach((segment) => {
      const sceneDoc = narrationSegmentToScene(segment, project);
      const segmentDuration = Number(segment.audio_duration || segment.duration) || 5;
      const clipStart = startTime;
      const rawWordTs = Array.isArray(segment.word_timestamps) && segment.word_timestamps.length
        ? segment.word_timestamps
        : null;
      const wordTimestamps = rawWordTs || estimateWordTimestamps(segment.text || '', segmentDuration);
      const timing = resolveVideoClipMediaTiming({ scene: sceneDoc, slotDur: segmentDuration });
      const motionFields = segmentToClipMotionFields(segment);
      const stillUrl = sceneDocumentMediaPlayback(sceneDoc).imageUrl || null;
      const clip = clampVideoClipTrimToMediaCap({
        id: segment.id,
        name: `Segment ${segment.segment_number || ''}`.trim(),
        sceneId: segment.id,
        videoUrl: mediaUrlForSegmentField(segment, 'video_url') || null,
        imageUrl: stillUrl,
        audioUrl: mediaUrlForSegmentField(segment, 'audio_url') || null,
        startTime: clipStart,
        duration: timing.duration,
        videoSourceDurationMax: timing.videoSourceDurationMax,
        videoMediaDurationSeconds: timing.videoMediaDurationSeconds,
        videoPlaybackSpeed: timing.videoPlaybackSpeed,
        playbackDuration: timing.playbackDuration,
        trimStart: timing.trimStart,
        trimEnd: timing.trimEnd,
        muted: false,
        volume: DEFAULT_VIDEO_EMBEDDED_AUDIO_VOLUME,
        sceneText: segment.authored_script || segment.text || '',
        narrationText: segment.authored_script || segment.text || '',
        wordTimestamps,
        motionPreset: motionFields.motionPreset || 'ken_burns',
        motionStrength: motionFields.motionStrength || 0.12,
        audioMotionTimeline: motionFields.audioMotionTimeline,
        beatSyncMetadata: motionFields.beatSyncMetadata,
      });
      clips.push(clip);
      startTime += segmentDuration;
    });
  }
  const audioSource = clips;
  const timelineClips = isSong
    ? clips
    : expandTrackWithVisualBeats(clips, project?.segments || []);
  const audioClips = isSong
    ? buildSongMasterAudioLane(project, project?.segments, mediaUrlForPlayback, project.id)
    : audioSource
      .filter((clip) => clip.audioUrl)
      .map((clip) => ({
        id: `audio-${clip.id}`,
        name: clip.name,
        sceneId: clip.sceneId || clip.id,
        url: clip.audioUrl,
        startTime: clip.startTime,
        duration: clip.duration,
        sourceDuration: clip.duration,
        volume: 0.8,
        muted: false,
        waveformUrl: `/narration/projects/${project.id}/segments/${clip.id}/waveform`,
      }));
  return {
    story: {
      id: editorId,
      title: project?.title || 'Narration Project',
      tone: project?.input?.tone,
      visual_style: project?.input?.visual_style,
      input: project?.input || {},
      language:
        resolveVideoProjectScriptLanguage(
          { input: project?.input },
          project?.segments || [],
        ) || 'en',
      final_video_url: project?.final_video_url || null,
      total_duration: project?.total_duration,
      music_url: project?.music_url || null,
      music_mood: project?.music_mood || null,
      music_prompt: project?.music_prompt || null,
      music_history: project?.music_history || [],
      music_target_duration: project?.music_target_duration ?? null,
      ...narrationStoryExtrasFromProject(project),
    },
    scenes: (project?.segments || []).map((segment) => {
      // Same scene projection as Scene View / Workflow (canonical image_url resolve).
      const sceneDoc = narrationSegmentToScene(segment, project);
      const segDur = isSong
        ? songSegmentWindow(segment).duration
        : Number(segment.audio_duration || segment.duration) || 5;
      const scriptText = sceneDoc.scene_text || sceneDoc.narration_text || '';
      const songWindow = isSong ? songSegmentWindow(segment) : null;
      const rawWordTs = Array.isArray(segment.word_timestamps) && segment.word_timestamps.length
        ? segment.word_timestamps
        : null;
      const wordTs = isSong
        ? (rawWordTs?.length
          ? songClipLocalWordTimestamps(segment, songWindow)
          : estimateWordTimestamps(scriptText, segDur))
        : (rawWordTs || estimateWordTimestamps(scriptText, segDur));
      return {
        ...sceneDoc,
        word_timestamps: wordTs,
        audio_url: isSong ? null : sceneDoc.audio_url || null,
        song_based_segment: isSong,
        song_master_audio_url: isSong ? masterSongUrl : null,
        song_timeline_duration: isSong ? songDuration : null,
        timeline_start: songWindow?.start ?? segment.timeline_start,
        timeline_end: songWindow?.end ?? segment.timeline_end,
        duration_seconds: segDur,
      };
    }),
    videoTracks: [timelineClips],
    audioTracks: audioClips.length ? [audioClips] : [[]],
    musicTracks: buildMusicTracksFromStoryUrl(
      project?.music_url,
      isSong
        ? songDuration
        : (clips.length ? timelineEndFromVideoClips(clips) : Number(project?.total_duration) || 5),
      { narrationProjectId: project.id },
    ),
  };
};

export const VideoEditorPage = () => {
  const { storyId, projectId } = useParams();
  const navigate = useNavigate();
  const { openInsufficientCredits } = useInsufficientCredits();
  const location = useLocation();
  const isNewsRoute = (location.pathname || '').startsWith('/news/');
  const isReelsShortsEditor = Boolean(projectId && (location.pathname || '').includes('/reels-shorts/'));
  const editorId = storyId || (projectId ? `${isNewsRoute ? 'news' : isReelsShortsEditor ? 'reels' : 'narration'}-${projectId}` : null);
  const isNarrationEditor = Boolean(projectId && !storyId && !isReelsShortsEditor);
  const isNewsVideoEditor = Boolean(isNarrationEditor && isNewsRoute);
  const { open: directorDialogOpen, setOpen: setDirectorDialogOpen } = useMovieDirector();

  const [story, setStory] = useState(null);
  const [loading, setLoading] = useState(true);
  const editorOnboarding = useOnboardingJourney(JOURNEY_IDS.VIDEO_EDITOR, { ready: !loading });
  const editorOnboardingSteps = videoEditorOnboardingSteps();
  const [videoTracks, setVideoTracks] = useState([[]]); // array of tracks, each track = array of clips
  const [audioTracks, setAudioTracks] = useState([[]]); // audio tracks
  const [musicTracks, setMusicTracks] = useState([[]]);
  const [subtitleTracks, setSubtitleTracks] = useState([[]]);
  const [effectTracks, setEffectTracks] = useState([[]]);
  const [overlayTracks, setOverlayTracks] = useState([[]]);
  const [reelsExporting, setReelsExporting] = useState(false);
  const [subtitleStyle, setSubtitleStyle] = useState(() => normalizeVideoSubtitleStyle(null));
  const videoTracksRef = useRef(videoTracks);
  videoTracksRef.current = videoTracks;
  const probedVideoUrlRef = useRef(new Map());
  const audioTracksRef = useRef(audioTracks);
  audioTracksRef.current = audioTracks;
  const musicTracksRef = useRef(musicTracks);
  musicTracksRef.current = musicTracks;
  const subtitleTracksRef = useRef(subtitleTracks);
  subtitleTracksRef.current = subtitleTracks;
  const effectTracksRef = useRef(effectTracks);
  effectTracksRef.current = effectTracks;
  const overlayTracksRef = useRef(overlayTracks);
  overlayTracksRef.current = overlayTracks;
  const reelsCutRef = useRef(null);
  const reelsIncludeBrollRef = useRef(true);
  const reelsIncludeSubtitlesRef = useRef(true);
  const subtitleStyleRef = useRef(subtitleStyle);
  subtitleStyleRef.current = subtitleStyle;
  const recordEditorHistoryRef = useRef(() => {});
  const previewStageRef = useRef(null);
  const previewImageRef = useRef(null);
  const previewMotionLayerRef = useRef(null);
  /** Active preview <video>/<img> for VFX capture + motion transforms. */
  const previewMediaRef = useRef(null);
  const previewFilterStyleRef = useRef({});
  const previewMotionExtraLayersRef = useRef([]);
  const onPlaybackFrameTickRef = useRef(null);
  const lastPlaybackTransitionKeyRef = useRef('');
  const lastVisualUiPublishRef = useRef(0);
  const visualCurrentTimeRef = useRef(0);
  const lastSubtitleCueIdRef = useRef('');
  const [subtitleCueRevision, setSubtitleCueRevision] = useState(0);
  const previewFrozenStyleTimeRef = useRef(0);
  const [previewFitMode, setPreviewFitMode] = useState('contain'); // 'contain' = fit aspect ratio | 'cover' = fill stage
  const [talkFocusCrop, setTalkFocusCrop] = useState(null);
  const talkFocusCacheRef = useRef(new Map());
  const [previewStageSize, setPreviewStageSize] = useState({ w: 0, h: 0 });
  const [viewportMin, setViewportMin] = useState(() => (
    typeof window !== 'undefined' ? Math.min(window.innerWidth, window.innerHeight) : 800
  ));
  const [isPreviewFullscreen, setIsPreviewFullscreen] = useState(false);
  const [previewTransportExpanded, setPreviewTransportExpanded] = useState(false);
  const [previewVolumeOpen, setPreviewVolumeOpen] = useState(false);
  const [volume, setVolume] = useState(0.8);
  const [masterAudioPlaybackSpeed, setMasterAudioPlaybackSpeed] = useState(1);
  const [isMuted, setIsMuted] = useState(false);
  /** Mutes embedded audio on all video timeline clips (preview + per-clip when synced). */
  const [videoMasterMuted, setVideoMasterMuted] = useState(DEFAULT_VIDEO_MASTER_MUTED);
  const [timelineZoom, setTimelineZoom] = useState(1);
  const [editingClip, setEditingClip] = useState(null);
  /** { clipId, trackIdx, fromClip, toClip, transitionOut, transitionDuration } */
  const [transitionPicker, setTransitionPicker] = useState(null);
  const [dragOverTrack, setDragOverTrack] = useState(null);
  const [exportDialogOpen, setExportDialogOpen] = useState(false);
  const [exportingFull, setExportingFull] = useState(false);
  const [exportingLocal, setExportingLocal] = useState(false);
  const localExportAbortRef = useRef(null);
  useEffect(() => () => localExportAbortRef.current?.abort(), []);
  const [exportingPremiere, setExportingPremiere] = useState(false);
  const [exportProgress, setExportProgress] = useState(0);
  const [exportStatusMessage, setExportStatusMessage] = useState('');
  const [exportedUrl, setExportedUrl] = useState(null);
  const exportPollStopRef = useRef(null);
  const exportJobStorageKeyRef = useRef(null);
  const [exportAspectRatio, setExportAspectRatio] = useState('16:9');
  const [exportQuality, setExportQuality] = useState('best');
  const [exportFraming, setExportFraming] = useState(() => normalizeVideoExportFraming(null));
  const [exportClipVideoToSubtitle, setExportClipVideoToSubtitle] = useState(false);
  const [videoBookends, setVideoBookends] = useState(() => ({ ...DEFAULT_VIDEO_BOOKENDS }));
  const [pitchVoicePolish, setPitchVoicePolish] = useState({
    enabled: false,
    clean_voice: true,
    remove_fillers: true,
    remove_long_breaths: true,
    remove_low_energy: true,
    remove_redundant: false,
    remove_repeated: false,
    remove_mistakes: false,
    remove_unnecessary_intros: false,
    mux_av: true,
  });
  const videoBookendsRef = useRef(videoBookends);
  const bookendIntroDurRef = useRef(0);
  const exportFramingRef = useRef(exportFraming);
  const exportClipVideoToSubtitleRef = useRef(exportClipVideoToSubtitle);
  const [youtubePublishOpen, setYoutubePublishOpen] = useState(false);
  const [instagramPublishOpen, setInstagramPublishOpen] = useState(false);
  const [showMediaLibrary, setShowMediaLibrary] = useState(false);
  // ── Figma Layout: Left & Right sidebar expand states ───────────────────────
  const [leftExpanded, setLeftExpanded] = useState(false);
  const [rightExpanded, setRightExpanded] = useState(false);
  const [subtitleSettingsOpen, setSubtitleSettingsOpen] = useState(false);
  // ───────────────────────────────────────────────────────────────────────────
  const [transcriptionDraft, setTranscriptionDraft] = useState('');
  const [savingTranscription, setSavingTranscription] = useState(false);
  const [rollingScriptEditClipId, setRollingScriptEditClipId] = useState(null);
  const [rollingScriptDraft, setRollingScriptDraft] = useState('');
  const [savingRollingScript, setSavingRollingScript] = useState(false);
  const [regenerateAudioAfterRollingScriptSave, setRegenerateAudioAfterRollingScriptSave] = useState(false);
  const [rollingRegenerateVoiceStyle, setRollingRegenerateVoiceStyle] = useState('storyteller');
  const [rollingRegenerateTtsProvider, setRollingRegenerateTtsProvider] = useState('auto');
  const [newsPackDraft, setNewsPackDraft] = useState({
    news_logo_url: '',
    ticker_text: '',
    ad_image_url: '',
    lower_third_text: '',
    overlay_notes: '',
    show_logo_preview: true,
    show_ticker_preview: true,
    show_ad_preview: false,
  });
  const [savingNewsPack, setSavingNewsPack] = useState(false);
  const [regeneratingRollingAudio, setRegeneratingRollingAudio] = useState(false);
  const [syncingTimingRules, setSyncingTimingRules] = useState(false);
  const [clippingVideoToMatchAudio, setClippingVideoToMatchAudio] = useState(false);
  const [magicEditLanePicker, setMagicEditLanePicker] = useState(null);
  const [matchingScriptToVideoDuration, setMatchingScriptToVideoDuration] = useState(false);
  const [adaptScriptThenRegenerateAudio, setAdaptScriptThenRegenerateAudio] = useState(false);
  const [visualCurrentTime, setVisualCurrentTime] = useState(0);
  const [playbackTransitionBlend, setPlaybackTransitionBlend] = useState(null);
  /** Last-loaded scenes from API (or from an opened project file) for snapshot saves. */
  const [scenesSnapshot, setScenesSnapshot] = useState([]);
  const scenesSnapshotRef = useRef(scenesSnapshot);
  scenesSnapshotRef.current = scenesSnapshot;
  const [engineSfxPlan, setEngineSfxPlan] = useState(null);
  const [engineSfxRemixUi, setEngineSfxRemixUi] = useState('');
  const engineSfxRemixKeyRef = useRef('');
  /** Dialogue camera shots per scene id (dialogue stories), for the export aspect. */
  const [dialogueShotsByScene, setDialogueShotsByScene] = useState({});
  const runMagicEditWithReferenceLaneRef = useRef(null);
  const mediaReplaceGuardUntilRef = useRef(0);
  const [mediaChangePersistNonce, setMediaChangePersistNonce] = useState(0);
  const [linkedProjectFolder, setLinkedProjectFolder] = useState(null);

  /** idle | saving | saved | error | dirty — cloud sync of timeline edits */
  const [cloudSaveStatus, setCloudSaveStatus] = useState('idle');
  const editorDirtyRef = useRef(false);
  const skipDirtyMarkRef = useRef(true);
  const cloudAutosaveIntervalRef = useRef(null);
  const [autoSegmentSyncEnabled, setAutoSegmentSyncEnabled] = useState(
    () => readVideoEditorAutoSegmentSyncEnabled(),
  );
  const [segmentSyncBusy, setSegmentSyncBusy] = useState(false);
  const [lastSegmentSyncAt, setLastSegmentSyncAt] = useState(null);
  const lastSegmentSyncAtRef = useRef(0);
  const segmentSyncIntervalRef = useRef(null);
  const editorReadyForCloudSyncRef = useRef(false);
  const languageDraftSaveTimerRef = useRef(null);
  const prevLanguageDraftRef = useRef(null);
  const [retryingNarration, setRetryingNarration] = useState(false);
  const [generatingBgMusic, setGeneratingBgMusic] = useState(false);
  const [bgMusicDialogOpen, setBgMusicDialogOpen] = useState(false);
  const [bgMusicPrompt, setBgMusicPrompt] = useState('');
  const [bgMusicProvider, setBgMusicProvider] = useState(DEFAULT_BG_MUSIC_PROVIDER);
  const [selectingBgMusicUrl, setSelectingBgMusicUrl] = useState(null);
  const [workspaceScriptLangTarget, setWorkspaceScriptLangTarget] = useState('');
  const [workspaceSwitchRegenerateAudio, setWorkspaceSwitchRegenerateAudio] = useState(false);
  const [workspaceSwitchFitVideoDuration, setWorkspaceSwitchFitVideoDuration] = useState(true);
  const [workspaceSwitchingLanguage, setWorkspaceSwitchingLanguage] = useState(false);
  /** Pending whole-script language preview (Apply); committed on Save project. */
  const [pendingLanguageCommit, setPendingLanguageCommit] = useState(null);
  const [replacementDialog, setReplacementDialog] = useState(null);
  /** Right panel tab: drives which media kind the left gallery lists (video / image / audio / upload-only). */
  const [replacementPanelTab, setReplacementPanelTab] = useState('upload');
  const [replacementOptions, setReplacementOptions] = useState([]);
  const [replacementLoading, setReplacementLoading] = useState(false);
  const [replacementDrafts, setReplacementDrafts] = useState({ image: '', video: '', audio: '' });
  const [replacementOverrides, setReplacementOverrides] = useState(() => defaultReplacementOverrides(null));
  const [regeneratingReplacement, setRegeneratingReplacement] = useState(null);
  const [replacementUploading, setReplacementUploading] = useState(false);
  const replacementUploadInputRef = useRef(null);
  const [extendVideoDialog, setExtendVideoDialog] = useState(null);
  const [extendVideoProvider, setExtendVideoProvider] = useState('minimax');
  const [extendVideoPrompt, setExtendVideoPrompt] = useState('');
  const [extendVideoBusy, setExtendVideoBusy] = useState(false);
  const [extendVideoFramePreview, setExtendVideoFramePreview] = useState(null);
  const [extendVideoFrameCapturing, setExtendVideoFrameCapturing] = useState(false);
  const [extendVideoFrameCaptureError, setExtendVideoFrameCaptureError] = useState(null);
  const [extendingAllVideos, setExtendingAllVideos] = useState(false);
  const [extendAllProgress, setExtendAllProgress] = useState({ current: 0, total: 0, name: '' });
  const [timelineActive, setTimelineActive] = useState(() => ({ ...DEFAULT_TIMELINE_ACTIVE }));
  /** Lanes minimized from the timeline label column, keyed by `${kind}:${trackIdx}`. */
  const [collapsedTimelineLanes, setCollapsedTimelineLanes] = useState({});
  const toggleTimelineLane = useCallback((key) => {
    setCollapsedTimelineLanes((prev) => toggleTimelineLaneCollapsed(prev, key));
  }, []);
  /** Overlay clip flashed in the timeline after being located from the preview. */
  const [highlightedOverlayClipId, setHighlightedOverlayClipId] = useState(null);
  const highlightOverlayTimerRef = useRef(null);
  /** Overlay clip shown in the details popup (opened by double-click on the lane). */
  const [overlayDetailsClipId, setOverlayDetailsClipId] = useState(null);
  const overlayDetailsClip = useMemo(() => {
    if (!overlayDetailsClipId) return null;
    for (const lane of (overlayTracks || [])) {
      const found = (lane || []).find((clip) => clip?.id === overlayDetailsClipId);
      if (found) return found;
    }
    return null;
  }, [overlayDetailsClipId, overlayTracks]);
  const [editorAnimationSpeed, setEditorAnimationSpeed] = useState(EDITOR_ANIMATION_SPEED_DEFAULT);
  const editorAnimationSpeedRef = useRef(editorAnimationSpeed);
  editorAnimationSpeedRef.current = editorAnimationSpeed;
  const songMotionPreviewOpts = useMemo(() => {
    if (!isSongBasedNarrationProject(story)) {
      return { defaultAnimationEnabled: true, animationSpeed: 1 };
    }
    return songAnimationSettingsFromInput(story?.input || {});
  }, [story]);
  const songMotionPreviewOptsRef = useRef(songMotionPreviewOpts);
  songMotionPreviewOptsRef.current = songMotionPreviewOpts;
  const previewMotionOptsRef = useRef({});
  previewMotionOptsRef.current = {
    presetAnimationSpeed: editorAnimationSpeed,
    ...(isSongBasedNarrationProject(story) ? songMotionPreviewOpts : {}),
  };
  const persistSongAnimationTimerRef = useRef(null);
  const isSongEditorProject = isSongBasedNarrationProject(story);

  const persistSongAnimationSettings = useCallback(
    (patch) => {
      if (!isNarrationEditor || !projectId || !isSongEditorProject) return;
      const next = {
        defaultAnimationEnabled: songMotionPreviewOpts.defaultAnimationEnabled !== false,
        animationSpeed: songMotionPreviewOpts.animationSpeed,
        ...patch,
      };
      setStory((prev) => ({
        ...prev,
        input: { ...(prev?.input || {}), ...songAnimationSettingsToApiPayload(next) },
      }));
      if (persistSongAnimationTimerRef.current) {
        clearTimeout(persistSongAnimationTimerRef.current);
      }
      persistSongAnimationTimerRef.current = setTimeout(async () => {
        try {
          await api.patch(
            `/narration/projects/${projectId}/song-animation-settings`,
            songAnimationSettingsToApiPayload(next),
          );
        } catch {
          /* local preview still updates */
        }
      }, 400);
    },
    [isNarrationEditor, projectId, isSongEditorProject, songMotionPreviewOpts],
  );

  const handleEditorAnimationSpeedChange = useCallback((speed) => {
    setEditorAnimationSpeed(clampEditorAnimationSpeed(speed));
  }, []);

  const handleSongBeatSyncSpeedChange = useCallback((speed) => {
    persistSongAnimationSettings({ animationSpeed: clampSongAnimationSpeed(speed) });
  }, [persistSongAnimationSettings]);
  const [subtitleDialogOpen, setSubtitleDialogOpen] = useState(false);
  const [subtitleDraft, setSubtitleDraft] = useState({ id: '', text: '', muted: false });
  const [batchVideoProvider, setBatchVideoProvider] = useState(isNarrationEditor ? 'narration_slideshow' : 'nano_banana');
  const [batchCameraMotion, setBatchCameraMotion] = useState('auto');
  const [generatingPendingVideos, setGeneratingPendingVideos] = useState(false);
  const [pendingVideoProgress, setPendingVideoProgress] = useState({ current: 0, total: 0, name: '' });
  const [generatingPendingAudios, setGeneratingPendingAudios] = useState(false);
  const [pendingAudioProgress, setPendingAudioProgress] = useState({ current: 0, total: 0, name: '' });
  const [bulkAudioDialogOpen, setBulkAudioDialogOpen] = useState(false);
  const [bulkAudioVoicePickerOpen, setBulkAudioVoicePickerOpen] = useState(false);
  const [replacementAudioVoicePickerOpen, setReplacementAudioVoicePickerOpen] = useState(false);
  const [audioTranslationLangTarget, setAudioTranslationLangTarget] = useState('hi');
  const [translatingAudioLane, setTranslatingAudioLane] = useState(false);
  const [audioTrackMeta, setAudioTrackMeta] = useState([{ languageCode: null, label: 'Original', isTranslation: false }]);
  const audioTrackMetaRef = useRef(audioTrackMeta);
  audioTrackMetaRef.current = audioTrackMeta;

  const timelineRef = useRef(null);
  const [timelineScrollLeft, setTimelineScrollLeft] = useState(0);
  const [timelineViewportWidth, setTimelineViewportWidth] = useState(0);
  const openProjectInputRef = useRef(null);
  const afterLoadProjectSeekRef = useRef(false);
  const audioInputRef = useRef(null);
  const trackRefs = useRef(new Map());
  const audioTrackRefs = useRef(new Map());
  const subtitleTrackRefs = useRef(new Map());
  const musicTrackRefs = useRef(new Map());
  const effectTrackRefs = useRef(new Map());
  const overlayTrackRefs = useRef(new Map());
  const scriptSectionRefs = useRef(new Map());
  const dragStateRef = useRef(null);
  const [draggingClipId, setDraggingClipId] = useState(null);
  const [selectedTimelineClips, setSelectedTimelineClips] = useState([]);
  const selectedTimelineClip = selectedTimelineClips.length
    ? selectedTimelineClips[selectedTimelineClips.length - 1]
    : null;
  const setSelectedTimelineClip = useCallback((sel) => {
    setSelectedTimelineClips(sel ? [sel] : []);
  }, []);
  const selectTimelineClip = useCallback((sel, { additive = false } = {}) => {
    if (!sel) {
      setSelectedTimelineClips([]);
      return;
    }
    setSelectedTimelineClips((prev) => {
      if (!additive) return [sel];
      const exists = prev.some((c) => timelineSelectionEquals(c, sel));
      if (exists) return prev.filter((c) => !timelineSelectionEquals(c, sel));
      if (!['image', 'video'].includes(sel.type)) return [sel];
      const keep = prev.filter((c) => ['image', 'video'].includes(c.type));
      return [...keep, sel];
    });
  }, []);
  const [multishotTimelineToggling, setMultishotTimelineToggling] = useState(false);

  const { isExporting, exportVideo, downloadIndividual } = useVideoExport();

  const handlePlaybackTimeUpdate = useCallback(() => {}, []);
  const handlePlaybackEnd = useCallback(() => {
    toast.info('Playback complete');
  }, []);

  const mainTimelineEnd = useMemo(
    () => timelineEndFromVideoClips(
      (videoTracks || []).flat().filter((c) => !isBookendTimelineClip(c)),
    ),
    [videoTracks],
  );

  const [brandOutroInfo, setBrandOutroInfo] = useState(null);

  useEffect(() => {
    let cancelled = false;
    const ar = String(exportAspectRatio || '16:9').trim() || '16:9';
    api
      .get('/branding/outro/info', { params: { aspect_ratio: ar } })
      .then((res) => {
        if (cancelled) return;
        const data = res?.data || {};
        const url = String(data.url || '').trim();
        if (!url) {
          setBrandOutroInfo(null);
          return;
        }
        setBrandOutroInfo({
          url: mediaUrlForPlayback(url) || url,
          durationSeconds: Number(data.duration_seconds) || 10,
          aspectRatio: String(data.aspect_ratio || ar),
        });
      })
      .catch(() => {
        if (!cancelled) setBrandOutroInfo(null);
      });
    return () => { cancelled = true; };
  }, [exportAspectRatio]);

  const bookendTimeline = useMemo(
    () => withBrandOutroOnTimeline(
      bookendClipsForMainEnd(videoBookends, mainTimelineEnd),
      brandOutroInfo,
    ),
    [videoBookends, mainTimelineEnd, brandOutroInfo],
  );
  bookendIntroDurRef.current = bookendTimeline.introDur || 0;

  useEffect(() => {
    const total = bookendTimeline.totalDuration;
    if (!(total > 0)) return;
    setMusicTracks((prev) => {
      const next = stretchBackgroundMusicForBookends(prev, total);
      return JSON.stringify(next) === JSON.stringify(prev) ? prev : next;
    });
  }, [bookendTimeline.totalDuration]);

  const animationLaneClips = useMemo(() => {
    const track = (videoTracks || [])[0] || [];
    const introDur = bookendTimeline.introDur;
    const refLane = resolveAnimationReferenceAudioLane({
      audioTracks,
      timelineActive,
    });
    return track
      .filter((clip) => clip.imageUrl && !clip.isBookend)
      .map((clip) => {
        const sceneId = clip.sceneId || clip.id;
        const audioBlock = sceneAudioBlockFromLane(
          sceneId,
          audioTracks,
          refLane,
          masterAudioPlaybackSpeed,
        );
        return {
          id: clip.id,
          name: clip.name,
          segmentNumber: clip.segmentNumber,
          motionPreset: clip.motionPreset,
          startTime: audioBlock
            ? audioBlock.startTime + introDur
            : (Number(clip.startTime) || 0) + introDur,
          duration: audioBlock
            ? audioBlock.duration
            : imageLaneClipDrawDuration(clip, clip),
        };
      });
  }, [
    videoTracks,
    audioTracks,
    timelineActive,
    bookendTimeline.introDur,
    masterAudioPlaybackSpeed,
  ]);

  const patchClipMotion = useCallback((clipId, preset) => {
    const normalized = normalizeMotionPreset(preset);
    setVideoTracks((prev) => prev.map((track) => track.map((clip) => (
      clip.id === clipId ? { ...clip, motionPreset: normalized } : clip
    ))));
  }, []);

  const applyMotionToAllImages = useCallback((preset) => {
    recordEditorHistoryRef.current();
    const normalized = normalizeMotionPreset(preset);
    setVideoTracks((prev) => prev.map((track, idx) => (
      idx === 0
        ? track.map((clip) => (
          clip.imageUrl && !clip.isBookend
            ? { ...clip, motionPreset: normalized }
            : clip
        ))
        : track
    )));
    toast.success(`Applied ${motionPresetLabel(normalized)} to all images`);
  }, []);

  const applyRandomMotionToImageClips = useCallback(({ storyOnly = false } = {}) => {
    recordEditorHistoryRef.current();
    setVideoTracks((prev) => prev.map((track, idx) => (
      idx === 0
        ? track.map((clip) => (
          clip.imageUrl && !clip.isBookend
            ? { ...clip, motionPreset: pickRandomMotionPreset({ storyOnly }) }
            : clip
        ))
        : track
    )));
    toast.success(
      storyOnly
        ? 'Random story-video animations applied to all images'
        : 'Random animations applied to all images',
    );
  }, []);

  const sceneByIdMap = useMemo(
    () => Object.fromEntries((scenesSnapshot || []).map((s) => [String(s.id), s])),
    [scenesSnapshot],
  );

  const playbackVideoTracks = useMemo(
    () => (videoTracks || []).map((track, trackIdx) => {
      const introDur = bookendTimeline.introDur;
      const laneBookends = trackIdx === 0
        ? [...bookendTimeline.clips.video, ...bookendTimeline.clips.image]
        : [];
      const merged = mergeLaneClipsWithBookends(track, laneBookends, introDur);
      return merged
        .map((clip) => {
          if (clip.videoUrl && timelineActive.video) return clip;
          if (clip.imageUrl && timelineActive.image) return { ...clip, videoUrl: null };
          return null;
        })
        .filter(Boolean);
    }),
    [videoTracks, timelineActive.image, timelineActive.video, bookendTimeline],
  );
  const resolveClipsForPlayback = useCallback(
    (tracks) => (tracks || []).map((track) => (track || []).map((c) => (
      c?.url ? { ...c, url: mediaUrlForPlayback(c.url) } : c
    ))),
    [],
  );
  const playbackAudioTracks = useMemo(() => {
    const lanes = audioTracks || [];
    const mask = audioLanePlaybackMask(timelineActive, lanes.length);
    const introDur = bookendTimeline.introDur;
    const masked = lanes.map((track, idx) => {
      const base = mask[idx] ? track : [];
      if (idx === 0) {
        return mergeLaneClipsWithBookends(base, bookendTimeline.clips.audio, introDur);
      }
      return (base || []).map((c) => offsetTimelineClipForBookendIntro(c, introDur));
    });
    return resolveClipsForPlayback(masked);
  }, [audioTracks, timelineActive, resolveClipsForPlayback, bookendTimeline]);
  const engineSfxState = useMemo(() => engineSfxPlaybackState({
    engineEnabled: Boolean(engineSfxPlan?.engine_enabled),
    plan: engineSfxPlan,
    scenes: scenesSnapshot,
    videoClips: (videoTracks || []).flat(),
    remixUi: engineSfxRemixUi,
  }), [engineSfxPlan, scenesSnapshot, videoTracks, engineSfxRemixUi]);

  useEffect(() => {
    setMusicTracks((prev) => ensureVisibleEngineSfxLane(prev, engineSfxPlan));
  }, [engineSfxPlan, musicTracks]);

  const displayedMusicLanes = useMemo(
    () => orderedMusicLanesForTimeline(musicTracks),
    [musicTracks],
  );

  const playbackMusicTracks = useMemo(() => {
    const masked = maskMusicTracksForPlayback(musicTracks, timelineActive);
    const withStem = applyEngineSfxPlayback(masked, engineSfxState);
    const introDur = bookendTimeline.introDur;
    const offset = (withStem || []).map((track) => (track || []).map((clip) => (
      isEngineSfxStemClip(clip) ? offsetTimelineClipForBookendIntro(clip, introDur) : clip
    )));
    const stretched = stretchBackgroundMusicForBookends(offset, bookendTimeline.totalDuration);
    return resolveClipsForPlayback(stretched);
  }, [musicTracks, timelineActive, resolveClipsForPlayback, bookendTimeline.totalDuration, bookendTimeline.introDur, engineSfxState]);

  const masterVideoPreviewMuted = isMuted || videoMasterMuted;

  const getVideoPreviewAudioState = useCallback((clip) => ({
    muted: effectiveVideoClipPreviewMuted(clip, masterVideoPreviewMuted),
    volume: effectiveVideoClipPreviewVolume(clip, masterVideoPreviewMuted, volume),
  }), [masterVideoPreviewMuted, volume]);

  useEffect(() => {
    masterAudioPlaybackSpeedRef.current = masterAudioPlaybackSpeed;
  }, [masterAudioPlaybackSpeed]);

  useEffect(() => {
    if (!storyId) {
      setEngineSfxPlan(null);
      setEngineSfxRemixUi('');
      return undefined;
    }
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get(`/stories/${storyId}/sound-events`);
        if (!cancelled) setEngineSfxPlan(data);
      } catch {
        if (!cancelled) setEngineSfxPlan(null);
      }
    })();
    return () => { cancelled = true; };
  }, [storyId]);

  useEffect(() => {
    if (!storyId || !engineSfxPlan?.engine_enabled) return undefined;
    const state = engineSfxPlaybackState({
      engineEnabled: true,
      plan: engineSfxPlan,
      scenes: scenesSnapshot,
      videoClips: (videoTracks || []).flat(),
      remixUi: engineSfxRemixUi === 'failed' ? 'failed' : engineSfxRemixUi,
    });
    if (!shouldTriggerEngineRemix(state) && engineSfxRemixUi !== 'updating') return undefined;
    const layoutKey = JSON.stringify(mixLayoutRows(scenesSnapshot));
    let cancelled = false;
    const poll = async () => {
      try {
        const { data } = await api.get(`/stories/${storyId}/sound-events`);
        if (cancelled) return;
        setEngineSfxPlan(data);
        const next = engineSfxPlaybackState({
          engineEnabled: Boolean(data?.engine_enabled),
          plan: data,
          scenes: scenesSnapshotRef.current,
          videoClips: (videoTracksRef.current || []).flat(),
          remixUi: '',
        });
        if (data?.status === 'failed') {
          setEngineSfxRemixUi('failed');
          return;
        }
        if (next.mode === 'stem') {
          setEngineSfxRemixUi('');
          engineSfxRemixKeyRef.current = layoutKey;
        }
      } catch {
        if (!cancelled) setEngineSfxRemixUi('failed');
      }
    };
    const start = async () => {
      if (engineSfxRemixKeyRef.current !== layoutKey) {
        engineSfxRemixKeyRef.current = layoutKey;
        setEngineSfxRemixUi('updating');
        try {
          const retryAssets = engineSfxPlan?.status === 'failed';
          await api.post(`/stories/${storyId}/soundtrack/regenerate`, {
            mix_only: !retryAssets,
            mode: retryAssets ? 'asset' : 'mix',
          });
        } catch {
          if (!cancelled) setEngineSfxRemixUi('failed');
          return;
        }
      }
      await poll();
    };
    start();
    const timer = window.setInterval(poll, 2500);
    return () => {
      cancelled = true;
      window.clearInterval(timer);
    };
  }, [storyId, engineSfxPlan, scenesSnapshot, videoTracks, engineSfxRemixUi]);

  const playback = useSequentialPlayback({
    videoTracks: playbackVideoTracks,
    audioTracks: playbackAudioTracks,
    musicTracks: playbackMusicTracks,
    onTimeUpdate: handlePlaybackTimeUpdate,
    onPlaybackEnd: handlePlaybackEnd,
    getVideoPreviewAudioState,
    masterAudioPlaybackSpeed,
    masterPreviewVolume: volume,
    masterPreviewMuted: isMuted,
    onFrameTick: (frame) => onPlaybackFrameTickRef.current?.(frame),
  });

  const playbackPlayingRef = useRef(false);
  playbackPlayingRef.current = playback.isPlaying;
  const deferredSegmentSyncRef = useRef(false);

  const currentClip = playback.currentClip;
  const activeBookendPreviewSlot = useMemo(() => {
    if (!currentClip?.isBookend || currentClip?.isBrandOutro) return null;
    const norm = normalizeVideoBookends(videoBookends, {
      defaultTitle: story?.title || '',
      aspectRatio: exportAspectRatio || '16:9',
    });
    return currentClip.bookendRole === 'outro' ? norm.outro : norm.intro;
  }, [currentClip, videoBookends, story?.title, exportAspectRatio]);
  const currentClipRef = useRef(currentClip);
  currentClipRef.current = currentClip;
  const timelineActiveAnimationRef = useRef(timelineActive.animation);
  timelineActiveAnimationRef.current = timelineActive.animation;
  const timelineActiveSubtitleRef = useRef(timelineActive.subtitle);
  timelineActiveSubtitleRef.current = timelineActive.subtitle;
  const timelineActiveRef = useRef(timelineActive);
  timelineActiveRef.current = timelineActive;

  const getEditorHistorySnapshot = useCallback(() => captureVideoEditorSnapshot({
    videoTracks: videoTracksRef.current,
    audioTracks: audioTracksRef.current,
    musicTracks: musicTracksRef.current,
    subtitleTracks: subtitleTracksRef.current,
    effectTracks: effectTracksRef.current,
    overlayTracks: overlayTracksRef.current,
    subtitleStyle: subtitleStyleRef.current,
    videoBookends: videoBookendsRef.current,
    timelineActive: timelineActiveRef.current,
    audioTrackMeta: audioTrackMetaRef.current,
    editorAnimationSpeed: editorAnimationSpeedRef.current,
  }), []);

  const applyEditorHistorySnapshot = useCallback((snap) => {
    setVideoTracks(snap.videoTracks || [[]]);
    setAudioTracks(snap.audioTracks || [[]]);
    setMusicTracks(snap.musicTracks || [[]]);
    setSubtitleTracks(snap.subtitleTracks || [[]]);
    setEffectTracks(snap.effectTracks || [[]]);
    setOverlayTracks(snap.overlayTracks || [[]]);
    setSubtitleStyle(snap.subtitleStyle || normalizeVideoSubtitleStyle(null));
    setVideoBookends(snap.videoBookends || { ...DEFAULT_VIDEO_BOOKENDS });
    setTimelineActive(snap.timelineActive || { ...DEFAULT_TIMELINE_ACTIVE });
    setAudioTrackMeta(snap.audioTrackMeta || [{ languageCode: null, label: 'Original', isTranslation: false }]);
    setEditorAnimationSpeed(snap.editorAnimationSpeed ?? EDITOR_ANIMATION_SPEED_DEFAULT);
    setEditingClip(null);
    setSelectedTimelineClip(null);
  }, []);

  const {
    pushHistory: recordEditorHistory,
    undo: undoEditorAction,
    redo: redoEditorAction,
    resetHistory: resetEditorHistory,
    canUndo: canUndoEditor,
    canRedo: canRedoEditor,
  } = useVideoEditorUndoRedo({
    getSnapshot: getEditorHistorySnapshot,
    applySnapshot: applyEditorHistorySnapshot,
  });
  recordEditorHistoryRef.current = recordEditorHistory;

  useEffect(() => {
    const onKeyDown = (e) => {
      const tag = String(e.target?.tagName || '').toLowerCase();
      if (tag === 'input' || tag === 'textarea' || tag === 'select' || e.target?.isContentEditable) return;
      const mod = e.metaKey || e.ctrlKey;
      if (!mod) return;
      if (e.key === 'z' && !e.shiftKey) {
        if (canUndoEditor) {
          e.preventDefault();
          undoEditorAction();
        }
        return;
      }
      if (e.key === 'Z' || (e.key === 'z' && e.shiftKey) || e.key === 'y') {
        if (canRedoEditor) {
          e.preventDefault();
          redoEditorAction();
        }
      }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [canUndoEditor, canRedoEditor, undoEditorAction, redoEditorAction]);

  const incomingTransitionVideoRef = useRef(null);
  const outgoingTransitionVideoRef = useRef(null);
  const incomingTransitionImageRef = useRef(null);
  const outgoingTransitionImageRef = useRef(null);

  const dialogueCameraOn = Boolean(storyId && usesDialogueCamera(story));
  const dialogueSceneMediaKey = useMemo(
    () => (scenesSnapshot || []).map((s) => `${s.id}:${s.image_url || ''}:${s.audio_url || ''}`).join('|'),
    [scenesSnapshot],
  );
  useEffect(() => {
    if (!dialogueCameraOn) {
      setDialogueShotsByScene({});
      return undefined;
    }
    let cancelled = false;
    api
      .get(`/stories/${storyId}/dialogue-shots`, { params: { aspect_ratio: exportAspectRatio, refresh: true } })
      .then(({ data }) => {
        if (!cancelled) setDialogueShotsByScene(shotsBySceneId(data));
      })
      .catch(() => {
        if (!cancelled) setDialogueShotsByScene({});
      });
    return () => {
      cancelled = true;
    };
  }, [dialogueCameraOn, storyId, exportAspectRatio, dialogueSceneMediaKey]);

  /** Camera shots for an image clip of a dialogue scene (null for videos, multishot parts, other stories). */
  const dialogueShotsForClip = useCallback((clip) => {
    if (!clip || clip.videoUrl || (clip.parentSceneId != null && clip.multishotPart != null)) return null;
    const sid = normalizeEditorSceneId(clip.sceneId || clip.id);
    return (sid && dialogueShotsByScene[sid]) || null;
  }, [dialogueShotsByScene]);

  const buildClipPreviewVisualStyle = useCallback((clip, timelineTime) => {
    if (!clip) return {};
    const filterStyle = clipFilterPreviewStyle(clipFiltersFromClip(clip));
    const sceneShots = dialogueShotsForClip(clip);
    if (sceneShots) {
      const rect = sceneCameraRectAt(sceneShots, clip.startTime, clip.trimStart, timelineTime);
      return mergePreviewLayerStyles(focusPreviewStyle(rect), filterStyle);
    }
    const motionOpts = {
      presetAnimationSpeed: editorAnimationSpeedRef.current,
      ...(isSongBasedNarrationProject(story) ? songMotionPreviewOptsRef.current : {}),
    };
    const songAnimOff = motionOpts.defaultAnimationEnabled === false;
    const motionCss = timelineActive.animation && !songAnimOff
      ? clipMotionPreviewStyle(clip, timelineTime, motionOpts)
      : {};
    return mergePreviewLayerStyles(motionCss, filterStyle);
  }, [timelineActive.animation, story, editorAnimationSpeed, dialogueShotsForClip]);

  /** Crossfade preview in the handoff window before the next clip starts. */
  const transitionPreviewBlend = useMemo(() => {
    const all = playback.allClips || [];
    return computeTransitionPreviewBlend(all, visualCurrentTime);
  }, [playback.allClips, visualCurrentTime]);

  const activeTransitionBlend = playback.isPlaying ? playbackTransitionBlend : transitionPreviewBlend;

  const outgoingDrivesMainVideo = Boolean(
    activeTransitionBlend
    && currentClip?.id === activeTransitionBlend.outgoing?.id
    && currentClip?.videoUrl,
  );
  const outgoingDrivesMainVideoRef = useRef(outgoingDrivesMainVideo);
  outgoingDrivesMainVideoRef.current = outgoingDrivesMainVideo;
  const transitionPreviewBlendRef = useRef(transitionPreviewBlend);
  transitionPreviewBlendRef.current = activeTransitionBlend;
  const playbackTransitionBlendRef = useRef(playbackTransitionBlend);
  playbackTransitionBlendRef.current = playbackTransitionBlend;

  const previewClipFilterStyle = useMemo(() => {
    if (!currentClip) return {};
    return clipFilterPreviewStyle(clipFiltersFromClip(currentClip));
  }, [currentClip]);
  previewFilterStyleRef.current = previewClipFilterStyle;

  const previewMotionActive = Boolean(
    playback.isPlaying
    && timelineActive.animation
    && songMotionPreviewOpts.defaultAnimationEnabled !== false
    && currentClip
    && (normalizeMotionPreset(currentClip.motionPreset) !== 'none' || clipHasAudioMotion(currentClip)),
  );

  useEffect(() => {
    if (!playback.isPlaying) {
      previewFrozenStyleTimeRef.current = visualCurrentTime;
    }
  }, [playback.isPlaying, visualCurrentTime]);

  const previewStyleTime = playback.isPlaying
    ? previewFrozenStyleTimeRef.current
    : visualCurrentTime;

  useEffect(() => {
    const videoEl = playback.videoRef.current;
    if (!videoEl || !currentClip?.videoUrl) return;
    const desired = mediaUrlWithCacheBust(
      currentClip.videoUrl,
      currentClip.sceneMediaUpdatedAt,
    );
    if (!desired) return;
    try {
      const current = new URL(videoEl.getAttribute('src') || videoEl.src, window.location.href).href;
      const next = new URL(desired, window.location.href).href;
      if (current !== next) {
        videoEl.src = desired;
        videoEl.load();
      }
    } catch {
      if (videoEl.src !== desired) {
        videoEl.src = desired;
        videoEl.load();
      }
    }
  }, [currentClip?.id, currentClip?.videoUrl, currentClip?.sceneMediaUpdatedAt, playback.videoRef]);

  const previewClipVisualStyle = useMemo(() => {
    if (!currentClip || previewMotionActive) return {};
    const scrubTime = Number(visualCurrentTime) || 0;
    const base = buildClipPreviewVisualStyle(currentClip, scrubTime);
    if (outgoingDrivesMainVideo) {
      return mergePreviewLayerStyles(base, activeTransitionBlend.layerStyles?.outgoing);
    }
    return base;
  }, [
    buildClipPreviewVisualStyle,
    currentClip,
    outgoingDrivesMainVideo,
    previewMotionActive,
    visualCurrentTime,
    activeTransitionBlend,
  ]);

  useEffect(() => {
    if (previewMotionActive) return;
    const layer = previewMotionLayerRef.current;
    if (!layer || !currentClip) return;
    applyPreviewLayerStylesToElement(layer, previewClipVisualStyle);
  }, [previewMotionActive, previewClipVisualStyle, currentClip?.id]);

  previewMotionExtraLayersRef.current = (() => {
    const blend = playback.isPlaying
      ? playbackTransitionBlendRef.current
      : activeTransitionBlend;
    return (
      outgoingDrivesMainVideo && blend?.layerStyles?.outgoing
        ? [blend.layerStyles.outgoing]
        : []
    );
  })();

  const allClipsRef = useRef(playback.allClips);
  allClipsRef.current = playback.allClips;
  const buildClipPreviewVisualStyleRef = useRef(buildClipPreviewVisualStyle);
  buildClipPreviewVisualStyleRef.current = buildClipPreviewVisualStyle;

  const UI_VISUAL_PUBLISH_INTERVAL_SEC = 0.25;

  onPlaybackFrameTickRef.current = ({ transportTime, clip }) => {
    let nextTime = transportTime;
    const videoEl = playback.videoRef.current;
    if (videoEl && clip?.videoUrl) {
      const trimStart = clip.trimStart || 0;
      const spd = clipVideoPlaybackSpeed(clip);
      const denom = spd > 0 ? spd : 1;
      nextTime = clip.startTime + Math.max(0, (videoEl.currentTime - trimStart) / denom);
    }

    visualCurrentTimeRef.current = nextTime;

    const blend = computeTransitionPreviewBlend(allClipsRef.current || [], nextTime);
    const blendKey = blend ? `${blend.outgoing.id}:${blend.incoming.id}` : '';
    if (blendKey !== lastPlaybackTransitionKeyRef.current) {
      lastPlaybackTransitionKeyRef.current = blendKey;
      setPlaybackTransitionBlend(blend);
    }
    playbackTransitionBlendRef.current = blend;

    const motionPlaying = Boolean(
      timelineActiveAnimationRef.current
      && songMotionPreviewOptsRef.current?.defaultAnimationEnabled !== false
      && clip
      && (normalizeMotionPreset(clip.motionPreset) !== 'none' || clipHasAudioMotion(clip)),
    );
    const outgoingMain = Boolean(
      blend
      && clip?.id === blend.outgoing?.id
      && clip?.videoUrl,
    );
    if (motionPlaying || (outgoingMain && blend)) {
      previewMotionExtraLayersRef.current = (
        outgoingMain && blend?.layerStyles?.outgoing
          ? [blend.layerStyles.outgoing]
          : []
      );
      applyMainClipPreviewFrame({
        layerRef: previewMotionLayerRef,
        getTransportTime: () => nextTime,
        clipRef: currentClipRef,
        animationEnabledRef: timelineActiveAnimationRef,
        filterStyleRef: previewFilterStyleRef,
        extraLayersRef: previewMotionExtraLayersRef,
        motionOptsRef: previewMotionOptsRef,
        blend,
        outgoingDrivesMain: outgoingMain,
        buildClipVisualStyle: (c, t) => buildClipPreviewVisualStyleRef.current(c, t),
      });
    }

    if (blend) {
      const syncTransitionVideo = (el, clip, mediaTime) => {
        if (!el || !clip?.videoUrl) return;
        const ts = Math.max(0, Number(clip.trimStart) || 0);
        const te = clipVideoSourceTrimEndSeconds(clip)
          || Number(clip.trimEnd)
          || Number(clip.duration)
          || ts + 1;
        const hi = Math.max(ts + 0.001, te);
        const target = Math.min(hi, Math.max(ts, mediaTime));
        if (Math.abs(el.currentTime - target) > 0.12) el.currentTime = target;
        el.muted = true;
        if (playbackPlayingRef.current) {
          if (el.paused) el.play().catch(() => {});
        } else if (!el.paused) {
          el.pause();
        }
      };
      const incomingMediaTime = clipPreviewMediaTime(
        blend.incoming,
        blend.handoff - blend.dur + (nextTime - blend.t0),
      );
      syncTransitionVideo(
        incomingTransitionVideoRef.current,
        blend.incoming,
        incomingMediaTime,
      );
      if (!outgoingMain) {
        syncTransitionVideo(
          outgoingTransitionVideoRef.current,
          blend.outgoing,
          clipPreviewMediaTime(blend.outgoing, nextTime),
        );
      }
      applyTransitionPreviewFrame({
        blend,
        timelineTime: nextTime,
        buildClipVisualStyle: (c, t) => buildClipPreviewVisualStyleRef.current(c, t),
        outgoingVideoRef: outgoingTransitionVideoRef,
        outgoingImageRef: outgoingTransitionImageRef,
        incomingVideoRef: incomingTransitionVideoRef,
        incomingImageRef: incomingTransitionImageRef,
      });
    }

    if (nextTime - lastVisualUiPublishRef.current >= UI_VISUAL_PUBLISH_INTERVAL_SEC) {
      lastVisualUiPublishRef.current = nextTime;
      setVisualCurrentTime(nextTime);

      if (timelineActiveSubtitleRef.current) {
        const introDur = bookendIntroDurRef.current;
        let cueId = '';
        const activeMatch = findActiveSubtitleCueOnActiveLanes(
          subtitleTracksRef.current,
          timelineActiveRef.current,
          nextTime,
          introDur,
        );
        if (activeMatch?.cue) {
          cueId = activeMatch.cue.id;
        } else if (!isPlayheadBeforeIntroBookend(introDur, nextTime)) {
          const clip = spokenScriptClipRef.current;
          if (clip && !clip.isBookend) cueId = `subtitle-preview-${clip.id}`;
        }
        if (cueId !== lastSubtitleCueIdRef.current) {
          lastSubtitleCueIdRef.current = cueId;
          setSubtitleCueRevision((n) => n + 1);
        }
      }
    }
  };

  const showDualTransitionOverlay = Boolean(
    activeTransitionBlend && !outgoingDrivesMainVideo,
  );

  useEffect(() => {
    const blend = activeTransitionBlend;
    if (!blend || playback.isPlaying) return undefined;
    const t = Number(playback.currentTime) || 0;
    const syncEl = (el, clip, mediaTime) => {
      if (!el || !clip?.videoUrl) return;
      const ts = Math.max(0, Number(clip.trimStart) || 0);
      const te = clipVideoSourceTrimEndSeconds(clip) || Number(clip.trimEnd) || Number(clip.duration) || ts + 1;
      const hi = Math.max(ts + 0.001, te);
      const target = Math.min(hi, Math.max(ts, mediaTime));
      if (Math.abs(el.currentTime - target) > 0.08) el.currentTime = target;
      el.pause();
    };

    const incomingMediaTime = clipPreviewMediaTime(
      blend.incoming,
      blend.handoff - blend.dur + (t - blend.t0),
    );

    if (outgoingDrivesMainVideo) {
      syncEl(
        playback.videoRef.current,
        blend.outgoing,
        clipPreviewMediaTime(blend.outgoing, t),
      );
    } else {
      syncEl(outgoingTransitionVideoRef.current, blend.outgoing, clipPreviewMediaTime(blend.outgoing, t));
    }
    syncEl(incomingTransitionVideoRef.current, blend.incoming, incomingMediaTime);

    applyTransitionPreviewFrame({
      blend,
      timelineTime: t,
      buildClipVisualStyle: (c, time) => buildClipPreviewVisualStyleRef.current(c, time),
      outgoingVideoRef: outgoingTransitionVideoRef,
      outgoingImageRef: outgoingTransitionImageRef,
      incomingVideoRef: incomingTransitionVideoRef,
      incomingImageRef: incomingTransitionImageRef,
    });
    return undefined;
  }, [
    activeTransitionBlend,
    outgoingDrivesMainVideo,
    playback.currentTime,
    playback.isPlaying,
    playback.videoRef,
  ]);

  useEffect(() => {
    const el = playback.videoRef.current;
    if (!el || !currentClip?.videoUrl) return;
    const { muted: m, volume: v } = getVideoPreviewAudioState(currentClip);
    el.muted = m;
    el.volume = v;
  }, [currentClip?.id, currentClip?.muted, currentClip?.volume, currentClip?.videoUrl, getVideoPreviewAudioState, playback.videoRef]);

  /** Scrub preview video when paused; during play the transport hook owns sync. */
  useEffect(() => {
    if (showDualTransitionOverlay || playback.isPlaying) return undefined;
    const el = playback.videoRef.current;
    if (!el || !currentClip?.videoUrl) return undefined;

    const mediaTime = clipPreviewMediaTime(currentClip, playback.currentTime);
    const ts = Math.max(0, Number(currentClip.trimStart) || 0);
    const te = clipVideoSourceTrimEndSeconds(currentClip)
      || Number(currentClip.trimEnd)
      || Number(currentClip.duration)
      || ts + 1;
    const hi = Math.max(ts + 0.001, te);
    const target = Math.min(hi, Math.max(ts, mediaTime));
    if (Number.isFinite(target) && Math.abs(el.currentTime - target) > 0.12) {
      el.currentTime = target;
    }
    if (!el.paused) el.pause();
    return undefined;
  }, [
    showDualTransitionOverlay,
    currentClip?.id,
    currentClip?.videoUrl,
    currentClip?.trimStart,
    currentClip?.trimEnd,
    currentClip?.duration,
    currentClip?.startTime,
    playback.currentTime,
    playback.isPlaying,
    playback.videoRef,
  ]);

  /** Ensure preview video resumes after timeline edits while transport is still playing. */
  useEffect(() => {
    if (!playback.isPlaying || showDualTransitionOverlay) return undefined;
    const el = playback.videoRef.current;
    if (!el || !currentClip?.videoUrl) return undefined;
    if (el.paused) el.play().catch(() => {});
    return undefined;
  }, [
    showDualTransitionOverlay,
    currentClip?.id,
    currentClip?.videoUrl,
    playback.isPlaying,
    playback.videoRef,
    videoTracks,
    audioTracks,
    musicTracks,
  ]);

  const audioFileDurations = playback.audioFileDurations || {};

  useEffect(() => {
    if (!Object.keys(audioFileDurations).length) return;
    setMusicTracks((prev) => prev.map((track) => track.map((c) => {
      if (!isStoryBackgroundMusicClip(c)) return c;
      const fd = Number(audioFileDurations[c.id]) || 0;
      if (fd <= 0) return c;
      const maxDur = Math.max(storyBackgroundMusicMaxDuration(c), fd);
      if (Number(c.sourceDuration) >= maxDur - 0.01) return c;
      return { ...c, sourceDuration: maxDur };
    })));
  }, [audioFileDurations]);

  const syncPreviewFullscreen = useCallback(() => {
    setIsPreviewFullscreen(document.fullscreenElement === previewStageRef.current);
  }, []);

  useEffect(() => {
    document.addEventListener('fullscreenchange', syncPreviewFullscreen);
    return () => document.removeEventListener('fullscreenchange', syncPreviewFullscreen);
  }, [syncPreviewFullscreen]);

  useEffect(() => {
    const onViewport = () => {
      setViewportMin(Math.min(window.innerWidth, window.innerHeight));
    };
    onViewport();
    window.addEventListener('resize', onViewport);
    return () => window.removeEventListener('resize', onViewport);
  }, []);

  useLayoutEffect(() => {
    const el = previewStageRef.current;
    if (!el) return undefined;
    const measure = () => {
      const rect = el.getBoundingClientRect();
      const w = Math.round(rect.width);
      const h = Math.round(rect.height);
      if (w < 1 && h < 1) return;
      setPreviewStageSize((prev) => (prev.w === w && prev.h === h ? prev : { w, h }));
    };
    measure();
    const raf1 = requestAnimationFrame(() => {
      measure();
      requestAnimationFrame(measure);
    });
    const ro = new ResizeObserver(() => measure());
    ro.observe(el);
    return () => {
      cancelAnimationFrame(raf1);
      ro.disconnect();
    };
  }, [
    isPreviewFullscreen,
    showMediaLibrary,
    exportAspectRatio,
    loading,
    videoTracks,
    directorDialogOpen,
  ]);

  const previewContentRect = useMemo(
    () => computeObjectContainContentRect(
      previewStageSize.w,
      previewStageSize.h,
      exportAspectRatio,
    ),
    [previewStageSize.w, previewStageSize.h, exportAspectRatio],
  );

  const togglePreviewFullscreen = useCallback(async () => {
    const el = previewStageRef.current;
    if (!el) return;
    try {
      if (document.fullscreenElement === el) {
        await document.exitFullscreen();
      } else {
        await el.requestFullscreen();
      }
    } catch {
      toast.error('Fullscreen is not supported in this browser');
    }
  }, []);

  const pxPerSec = PX_PER_SEC * timelineZoom;
  const totalDuration = Math.max(playback.totalDuration || 1, bookendTimeline.totalDuration || 1);
  const stopPlayback = playback.stop;

  useEffect(() => {
    const el = timelineRef.current;
    if (!el || typeof ResizeObserver === 'undefined') return undefined;
    const apply = () => setTimelineViewportWidth(el.clientWidth || 0);
    apply();
    const ro = new ResizeObserver(apply);
    ro.observe(el);
    return () => ro.disconnect();
  }, [editorId, story?.id]);

  const timelineViewportRange = useMemo(
    () => timelineViewportTimeRange({
      scrollLeft: timelineScrollLeft,
      viewportWidth: timelineViewportWidth,
      pxPerSec,
      labelWidth: TIMELINE_LABEL_WIDTH_PX,
    }),
    [timelineScrollLeft, timelineViewportWidth, pxPerSec],
  );

  const timelineForceClipIds = useMemo(() => {
    const ids = new Set();
    selectedTimelineClips.forEach((sel) => {
      if (sel?.id) ids.add(sel.id);
    });
    if (draggingClipId) ids.add(draggingClipId);
    return ids;
  }, [selectedTimelineClips, draggingClipId]);

  const visibleTimelineClips = useCallback(
    (clips, { getDuration, timeOffsetSec = 0 } = {}) => clipsInTimelineViewport(clips, {
      startSec: timelineViewportRange.startSec,
      endSec: timelineViewportRange.endSec,
      forceIds: timelineForceClipIds,
      getDuration,
      timeOffsetSec,
    }),
    [timelineViewportRange, timelineForceClipIds],
  );

  const visibleTimelineTimeMarks = useMemo(
    () => timeMarksInTimelineViewport({
      startSec: timelineViewportRange.startSec,
      endSec: timelineViewportRange.endSec,
      totalDuration,
    }),
    [timelineViewportRange, totalDuration],
  );
  const generatedMediaLibraryItems = useMemo(() => {
    const items = [];
    const seen = new Set();
    scenesSnapshot.forEach((scene, index) => {
      sceneMediaToLibraryItems(scene, index).forEach((item) => addMediaLibraryItem(items, seen, item));
    });
    if (isNewsVideoEditor) {
      const input = story?.input && typeof story.input === 'object' ? story.input : {};
      const atts = Array.isArray(input.news_attachments) ? input.news_attachments : [];
      const prompter = input.prompter && typeof input.prompter === 'object' ? input.prompter : {};
      const portrait = String(prompter.portrait_image_url || '').trim();
      if (portrait) {
        addMediaLibraryItem(items, seen, {
          id: `${editorId || 'project'}-news-prompter-portrait`,
          name: 'Prompter portrait',
          url: portrait,
          type: 'image',
          duration: 5,
          source: 'news_ref',
        });
      }
      atts.forEach((a, idx) => {
        const url = a && typeof a === 'object' ? String(a.url || '').trim() : '';
        if (!url) return;
        const kind = a && typeof a === 'object' ? String(a.kind || 'image').trim().toLowerCase() : 'image';
        const type = kind === 'video' ? 'video' : kind === 'audio' ? 'audio' : 'image';
        addMediaLibraryItem(items, seen, {
          id: `${editorId || 'project'}-news-attachment-${idx}`,
          name: (a && typeof a === 'object' && a.note ? String(a.note).trim() : '') || `News attachment ${idx + 1}`,
          url,
          type,
          duration: type === 'audio' ? 5 : 5,
          source: 'news_ref',
        });
      });
    }
    addMediaLibraryItem(items, seen, {
      id: `${editorId || 'project'}-final-video`,
      name: `${story?.title || 'Project'} final video`,
      url: story?.final_video_url || story?.timeline_url || null,
      type: 'video',
      duration: Number(story?.total_duration) || totalDuration || 5,
      source: 'final',
    });
    if (story?.music_url) {
      const musicDur =
        Number(story.music_target_duration) > 0
          ? Number(story.music_target_duration)
          : totalDuration || Number(story?.total_duration) || 5;
      addMediaLibraryItem(items, seen, {
        id: `${editorId || 'project'}-story-bg-music`,
        name: story?.music_mood ? `Background music (${story.music_mood})` : 'Background music',
        url: story.music_url,
        type: 'music',
        thumbnail: null,
        duration: musicDur,
        source: 'background_music',
        volume: 0.22,
      });
    }
    return items;
  }, [editorId, scenesSnapshot, story, totalDuration, isNewsVideoEditor]);

  const narrationLangClipTexts = useMemo(
    () =>
      (videoTracks || [])
        .flat()
        .map((c) => String(c.narrationText || c.sceneText || '').trim())
        .filter(Boolean),
    [videoTracks],
  );

  const narrationProjectLanguage = useMemo(
    () =>
      resolveVideoProjectScriptLanguage(story, scenesSnapshot, narrationLangClipTexts) || 'en',
    [story, scenesSnapshot, narrationLangClipTexts],
  );
  const workspaceScriptLangChoices = useMemo(
    () => LANGUAGE_OPTIONS.filter((o) => o.value !== narrationProjectLanguage),
    [narrationProjectLanguage],
  );

  const preferredYoutubeVideoUrl = useMemo(() => {
    if (exportedUrl) {
      const fromExport = mediaUrlForPlayback(exportedUrl);
      if (fromExport) return fromExport;
    }
    const raw = story?.final_video_url || story?.timeline_url;
    if (!raw) return '';
    return resolveMediaPlaybackUrl({
      url: raw,
      s3Url: story?.final_video_s3_url,
      localUrl: story?.final_video_local_url,
    }) || mediaUrlForPlayback(raw);
  }, [exportedUrl, story?.final_video_url, story?.final_video_local_url, story?.final_video_s3_url, story?.timeline_url]);

  /** Narration/script excerpt for YouTube AI metadata (timeline clips preferred; fallback scenes). */
  const youtubePublishScriptContext = useMemo(() => {
    const clipJoined = narrationLangClipTexts.join('\n\n').trim();
    if (clipJoined.length >= 80) return clipJoined.slice(0, 12000);
    const sceneJoined = (scenesSnapshot || [])
      .map((s) => String(s.narration_text || s.scene_text || s.text || '').trim())
      .filter(Boolean)
      .join('\n\n')
      .trim();
    return (sceneJoined || clipJoined).slice(0, 12000);
  }, [scenesSnapshot, narrationLangClipTexts]);

  const buildCurrentEditorDocument = useCallback(
    () =>
      buildVideoProjectDocument({
        storyId: editorId,
        story,
        scenes: scenesSnapshotRef.current,
        videoTracks: videoTracksRef.current,
        audioTracks: audioTracksRef.current,
        musicTracks: musicTracksRef.current,
        subtitleTracks: subtitleTracksRef.current,
        effectTracks: effectTracksRef.current,
        overlayTracks: overlayTracksRef.current,
        subtitleStyle: subtitleStyleRef.current,
        timelineZoom,
        timelineActive,
        volume,
        masterAudioPlaybackSpeed,
        isMuted,
        videoMasterMuted,
        exportAspectRatio,
        exportQuality,
        exportFraming,
        exportClipVideoToSubtitle,
        videoBookends: videoBookendsRef.current,
        audioTrackMeta: audioTrackMetaRef.current,
        editorAnimationSpeed: editorAnimationSpeedRef.current,
      }),
    [
      editorId,
      story,
      timelineZoom,
      timelineActive,
      volume,
      masterAudioPlaybackSpeed,
      isMuted,
      videoMasterMuted,
      exportAspectRatio,
      exportQuality,
      exportFraming,
      exportClipVideoToSubtitle,
      videoBookends,
      audioTrackMeta,
      editorAnimationSpeed,
    ],
  );

  useEffect(() => {
    exportFramingRef.current = exportFraming;
  }, [exportFraming]);

  useEffect(() => {
    exportClipVideoToSubtitleRef.current = exportClipVideoToSubtitle;
  }, [exportClipVideoToSubtitle]);

  useEffect(() => {
    videoBookendsRef.current = videoBookends;
  }, [videoBookends]);

  /* ---- Load data ---- */
  const loadStory = useCallback(async () => {
    setLoading(true);
    const pendingDoc = readAndClearPendingVideoProject(editorId);
    editorReadyForCloudSyncRef.current = false;
    skipDirtyMarkRef.current = true;
    try {
      if (isReelsShortsEditor) {
        const { data } = await api.get(`/reels-shorts/projects/${projectId}`, {
          params: { _editorFresh: Date.now() },
        });
        const state = reelsShortsProjectToEditorState(data.project, editorId);
        reelsCutRef.current = data.project?.editor_cut || {};
        setStory(state.story);
        setScenesSnapshot(state.scenes);
        setVideoTracks(JSON.parse(JSON.stringify(state.videoTracks || [[]])));
        setAudioTracks(JSON.parse(JSON.stringify(state.audioTracks || [[]])));
        setMusicTracks(JSON.parse(JSON.stringify(state.musicTracks || [[]])));
        setSubtitleTracks(JSON.parse(JSON.stringify(state.subtitleTracks || [[]])));
        setOverlayTracks(padReelsOverlayTracks(
          loadHostOverlayTracksFromEditor({ overlayTracks: state.overlayTracks }, '9:16'),
        ));
        setEffectTracks([[]]);
        setExportAspectRatio('9:16');
        setVideoMasterMuted(true);
        afterLoadProjectSeekRef.current = true;
        return;
      } else if (isNarrationEditor) {
        let narrationProject;
        if (projectId === 'demo-preview') {
          narrationProject = {
            id: 'demo-preview',
            title: 'THE DOG STORY',
            status: 'completed',
            total_duration: 14,
            image_provider: 'nano_banana',
            music_url: '/intro_test_audio.mp3',
            input: { topic: 'A dog adventure in the desert', visual_style: 'pixar', tone: 'warm' },
            segments: [
              {
                id: 'seg-1',
                segment_number: 1,
                name: 'Scene_01',
                text: 'Our furry friend spots something curious in the desert.',
                visual: 'A close-up shot of an adorable scruffy dog in the desert.',
                image_url: '/flowImages/1.png',
                video_url: null,
                audio_url: '/intro_test_audio.mp3',
                audio_duration: 6,
                duration: 6,
              },
              {
                id: 'seg-2',
                segment_number: 2,
                name: 'Studio_Entrance_Main',
                text: 'Joined by loyal companions on the dusty trail under the sun.',
                visual: 'Three dog friends sitting together under the sunny sky.',
                image_url: '/flowImages/2.png',
                video_url: null,
                audio_url: null,
                audio_duration: 8,
                duration: 8,
              },
            ],
          };
        } else {
          const applyIncomingTimelineLane = (row) => {
            const tracks = JSON.parse(JSON.stringify(row?.tracks || [[]]));
            if (row?.field === 'videoTracks') setVideoTracks(clampVideoTracksForMediaCap(tracks));
            else if (row?.field === 'audioTracks') setAudioTracks(tracks);
            else if (row?.field === 'musicTracks') setMusicTracks(tracks);
            else if (row?.field === 'subtitleTracks') setSubtitleTracks(tracks);
            else if (row?.field === 'effectTracks') setEffectTracks(tracks.length ? tracks : [[]]);
            else if (row?.field === 'overlayTracks') setOverlayTracks(tracks.length ? tracks : [[]]);
          };
          const applyShell = (shell) => {
            setStory((prev) => ({
              ...(prev || {}),
              id: editorId,
              title: shell?.title || 'Narration Project',
              tone: shell?.input?.tone,
              visual_style: shell?.input?.visual_style,
              input: shell?.input || {},
              app: shell?.app || null,
              music_url: shell?.music_url || null,
              music_mood: shell?.music_mood || null,
              music_prompt: shell?.music_prompt || null,
              music_history: shell?.music_history || [],
              music_target_duration: shell?.music_target_duration ?? null,
              total_duration: shell?.total_duration,
              final_video_url: shell?.final_video_url || null,
              image_provider: shell?.image_provider,
            }));
            const savedRatio = shell?.editor_settings?.exportAspectRatio;
            setExportAspectRatio(
              savedRatio || resolveNarrationProjectAspectRatio({ input: shell?.input }),
            );
            return shell;
          };
          let shell;
          let scenesPayload;
          let trackFields;
          try {
            const shellTask = fetchNarrationEditorShell(projectId).then(applyShell);
            [shell, scenesPayload, trackFields] = await Promise.all([
              shellTask,
              fetchNarrationEditorScenes(projectId),
              fetchNarrationEditorTimelines(projectId, applyIncomingTimelineLane),
            ]);
          } catch (splitErr) {
            // Fallback when split editor reads fail (timeout / older API / media resolve).
            console.warn('Split editor load failed; falling back to full project', splitErr);
            const { data: fullProject } = await api.get(`/narration/projects/${projectId}`, {
              params: { _editorFresh: Date.now() },
            });
            shell = applyShell({
              ...fullProject,
              editor_settings: fullProject?.video_editor_snapshot || {},
            });
            scenesPayload = { segments: fullProject?.segments || [] };
            trackFields = {
              videoTracks: fullProject?.video_editor_snapshot?.videoTracks || [[]],
              audioTracks: fullProject?.video_editor_snapshot?.audioTracks || [[]],
              musicTracks: fullProject?.video_editor_snapshot?.musicTracks || [[]],
              subtitleTracks: fullProject?.video_editor_snapshot?.subtitleTracks || [[]],
              effectTracks: fullProject?.video_editor_snapshot?.effectTracks || [[]],
              overlayTracks: fullProject?.video_editor_snapshot?.overlayTracks || [[]],
            };
          }
          narrationProject = {
            ...shell,
            segments: scenesPayload?.segments || [],
            video_editor_snapshot: {
              ...(shell?.editor_settings || {}),
              ...trackFields,
            },
          };
        }
        const state = narrationProjectToEditorState(narrationProject, editorId);
        setStory(state.story);
        setScenesSnapshot(state.scenes);

        const applySavedLayout = (savedEditor, toastMsg, sceneRowsOverride, { mediaPolicy } = {}) => {
          const sceneRows = sceneRowsOverride || state.scenes;
          // Default to live segment media from Mongo; only freeze when explicitly restoring a version.
          const policy = mediaPolicy ?? 'live';
          const merged = mergeFreshScenesWithSavedEditor(sceneRows, savedEditor, {
            mediaPolicy: policy,
            narrationProjectId: projectId || null,
          });
          if (!merged) return false;
          if (sceneRowsOverride) {
            setScenesSnapshot(sceneRows);
          }
          let musicTracks = JSON.parse(JSON.stringify(merged.musicTracks || [[]]));
          const musicSrc = narrationProject?.music_url || state.story?.music_url;
          if (musicTracksNeedStoryMusic(musicTracks) && musicSrc) {
            const clipEnd = timelineEndFromVideoClips(merged.videoTracks.flat());
            const dur =
              Number(narrationProject?.music_target_duration) > 0
                ? Number(narrationProject.music_target_duration)
                : clipEnd || Number(narrationProject?.total_duration) || 5;
            musicTracks = withStoryMusicLane(
              musicTracks,
              buildMusicTracksFromStoryUrl(musicSrc, dur, { narrationProjectId: projectId }),
            );
          } else if (musicSrc) {
            const resolvedMusic = mediaUrlForPlayback(musicSrc);
            musicTracks = musicTracks.map((tr) =>
              tr.map((c) => {
                if (c.id !== 'story-background-music' && c.source !== 'background_music') return c;
                return {
                  ...c,
                  url: resolvedMusic,
                  source: 'background_music',
                  ...(projectId && !c.waveformUrl
                    ? { waveformUrl: `/narration/projects/${projectId}/story-background-music/waveform` }
                    : {}),
                };
              }),
            );
          }
          musicTracks = mergeSceneSfxIntoMusicTracks(musicTracks, sceneRows, merged.videoTracks.flat());
          const normalized = normalizeEditorTracksForPlayback(
            merged.videoTracks,
            merged.audioTracks,
            musicTracks,
          );
          const flatVideo = normalized.videoTracks.flat();
          const audioTracksToSet = resolveNarrationEditorAudioTracks(
            sceneRows,
            flatVideo,
            projectId,
            normalized.audioTracks,
            merged.audioTrackMeta,
          );
          let finalVideoTracks = normalized.videoTracks;
          let finalAudioTracks = audioTracksToSet;
          let finalMusicTracks = normalized.musicTracks;
          let finalSubtitleTracks = JSON.parse(JSON.stringify(merged.subtitleTracks || [[]]));
          const realigned = applySongMultishotTimelineRealign(
            merged.videoTracks,
            audioTracksToSet,
            normalized.musicTracks,
            sceneRows,
            {
              narrationProjectId: projectId || null,
              preserveExtraAudioTracks: merged.audioTracks,
              preserveAudioTrackMeta: merged.audioTrackMeta,
            },
          );
          if (realigned) {
            finalVideoTracks = realigned.videoTracks;
            finalAudioTracks = realigned.audioTracks;
            finalMusicTracks = realigned.musicTracks;
            finalSubtitleTracks = rebuildSubtitleTracksFromAudio(
              realigned.audioTracks,
              sceneRows,
              merged.subtitleTracks,
              realigned.videoTracks,
            );
          }
          setVideoTracks(clampVideoTracksForMediaCap(finalVideoTracks));
          setAudioTracks(finalAudioTracks);
          setAudioTrackMeta(
            mergeAudioTrackMeta(
              merged.audioTrackMeta || audioTrackMetaRef.current,
              finalAudioTracks,
            ),
          );
          setMusicTracks(ensureMusicTracksWaveformUrls(finalMusicTracks, projectId));
          setSubtitleTracks(finalSubtitleTracks);
          setEffectTracks(loadEffectTracksFromEditor(merged));
          setOverlayTracks(loadHostOverlayTracksFromEditor(merged));
          setSubtitleStyle(merged.subtitleStyle);
          setTimelineZoom(merged.timelineZoom);
          setEditorAnimationSpeed(merged.editorAnimationSpeed ?? EDITOR_ANIMATION_SPEED_DEFAULT);
          setVolume(merged.volume);
          setMasterAudioPlaybackSpeed(merged.masterAudioPlaybackSpeed ?? 1);
          setIsMuted(merged.isMuted);
          setVideoMasterMuted(merged.videoMasterMuted);
          setTimelineActive(mergeTimelineActiveForEditorLoad(merged.timelineActive, finalAudioTracks.length));
          setExportAspectRatio(resolveNarrationProjectAspectRatio(state.story));
          setExportQuality(normalizeExportQuality(merged.exportQuality));
          setExportFraming(normalizeVideoExportFraming(merged.exportFraming));
          setExportClipVideoToSubtitle(Boolean(merged.exportClipVideoToSubtitle));
          setVideoBookends(restoredVideoBookends(merged.videoBookends, {
            defaultTitle: state.story?.title || '',
            aspectRatio: merged.exportAspectRatio || '16:9',
          }));
          afterLoadProjectSeekRef.current = true;
          if (merged.appendedSceneCount > 0) {
            toast.success(
              `Added ${merged.appendedSceneCount} new segment${merged.appendedSceneCount === 1 ? '' : 's'} to the timeline`,
            );
          }
          if (toastMsg) toast.success(toastMsg);
          return true;
        };

        if (pendingDoc?.editor && applySavedLayout(pendingDoc.editor, 'Loaded project layout from file — narration refreshed from server')) {
          return;
        }

        const cloudSnap = narrationProject?.video_editor_snapshot;
        const linkedDoc = await readLinkedRootProjectFile(editorId);
        const cloudHas = videoEditorSnapshotHasClips(cloudSnap);
        const localHas = linkedDoc?.editor && videoEditorSnapshotHasClips(linkedDoc.editor);
        const langDraft = scriptLanguageDraftFromProject(narrationProject);
        if (langDraft) setPendingLanguageCommit(langDraft);

        if (cloudHas && applySavedLayout(cloudSnap, 'Restored timeline from Twin Tales cloud')) {
          return;
        }
        if (localHas && applySavedLayout(linkedDoc.editor, 'Restored timeline from linked project.json')) {
          return;
        }

        setVideoTracks(state.videoTracks);
        setAudioTracks(state.audioTracks);
        setMusicTracks(state.musicTracks || [[]]);
        setSubtitleTracks(
          rebuildSubtitleTracksFromAudio(
            state.audioTracks,
            state.scenes,
            [[]],
            state.videoTracks,
          ),
        );
        setSubtitleStyle(normalizeVideoSubtitleStyle(null));
        setExportAspectRatio(resolveNarrationProjectAspectRatio(state.story));
        if (cloudSnap?.videoBookends) {
          setVideoBookends(restoredVideoBookends(cloudSnap.videoBookends, {
            defaultTitle: state.story?.title || '',
            aspectRatio: resolveNarrationProjectAspectRatio(state.story),
          }));
        }
        afterLoadProjectSeekRef.current = true;
        return;
      }

      if (!storyId) {
        throw new Error(isReelsShortsEditor ? 'REEL&SHORTS project not found' : 'Story not found');
      }

      const { data: storyData } = await api.get(`/stories/${storyId}`, {
        params: { _editorFresh: Date.now() },
      });
      setStory(storyData);

      let sorted;
      const pitchScenes = await loadPitchStoryEditorScenes(api, storyData);
      if (pitchScenes != null) {
        sorted = pitchScenes;
      } else {
        const { data: scenesData } = await api.get(`/stories/${storyId}/scenes`, {
          params: { _editorFresh: Date.now() },
        });
        sorted = [...scenesData].sort((a, b) => (a.scene_number || 0) - (b.scene_number || 0));
      }

      if (pendingDoc?.editor) {
        const merged = mergeFreshScenesWithSavedEditor(sorted, pendingDoc.editor);
        if (merged) {
          setScenesSnapshot(sorted);
          let musicTracks = JSON.parse(JSON.stringify(merged.musicTracks || [[]]));
          if (musicTracksNeedStoryMusic(musicTracks) && storyData?.music_url) {
            const clipEnd = timelineEndFromVideoClips(merged.videoTracks.flat());
            const dur =
              Number(storyData.music_target_duration) > 0
                ? Number(storyData.music_target_duration)
                : clipEnd || 5;
            musicTracks = withStoryMusicLane(musicTracks, buildMusicTracksFromStoryUrl(storyData.music_url, dur));
          }
          musicTracks = mergeSceneSfxIntoMusicTracks(musicTracks, sorted, merged.videoTracks.flat());
          const normalized = normalizeEditorTracksForPlayback(
            merged.videoTracks,
            merged.audioTracks,
            musicTracks,
          );
          setVideoTracks(clampVideoTracksForMediaCap(
            tagPitchRecordingVideoTracks(normalized.videoTracks, storyData),
          ));
          const restoredAudioTracks = ensureAudioTracksWaveformUrls(
            audioTracksWithStorySpeech(
              normalized.audioTracks,
              sorted,
              normalized.videoTracks.flat(),
              projectId,
            ),
            projectId,
          );
          setAudioTracks(restoredAudioTracks);
          setMusicTracks(ensureMusicTracksWaveformUrls(normalized.musicTracks, projectId));
          setSubtitleTracks(JSON.parse(JSON.stringify(merged.subtitleTracks || [[]])));
          setEffectTracks(loadEffectTracksFromEditor(merged));
          setOverlayTracks(loadHostOverlayTracksFromEditor(merged));
          setSubtitleStyle(merged.subtitleStyle);
          setTimelineZoom(merged.timelineZoom);
          setEditorAnimationSpeed(merged.editorAnimationSpeed ?? EDITOR_ANIMATION_SPEED_DEFAULT);
          setVolume(merged.volume);
          setMasterAudioPlaybackSpeed(merged.masterAudioPlaybackSpeed ?? 1);
          setIsMuted(merged.isMuted);
          setVideoMasterMuted(merged.videoMasterMuted);
          setTimelineActive(mergeTimelineActiveForEditorLoad(merged.timelineActive, restoredAudioTracks.length));
          setExportAspectRatio(
            merged.exportAspectRatio
            || defaultReplacementOverrides(storyData, false).image_aspect_ratio,
          );
          setExportQuality(normalizeExportQuality(merged.exportQuality));
          setExportFraming(normalizeVideoExportFraming(merged.exportFraming));
          setExportClipVideoToSubtitle(Boolean(merged.exportClipVideoToSubtitle));
          setVideoBookends(restoredVideoBookends(merged.videoBookends, {
            defaultTitle: state.story?.title || '',
            aspectRatio: merged.exportAspectRatio || '16:9',
          }));
          afterLoadProjectSeekRef.current = true;
          toast.success('Loaded project layout from file — scenes refreshed from server');
          return;
        }
      }

      const linkedDoc = await readLinkedRootProjectFile(editorId);
      const localHas = linkedDoc?.editor && videoEditorSnapshotHasClips(linkedDoc.editor);

      const applyMergedStoryLayout = (savedEditor, toastMsg, sceneRowsOverride, { mediaPolicy } = {}) => {
        const sceneRows = sceneRowsOverride || sorted;
        const policy = mediaPolicy ?? 'live';
        const merged = mergeFreshScenesWithSavedEditor(sceneRows, savedEditor, { mediaPolicy: policy });
        if (!merged) return false;
        setScenesSnapshot(sceneRows);
        let musicTracks = JSON.parse(JSON.stringify(merged.musicTracks || [[]]));
        if (musicTracksNeedStoryMusic(musicTracks) && storyData?.music_url) {
          const clipEnd = timelineEndFromVideoClips(merged.videoTracks.flat());
          const dur =
            Number(storyData.music_target_duration) > 0
              ? Number(storyData.music_target_duration)
              : clipEnd || 5;
          musicTracks = withStoryMusicLane(musicTracks, buildMusicTracksFromStoryUrl(storyData.music_url, dur));
        }
        musicTracks = mergeSceneSfxIntoMusicTracks(musicTracks, sceneRows, merged.videoTracks.flat());
        const normalized = normalizeEditorTracksForPlayback(
          merged.videoTracks,
          merged.audioTracks,
          musicTracks,
        );
        setVideoTracks(clampVideoTracksForMediaCap(
          tagPitchRecordingVideoTracks(normalized.videoTracks, storyData),
        ));
        const restoredAudioTracks = ensureAudioTracksWaveformUrls(
          audioTracksWithStorySpeech(
            normalized.audioTracks,
            sceneRows,
            normalized.videoTracks.flat(),
            projectId,
          ),
          projectId,
        );
        setAudioTracks(restoredAudioTracks);
        setMusicTracks(ensureMusicTracksWaveformUrls(normalized.musicTracks, projectId));
        setSubtitleTracks(JSON.parse(JSON.stringify(merged.subtitleTracks || [[]])));
        setEffectTracks(loadEffectTracksFromEditor(merged));
        setOverlayTracks(loadHostOverlayTracksFromEditor(merged));
        setSubtitleStyle(merged.subtitleStyle);
        setTimelineZoom(merged.timelineZoom);
        setEditorAnimationSpeed(merged.editorAnimationSpeed ?? EDITOR_ANIMATION_SPEED_DEFAULT);
        setVolume(merged.volume);
        setMasterAudioPlaybackSpeed(merged.masterAudioPlaybackSpeed ?? 1);
        setIsMuted(merged.isMuted);
        setVideoMasterMuted(merged.videoMasterMuted);
        setTimelineActive(mergeTimelineActiveForEditorLoad(merged.timelineActive, restoredAudioTracks.length));
        setExportAspectRatio(
          merged.exportAspectRatio
          || defaultReplacementOverrides(storyData, false).image_aspect_ratio,
        );
        setExportQuality(normalizeExportQuality(merged.exportQuality));
        setExportFraming(normalizeVideoExportFraming(merged.exportFraming));
        setExportClipVideoToSubtitle(Boolean(merged.exportClipVideoToSubtitle));
        setVideoBookends(restoredVideoBookends(merged.videoBookends, {
          defaultTitle: storyData?.title || '',
          aspectRatio: merged.exportAspectRatio || defaultReplacementOverrides(storyData, false).image_aspect_ratio || '16:9',
        }));
        afterLoadProjectSeekRef.current = true;
        if (toastMsg) toast.success(toastMsg);
        return true;
      };

      const cloudStoryEd = storyData?.video_editor_snapshot;
      const cloudHas = videoEditorSnapshotHasClips(cloudStoryEd);

      if (cloudHas && applyMergedStoryLayout(cloudStoryEd, 'Restored timeline from Twin Tales cloud')) {
        return;
      }
      if (localHas && applyMergedStoryLayout(linkedDoc.editor, 'Restored timeline from linked project.json')) {
        return;
      }

      setScenesSnapshot(sorted);
      let t = 0;
      const clips = sorted.map(scene => {
        const clip = sceneToClip(scene, t);
        t += sceneSlotDurationSeconds(scene);
        return clip;
      });
      setVideoTracks([clips]);

      const audioClips = sorted
        .filter(s => s.audio_url)
        .map((s, i) => ({
          id: `audio-${s.id}`,
          name: `Narration ${i + 1}`,
          sceneId: s.id,
          url: mediaUrlForPlayback(s.audio_url),
          startTime: clips.find(c => c.sceneId === s.id)?.startTime || 0,
          duration: sceneAudioDurationSeconds(s),
          sourceDuration: sceneAudioDurationSeconds(s),
          volume: 0.8,
          muted: false,
          sourceType: 'scene_mix',
        }));
      const segClips = buildSegmentAudioLaneClipsForScenes(sorted, clips, projectId);
      const audioTracksBuilt = ensureAudioTracksWaveformUrls(
        segClips.length ? [segClips] : (audioClips.length ? [audioClips.map((c) => ({
          ...c,
          ...(projectId ? { waveformUrl: segmentWaveformUrl(projectId, c.sceneId) } : {}),
        }))] : [[]]),
        projectId,
      );
      setAudioTracks(audioTracksBuilt);
      setSubtitleTracks(
        rebuildSubtitleTracksFromAudio(audioTracksBuilt, sorted, [[]], [clips]),
      );
      const totalT = timelineEndFromVideoClips(clips);
      const defaultMusicTracks = buildMusicTracksFromStoryUrl(storyData.music_url, totalT, {
        narrationProjectId: projectId,
      });
      setMusicTracks(ensureMusicTracksWaveformUrls(
        mergeSceneSfxIntoMusicTracks(defaultMusicTracks, sorted, clips),
        projectId,
      ));
      setSubtitleStyle(normalizeVideoSubtitleStyle(null));
      setExportAspectRatio(defaultReplacementOverrides(storyData, false).image_aspect_ratio);
      if (cloudStoryEd?.videoBookends) {
        setVideoBookends(restoredVideoBookends(cloudStoryEd.videoBookends, {
          defaultTitle: storyData?.title || '',
          aspectRatio: defaultReplacementOverrides(storyData, false).image_aspect_ratio || '16:9',
        }));
      }
    } catch (err) {
      toast.error(
        formatApiError(
          err,
          isReelsShortsEditor
            ? 'Failed to load REEL&SHORTS project'
            : isNarrationEditor ? 'Failed to load narration project' : 'Failed to load story',
        ),
      );
      // Keep the user on the project — do not bounce to Create agent (looks like the editor "won't open").
      navigate(
        isReelsShortsEditor
          ? (projectId
            ? `/reels-shorts/create?project=${projectId}`
            : '/dashboard')
          : isNarrationEditor && projectId && !isNewsRoute
            ? `/narration/${projectId}/edit`
            : '/dashboard',
      );
    } finally {
      setLoading(false);
      editorReadyForCloudSyncRef.current = true;
    }
  }, [
    editorId,
    isNarrationEditor,
    isReelsShortsEditor,
    isNewsRoute,
    projectId,
    storyId,
    navigate,
  ]);

  useEffect(() => { loadStory(); }, [loadStory]);

  const handleExportJobUpdate = useCallback((job) => {
    setExportProgress(job.progress || 0);
    setExportStatusMessage(String(job.message || '').trim());
    if (job.status === 'completed' && job.result_url) {
      if (exportPollStopRef.current) {
        exportPollStopRef.current();
        exportPollStopRef.current = null;
      }
      clearActiveExportJob(exportJobStorageKeyRef.current);
      setExportingFull(false);
      setExportedUrl(job.result_url);
      toast.success('Video compiled and ready to download!');
    } else if (job.status === 'failed') {
      if (exportPollStopRef.current) {
        exportPollStopRef.current();
        exportPollStopRef.current = null;
      }
      clearActiveExportJob(exportJobStorageKeyRef.current);
      setExportingFull(false);
      toast.error(job.error_message || 'Export failed');
    } else if (job.status === 'cancelled') {
      if (exportPollStopRef.current) {
        exportPollStopRef.current();
        exportPollStopRef.current = null;
      }
      clearActiveExportJob(exportJobStorageKeyRef.current);
      setExportingFull(false);
    } else if (job.status === 'pending' || job.status === 'running') {
      setExportingFull(true);
    }
  }, []);

  useEffect(() => {
    const storageKey = exportJobStorageKey({ storyId, projectId });
    exportJobStorageKeyRef.current = storageKey;
    const savedJobId = loadActiveExportJob(storageKey);
    if (!savedJobId) return undefined;

    setExportingFull(true);
    if (exportPollStopRef.current) exportPollStopRef.current();
    exportPollStopRef.current = pollExportJob(api, savedJobId, {
      onProgress: handleExportJobUpdate,
    });

    return () => {
      if (exportPollStopRef.current) {
        exportPollStopRef.current();
        exportPollStopRef.current = null;
      }
    };
  }, [storyId, projectId, handleExportJobUpdate]);

  /** Pull segment script/media changes from /edit (or other tabs) into the open timeline. */
  const refreshNarrationTimelineFromServer = useCallback(async ({ silent = true } = {}) => {
    if (!isNarrationEditor || !projectId || !editorId || loading) return false;
    if (Date.now() < mediaReplaceGuardUntilRef.current) return false;
    // Replacing the lanes mid-play recreates audio elements whose clip id/URL changed and cuts the sound.
    if (silent && playbackPlayingRef.current) {
      deferredSegmentSyncRef.current = true;
      return false;
    }
    if (!silent) setSegmentSyncBusy(true);
    try {
      const { data: narrationProject } = await api.get(`/narration/projects/${projectId}`, {
        params: { _editorFresh: Date.now() },
      });
      const state = narrationProjectToEditorState(narrationProject, editorId);
      setStory(state.story);
      setScenesSnapshot(state.scenes);

      const refEditorBaseline = () => ({
        videoTracks: videoTracksRef.current,
        audioTracks: audioTracksRef.current,
        musicTracks: musicTracksRef.current,
        subtitleTracks: subtitleTracksRef.current,
        overlayTracks: overlayTracksRef.current,
        effectTracks: effectTracksRef.current,
        subtitleStyle: subtitleStyleRef.current,
        audioTrackMeta: audioTrackMetaRef.current,
        videoBookends: videoBookendsRef.current,
        timelineZoom,
        timelineActive,
        volume,
        masterAudioPlaybackSpeed,
        isMuted,
        videoMasterMuted,
        exportAspectRatio,
        exportQuality,
        exportFraming,
      });

      const savedEditor = refEditorBaseline();

      const merged = mergeFreshScenesWithSavedEditor(state.scenes, savedEditor, {
        mediaPolicy: 'live',
        narrationProjectId: projectId || null,
      });
      if (!merged) return false;

      let musicTracks = JSON.parse(JSON.stringify(merged.musicTracks || [[]]));
      const musicSrc = narrationProject?.music_url || state.story?.music_url;
      if (musicTracksNeedStoryMusic(musicTracks) && musicSrc) {
        const clipEnd = timelineEndFromVideoClips(merged.videoTracks.flat());
        const dur =
          Number(narrationProject?.music_target_duration) > 0
            ? Number(narrationProject.music_target_duration)
            : clipEnd || Number(narrationProject?.total_duration) || 5;
        musicTracks = withStoryMusicLane(
          musicTracks,
          buildMusicTracksFromStoryUrl(musicSrc, dur, { narrationProjectId: projectId }),
        );
      } else if (musicSrc) {
        const resolvedMusic = mediaUrlForPlayback(musicSrc);
        musicTracks = musicTracks.map((tr) =>
          tr.map((c) => {
            if (c.id !== 'story-background-music' && c.source !== 'background_music') return c;
            return {
              ...c,
              url: resolvedMusic,
              source: 'background_music',
              ...(projectId && !c.waveformUrl
                ? { waveformUrl: bgMusicWaveformUrl(projectId) }
                : {}),
            };
          }),
        );
      }
      musicTracks = mergeSceneSfxIntoMusicTracks(musicTracks, state.scenes, merged.videoTracks.flat());

      const normalized = normalizeEditorTracksForPlayback(
        merged.videoTracks,
        merged.audioTracks,
        musicTracks,
      );
      const flatVideo = normalized.videoTracks.flat();
      const audioTracksToSet = resolveNarrationEditorAudioTracks(
        state.scenes,
        flatVideo,
        projectId,
        normalized.audioTracks,
        merged.audioTrackMeta,
      );

      let nextSubtitleTracks = rebuildSubtitleTracksFromAudio(
        audioTracksToSet,
        state.scenes,
        merged.subtitleTracks,
        normalized.videoTracks,
      );

      let finalVideoTracks = normalized.videoTracks;
      let finalAudioTracks = audioTracksToSet;
      let finalMusicTracks = normalized.musicTracks;
      let finalSubtitleTracks = nextSubtitleTracks;
      const realigned = applySongMultishotTimelineRealign(
        merged.videoTracks,
        audioTracksToSet,
        normalized.musicTracks,
        state.scenes,
        {
          narrationProjectId: projectId || null,
          preserveExtraAudioTracks: merged.audioTracks,
          preserveAudioTrackMeta: merged.audioTrackMeta || audioTrackMetaRef.current,
        },
      );
      if (realigned) {
        finalVideoTracks = realigned.videoTracks;
        finalAudioTracks = realigned.audioTracks;
        finalMusicTracks = realigned.musicTracks;
        finalSubtitleTracks = rebuildSubtitleTracksFromAudio(
          realigned.audioTracks,
          state.scenes,
          subtitleTracksRef.current,
          realigned.videoTracks,
        );
      }

      setVideoTracks(clampVideoTracksForMediaCap(finalVideoTracks));
      setAudioTracks(finalAudioTracks);
      setAudioTrackMeta(
        mergeAudioTrackMeta(
          merged.audioTrackMeta || audioTrackMetaRef.current,
          finalAudioTracks,
        ),
      );
      setMusicTracks(ensureMusicTracksWaveformUrls(finalMusicTracks, projectId));
      setSubtitleTracks(finalSubtitleTracks);
      setOverlayTracks(loadHostOverlayTracksFromEditor(merged, merged.exportAspectRatio || exportAspectRatio));
      setSubtitleStyle(merged.subtitleStyle);
      setTimelineZoom(merged.timelineZoom);
      setEditorAnimationSpeed(merged.editorAnimationSpeed ?? EDITOR_ANIMATION_SPEED_DEFAULT);
      setVolume(merged.volume);
      setMasterAudioPlaybackSpeed(merged.masterAudioPlaybackSpeed ?? 1);
      setIsMuted(merged.isMuted);
      setVideoMasterMuted(merged.videoMasterMuted);
      setTimelineActive(mergeTimelineActiveForEditorLoad(merged.timelineActive, finalAudioTracks.length));
      setExportAspectRatio(resolveNarrationProjectAspectRatio(state.story));
      setExportQuality(normalizeExportQuality(merged.exportQuality));
      setExportFraming(normalizeVideoExportFraming(merged.exportFraming));
      setExportClipVideoToSubtitle(Boolean(merged.exportClipVideoToSubtitle));
      setVideoBookends(mergeRestoredVideoBookends(videoBookendsRef.current, merged.videoBookends, {
        defaultTitle: state.story?.title || '',
        aspectRatio: merged.exportAspectRatio || exportAspectRatio || '16:9',
      }));
      const syncedAt = Date.now();
      lastSegmentSyncAtRef.current = syncedAt;
      setLastSegmentSyncAt(syncedAt);
      if (!silent) {
        toast.success('Synced segment images and audio from the database');
      }
      return true;
    } catch (err) {
      console.warn('Narration timeline refresh failed', err);
      if (!silent) {
        toast.error('Could not sync segments from the database');
      }
      return false;
    } finally {
      if (!silent) setSegmentSyncBusy(false);
    }
  }, [
    editorId,
    exportAspectRatio,
    exportQuality,
    exportFraming,
    isMuted,
    videoMasterMuted,
    isNarrationEditor,
    loading,
    projectId,
    timelineActive,
    timelineZoom,
    volume,
    masterAudioPlaybackSpeed,
  ]);

  const handleExplicitSegmentSync = useCallback(async () => {
    await refreshNarrationTimelineFromServer({ silent: false });
  }, [refreshNarrationTimelineFromServer]);

  const handleAutoSegmentSyncToggle = useCallback((enabled) => {
    setAutoSegmentSyncEnabled(enabled);
    writeVideoEditorAutoSegmentSyncEnabled(enabled);
    if (enabled) {
      toast.info('Auto-sync on — timeline pulls segment media every 4 minutes');
    } else {
      toast.info('Auto-sync off — use Sync to pull segment media manually');
    }
  }, []);

  useEffect(() => {
    if (!isNarrationEditor || loading || !autoSegmentSyncEnabled) return undefined;
    const tick = () => {
      if (document.visibilityState && document.visibilityState !== 'visible') return;
      refreshNarrationTimelineFromServer({ silent: true });
    };
    segmentSyncIntervalRef.current = setInterval(tick, VIDEO_EDITOR_SEGMENT_SYNC_INTERVAL_MS);
    return () => {
      if (segmentSyncIntervalRef.current) {
        clearInterval(segmentSyncIntervalRef.current);
        segmentSyncIntervalRef.current = null;
      }
    };
  }, [isNarrationEditor, loading, autoSegmentSyncEnabled, refreshNarrationTimelineFromServer]);

  useEffect(() => {
    if (!isNarrationEditor || loading || !autoSegmentSyncEnabled) return undefined;
    let refreshTimer = null;
    const scheduleRefresh = () => {
      if (document.visibilityState && document.visibilityState !== 'visible') return;
      if (!segmentSyncThrottleElapsed(lastSegmentSyncAtRef.current)) return;
      if (refreshTimer) clearTimeout(refreshTimer);
      refreshTimer = setTimeout(() => {
        refreshNarrationTimelineFromServer({ silent: true });
      }, 400);
    };
    window.addEventListener('focus', scheduleRefresh);
    document.addEventListener('visibilitychange', scheduleRefresh);
    return () => {
      if (refreshTimer) clearTimeout(refreshTimer);
      window.removeEventListener('focus', scheduleRefresh);
      document.removeEventListener('visibilitychange', scheduleRefresh);
    };
  }, [isNarrationEditor, loading, autoSegmentSyncEnabled, refreshNarrationTimelineFromServer]);

  useEffect(() => {
    if (playback.isPlaying || !deferredSegmentSyncRef.current) return;
    deferredSegmentSyncRef.current = false;
    if (autoSegmentSyncEnabled) refreshNarrationTimelineFromServer({ silent: true });
  }, [playback.isPlaying, autoSegmentSyncEnabled, refreshNarrationTimelineFromServer]);

  useEffect(() => {
    if (!isNarrationEditor || !projectId || !autoSegmentSyncEnabled) return undefined;
    const onMultishotSelectionChanged = (event) => {
      if (String(event?.detail?.projectId || '') !== String(projectId)) return;
      if (!segmentSyncThrottleElapsed(lastSegmentSyncAtRef.current)) return;
      refreshNarrationTimelineFromServer({ silent: true });
    };
    window.addEventListener('twin-tales-multishot-selection-changed', onMultishotSelectionChanged);
    return () => window.removeEventListener('twin-tales-multishot-selection-changed', onMultishotSelectionChanged);
  }, [isNarrationEditor, projectId, autoSegmentSyncEnabled, refreshNarrationTimelineFromServer]);

  const queuePersistEditorAfterMediaChange = useCallback(() => {
    setMediaChangePersistNonce((n) => n + 1);
  }, []);

  const flushCloudEditorSave = useCallback(
    async () => {
      if (isReelsShortsEditor && projectId) {
        if (!editorReadyForCloudSyncRef.current) return;
        const payload = editorOverlaysToCutPatch(overlayTracksRef.current, reelsCutRef.current || {});
        setCloudSaveStatus('saving');
        const { data } = await api.patch(`/reels-shorts/projects/${projectId}/cut`, payload);
        if (data?.project?.editor_cut) reelsCutRef.current = data.project.editor_cut;
        editorDirtyRef.current = false;
        setCloudSaveStatus('saved');
        return data;
      }
      const canSync = isNarrationEditor ? Boolean(projectId) : Boolean(storyId);
      if (!canSync || !editorId || !editorReadyForCloudSyncRef.current) return;
      const doc = buildCurrentEditorDocument();
      const payload = cloudSnapshotPayloadFromDoc(doc);
      if (!payload) return;
      setCloudSaveStatus('saving');
      const result = await patchVideoEditorSnapshotToCloud({
        isNarration: isNarrationEditor,
        projectId,
        storyId,
        editor: payload.editor,
        scenes: payload.scenes,
        story: payload.story,
        recordVersion: false,
      });
      editorDirtyRef.current = false;
      setCloudSaveStatus('saved');
      return result;
    },
    [buildCurrentEditorDocument, editorId, isNarrationEditor, isReelsShortsEditor, projectId, storyId],
  );

  /**
   * Generate / re-generate the asset for one B-ROLL suggestion.
   * The cut is flushed first so the server can find the suggestion row being generated.
   */
  const generateOverlayArtifact = useCallback(
    async (clip, { media = 'image', prompt = '', visualStyle, hasBackground } = {}) => {
      if (!projectId || !clip?.suggestionId) return;
      const look = visualStyle || clip.visualStyle || '';
      const framed = hasBackground != null ? hasBackground : clip.hasBackground;
      try {
        await flushCloudEditorSave();
        const { data } = await api.post(
          `/reels-shorts/projects/${projectId}/artifacts/${clip.suggestionId}/generate`,
          {
            media,
            prompt,
            visual_style: look || undefined,
            has_background: framed,
            // A serial-cards suggestion holds several cards; target the one that was opened.
            ...(clip.cardId ? { card_id: clip.cardId } : {}),
            ...(Number.isInteger(clip.cardIndex) ? { card_index: clip.cardIndex } : {}),
          },
        );
        if (data?.project?.editor_cut) reelsCutRef.current = data.project.editor_cut;
        recordEditorHistory();
        setOverlayTracks((prev) => patchHostOverlayClip(prev, clip.id, {
          imageUrl: data?.url || '',
          generatedMedia: data?.media || media,
          ...(prompt ? { generationPrompt: prompt } : {}),
          ...(look ? { visualStyle: look } : {}),
          ...(framed != null ? { hasBackground: framed } : {}),
        }));
        toast.success(`Artifact ${media} generated`);
      } catch (err) {
        toast.error(formatApiError(err, `Could not generate the artifact ${media}`));
      }
    },
    [projectId, flushCloudEditorSave, recordEditorHistory],
  );

  const confirmLeaveEditor = useCallback(async () => {
    if (cloudSaveStatus === 'saving') {
      return window.confirm('Timeline is still saving. Leave anyway?');
    }
    if (!editorDirtyRef.current && cloudSaveStatus !== 'error') return true;
    const shouldSave = window.confirm(
      'Timeline changes are not saved to the server yet. Save now before leaving?',
    );
    if (shouldSave) {
      try {
        await flushCloudEditorSave();
        return true;
      } catch {
        toast.error('Could not save timeline before leaving');
        return false;
      }
    }
    return window.confirm('Leave without saving timeline changes?');
  }, [cloudSaveStatus, flushCloudEditorSave]);

  const handleVideoBookendsChange = useCallback((next) => {
    recordEditorHistoryRef.current();
    setVideoBookends((prev) => {
      const raw = typeof next === 'function' ? next(prev) : next;
      return normalizeVideoBookends(raw, {
        defaultTitle: story?.title || '',
        aspectRatio: exportAspectRatio || '16:9',
      });
    });
  }, [story?.title, exportAspectRatio]);

  const persistVideoBookends = useCallback(() => {
    editorDirtyRef.current = true;
    setCloudSaveStatus('dirty');
    flushCloudEditorSave().catch(() => {});
  }, [flushCloudEditorSave]);

  const timelineAutosaveDeps = [
    loading,
    editorId,
    isNarrationEditor,
    projectId,
    storyId,
    videoTracks,
    audioTracks,
    musicTracks,
    subtitleTracks,
    subtitleStyle,
    overlayTracks,
    effectTracks,
    timelineZoom,
    volume,
    masterAudioPlaybackSpeed,
    isMuted,
    videoMasterMuted,
    scenesSnapshot,
    videoBookends,
    mediaChangePersistNonce,
    timelineActive,
    editorAnimationSpeed,
    audioTrackMeta,
    exportAspectRatio,
    exportQuality,
    exportFraming,
    exportClipVideoToSubtitle,
  ];

  useEffect(() => {
    if (loading || !editorReadyForCloudSyncRef.current) {
      skipDirtyMarkRef.current = true;
      return undefined;
    }
    if (skipDirtyMarkRef.current) {
      skipDirtyMarkRef.current = false;
      editorDirtyRef.current = false;
      return undefined;
    }
    editorDirtyRef.current = true;
    setCloudSaveStatus((prev) => (prev === 'saving' ? prev : 'dirty'));
    return undefined;
  }, timelineAutosaveDeps);

  useEffect(() => {
    if (loading || !editorReadyForCloudSyncRef.current) return undefined;
    const canSync = isReelsShortsEditor
      ? Boolean(projectId)
      : (isNarrationEditor ? Boolean(projectId) : Boolean(storyId));
    if (!canSync || !editorId) return undefined;

    const tick = async () => {
      if (!editorDirtyRef.current) return;
      try {
        await flushCloudEditorSave();
      } catch (err) {
        console.warn('Cloud editor autosave failed', err);
        setCloudSaveStatus('error');
      }
    };

    if (cloudAutosaveIntervalRef.current) clearInterval(cloudAutosaveIntervalRef.current);
    cloudAutosaveIntervalRef.current = setInterval(tick, CLOUD_EDITOR_AUTOSAVE_INTERVAL_MS);

    return () => {
      if (cloudAutosaveIntervalRef.current) {
        clearInterval(cloudAutosaveIntervalRef.current);
        cloudAutosaveIntervalRef.current = null;
      }
    };
  }, [loading, editorId, isNarrationEditor, isReelsShortsEditor, projectId, storyId, flushCloudEditorSave]);

  useEffect(() => {
    if (loading || !editorReadyForCloudSyncRef.current) return undefined;
    const onBeforeUnload = (event) => {
      if (!editorDirtyRef.current && cloudSaveStatus !== 'saving' && cloudSaveStatus !== 'error') return;
      event.preventDefault();
      event.returnValue = '';
    };
    window.addEventListener('beforeunload', onBeforeUnload);
    return () => window.removeEventListener('beforeunload', onBeforeUnload);
  }, [loading, cloudSaveStatus]);

  useEffect(() => {
    if (!isNarrationEditor || !projectId || loading) return undefined;
    if (languageDraftSaveTimerRef.current) clearTimeout(languageDraftSaveTimerRef.current);
    languageDraftSaveTimerRef.current = setTimeout(async () => {
      try {
        if (pendingLanguageCommit) {
          await saveScriptLanguageDraftToCloud(projectId, pendingLanguageCommit);
        } else if (prevLanguageDraftRef.current) {
          await saveScriptLanguageDraftToCloud(projectId, null);
        }
        prevLanguageDraftRef.current = pendingLanguageCommit;
      } catch (err) {
        console.warn('Cloud script-language draft save failed', err);
      }
    }, 1500);
    return () => {
      if (languageDraftSaveTimerRef.current) clearTimeout(languageDraftSaveTimerRef.current);
    };
  }, [isNarrationEditor, projectId, loading, pendingLanguageCommit]);

  useEffect(() => {
    if (loading || !editorReadyForCloudSyncRef.current) return undefined;
    const onLeave = () => {
      if (!editorDirtyRef.current) return;
      flushCloudEditorSave().catch(() => {});
    };
    window.addEventListener('pagehide', onLeave);
    return () => window.removeEventListener('pagehide', onLeave);
  }, [loading, flushCloudEditorSave]);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const msg = params.get('message');
    const yt = params.get('youtube');
    const ig = params.get('instagram');
    if (!yt && !ig) return;
    if (yt === 'connected') {
      toast.success('YouTube connected.');
      setYoutubePublishOpen(true);
    } else if (yt === 'error') {
      toast.error(msg ? decodeURIComponent(msg) : 'YouTube connection failed');
    }
    if (ig === 'connected') {
      toast.success('Instagram connected.');
      setInstagramPublishOpen(true);
    } else if (ig === 'error') {
      toast.error(msg ? decodeURIComponent(msg) : 'Instagram connection failed');
    }
    navigate({ pathname: location.pathname, hash: location.hash || '' }, { replace: true });
  }, [location.search, location.pathname, location.hash, navigate]);

  useEffect(() => {
    if (!isNarrationEditor) return;
    setWorkspaceScriptLangTarget((prev) => {
      if (workspaceScriptLangChoices.some((o) => o.value === prev)) return prev;
      return workspaceScriptLangChoices[0]?.value || '';
    });
  }, [isNarrationEditor, workspaceScriptLangChoices]);

  useEffect(() => {
    const defaults = defaultReplacementOverrides(story, isNarrationEditor);
    setBatchVideoProvider(defaults.video_provider);
  }, [story, isNarrationEditor]);

  useEffect(() => {
    const d = defaultReplacementOverrides(story, isNarrationEditor);
    setRollingRegenerateVoiceStyle(d.voice_style || 'storyteller');
    setRollingRegenerateTtsProvider(d.tts_provider || 'auto');
  }, [story, isNarrationEditor]);

  useEffect(() => {
    if (!isNewsVideoEditor) return;
    const np = story?.news_pack && typeof story.news_pack === 'object' ? story.news_pack : {};
    setNewsPackDraft({
      news_logo_url: '',
      ticker_text: '',
      ad_image_url: '',
      lower_third_text: '',
      overlay_notes: '',
      show_logo_preview: true,
      show_ticker_preview: true,
      show_ad_preview: false,
      ...np,
    });
  }, [isNewsVideoEditor, story?.id]);

  useEffect(() => {
    if (!afterLoadProjectSeekRef.current) return;
    afterLoadProjectSeekRef.current = false;
    stopPlayback();
  }, [videoTracks, stopPlayback]);

  /** One subtitle row per narration clip on audio lane 1; text follows scene script unless manually edited. */
  useEffect(() => {
    setSubtitleTracks((prevRows) =>
      rebuildSubtitleTracksFromAudio(
        audioTracks,
        scenesSnapshot,
        prevRows,
        videoTracks,
      ),
    );
  }, [audioTracks, scenesSnapshot, masterAudioPlaybackSpeed, videoTracks]);

  /** Probe hosted video duration so timeline bars don't extend past real media length (leave blank padding in-slot). */
  useEffect(() => {
    let cancelled = false;
    videoTracks.flat().forEach((clip) => {
      if (!clip.videoUrl) return;
      const url = mediaUrlForPlayback(clip.videoUrl);
      if (!url) return;
      const clipId = clip.id;
      const prevProbedUrl = probedVideoUrlRef.current.get(clipId);
      if (prevProbedUrl === url) return;

      const sceneRow = (scenesSnapshot || []).find(
        (s) => String(s.id) === String(clip.sceneId || clip.id),
      );

      probeVideoFileDurationSeconds(url).then((d) => {
        if (cancelled || !(d > 0)) return;
        probedVideoUrlRef.current.set(clipId, url);
        const urlChanged = prevProbedUrl != null && prevProbedUrl !== url;
        setVideoTracks((prev) =>
          tagPitchRecordingVideoTracks(
            prev.map((track) =>
              track.map((c) => {
                if (c.id !== clipId) return c;
                if (isPitchRecordingStory(story) || isPitchTimelineClip(c)) {
                  const pitchLocked = pitchVideoClipAfterProbe(c, d, sceneRow);
                  return {
                    ...pitchLocked,
                    playbackDuration: clipPlaybackFromTrim(pitchLocked),
                  };
                }
                const slotDur = Number(c.duration) || 0;
                const coversSlot = slotDur > 0 && d >= slotDur - EXTEND_MATCH_EPSILON_SEC;
                const hasUserTrim = clipHasUserVideoTrim(c, d);
                if (hasUserTrim && !urlChanged) {
                  const updated = clampVideoClipTrimToMediaCap({
                    ...c,
                    videoMediaDurationSeconds: d,
                    videoSourceDurationMax: d,
                  });
                  return { ...updated, playbackDuration: clipPlaybackFromTrim(updated) };
                }
                const updated = clampVideoClipTrimToMediaCap({
                  ...c,
                  videoMediaDurationSeconds: d,
                  videoSourceDurationMax: d,
                  trimStart: urlChanged || coversSlot ? 0 : (c.trimStart ?? 0),
                  trimEnd: urlChanged || coversSlot
                    ? Math.max(d, slotDur || d)
                    : (slotDur > 0 ? Math.min(d, slotDur) : d),
                  videoPlaybackSpeed: urlChanged || coversSlot ? 1 : c.videoPlaybackSpeed,
                });
                return { ...updated, playbackDuration: clipPlaybackFromTrim(updated) };
              }),
            ),
            story,
          ),
        );
      });
    });
    return () => {
      cancelled = true;
    };
  }, [videoTracks, story, scenesSnapshot]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await getPersistedProjectFolder(editorId);
        if (cancelled) return;
        setLinkedProjectFolder(r?.folderName ?? null);
      } catch {
        if (!cancelled) setLinkedProjectFolder(null);
      }
    })();
    return () => { cancelled = true; };
  }, [editorId]);

  const applyProjectDocument = useCallback((doc) => {
    const merged = reconcileLoadedProjectDocSceneAudio(doc, mediaUrlForPlayback);
    setPendingLanguageCommit(null);
    setScenesSnapshot((prev) => (Array.isArray(merged.scenes) ? merged.scenes : prev));
    let vTracks = coerceTimelineTracks(
      JSON.parse(JSON.stringify(merged.editor.videoTracks || [[]])),
    );
    const sceneById = Object.fromEntries(
      (Array.isArray(merged.scenes) ? merged.scenes : []).map((s) => [String(s.id), s]),
    );
    vTracks = vTracks.map((track) =>
      track.map((c) => stripLegacyVideoClipEmbeddedMute(c, sceneById[String(c.sceneId)])),
    );
    let aTracks = coerceTimelineTracks(
      JSON.parse(JSON.stringify(merged.editor.audioTracks || [[]])),
    );
    let mTracks = coerceTimelineTracks(
      JSON.parse(JSON.stringify(merged.editor.musicTracks || [[]])),
    );
    const sceneRows = Array.isArray(merged.scenes) ? merged.scenes : [];
    const narrPidForRepack = narrationProjectIdFromEditorStoryId(merged.story?.id);
    if (sceneRows.length) {
      const { videoTracks: withNewScenes } = appendMissingScenesToVideoTracks(vTracks, sceneRows);
      vTracks = withNewScenes;
    }
    if (narrPidForRepack && sceneRows.length && !isSongBasedEditorScenes(sceneRows)) {
      const repaired = reconcileNarrationTimelineLayout(
        vTracks,
        aTracks,
        mTracks,
        sceneRows,
        { narrationProjectId: narrPidForRepack },
      );
      if (repaired) {
        vTracks = repaired.videoTracks;
        aTracks = repaired.audioTracks;
        mTracks = repaired.musicTracks;
      }
    }
    const persistedMusicUrl = merged.story?.music_url;
    if (musicTimelineEmptyOrUnreadable(mTracks) && persistedMusicUrl) {
      const clipEnd = timelineEndFromVideoClips(vTracks.flat());
      const dur =
        Number(merged.story?.music_target_duration) > 0
          ? Number(merged.story.music_target_duration)
          : clipEnd || Number(merged.story?.total_duration) || 5;
      const narrPid = narrationProjectIdFromEditorStoryId(merged.story?.id);
      const isNarrationLikeProject =
        ['narration_project', 'news_video_project'].includes(merged.story?.story_type) && narrPid;
      if (isNarrationLikeProject) {
        mTracks = buildMusicTracksFromStoryUrl(persistedMusicUrl, dur, { narrationProjectId: narrPid });
      }
    } else {
      const narrPidRestore = narrationProjectIdFromEditorStoryId(merged.story?.id);
      const isNarrationLikeRestore =
        ['narration_project', 'news_video_project'].includes(merged.story?.story_type) && narrPidRestore;
      const musicHref = persistedMusicUrl || merged.story?.music_url;
      if (isNarrationLikeRestore && musicHref) {
        mTracks = mTracks.map((tr) =>
          tr.map((c) =>
            c.id === 'story-background-music' && !c.waveformUrl
              ? {
                  ...c,
                  url: c.url || mediaUrlForPlayback(musicHref),
                  waveformUrl: `/narration/projects/${narrPidRestore}/story-background-music/waveform`,
                }
              : c,
          ),
        );
      }
    }
    const normalized = normalizeEditorTracksForPlayback(vTracks, aTracks, mTracks);
    setVideoTracks(clampVideoTracksForMediaCap(normalized.videoTracks));
    setAudioTracks(normalized.audioTracks);
    setAudioTrackMeta(
      mergeAudioTrackMeta(
        merged.editor.audioTrackMeta || audioTrackMetaRef.current,
        normalized.audioTracks,
      ),
    );
    setMusicTracks(normalized.musicTracks);
    setSubtitleTracks(
      rebuildSubtitleTracksFromAudio(
        normalized.audioTracks,
        Array.isArray(merged.scenes) ? merged.scenes : [],
        coerceTimelineTracks(JSON.parse(JSON.stringify(merged.editor.subtitleTracks || [[]]))),
        normalized.videoTracks,
      ),
    );
    setEffectTracks(loadEffectTracksFromEditor(merged.editor));
    setOverlayTracks(loadHostOverlayTracksFromEditor(merged.editor));
    setSubtitleStyle(normalizeVideoSubtitleStyle(merged.editor.subtitleStyle));
    setTimelineZoom(merged.editor.timelineZoom ?? 1);
    setEditorAnimationSpeed(editorAnimationSpeedFromSnapshot(merged.editor));
    setVolume(merged.editor.volume ?? 0.8);
    setMasterAudioPlaybackSpeed(clampPlaybackSpeed(merged.editor.masterAudioPlaybackSpeed ?? 1));
    setIsMuted(Boolean(merged.editor.isMuted));
    setVideoMasterMuted(
      merged.editor.videoMasterMuted != null
        ? Boolean(merged.editor.videoMasterMuted)
        : DEFAULT_VIDEO_MASTER_MUTED,
    );
    setTimelineActive(mergeTimelineActiveForEditorLoad({
      ...(merged.editor.timelineActive || {}),
    }, normalized.audioTracks.length));
    const narrationLikeDoc = ['narration_project', 'news_video_project'].includes(merged.story?.story_type);
    setExportAspectRatio(
      narrationLikeDoc
        ? resolveNarrationProjectAspectRatio(merged.story)
        : (merged.editor.exportAspectRatio || '16:9'),
    );
    setExportQuality(normalizeExportQuality(merged.editor.exportQuality));
    setExportFraming(normalizeVideoExportFraming(merged.editor.exportFraming));
    setExportClipVideoToSubtitle(Boolean(merged.editor.exportClipVideoToSubtitle));
    setVideoBookends(restoredVideoBookends(merged.editor.videoBookends, {
      defaultTitle: doc.story?.title || doc.storyId || '',
      aspectRatio: merged.editor.exportAspectRatio || '16:9',
    }));
    if (doc.story) {
      setStory((prev) => {
        const mergedStory = { ...(prev || {}), ...doc.story };
        const scenesForLang = Array.isArray(merged.scenes) ? merged.scenes : [];
        const clipTextsForLang = (merged.editor?.videoTracks || [])
          .flat()
          .map((c) => String(c.narrationText || c.sceneText || '').trim())
          .filter(Boolean);
        const resolved =
          resolveVideoProjectScriptLanguage(mergedStory, scenesForLang, clipTextsForLang) || 'en';
        mergedStory.language = resolved;
        mergedStory.input = { ...(mergedStory.input || {}), language: resolved };
        return mergedStory;
      });
    }
    afterLoadProjectSeekRef.current = true;
    skipDirtyMarkRef.current = true;
    editorDirtyRef.current = false;
    setCloudSaveStatus('idle');
    resetEditorHistory();
  }, [resetEditorHistory]);

  /* ---- Clip actions ---- */
  const removeClipFromTrack = (trackIdx, clipId, type = 'video') => {
    recordEditorHistory();
    if (type === 'video') {
      setVideoTracks(prev => prev.map((track, i) =>
        i === trackIdx ? track.filter(c => c.id !== clipId) : track
      ));
    } else if (type === 'music') {
      setMusicTracks(prev => prev.map((track, i) =>
        i === trackIdx ? track.filter(c => c.id !== clipId) : track
      ));
    } else {
      setAudioTracks(prev => prev.map((track, i) =>
        i === trackIdx ? track.filter(c => c.id !== clipId) : track
      ));
    }
  };

  const closeTimelineGap = (gap) => {
    recordEditorHistory();
    const delta = -gap.duration;
    setVideoTracks(prev => prev.map(track => shiftTrackAfter(track, gap.endTime, delta)));
    setAudioTracks(prev => prev.map(track => shiftTrackAfter(track, gap.endTime, delta)));
    setMusicTracks(prev => prev.map(track => shiftTrackAfter(track, gap.endTime, delta)));
    setSelectedTimelineClip(null);
  };

  const removeAllTimelineGaps = useCallback(() => {
    recordEditorHistory();
    const result = packVideoTimelineAndAlignSceneMedia(
      videoTracksRef.current,
      audioTracksRef.current,
      musicTracksRef.current,
      scenesSnapshot,
      { narrationProjectId: projectId || null },
    );
    setVideoTracks(clampVideoTracksForMediaCap(result.videoTracks));
    setAudioTracks(result.audioTracks);
    setMusicTracks(result.musicTracks);
    setSubtitleTracks((prev) =>
      rebuildSubtitleTracksFromAudio(
        result.audioTracks,
        scenesSnapshot,
        prev,
        result.videoTracks,
      ),
    );
    setSelectedTimelineClip(null);
    toast.success('Removed video timeline gaps and aligned scene images, video, and audio.');
  }, [scenesSnapshot, projectId, recordEditorHistory]);

  const refreshPackedSceneTimeline = useCallback(() => {
    const scenes = scenesSnapshot || [];
    if (!scenes.length) {
      toast.info('No scenes to place on the timeline.');
      return;
    }
    recordEditorHistory();
    const packed = rebuildTimelinePackedScenes(scenes, {
      narrationProjectId: projectId || null,
      preserveExtraAudioTracks: audioTracksRef.current,
      preserveAudioTrackMeta: audioTrackMetaRef.current,
    });
    const flatVideo = packed.videoTracks.flat();
    let musicTracks = mergeSceneSfxIntoMusicTracks(
      JSON.parse(JSON.stringify(musicTracksRef.current || [[]])),
      scenes,
      flatVideo,
    );
    const clipEnd = timelineEndFromVideoClips(flatVideo);
    const musicSrc = story?.music_url;
    if (musicTracksNeedStoryMusic(musicTracks) && musicSrc && clipEnd > 0) {
      musicTracks = withStoryMusicLane(musicTracks, buildMusicTracksFromStoryUrl(musicSrc, clipEnd, {
        narrationProjectId: projectId || null,
      }));
    }
    const normalized = normalizeEditorTracksForPlayback(
      packed.videoTracks,
      packed.audioTracks,
      musicTracks,
    );
    setVideoTracks(clampVideoTracksForMediaCap(normalized.videoTracks));
    setAudioTracks(normalized.audioTracks);
    setMusicTracks(normalized.musicTracks);
    setSubtitleTracks(coerceTimelineTracks(packed.subtitleTracks));
    setTimelineActive((prev) => ({
      ...prev,
      image: true,
      video: true,
      audio: true,
      music: true,
      subtitle: true,
    }));
    setSelectedTimelineClip(null);
    toast.success(
      `Timeline refreshed: ${scenes.length} scene${scenes.length === 1 ? '' : 's'} placed side by side (length = longest audio/video per scene).`,
    );
  }, [projectId, scenesSnapshot, story?.music_url, recordEditorHistory]);

  const addTranslationAudioLane = useCallback(async () => {
    if (!isNarrationEditor || !projectId) {
      toast.error('Translation audio lanes are available in narration projects.');
      return;
    }
    const lang = String(audioTranslationLangTarget || '').toLowerCase();
    if (!lang) {
      toast.error('Choose a target language.');
      return;
    }
    const existingIdx = audioTrackMetaRef.current.findIndex(
      (m) => m?.isTranslation && String(m?.languageCode || '').toLowerCase() === lang,
    );
    if (existingIdx >= 0) {
      const audioLane = (audioTracksRef.current || [])[existingIdx] || [];
      setTimelineActive((prev) => {
        const lanes = [...normalizeAudioLaneActiveFlags(prev, audioTracksRef.current.length).lanes];
        const subtitleLanes = [...normalizeSubtitleLaneActiveFlags(prev, audioTracksRef.current.length).lanes];
        while (lanes.length < audioTracksRef.current.length) lanes.push(false);
        while (subtitleLanes.length < audioTracksRef.current.length) subtitleLanes.push(false);
        lanes.fill(false);
        subtitleLanes.fill(false);
        lanes[existingIdx] = true;
        subtitleLanes[existingIdx] = true;
        return { ...prev, audio: true, audioLanes: lanes, subtitle: true, subtitleLanes };
      });
      if (audioLane.length) {
        const nextSubtitleTracks = padSubtitleTracksToAudioLanes(
          subtitleTracksRef.current,
          audioTracksRef.current.length,
        );
        nextSubtitleTracks[existingIdx] = rebuildSingleSubtitleLaneFromAudio(
          audioLane,
          scenesSnapshotRef.current,
          nextSubtitleTracks[existingIdx],
          videoTracksRef.current,
        );
        setSubtitleTracks(nextSubtitleTracks);
        subtitleTracksRef.current = nextSubtitleTracks;
      }
      toast.info(`${audioTranslationLanguageLabel(lang)} lane is already on the timeline — set active for playback.`);
      return;
    }

    setTranslatingAudioLane(true);
    try {
      const { data } = await api.post(`/narration/projects/${projectId}/translate-audio-lane`, {
        language: lang,
      });
      const freshScenes = (data?.segments || []).map((segment) => ({
        ...segment,
        id: segment.id,
        scene_number: segment.segment_number,
        title: `Segment ${segment.segment_number || ''}`.trim(),
        scene_text: segment.text || '',
        narration_text: segment.text || '',
        duration_seconds: Number(segment.audio_duration || segment.duration) || 5,
      }));
      if (freshScenes.length) {
        setScenesSnapshot(freshScenes);
        scenesSnapshotRef.current = freshScenes;
      }

      const sourceLane = (audioTracksRef.current || [])[0] || [];
      const newLane = buildTranslationAudioLaneFromScenes(
        freshScenes.length ? freshScenes : scenesSnapshot,
        sourceLane,
        lang,
        projectId,
      );
      if (!newLane.length) {
        toast.error('No translated audio was produced. Ensure segments have narration text.');
        return;
      }

      const nextTracks = [...audioTracksRef.current, newLane];
      const nextMeta = [
        ...audioTrackMetaRef.current,
        {
          languageCode: lang,
          label: audioTranslationLanguageLabel(lang),
          isTranslation: true,
        },
      ];
      const laneCount = nextTracks.length;
      setAudioTracks(nextTracks);
      setAudioTrackMeta(nextMeta);
      setTimelineActive((prev) => {
        const lanes = Array(laneCount).fill(false);
        lanes[laneCount - 1] = true;
        const subtitleLanes = Array(laneCount).fill(false);
        subtitleLanes[laneCount - 1] = true;
        return { ...prev, audio: true, audioLanes: lanes, subtitle: true, subtitleLanes };
      });
      audioTracksRef.current = nextTracks;
      audioTrackMetaRef.current = nextMeta;
      const newLaneIndex = laneCount - 1;
      const nextSubtitleTracks = padSubtitleTracksToAudioLanes(subtitleTracksRef.current, laneCount);
      nextSubtitleTracks[newLaneIndex] = rebuildSingleSubtitleLaneFromAudio(
        newLane,
        freshScenes.length ? freshScenes : scenesSnapshotRef.current,
        nextSubtitleTracks[newLaneIndex],
        videoTracksRef.current,
      );
      setSubtitleTracks(nextSubtitleTracks);
      subtitleTracksRef.current = nextSubtitleTracks;
      toast.success(
        `Added ${audioTranslationLanguageLabel(lang)} audio lane. Aligning segments to translated audio…`,
      );
      setTimeout(() => {
        runMagicEditWithReferenceLaneRef.current?.(newLaneIndex);
      }, 0);
    } catch (err) {
      toast.error(err.response?.data?.detail || err.message || 'Audio translation failed');
    } finally {
      setTranslatingAudioLane(false);
    }
  }, [audioTranslationLangTarget, isNarrationEditor, projectId, scenesSnapshot]);

  const runMagicEditWithReferenceLane = useCallback((referenceAudioLaneIndex) => {
    const sceneClips = videoTracksRef.current
      .flat()
      .filter((c) => c.sceneId);
    if (!sceneClips.length) {
      toast.info('No scene clips on the timeline.');
      return;
    }

    const isSong = isSongBasedEditorScenes(scenesSnapshotRef.current);
    let missingAlignCount = 0;
    if (isSong) {
      missingAlignCount = (scenesSnapshotRef.current || []).filter((s) => s?.id && !songSceneHasAlignTiming(s)).length;
      if (missingAlignCount > 0) {
        toast.warning(
          `${missingAlignCount} stanza(s) lack aligned song timestamps — run WhisperX align in SongAI Flow first.`,
        );
      }
    }

    const laneLabel = audioLaneChoiceLabel(referenceAudioLaneIndex, audioTrackMetaRef.current);

    setClippingVideoToMatchAudio(true);
    try {
      const result = applyMagicEditToTimeline(
        videoTracksRef.current,
        audioTracksRef.current,
        musicTracksRef.current,
        scenesSnapshotRef.current,
        {
          narrationProjectId: projectId || null,
          preserveExtraAudioTracks: audioTracksRef.current,
          preserveAudioTrackMeta: audioTrackMetaRef.current,
          referenceAudioLaneIndex,
          prevSubtitleTracks: subtitleTracksRef.current,
        },
      );
      const { trimmed, extended, skipped, realigned, totalScenes } = result.stats;
      setVideoTracks(clampVideoTracksForMediaCap(result.videoTracks));
      setAudioTracks(result.audioTracks);
      setMusicTracks(result.musicTracks);
      setSubtitleTracks(result.subtitleTracks || rebuildSubtitleTracksFromAudio(
        result.audioTracks,
        scenesSnapshotRef.current,
        subtitleTracksRef.current,
        result.videoTracks,
      ));
      setSelectedTimelineClip(null);
      if (isSong) {
        toast.success(
          `Magic edit: placed ${realigned || totalScenes || 0} stanza(s) on aligned song timestamps with lyrics.${missingAlignCount ? ` (${missingAlignCount} without align)` : ''}`,
        );
        return;
      }
      const laneNote = referenceAudioLaneIndex > 0 || audioTracksRef.current.length > 1
        ? ` (timed to ${laneLabel})`
        : '';
      if (!trimmed && !extended) {
        toast.success(
          `Magic edit: packed all segments side by side with no gaps or overlaps.${skipped ? ` (${skipped} already matched)` : ''}${laneNote}`,
        );
        return;
      }
      const parts = [];
      if (trimmed) parts.push(`${trimmed} clipped`);
      if (extended) parts.push(`${extended} extended (last frame)`);
      toast.success(
        `Magic edit: ${parts.join(', ')} — all segments packed side by side.${skipped ? ` (${skipped} unchanged)` : ''}${laneNote}`,
      );
    } finally {
      setClippingVideoToMatchAudio(false);
    }
  }, [projectId]);

  runMagicEditWithReferenceLaneRef.current = runMagicEditWithReferenceLane;

  const clipAllSceneVideoToMatchAudio = useCallback(() => {
    const sceneClips = videoTracksRef.current
      .flat()
      .filter((c) => c.sceneId);
    if (!sceneClips.length) {
      toast.info('No scene clips on the timeline.');
      return;
    }

    const resolved = resolveMagicEditReferenceAudioLane({
      audioTracks: audioTracksRef.current,
      audioTrackMeta: audioTrackMetaRef.current,
      timelineActive,
    });
    if (resolved.needsPrompt) {
      setMagicEditLanePicker({ choices: resolved.choices });
      return;
    }
    runMagicEditWithReferenceLane(resolved.laneIndex ?? 0);
  }, [runMagicEditWithReferenceLane, timelineActive]);

  const selectedSongMultishotClips = useMemo(() => {
    if (!isSongBasedEditorScenes(scenesSnapshot)) return [];
    const flat = (videoTracks || []).flat();
    return selectedTimelineClips
      .filter((sel) => sel?.type === 'image' || sel?.type === 'video')
      .map((sel) => {
        const id = resolveVideoClipIdFromTimelineSelection(sel, videoTracks);
        return flat.find((c) => c.id === id) || null;
      })
      .filter((clip) => clip && clip.parentSceneId != null && clip.multishotPart != null);
  }, [selectedTimelineClips, videoTracks, scenesSnapshot]);

  const applyMultishotTimelineSelectionAndRefresh = useCallback(async (scenePatches) => {
    if (!projectId || !scenePatches.length) return;
    setMultishotTimelineToggling(true);
    try {
      let nextScenes = [...(scenesSnapshot || [])];
      for (const { sceneId, patch } of scenePatches) {
        const scene = nextScenes.find((s) => String(s.id) === String(sceneId));
        if (!scene) continue;
        const { data } = await api.patch(
          `/narration/projects/${projectId}/segments/${sceneId}/multishot-selection`,
          patch,
        );
        const updated = data?.id ? data : applyMultishotSelectionPatch(scene, patch);
        nextScenes = nextScenes.map((s) => (String(s.id) === String(sceneId) ? updated : s));
      }
      setScenesSnapshot(nextScenes);
      const referenceLane = resolveMagicEditReferenceAudioLane({
        audioTracks: audioTracksRef.current,
        audioTrackMeta: audioTrackMetaRef.current,
        timelineActive,
      }).laneIndex ?? 0;
      const result = applyMagicEditToTimeline(
        videoTracksRef.current,
        audioTracksRef.current,
        musicTracksRef.current,
        nextScenes,
        {
          narrationProjectId: projectId || null,
          preserveExtraAudioTracks: audioTracksRef.current,
          preserveAudioTrackMeta: audioTrackMetaRef.current,
          referenceAudioLaneIndex: referenceLane,
          prevSubtitleTracks: subtitleTracksRef.current,
        },
      );
      setVideoTracks(clampVideoTracksForMediaCap(result.videoTracks));
      setAudioTracks(result.audioTracks);
      setMusicTracks(result.musicTracks);
      setSubtitleTracks(result.subtitleTracks || rebuildSubtitleTracksFromAudio(
        result.audioTracks,
        nextScenes,
        subtitleTracksRef.current,
        result.videoTracks,
      ));
      setSelectedTimelineClips([]);
      toast.success('Timeline updated for multishot selection.');
    } catch (err) {
      toast.error(formatApiError(err, 'Failed to update multishot timeline selection'));
    } finally {
      setMultishotTimelineToggling(false);
    }
  }, [projectId, scenesSnapshot]);

  const hideSelectedMultishotsFromTimeline = useCallback(async () => {
    if (!selectedSongMultishotClips.length) return;
    const byScene = new Map();
    selectedSongMultishotClips.forEach((clip) => {
      const sceneId = String(clip.parentSceneId);
      if (!byScene.has(sceneId)) {
        byScene.set(sceneId, { master: null, shots: new Map() });
      }
      const entry = byScene.get(sceneId);
      if (clip.multishotIsMaster) entry.master = false;
      else if (clip.multishotShotIndex != null) entry.shots.set(Number(clip.multishotShotIndex), false);
    });
    const scenePatches = [];
    byScene.forEach((entry, sceneId) => {
      const scene = (scenesSnapshot || []).find((s) => String(s.id) === sceneId);
      if (!scene) return;
      const payload = multishotSelectionPayloadFromScene(scene);
      scenePatches.push({
        sceneId,
        patch: {
          include_master: entry.master != null ? entry.master : payload.include_master,
          shots: payload.shots.map((row) => (
            entry.shots.has(Number(row.index))
              ? { ...row, enabled: false }
              : row
          )),
        },
      });
    });
    await applyMultishotTimelineSelectionAndRefresh(scenePatches);
  }, [applyMultishotTimelineSelectionAndRefresh, scenesSnapshot, selectedSongMultishotClips]);

  const showSelectedMultishotsOnTimeline = useCallback(async () => {
    if (!selectedSongMultishotClips.length) return;
    const byScene = new Map();
    selectedSongMultishotClips.forEach((clip) => {
      const sceneId = String(clip.parentSceneId);
      if (!byScene.has(sceneId)) {
        byScene.set(sceneId, { master: null, shots: new Map() });
      }
      const entry = byScene.get(sceneId);
      if (clip.multishotIsMaster) entry.master = true;
      else if (clip.multishotShotIndex != null) entry.shots.set(Number(clip.multishotShotIndex), true);
    });
    const scenePatches = [];
    byScene.forEach((entry, sceneId) => {
      const scene = (scenesSnapshot || []).find((s) => String(s.id) === sceneId);
      if (!scene) return;
      const payload = multishotSelectionPayloadFromScene(scene);
      scenePatches.push({
        sceneId,
        patch: {
          include_master: entry.master != null ? entry.master : payload.include_master,
          shots: payload.shots.map((row) => (
            entry.shots.has(Number(row.index))
              ? { ...row, enabled: true }
              : row
          )),
        },
      });
    });
    await applyMultishotTimelineSelectionAndRefresh(scenePatches);
  }, [applyMultishotTimelineSelectionAndRefresh, scenesSnapshot, selectedSongMultishotClips]);

  const beginGapResize = (event, gap, type, trackIdx) => {
    if (event.button !== 0) return;
    event.stopPropagation();
    event.preventDefault();
    recordEditorHistory();
    setSelectedTimelineClip({ type: 'gap', trackType: type, trackIdx, id: gap.id });

    const origX = event.clientX;
    const videoResizeBaseline = videoTracks.map(track => track.map(c => ({ ...c })));
    const audioResizeBaseline = audioTracks.map(track => track.map(c => ({ ...c })));
    const musicResizeBaseline = musicTracks.map(track => track.map(c => ({ ...c })));
    let rafId = null;
    let latestEvent = null;

    const applyMove = () => {
      const moveEvent = latestEvent;
      rafId = null;
      if (!moveEvent) return;
      const dSec = (moveEvent.clientX - origX) / pxPerSec;
      const nextDuration = Math.max(0, gap.duration + dSec);
      const delta = nextDuration - gap.duration;
      setVideoTracks(videoResizeBaseline.map(track => shiftTrackAfter(track, gap.endTime, delta)));
      setAudioTracks(audioResizeBaseline.map(track => shiftTrackAfter(track, gap.endTime, delta)));
      setMusicTracks(musicResizeBaseline.map(track => shiftTrackAfter(track, gap.endTime, delta)));
    };

    const handleMove = (moveEvent) => {
      latestEvent = moveEvent;
      if (rafId == null) rafId = requestAnimationFrame(applyMove);
    };

    const handleUp = () => {
      if (rafId != null) cancelAnimationFrame(rafId);
      clearWindowDragHandlers(handleMove, handleUp);
    };

    installWindowDragHandlers(handleMove, handleUp);
  };

  const saveEditedClip = (updated) => {
    recordEditorHistory();
    const next = { ...updated, playbackDuration: clipPlaybackFromTrim(updated) };
    setVideoTracks(prev => prev.map((track, i) =>
      i === editingClip.trackIdx ? track.map(c => c.id === updated.id ? next : c) : track
    ));
    setEditingClip(null);
  };

  const openTransitionPicker = useCallback((payload) => {
    if (!payload?.clipId || !payload?.fromClip || !payload?.toClip) return;
    setTransitionPicker(payload);
  }, []);

  const applyTransitionFromPicker = useCallback((draft) => {
    if (!transitionPicker?.clipId) return;
    const { clipId, trackIdx } = transitionPicker;
    recordEditorHistory();
    setVideoTracks((prev) => prev.map((track, i) => (
      i === trackIdx
        ? track.map((c) => (
          c.id === clipId
            ? {
              ...c,
              transitionOut: draft.transitionOut,
              transitionDuration: draft.transitionDuration,
            }
            : c
        ))
        : track
    )));
    setTransitionPicker(null);
  }, [transitionPicker, recordEditorHistory]);

  /** Live-sync clip editor changes to timeline (preview + export) without closing the dialog. */
  const patchEditedClip = useCallback((updated) => {
    const next = { ...updated, playbackDuration: clipPlaybackFromTrim(updated) };
    setEditingClip((ed) => {
      if (!ed || ed.clip?.id !== updated.id) return ed;
      setVideoTracks((prev) => prev.map((track, i) => (
        i === ed.trackIdx
          ? track.map((c) => (c.id === updated.id ? next : c))
          : track
      )));
      return { ...ed, clip: next };
    });
  }, []);

  const sourceIdForClip = (clip) => clipSceneSourceId(clip);

  const pendingVideoClipEntries = () => {
    const seen = new Set();
    const pending = [];
    videoTracks.forEach((track, trackIdx) => {
      track.forEach((clip) => {
        const sourceId = sourceIdForClip(clip);
        if (!sourceId || seen.has(sourceId)) return;
        if (sourceId.startsWith('media-') || sourceId.startsWith('audio-upload') || sourceId.startsWith('audio-drop')) return;
        const imageUrl = clipImageUrlForTimelineDisplay(clip, sceneByIdMap, scenesSnapshot);
        if (!imageUrl || clip.videoUrl) return;
        seen.add(sourceId);
        pending.push({ clip: { ...clip, imageUrl }, trackIdx, sourceId });
      });
    });
    return pending;
  };

  /** Timeline segments that already have a video — batch re-run with chosen provider / camera. */
  const regenerateVideoClipEntries = () => {
    const seen = new Set();
    const rows = [];
    videoTracks.forEach((track, trackIdx) => {
      track.forEach((clip) => {
        const sourceId = sourceIdForClip(clip);
        if (!sourceId || seen.has(sourceId)) return;
        if (sourceId.startsWith('media-') || sourceId.startsWith('audio-upload') || sourceId.startsWith('audio-drop')) return;
        if (String(sourceId).startsWith('narration-final-')) return;
        if (!clip.videoUrl) return;
        seen.add(sourceId);
        rows.push({ clip, trackIdx, sourceId });
      });
    });
    return rows;
  };

  /** Segments/scenes with narration script but no clip audio yet (e.g. after script edits cleared audio). */
  const pendingAudioClipEntries = () => {
    const seen = new Set();
    const pending = [];
    videoTracks.forEach((track, trackIdx) => {
      track.forEach((clip) => {
        const sourceId = sourceIdForClip(clip);
        if (!clip.sceneId || !sourceId || seen.has(sourceId)) return;
        if (sourceId.startsWith('media-') || sourceId.startsWith('audio-upload') || sourceId.startsWith('audio-drop')) return;
        const script = (clip.narrationText || clip.sceneText || '').trim();
        if (!script || clip.audioUrl) return;
        seen.add(sourceId);
        pending.push({ clip, trackIdx, sourceId });
      });
    });
    return pending;
  };

  /** Every timeline segment/scene with script text (deduped), including those that already have audio. */
  const allAudioClipEntries = () => {
    const seen = new Set();
    const rows = [];
    videoTracks.forEach((track, trackIdx) => {
      track.forEach((clip) => {
        const sourceId = sourceIdForClip(clip);
        if (!clip.sceneId || !sourceId || seen.has(sourceId)) return;
        if (sourceId.startsWith('media-') || sourceId.startsWith('audio-upload') || sourceId.startsWith('audio-drop')) return;
        const script = (clip.narrationText || clip.sceneText || '').trim();
        if (!script) return;
        seen.add(sourceId);
        rows.push({ clip, trackIdx, sourceId });
      });
    });
    return rows;
  };

  /**
   * Single sync path: scene/segment document → video clips, audio clips, replace dialog, preview.
   * scenesSnapshot holds canonical *_url fields; timeline clips mirror them here.
   */
  const markMediaReplaceInProgress = useCallback(() => {
    mediaReplaceGuardUntilRef.current = Date.now() + 8000;
  }, []);

  const syncTimelineFromSceneDocument = useCallback((sourceId, scene, options = {}) => {
    if (!sourceId || !scene) return null;
    const sid = String(sourceId);
    const media = sceneDocumentMediaPlayback(scene);
    const slotDur = sceneSlotDurationSeconds(scene);
    const audioDur = Number(
      scene.audio_duration
      || scene.audio_duration_seconds
      || scene.duration_seconds
      || scene.duration,
    ) || slotDur;
    const script = scenePrimaryScriptText(scene);
    const audioCacheBust = scene.updated_at || scene.created_at || options.cacheBust || '';
    const playbackMediaUrl = (rawUrl) => (
      rawUrl ? mediaUrlWithCacheBust(rawUrl, audioCacheBust) : rawUrl
    );
    const playbackAudioUrl = (rawUrl) => playbackMediaUrl(rawUrl);
    let primaryClip = null;
    let segmentStartTime = 0;

    setVideoTracks((prev) => {
      const existing = prev.flat().find((c) => clipMatchesSceneSource(c, sid));
      segmentStartTime = Number(existing?.startTime) || 0;
      const nextTracks = prev.map((track) => track.map((item) => {
      if (!clipMatchesSceneSource(item, sid)) return item;
      const videoUrlRaw = resolveClipVideoUrlAfterImageChange(scene, item, media.imageUrl);
      const videoUrl = videoUrlRaw ? playbackMediaUrl(videoUrlRaw) : null;
      const urlChanged = sceneDocumentVideoUrlChanged(item, scene)
        || sceneDocumentImageUrlChanged(item, scene)
        || Boolean(options.forceMediaReload);
      const timing = resolveVideoClipMediaTiming({
        scene,
        slotDur,
        existingClip: item,
        forceUrlChanged: urlChanged,
      });
      if (urlChanged) probedVideoUrlRef.current.delete(item.id);
      const wordTimestamps = Array.isArray(scene.word_timestamps) && scene.word_timestamps.length
        ? scene.word_timestamps
        : estimateWordTimestamps(script, timing.playbackDuration);
      const sceneMediaUpdatedAt = scene.updated_at || options.cacheBust || null;
      const next = clampVideoClipTrimToMediaCap({
        ...item,
        name: scene.title || scene.scene_title || item.name,
        sceneText: scene.scene_text ?? item.sceneText,
        narrationText: script || item.narrationText,
        videoUrl,
        imageUrl: playbackMediaUrl(media.imageUrl),
        audioUrl: media.audioUrl,
        sceneMediaUpdatedAt,
        duration: timing.duration,
        videoSourceDurationMax: timing.videoSourceDurationMax,
        videoMediaDurationSeconds: urlChanged
          ? timing.videoMediaDurationSeconds
          : (timing.videoMediaDurationSeconds ?? item.videoMediaDurationSeconds),
        videoPlaybackSpeed: timing.videoPlaybackSpeed,
        playbackDuration: timing.playbackDuration,
        trimStart: timing.trimStart,
        trimEnd: timing.trimEnd,
        wordTimestamps,
      });
      if (!item.sourceClipId && !String(item.id || '').endsWith('__image')) {
        primaryClip = next;
      }
      return next;
      }));
      videoTracksRef.current = nextTracks;
      return nextTracks;
    });

    setAudioTracks((prev) => {
      const lane0 = Array.isArray(prev?.[0]) ? [...prev[0]] : [];
      const keep = lane0.filter((c) => !(
        String(c.id || '').startsWith(`seg-audio-${sid}-seg-`)
        || String(c.id || '') === `audio-${sid}`
      ));

      const timeline = Array.isArray(scene.audio_timeline) ? scene.audio_timeline : [];
      const segs = timeline
        .filter((t) => t && t.segment_audio_url)
        .sort((a, b) => (Number(a.index) || 0) - (Number(b.index) || 0))
        .map((t) => {
          const idx = Number(t.index) || 0;
          const speaker = String(t.speaker || '').trim();
          const kind = String(t.kind || '').toLowerCase();
          const label = kind === 'narration' ? 'Narrator' : (speaker || 'Dialogue');
          const startOffset = Number(t.start_seconds) || 0;
          const dur = Number(t.duration_seconds) || 0;
          return {
            id: `seg-audio-${sid}-seg-${idx}`,
            name: scene.scene_number != null ? `Scene ${scene.scene_number} · ${label}` : `Scene · ${label}`,
            sceneId: sid,
            url: playbackAudioUrl(t.segment_audio_url),
            startTime: segmentStartTime + startOffset,
            duration: dur > 0 ? dur : 1,
            sourceDuration: dur > 0 ? dur : 1,
            volume: 0.8,
            muted: false,
            sourceType: 'scene_segment',
          };
        });

      if (segs.length) {
        return [[...keep, ...segs], ...(prev || []).slice(1).map((t) => [...t])];
      }

      if (media.audioUrl) {
        const mix = {
          id: `audio-${sid}`,
          name: scene.scene_number != null ? `Narration ${scene.scene_number}` : 'Narration',
          sceneId: sid,
          url: playbackAudioUrl(scene.audio_url || media.audioUrl),
          startTime: segmentStartTime,
          duration: audioDur,
          sourceDuration: audioDur,
          volume: 0.8,
          muted: false,
          sourceType: 'scene_mix',
          ...(isNarrationEditor && projectId ? {
            waveformUrl: `/narration/projects/${projectId}/segments/${sid}/waveform`,
          } : {}),
        };
        return [[...keep, mix], ...(prev || []).slice(1).map((t) => [...t])];
      }

      return prev;
    });

    setReplacementDialog((prev) => {
      if (!prev || String(prev.sourceId) !== sid) return prev;
      const clip = primaryClip || prev.clip;
      return clip ? { ...prev, clip } : prev;
    });

    if (options.seekPreview !== false && primaryClip) {
      playback.seek(Number(primaryClip.startTime) || 0);
    }

    return primaryClip;
  }, [isNarrationEditor, playback, projectId]);

  const openReplacementDialog = async (clip, trackIdx, trackType) => {
    let workingClip = clip;
    if (trackType === 'image') {
      const sid = clipSceneSourceId(clip);
      workingClip = primaryVideoClipForScene(videoTracksRef.current, sid) || clip;
    }
    const kind = trackType === 'audio' ? 'audio' : (workingClip.videoUrl ? 'video' : 'image');
    const sourceId = clipSceneSourceId(workingClip);
    if (!sourceId || sourceId.startsWith('media-') || sourceId.startsWith('audio-upload') || sourceId.startsWith('audio-drop')) {
      toast.info('Replacement history is available for generated project snippets.');
      return;
    }

    const liveClip = primaryVideoClipForScene(videoTracksRef.current, sourceId) || workingClip;
    const nextDialog = { clip: liveClip, trackIdx, trackType, kind, sourceId };
    setReplacementDialog(nextDialog);
    setReplacementPanelTab(kind);
    setReplacementOverrides(defaultReplacementOverrides(story, isNarrationEditor));
    setReplacementLoading(true);
    try {
      const endpoint = isNarrationEditor
        ? `/narration/projects/${projectId}/segments/${sourceId}/media-options`
        : `/scenes/${sourceId}/media-options`;
      const { data } = await api.get(endpoint, { params: { kind } });
      setReplacementOptions(Array.isArray(data.options) ? data.options : []);
      const liveScene = (scenesSnapshotRef.current || []).find(
        (s) => String(s.id) === String(sourceId),
      );
      const spokenAudio = spokenSubtitleTextForScene(liveScene)
        || String(data.prompts?.audio || '').trim()
        || '';
      setReplacementDrafts({
        image: data.prompts?.image || data.prompt || '',
        video: data.prompts?.video || data.prompt || '',
        // Prefer spoken dialogue/narration; never fall back to scene overview alone.
        audio: spokenAudio || (kind === 'audio' ? (data.prompt || '') : ''),
      });
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not load generated snippets');
      setReplacementDialog(null);
    } finally {
      setReplacementLoading(false);
    }
  };

  const replaceTimelineSnippet = async (option) => {
    if (!replacementDialog || !option?.url) return;
    const { sourceId } = replacementDialog;
    const selectKind =
      replacementPanelTab === 'upload' ? replacementDialog.kind : replacementPanelTab;

    const applySelectedSegmentToTimeline = (updatedDoc) => {
      if (!updatedDoc) return;
      markMediaReplaceInProgress();
      if (isNarrationEditor) {
        const narrationProjectStub = {
          id: projectId,
          title: story?.title,
          input: story?.input || {
            topic: story?.user_topic,
            language: story?.language,
            visual_style: story?.visual_style,
            image_provider: story?.image_provider,
          },
        };
        const sceneDoc = narrationSegmentToScene(updatedDoc, narrationProjectStub);
        const nextScenes = scenesSnapshotRef.current.map((s) => (
          String(s.id) === String(sourceId) ? sceneDoc : s
        ));
        scenesSnapshotRef.current = nextScenes;
        setScenesSnapshot(nextScenes);
        syncTimelineFromSceneDocument(sourceId, sceneDoc, {
          seekPreview: false,
          forceMediaReload: true,
        });
        return sceneDoc;
      }
      const nextScenes = scenesSnapshotRef.current.map((s) => (
        String(s.id) === String(sourceId) ? { ...s, ...updatedDoc } : s
      ));
      scenesSnapshotRef.current = nextScenes;
      setScenesSnapshot(nextScenes);
      syncTimelineFromSceneDocument(sourceId, updatedDoc, {
        seekPreview: false,
        forceMediaReload: true,
      });
      return updatedDoc;
    };

    try {
      const endpoint = isNarrationEditor
        ? `/narration/projects/${projectId}/segments/${sourceId}/select-media`
        : `/scenes/${sourceId}/select-media`;
      const { data: updatedDoc } = await api.post(endpoint, { kind: selectKind, url: option.url });
      if (isNarrationEditor) {
        const segment = await loadNarrationSegment(sourceId, { skipVideoTrackSync: true });
        const narrationProjectStub = {
          id: projectId,
          title: story?.title,
          input: story?.input || {
            topic: story?.user_topic,
            language: story?.language,
            visual_style: story?.visual_style,
            image_provider: story?.image_provider,
          },
        };
        applySelectedSegmentToTimeline(
          narrationSegmentToScene(segment || updatedDoc, narrationProjectStub),
        );
      } else if (updatedDoc?.id) {
        applySelectedSegmentToTimeline(updatedDoc);
      } else {
        const scene = await loadStoryScene(sourceId);
        applySelectedSegmentToTimeline(scene);
      }
      await refreshReplacementOptions(selectKind, sourceId);
      queuePersistEditorAfterMediaChange();
      toast.success('Timeline snippet replaced');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not replace snippet');
    }
  };

  const refreshReplacementOptions = async (kind, sourceId) => {
    const endpoint = isNarrationEditor
      ? `/narration/projects/${projectId}/segments/${sourceId}/media-options`
      : `/scenes/${sourceId}/media-options`;
    const params = { kind };
    const { data } = await api.get(endpoint, { params });
    setReplacementOptions(Array.isArray(data.options) ? data.options : []);
  };

  const handleReplacementTabChange = async (tab) => {
    setReplacementPanelTab(tab);
    if (!replacementDialog?.sourceId) return;
    if (tab === 'upload') {
      setReplacementOptions([]);
      return;
    }
    setReplacementLoading(true);
    try {
      await refreshReplacementOptions(tab, replacementDialog.sourceId);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not load snippets for this tab');
      setReplacementOptions([]);
    } finally {
      setReplacementLoading(false);
    }
  };

  const waitForRegenerationJob = async (jobId) => {
    for (let attempt = 0; attempt < REGENERATION_JOB_MAX_POLLS; attempt += 1) {
      await sleep(REGENERATION_JOB_POLL_MS);
      const { data: job } = await api.get(`/jobs/${jobId}`);
      if (job.status === 'completed') return job;
      if (job.status === 'failed') {
        throw new Error(job.error_message || 'Video regeneration failed');
      }
    }
    throw new Error('Video regeneration is still running. Check Tasks for the latest status.');
  };

  const applyExtendedVideoToTimeline = useCallback(async (sourceId, segment, extendGap) => {
    if (!sourceId || !extendGap) return;
    const playableVideo = mediaUrlForSegmentField(segment, 'video_url');
    const audioSec = Number(extendGap.audioSec ?? segment?.audio_duration ?? segment?.duration) || 0;
    let videoFileSec = Number(segment?.video_duration_seconds)
      || Number(segment?.duration_seconds)
      || audioSec;

    if (playableVideo) {
      const probed = await probeVideoDurationSeconds(playableVideo);
      if (probed > 0) {
        videoFileSec = Math.max(videoFileSec, probed);
      }
    }
    videoFileSec = Math.max(videoFileSec, audioSec);

    const currentClip = videoTracksRef.current.flat().find(
      (c) => String(c.sceneId || c.id) === String(sourceId),
    );
    if (!currentClip) return;

    const clipStart = Number(currentClip.startTime) || 0;
    const oldSlotEnd = clipStart + Number(extendGap.slotTimelineSec ?? extendGap.audioSec ?? audioSec);
    const excludeId = currentClip.id;

    if (playableVideo) {
      probedVideoUrlRef.current.delete(excludeId);
    }

    const buildUpdatedClip = (item) => buildExtendedTimelineClip(item, {
      playableVideo,
      audioSec,
      videoFileSec,
    });

    const previewClip = buildUpdatedClip(currentClip);
    const newDrawSec = clipVideoSnippetDrawDuration(previewClip);
    const newSlotEnd = clipStart + Math.max(audioSec, newDrawSec);
    const delta = newSlotEnd - oldSlotEnd;

    setVideoTracks((prev) => prev.map((track) => {
      let nextTrack = track.map((item) => (
        String(item.sceneId || item.id) === String(sourceId) ? buildUpdatedClip(item) : item
      ));
      if (delta > 0.01) {
        nextTrack = shiftTrackAfter(nextTrack, oldSlotEnd, delta, excludeId);
      }
      return nextTrack;
    }));

    if (delta > 0.01) {
      setAudioTracks((prev) => prev.map((track) => shiftTrackAfter(track, oldSlotEnd, delta)));
      setMusicTracks((prev) => prev.map((track) => shiftTrackAfter(track, oldSlotEnd, delta)));
      setSubtitleTracks((prev) => prev.map((track) => shiftTrackAfter(track, oldSlotEnd, delta)));
    }

    if (playableVideo) {
      probedVideoUrlRef.current.set(excludeId, playableVideo);
    }

    setScenesSnapshot((prev) => prev.map((scene) => (
      String(scene.id) === String(sourceId)
        ? {
          ...scene,
          video_url: segment?.video_url ?? scene.video_url,
          video_duration_seconds: videoFileSec,
          duration: audioSec,
          audio_duration: audioSec,
          duration_seconds: audioSec,
          trim_start_seconds: 0,
          trim_end_seconds: videoFileSec,
        }
        : scene
    )));
  }, []);

  const loadNarrationSegment = async (
    sourceId,
    { skipVideoTrackSync = false, skipScenesSnapshotReplace = false } = {},
  ) => {
    const { data: narrationProject } = await api.get(`/narration/projects/${projectId}`, {
      params: { _editorFresh: Date.now() },
    });
    const segment = (narrationProject?.segments || []).find(item => item.id === sourceId);
    if (!segment) throw new Error('Could not load regenerated segment');
    setStory((prev) => ({
      ...(prev || {}),
      id: editorId,
      title: narrationProject?.title || prev?.title || 'Narration Project',
      input: narrationProject?.input || prev?.input || {},
      final_video_url: narrationProject?.final_video_url || prev?.final_video_url || null,
      total_duration: narrationProject?.total_duration ?? prev?.total_duration,
      music_url: narrationProject?.music_url ?? prev?.music_url,
      music_mood: narrationProject?.music_mood ?? prev?.music_mood,
      music_prompt: narrationProject?.music_prompt ?? prev?.music_prompt,
      music_history: narrationProject?.music_history ?? prev?.music_history ?? [],
      music_target_duration: narrationProject?.music_target_duration ?? prev?.music_target_duration,
      ...narrationStoryExtrasFromProject(narrationProject),
    }));
    if (!skipScenesSnapshotReplace) {
      const nextScenes = narrationSegmentsFromProject(narrationProject);
      scenesSnapshotRef.current = nextScenes;
      setScenesSnapshot(nextScenes);
    }

    if (!skipVideoTrackSync) {
      syncTimelineFromSceneDocument(sourceId, segment);
    }
    return segment;
  };

  const loadStoryScene = async (sourceId, { skipVideoTrackSync = false } = {}) => {
    const { data: scenesData } = await api.get(`/stories/${storyId}/scenes`);
    const sorted = [...scenesData].sort((a, b) => (a.scene_number || 0) - (b.scene_number || 0));
    const scene = sorted.find(item => item.id === sourceId);
    if (!scene) throw new Error('Could not load regenerated scene');
    setScenesSnapshot(sorted);
    const playableSfx = mediaUrlForSegmentField(scene, 'sound_effect_url');
    const duration = Number(scene.duration_seconds || scene.audio_duration_seconds) || 5;

    if (!skipVideoTrackSync) {
      syncTimelineFromSceneDocument(sourceId, scene);
    }

    const segmentStartTime =
      Number(videoTracksRef.current.flat().find((c) => clipMatchesSceneSource(c, sourceId))?.startTime) || 0;

    setMusicTracks((prev) => {
      const sid = String(sourceId);
      const sfxId = `scene-sfx-${sid}`;
      const slot = sceneSlotsFromClips(videoTracksRef.current.flat().filter((c) => clipMatchesSceneSource(c, sid)))
        .values().next().value;
      const clipStart = slot ? slot.start : segmentStartTime;
      const sfxDur = sceneSfxSeconds(positiveDuration(scene.sound_effect_duration_seconds, duration, 5), slot);
      if (!playableSfx) {
        if (!(prev || []).flat().some((c) => String(c?.id) === sfxId)) return prev;
        return placeSceneSfxOnOwnLane((prev || []).map((t) => t.filter((c) => String(c?.id) !== sfxId)), []);
      }
      return placeSceneSfxOnOwnLane(prev, [{
        id: sfxId,
        name: scene.scene_number != null ? `SFX Scene ${scene.scene_number}` : 'Scene SFX',
        sceneId: sid,
        url: playableSfx,
        startTime: clipStart,
        duration: sfxDur,
        sourceDuration: sfxDur,
        trimStart: 0,
        volume: 0.28,
        muted: false,
        sourceType: 'scene_sfx',
      }]);
    });

    return scene;
  };

  const handleReplacementLocalUpload = async (event) => {
    const file = event.target.files?.[0];
    if (!file || !replacementDialog) return;
    event.target.value = '';
    const { kind, sourceId, clip } = replacementDialog;
    const { segmentId } = narrationSegmentApiTarget(
      sourceId,
      scenesSnapshotRef.current,
      clip,
    );
    setReplacementUploading(true);
    try {
      const fd = new FormData();
      fd.append('kind', kind);
      fd.append('file', file);
      const endpoint = isNarrationEditor
        ? `/narration/projects/${projectId}/segments/${segmentId}/upload-media`
        : `/scenes/${sourceId}/upload-media`;
      const { data } = await api.post(endpoint, fd);
      const canonicalUrl = data?.[`${kind}_url`];
      if (!canonicalUrl) {
        toast.error('Upload finished but media URL was missing');
        return;
      }
      if (isNarrationEditor) {
        await loadNarrationSegment(segmentId);
      } else {
        await loadStoryScene(sourceId);
      }
      if (kind === 'image') {
        setReplacementDialog((prev) => (prev ? { ...prev, uploadedImageUrl: canonicalUrl } : prev));
      }
      const reloadKind =
        replacementPanelTab === 'upload' ? replacementDialog.kind : replacementPanelTab;
      await refreshReplacementOptions(reloadKind, sourceId);
      if (kind === 'image') {
        await refreshReplacementOptions('video', sourceId);
      }
      toast.success('Snippet replaced from upload');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Upload failed');
    } finally {
      setReplacementUploading(false);
    }
  };

  const resolveVideoPromptForTarget = async (
    { clip, sourceId },
    { videoProvider, cameraMotion, fallbackPrompt = '' } = {},
  ) => {
    const manual = String(fallbackPrompt || '').trim();
    if (manual) return manual;
    try {
      const { segmentId, segmentNumber } = narrationSegmentApiTarget(
        sourceId,
        scenesSnapshotRef.current,
        clip,
      );
      const data = await fetchVideoGenerationPrompt({
        sceneId: isNarrationEditor ? undefined : sourceId,
        projectId: isNarrationEditor ? projectId : undefined,
        segmentId: isNarrationEditor ? segmentId : undefined,
        segmentNumber: isNarrationEditor ? segmentNumber : undefined,
        provider: videoProvider || batchVideoProvider,
        cameraMotion: cameraMotion || batchCameraMotion,
      });
      return String(data?.prompt || '').trim();
    } catch {
      return String(clip?.narrationText || clip?.sceneText || '').trim();
    }
  };

  const queueOneVideoGeneration = async (
    { clip, sourceId },
    editedPrompt,
    {
      videoProvider,
      cameraMotion,
      waitForCompletion = false,
      extendToAudio = false,
      lipSyncWithAudio = false,
    } = {},
  ) => {
    const prov = videoProvider || batchVideoProvider;
    const motion = cameraMotion || batchCameraMotion;
    const cameraPayload = motion && motion !== 'auto' ? { camera_motion: motion } : {};
    const prompt = String(editedPrompt || '').trim();

    if (isNarrationEditor) {
      const { segmentId, segmentNumber } = narrationSegmentApiTarget(
        sourceId,
        scenesSnapshotRef.current,
        clip,
      );
      const { data } = await api.post(
        `/narration/projects/${projectId}/segments/${segmentId}/generate-video`,
        {
          video_provider: prov,
          async_job: true,
          prompt,
          ...(segmentNumber ? { segment_number: segmentNumber } : {}),
          ...cameraPayload,
        },
      );
      if (!data?.job_id) throw new Error(`Could not queue ${clip?.name || segmentId}`);
      if (waitForCompletion) {
        await waitForRegenerationJob(data.job_id);
        const segment = await loadNarrationSegment(segmentId);
        applyRegeneratedNarrationSegment('video', segment);
      }
      return data.job_id;
    }

    const { data } = await api.post(`/scenes/${sourceId}/generate-video`, {
      provider: prov,
      prompt,
      ...cameraPayload,
      ...(extendToAudio ? { extend_duration_to_match_audio: true } : {}),
      ...(lipSyncWithAudio ? { lip_sync_with_audio: true } : {}),
    });
    if (!data?.job_id) throw new Error(`Could not queue ${clip?.name || sourceId}`);
    if (waitForCompletion) {
      await waitForRegenerationJob(data.job_id);
      await loadStoryScene(sourceId);
    }
    return data.job_id;
  };

  const generatePendingVideos = async () => {
    const pendingFirst = pendingVideoClipEntries();
    const regenSlots = regenerateVideoClipEntries();
    const targets = pendingFirst.length ? pendingFirst : regenSlots;
    const mode = pendingFirst.length ? 'generate' : 'regenerate';

    if (!targets.length) {
      toast.info('No segment clips on the timeline to generate video for or to regenerate.');
      return;
    }
    if (!(isNarrationEditor ? projectId : storyId)) {
      toast.error('Missing project — cannot run batch video.');
      return;
    }

    setGeneratingPendingVideos(true);
    setPendingVideoProgress({ current: 0, total: targets.length, name: '' });
    try {
      let queued = 0;
      let completed = 0;
      for (let i = 0; i < targets.length; i += 1) {
        const target = targets[i];
        setPendingVideoProgress({
          current: i + 1,
          total: targets.length,
          name: target.clip?.name || `Clip ${i + 1}`,
        });
        const prompt = await resolveVideoPromptForTarget(target, {
          videoProvider: batchVideoProvider,
          cameraMotion: batchCameraMotion,
        });
        try {
          await queueOneVideoGeneration(target, prompt, {
            videoProvider: batchVideoProvider,
            cameraMotion: batchCameraMotion,
            // Wait so Video lane updates as each clip finishes (fire-and-forget left the lane empty).
            waitForCompletion: true,
          });
          queued += 1;
          completed += 1;
        } catch (err) {
          if (handleInsufficientCreditsError(err, openInsufficientCredits)) {
            break;
          }
          toast.error(formatApiError(err, `Could not generate ${target.clip?.name || 'clip'}`));
        }
      }
      if (completed > 0) {
        toast.success(
          `${completed} video${completed === 1 ? '' : 's'} ready on the Video lane (${batchVideoProvider}).`,
        );
      } else if (queued === 0) {
        toast.error('No videos were generated. Check provider credits and scene images.');
      }
    } catch (err) {
      toast.error(formatApiError(err, `Could not ${mode} videos`));
    } finally {
      setGeneratingPendingVideos(false);
      setPendingVideoProgress({ current: 0, total: 0, name: '' });
    }
  };

  const runBulkAudioGeneration = async (entries, { allScenes = false } = {}) => {
    if (!entries.length) {
      toast.info(allScenes ? 'No segments with script text on the timeline.' : 'No segments need audio generation.');
      return;
    }
    const defs = replacementOverrides;
    setGeneratingPendingAudios(true);
    setPendingAudioProgress({ current: 0, total: entries.length, name: '' });
    let successCount = 0;
    const sceneErrors = [];
    try {
      for (let index = 0; index < entries.length; index += 1) {
        const { clip, sourceId } = entries[index];
        setPendingAudioProgress({ current: index + 1, total: entries.length, name: clip.name || `Clip ${index + 1}` });
        const script = (clip.narrationText || clip.sceneText || '').trim();
        if (isNarrationEditor && projectId) {
          const { segmentId, segmentNumber } = narrationSegmentApiTarget(
            sourceId,
            scenesSnapshotRef.current,
            clip,
          );
          const { data: updatedSegment } = await api.post(
            `/narration/projects/${projectId}/segments/${segmentId}/regenerate-audio`,
            {
              prompt: script,
              ...(segmentNumber ? { segment_number: segmentNumber } : {}),
              tts_provider: defs.tts_provider,
              voice_style: defs.voice_style,
              language: defs.language,
              narrator_elevenlabs_voice_id: defs.narrator_elevenlabs_voice_id || undefined,
              narrator_murf_voice_id: defs.narrator_murf_voice_id || undefined,
              narrator_murf_locale: defs.narrator_murf_locale || undefined,
              narrator_minimax_voice_id: defs.narrator_minimax_voice_id || undefined,
            },
          );
          applyRegeneratedNarrationSegment('audio', updatedSegment);
          successCount += 1;
        } else if (storyId) {
          const scene = (scenesSnapshotRef.current || []).find(
            (row) => String(row.id) === String(sourceId),
          ) || null;
          const payload = storySceneAudioGenerationPayload(defs, story, scene, script);
          try {
            const { data } = await api.post(`/stories/${storyId}/scenes/${sourceId}/generate-audio`, payload);
            if (!data?.job_id) throw new Error(`Could not queue ${clip.name || sourceId}`);
            await waitForRegenerationJob(data.job_id);
            await loadStoryScene(sourceId);
            successCount += 1;
          } catch (sceneErr) {
            const msg = sceneErr.response?.data?.detail || sceneErr.message || 'Audio generation failed';
            sceneErrors.push(`${clip.name || `Clip ${index + 1}`}: ${msg}`);
          }
        }
      }
      const label = allScenes ? 'scene' : 'audio';
      if (successCount > 0) {
        toast.success(`Generated ${successCount} ${label}${successCount === 1 ? '' : 's'} and placed on the timeline.`);
      }
      if (sceneErrors.length > 0) {
        toast.error(`${sceneErrors.length} scene${sceneErrors.length === 1 ? '' : 's'} could not generate audio: ${sceneErrors[0]}${sceneErrors.length > 1 ? ` (+${sceneErrors.length - 1} more)` : ''}`);
      }
      if (successCount === 0 && sceneErrors.length === 0) {
        toast.info('No audio was generated.');
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || err.message || 'Could not generate audios');
    } finally {
      setGeneratingPendingAudios(false);
      setPendingAudioProgress({ current: 0, total: 0, name: '' });
    }
  };

  const generatePendingAudios = async () => {
    await runBulkAudioGeneration(pendingAudioClipEntries(), { allScenes: false });
  };

  const generateAllAudios = async () => {
    await runBulkAudioGeneration(allAudioClipEntries(), { allScenes: true });
  };

  const handleBulkAudioChoice = async (mode) => {
    setBulkAudioDialogOpen(false);
    if (mode === 'all') {
      await generateAllAudios();
    } else {
      await generatePendingAudios();
    }
  };

  const applyTimelineClipSpeed = useCallback((kind, clipId, speedVal) => {
    const s = clampPlaybackSpeed(speedVal);
    if (kind === 'audio') {
      setAudioTracks((prev) =>
        prev.map((tr) => tr.map((c) => (c.id === clipId ? { ...c, audioPlaybackSpeed: s } : c))));
      return;
    }
    setVideoTracks((prev) =>
      prev.map((tr) =>
        tr.map((c) => {
          if (c.id !== clipId && c.sourceClipId !== clipId) return c;
          return { ...c, videoPlaybackSpeed: s };
        }),
      ));
  }, []);

  const applyTimelineClipVolume = useCallback((lane, clipId, rawVol) => {
    const vol = clampTimelineClipVolume(rawVol);
    const patchVol = (c) => {
      if (c.id !== clipId) return c;
      const next = { ...c, volume: vol };
      if (vol <= 0.001) return { ...next, muted: true };
      if (c.muted && vol > 0) return { ...next, muted: false };
      return next;
    };
    if (lane === 'video') {
      setVideoTracks((prev) => prev.map((tr) => tr.map(patchVol)));
      return;
    }
    if (lane === 'audio') {
      setAudioTracks((prev) => prev.map((tr) => tr.map(patchVol)));
      return;
    }
    if (lane === 'music') {
      setMusicTracks((prev) => prev.map((tr) => tr.map(patchVol)));
    }
  }, []);

  const applyRegeneratedNarrationSegment = (kind, segment) => {
    if (!segment?.id) return;
    markMediaReplaceInProgress();
    const sourceId = segment.id;

    const narrationProjectStub = {
      id: projectId,
      title: story?.title,
      input: story?.input || {
        topic: story?.user_topic,
        language: story?.language,
        visual_style: story?.visual_style,
        image_provider: story?.image_provider,
      },
    };
    const sceneDoc = narrationSegmentToScene(segment, narrationProjectStub);
    const nextScenes = scenesSnapshotRef.current.map((scene) => (
      scene.id === sourceId ? sceneDoc : scene
    ));
    scenesSnapshotRef.current = nextScenes;
    setScenesSnapshot(nextScenes);
    syncTimelineFromSceneDocument(sourceId, sceneDoc, {
      seekPreview: false,
      forceMediaReload: kind === 'video' || kind === 'image' || kind === 'audio',
    });
    queuePersistEditorAfterMediaChange();
    if (kind === 'audio' && isNarrationEditor && projectId) {
      finalizeNarrationSegmentAudioRegeneration(projectId, segment, {
        silent: true,
      }).catch(() => {});
    }

    if (
      replacementDialog?.sourceId === sourceId
      && (replacementDialog?.kind === kind || replacementPanelTab === kind)
    ) {
      refreshReplacementOptions(kind, sourceId).catch(() => {});
    }
  };

  const updateReplacementOverride = (key, value) => {
    setReplacementOverrides(prev => ({ ...prev, [key]: value }));
  };

  const bulkAudioSettings = useMemo(
    () => narrationAudioSettingsFromInput({
      tts_provider: replacementOverrides.tts_provider,
      voice_style: replacementOverrides.voice_style,
      language: replacementOverrides.language,
      narrator_elevenlabs_voice_id: replacementOverrides.narrator_elevenlabs_voice_id,
      narrator_murf_voice_id: replacementOverrides.narrator_murf_voice_id,
      narrator_murf_locale: replacementOverrides.narrator_murf_locale,
      narrator_minimax_voice_id: replacementOverrides.narrator_minimax_voice_id,
    }),
    [replacementOverrides],
  );

  const replacementAudioSettings = bulkAudioSettings;

  const onBulkAudioSettingsChange = useCallback((next) => {
    const s = narrationAudioSettingsFromInput(next);
    setReplacementOverrides((prev) => ({
      ...prev,
      tts_provider: s.tts_provider,
      voice_style: s.voice_style,
      language: s.language,
      narrator_elevenlabs_voice_id: s.narrator_elevenlabs_voice_id,
      narrator_murf_voice_id: s.narrator_murf_voice_id,
      narrator_murf_locale: s.narrator_murf_locale,
      narrator_minimax_voice_id: s.narrator_minimax_voice_id,
    }));
  }, []);

  const selectBulkAudioVoice = useCallback((voice) => {
    const prov = replacementOverrides.tts_provider;
    setReplacementOverrides((prev) => {
      const next = { ...prev };
      if (prov === 'elevenlabs') next.narrator_elevenlabs_voice_id = voice.voice_id;
      if (prov === 'murf') {
        next.narrator_murf_voice_id = voice.voice_id;
        next.narrator_murf_locale = voice.locale || prev.narrator_murf_locale;
      }
      if (prov === 'minimax') next.narrator_minimax_voice_id = voice.voice_id;
      return next;
    });
    setBulkAudioVoicePickerOpen(false);
    toast.success('Voice selected for audio generation');
  }, [replacementOverrides.tts_provider]);

  const onReplacementAudioSettingsChange = onBulkAudioSettingsChange;

  const selectReplacementAudioVoice = useCallback((voice) => {
    const prov = replacementOverrides.tts_provider;
    setReplacementOverrides((prev) => {
      const next = { ...prev };
      if (prov === 'elevenlabs') next.narrator_elevenlabs_voice_id = voice.voice_id;
      if (prov === 'murf') {
        next.narrator_murf_voice_id = voice.voice_id;
        next.narrator_murf_locale = voice.locale || prev.narrator_murf_locale;
      }
      if (prov === 'minimax') next.narrator_minimax_voice_id = voice.voice_id;
      return next;
    });
    setReplacementAudioVoicePickerOpen(false);
    toast.success('Voice selected for audio generation');
  }, [replacementOverrides.tts_provider]);

  const resolveReplacementSourceImageUrl = useCallback((sourceId) => {
    if (!sourceId) return '';
    const fromScene = (scenesSnapshot || []).find((s) => String(s.id) === String(sourceId));
    if (fromScene?.image_url) return fromScene.image_url;
    const clip = replacementDialog?.clip;
    if (clip && sourceIdForClip(clip) === String(sourceId) && clip.imageUrl) {
      return clip.imageUrl;
    }
    return replacementDialog?.uploadedImageUrl || '';
  }, [scenesSnapshot, replacementDialog]);

  const narrationRegenerationPayload = (kind, prompt) => {
    const payload = { prompt };
    if (kind === 'image') {
      payload.image_provider = replacementOverrides.image_provider;
      // Always async — Celery owns concurrency; avoids proxy timeouts on 15–30 s Gemini calls
      if (isNarrationEditor) {
        payload.async_job = true;
      }
    } else if (kind === 'video') {
      payload.video_provider = replacementOverrides.video_provider;
      payload.camera_motion = replacementOverrides.camera_motion;
      const sourceImage = resolveReplacementSourceImageUrl(replacementDialog?.sourceId);
      if (sourceImage) payload.source_image_url = sourceImage;
      if (isNarrationEditor) {
        payload.async_job = true;
      }
    } else {
      Object.assign(payload, buildNarrationAudioApiPayload(replacementOverrides, { prompt }));
    }
    return payload;
  };

  const storyRegenerationPayload = (kind, prompt, scene = null) => {
    if (kind === 'image') {
      return {
        prompt,
        provider: replacementOverrides.image_provider,
        aspect_ratio: replacementOverrides.image_aspect_ratio,
      };
    }
    if (kind === 'video') {
      return {
        prompt,
        provider: replacementOverrides.video_provider,
        camera_motion: replacementOverrides.camera_motion,
        extend_duration_to_match_audio: Boolean(replacementOverrides.extend_scene_video_to_audio_duration),
        lip_sync_with_audio: Boolean(replacementOverrides.lip_sync_scene_video_with_audio),
      };
    }
    return storySceneAudioGenerationPayload(replacementOverrides, story, scene, prompt);
  };

  const regenerateReplacementSnippet = async (targetKind = replacementDialog?.kind) => {
    if (!replacementDialog) return;
    const { sourceId } = replacementDialog;
    const kind = targetKind;
    const prompt = (replacementDrafts[kind] || '').trim();
    if (!prompt && kind !== 'video') {
      toast.error(kind === 'audio' ? 'Add an audio script first' : 'Add a prompt first');
      return;
    }
    if (kind === 'video') {
      setRegeneratingReplacement(kind);
      try {
        const { clip } = replacementDialog;
        const prov = replacementOverrides.video_provider || batchVideoProvider;
        const motion = replacementOverrides.camera_motion || batchCameraMotion;
        const resolvedPrompt = await resolveVideoPromptForTarget(
          { clip, sourceId },
          { videoProvider: prov, cameraMotion: motion, fallbackPrompt: prompt },
        );
        await queueOneVideoGeneration(
          { clip, sourceId },
          resolvedPrompt,
          {
            videoProvider: prov,
            cameraMotion: motion,
            waitForCompletion: false,
            extendToAudio: !isNarrationEditor && Boolean(replacementOverrides.extend_scene_video_to_audio_duration),
            lipSyncWithAudio: !isNarrationEditor && Boolean(replacementOverrides.lip_sync_scene_video_with_audio),
          },
        );
        toast.success('Video generation queued. Check Tasks for progress, then pick the new version under the Video tab.');
      } catch (err) {
        if (handleInsufficientCreditsError(err, openInsufficientCredits)) {
          /* pricing dialog shown */
        } else {
          toast.error(formatApiError(err, 'Could not queue video generation'));
        }
      } finally {
        setRegeneratingReplacement(null);
      }
      return;
    }
    setRegeneratingReplacement(kind);
    try {
      if (isNarrationEditor) {
        const { clip } = replacementDialog;
        const { segmentId, segmentNumber } = narrationSegmentApiTarget(
          sourceId,
          scenesSnapshotRef.current,
          clip,
        );
        const action = kind === 'video' ? 'generate-video' : `regenerate-${kind}`;
        const { data: updatedSegment } = await api.post(
          `/narration/projects/${projectId}/segments/${segmentId}/${action}`,
          {
            ...narrationRegenerationPayload(kind, prompt),
            ...(segmentNumber ? { segment_number: segmentNumber } : {}),
          },
        );
        if (updatedSegment?.job_id) {
          setReplacementDialog(prev => (prev ? { ...prev, kind } : prev));
          const kindLabel = kind === 'image' ? 'Image' : kind === 'video' ? 'Video' : 'Audio';
          toast.info(`${kindLabel} regeneration queued — waiting for it to finish…`);
          await waitForRegenerationJob(updatedSegment.job_id);
          const regeneratedSegment = await loadNarrationSegment(segmentId, {
            skipVideoTrackSync: true,
            skipScenesSnapshotReplace: true,
          });
          applyRegeneratedNarrationSegment(kind, regeneratedSegment);
          if (kind === 'video') setReplacementPanelTab('video');
          if (kind === 'image') setReplacementPanelTab('image');
          await refreshReplacementOptions(kind, segmentId);
          toast.success(`Regenerated ${kindLabel.toLowerCase()} is ready and updated on the timeline.`);
          return;
        }
        setReplacementDialog(prev => (prev ? { ...prev, kind } : prev));
        let segmentDoc = updatedSegment;
        if (kind === 'audio' || kind === 'video' || kind === 'image') {
          const reloaded = await loadNarrationSegment(segmentId, {
            skipVideoTrackSync: true,
            skipScenesSnapshotReplace: true,
          });
          segmentDoc = reloaded || segmentDoc;
          setReplacementPanelTab(kind);
        }
        applyRegeneratedNarrationSegment(kind, segmentDoc);
        await refreshReplacementOptions(kind, segmentId);
        toast.success('Regenerated snippet is ready and updated on the timeline.');
      } else {
        let response;
        const sceneForAudio = (scenesSnapshotRef.current || []).find(
          (s) => String(s.id) === String(sourceId),
        ) || null;
        const payload = storyRegenerationPayload(kind, prompt, sceneForAudio);
        if (kind === 'image') {
          response = await api.post(`/stories/${storyId}/scenes/${sourceId}/regenerate-image`, payload);
        } else         if (kind === 'video') {
          response = await api.post(`/scenes/${sourceId}/generate-video`, payload);
        } else {
          response = await api.post(`/stories/${storyId}/scenes/${sourceId}/generate-audio`, payload);
        }
        const jobId = response?.data?.job_id;
        if (jobId && kind === 'audio') {
          toast.info('Generating audio…');
          await waitForRegenerationJob(jobId);
          const scene = await loadStoryScene(sourceId);
          syncTimelineFromSceneDocument(sourceId, scene, { seekPreview: false });
          await refreshReplacementOptions('audio', sourceId);
          setReplacementPanelTab('audio');
          toast.success('Regenerated audio is ready and updated on the timeline.');
        } else if (jobId) {
          toast.success('Regeneration queued. You will see it in Tasks when complete, then double-click again to select it.');
        } else {
          toast.success('Regeneration started. Double-click again after it completes to select it.');
        }
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || err.message || 'Could not regenerate snippet');
    } finally {
      setRegeneratingReplacement(null);
    }
  };

  const addVideoTrack = () => {
    recordEditorHistory();
    setVideoTracks(prev => [...prev, []]);
  };
  const addAudioTrack = () => {
    setAudioTracks((prev) => [...prev, []]);
    setSubtitleTracks((prev) => [...prev, []]);
    setAudioTrackMeta((prev) => [
      ...prev,
      { languageCode: null, label: `Audio ${prev.length + 1}`, isTranslation: false },
    ]);
  };

  const toggleAudioLaneActive = useCallback((trackIdx) => {
    setTimelineActive((prev) => {
      const n = audioTracksRef.current.length;
      const { lanes } = normalizeAudioLaneActiveFlags(prev, n);
      const nextLanes = [...lanes];
      while (nextLanes.length < n) nextLanes.push(true);
      nextLanes[trackIdx] = !nextLanes[trackIdx];
      return { ...prev, audio: nextLanes.some(Boolean), audioLanes: nextLanes };
    });
  }, []);

  const removeAudioTrackAt = useCallback((trackIdx) => {
    if (trackIdx <= 0) return;
    recordEditorHistoryRef.current();
    setAudioTracks((prev) => prev.filter((_, i) => i !== trackIdx));
    setAudioTrackMeta((prev) => prev.filter((_, i) => i !== trackIdx));
    setSubtitleTracks((prev) => prev.filter((_, i) => i !== trackIdx));
    setTimelineActive((prev) => {
      const { lanes } = normalizeAudioLaneActiveFlags(prev, audioTracksRef.current.length);
      const nextLanes = lanes.filter((_, i) => i !== trackIdx);
      const subLanes = (Array.isArray(prev.subtitleLanes) ? prev.subtitleLanes : []).filter((_, i) => i !== trackIdx);
      return {
        ...prev,
        audioLanes: nextLanes,
        audio: nextLanes.some(Boolean),
        subtitleLanes: subLanes,
        subtitle: subLanes.some(Boolean),
      };
    });
  }, []);

  const toggleSubtitleLaneActive = useCallback((trackIdx) => {
    setTimelineActive((prev) => {
      const n = audioTracksRef.current.length;
      const { lanes } = normalizeSubtitleLaneActiveFlags(prev, n);
      const nextLanes = [...lanes];
      while (nextLanes.length < n) nextLanes.push(true);
      nextLanes[trackIdx] = !nextLanes[trackIdx];
      return { ...prev, subtitle: nextLanes.some(Boolean), subtitleLanes: nextLanes };
    });
  }, []);

  const mediaTrackRefKey = (kind, trackIdx) => `${kind}:${trackIdx}`;

  const setTrackRef = (kind, trackIdx, node) => {
    const key = mediaTrackRefKey(kind, trackIdx);
    if (node) trackRefs.current.set(key, node);
    else trackRefs.current.delete(key);
  };

  const setAudioTrackRef = (trackIdx, node) => {
    if (node) audioTrackRefs.current.set(trackIdx, node);
    else audioTrackRefs.current.delete(trackIdx);
  };

  const setMusicTrackRef = (trackIdx, node) => {
    if (node) musicTrackRefs.current.set(trackIdx, node);
    else musicTrackRefs.current.delete(trackIdx);
  };

  const setEffectTrackRef = (trackIdx, node) => {
    if (node) effectTrackRefs.current.set(trackIdx, node);
    else effectTrackRefs.current.delete(trackIdx);
  };

  const setOverlayTrackRef = (trackIdx, node) => {
    if (node) overlayTrackRefs.current.set(trackIdx, node);
    else overlayTrackRefs.current.delete(trackIdx);
  };

  const flatEffectClips = useMemo(() => (effectTracks || []).flat(), [effectTracks]);

  const selectedEffectClip = useMemo(() => {
    if (selectedTimelineClip?.type !== 'effect') return null;
    return flatEffectClips.find((c) => c.id === selectedTimelineClip.id) || null;
  }, [flatEffectClips, selectedTimelineClip]);

  const loadDemoVfxTimeline = useCallback(() => {
    setEffectTracks(deserializeEffectTracks(demoVfxTimeline.effects || []));
    toast.success('Loaded core VFX demo (5 effects)');
  }, []);

  const loadEnvironmentalVfxDemo = useCallback(() => {
    setEffectTracks(deserializeEffectTracks(environmentalDemoTimeline.effects || []));
    toast.success('Loaded environmental VFX demo (15 effects)');
  }, []);

  const handleAddVfxEffect = useCallback((type) => {
    recordEditorHistoryRef.current();
    setEffectTracks((prev) => normalizeEffectTracks(addEffectToTrack(prev, type, playback.currentTime, 5)));
    queuePersistEditorAfterMediaChange();
    toast.success(`Added ${type} effect at playhead`);
  }, [playback.currentTime, queuePersistEditorAfterMediaChange]);

  const handleRemoveVfxClip = useCallback((clipId) => {
    recordEditorHistoryRef.current();
    setEffectTracks((prev) => removeEffectClip(prev, clipId));
    setSelectedTimelineClip((prev) => (
      prev?.type === 'effect' && String(prev.id) === String(clipId) ? null : prev
    ));
    setDraggingClipId((prev) => (prev === clipId ? null : prev));
    queuePersistEditorAfterMediaChange();
  }, [queuePersistEditorAfterMediaChange]);

  const handleClearAllVfx = useCallback(() => {
    recordEditorHistoryRef.current();
    setEffectTracks(clearAllEffectClips());
    setSelectedTimelineClip((prev) => (prev?.type === 'effect' ? null : prev));
    setDraggingClipId(null);
    queuePersistEditorAfterMediaChange();
    toast.success('Cleared all VFX from timeline');
  }, [queuePersistEditorAfterMediaChange]);

  const handleUpdateEffectClip = useCallback((clipId, patch) => {
    if (!clipId) return;
    setEffectTracks((prev) => patchEffectClip(prev, clipId, patch));
    queuePersistEditorAfterMediaChange();
  }, [queuePersistEditorAfterMediaChange]);

  const selectedHostOverlayClip = useMemo(() => {
    if (selectedTimelineClip?.type !== 'hostOverlay') return null;
    return (overlayTracks || []).flat().find((c) => c.id === selectedTimelineClip.id) || null;
  }, [overlayTracks, selectedTimelineClip]);

  const activeHostOverlayClips = useMemo(() => {
    if (!timelineActive.hostOverlay) return [];
    return hostOverlaysForPreview(overlayTracks, visualCurrentTime, {
      isPlaying: playback.isPlaying,
      selectedClipId: selectedHostOverlayClip?.id || null,
      trackActive: timelineActive.hostOverlay,
    });
  }, [
    overlayTracks,
    timelineActive.hostOverlay,
    visualCurrentTime,
    playback.isPlaying,
    selectedHostOverlayClip?.id,
  ]);

  const hostOverlayPreviewClips = activeHostOverlayClips;
  const backgroundOverlayPreviewClips = useMemo(
    () => hostOverlayPreviewClips.filter(isBackgroundOverlayClip),
    [hostOverlayPreviewClips],
  );
  const foregroundHostOverlayPreviewClips = useMemo(
    () => hostOverlayPreviewClips.filter((clip) => (
      !isBackgroundOverlayClip(clip) && !isSlidingCardCarouselClip(clip)
    )),
    [hostOverlayPreviewClips],
  );
  const slidingCardCarouselClip = useMemo(
    () => hostOverlayPreviewClips.find((clip) => isSlidingCardCarouselClip(clip)) || null,
    [hostOverlayPreviewClips],
  );
  const activeLayoutBrollClip = useMemo(
    () => (isReelsShortsEditor
      ? hostOverlayPreviewClips.find((clip) => isLayoutBrollClip(clip))
      : null),
    [isReelsShortsEditor, hostOverlayPreviewClips],
  );
  const talkHeadPane = useMemo(
    () => talkingHeadPaneFromLayoutClip(activeLayoutBrollClip),
    [activeLayoutBrollClip],
  );
  const talkHeadScreen = useMemo(
    () => (talkHeadPane ? talkPaneScreenRect(previewContentRect, talkHeadPane) : null),
    [talkHeadPane, previewContentRect],
  );
  const talkHeadVideoStyle = useMemo(() => {
    if (!talkHeadScreen || !talkFocusCrop?.crop) {
      return talkHeadScreen
        ? { width: '100%', height: '100%', objectFit: 'cover', objectPosition: '50% 38%' }
        : undefined;
    }
    return videoSourceCropStyle(
      talkFocusCrop.srcW,
      talkFocusCrop.srcH,
      talkFocusCrop.crop,
      talkHeadScreen.width,
      talkHeadScreen.height,
    ) || { width: '100%', height: '100%', objectFit: 'cover', objectPosition: '50% 38%' };
  }, [talkHeadScreen, talkFocusCrop]);

  useEffect(() => {
    if (!isReelsShortsEditor || !talkHeadPane || !currentClip?.videoUrl) {
      setTalkFocusCrop(null);
      return undefined;
    }
    const url = currentClip.videoUrl;
    const destH = Math.max(2, Math.round(1080 * (talkHeadPane.h / Math.max(talkHeadPane.w, 0.05))));
    const paneKey = `${talkHeadPane.x}:${talkHeadPane.y}:${talkHeadPane.w}:${talkHeadPane.h}`;
    const cacheKey = `${url}:1080x${destH}:${paneKey}`;
    const cached = talkFocusCacheRef.current.get(cacheKey);
    setTalkFocusCrop(cached || null);
    if (cached) {
      return undefined;
    }

    let cancelled = false;
    const apply = (next) => {
      if (cancelled || !next?.crop) return;
      talkFocusCacheRef.current.set(cacheKey, next);
      setTalkFocusCrop(next);
    };

    const run = async () => {
      const grabbed = grabFrameFromVideoElement(playback.videoRef?.current)
        || { dataUrl: await grabVideoFrameDataUrl(url, 0.7), srcW: 0, srcH: 0, canvas: null };
      if (cancelled || !grabbed?.dataUrl) return;
      if (grabbed.canvas) {
        apply(localFillCropFromCanvas(grabbed.canvas, 1080, destH));
      }
      try {
        const { data } = await api.post('/reels-shorts/talk-focus', {
          image_data_url: grabbed.dataUrl,
          dest_width: 1080,
          dest_height: destH,
        });
        if (cancelled || !data?.crop) return;
        apply({
          srcW: data.src_w || grabbed.srcW,
          srcH: data.src_h || grabbed.srcH,
          crop: data.crop,
          method: data.method,
        });
      } catch {
        /* local subject crop already applied when canvas was available */
      }
    };
    const timer = window.setTimeout(run, 160);
    const videoEl = playback.videoRef?.current;
    const onReady = () => { run(); };
    videoEl?.addEventListener('loadeddata', onReady);
    return () => {
      cancelled = true;
      window.clearTimeout(timer);
      videoEl?.removeEventListener('loadeddata', onReady);
    };
  }, [
    isReelsShortsEditor,
    currentClip?.videoUrl,
    currentClip?.id,
    talkHeadPane?.x,
    talkHeadPane?.y,
    talkHeadPane?.w,
    talkHeadPane?.h,
    playback.videoRef,
  ]);

  const handleAddHostOverlayClip = useCallback((imageUrl, name = 'Host photo') => {
    recordEditorHistoryRef.current();
    const clip = resolveHostOverlayClipForPlayback(
      createHostOverlayClip({
        playhead: playback.currentTime,
        duration: 8,
        aspectRatio: exportAspectRatio,
        imageUrl,
        name,
      }),
      exportAspectRatio,
    );
    setOverlayTracks((prev) => prev.map((lane, idx) => (idx === 0 ? [...lane, clip] : lane)));
    setSelectedTimelineClip({ type: 'hostOverlay', id: clip.id });
    setTimelineActive((prev) => ({ ...prev, hostOverlay: true }));
    queuePersistEditorAfterMediaChange();
  }, [playback.currentTime, exportAspectRatio, queuePersistEditorAfterMediaChange]);

  const handleAddBackgroundTemplate = useCallback((templateId) => {
    recordEditorHistoryRef.current();
    const clip = resolveHostOverlayClipForPlayback(
      createBackgroundTemplateClip({
        templateId,
        playhead: playback.currentTime,
        duration: Math.max(0.5, totalDuration || DEFAULT_HOST_OVERLAY_DURATION),
        aspectRatio: exportAspectRatio,
      }),
      exportAspectRatio,
    );
    setOverlayTracks((prev) => prev.map((lane, idx) => {
      if (idx !== 0) return lane;
      const withoutBg = lane.filter((c) => !isBackgroundOverlayClip(c));
      return [...withoutBg, clip];
    }));
    setSelectedTimelineClip({ type: 'hostOverlay', id: clip.id });
    setTimelineActive((prev) => ({ ...prev, hostOverlay: true }));
    queuePersistEditorAfterMediaChange();
  }, [playback.currentTime, totalDuration, exportAspectRatio, queuePersistEditorAfterMediaChange]);

  const handleAddCardCarousel = useCallback((promptText = '') => {
    recordEditorHistoryRef.current();
    const parsed = parseCarouselCreatePrompt(promptText || '4. Sunscreens\n5. Lipstick\n6. Nail Polish');
    const clips = buildCarouselClips({
      items: parsed.items.map((title, i) => ({ number: String(i + 1), title })),
      playhead: playback.currentTime,
      aspectRatio: exportAspectRatio,
      params: parsed.params,
      holdDuration: Math.max(2.2, Math.min(5, (totalDuration || 8) * 0.4)),
    }).map((clip) => resolveHostOverlayClipForPlayback(clip, exportAspectRatio));
    if (!clips.length) return;
    const laneIdx = isReelsShortsEditor ? Math.min(1, Math.max(0, (overlayTracks || []).length - 1)) : 0;
    setOverlayTracks((prev) => {
      const lanes = Array.isArray(prev) && prev.length ? prev.map((lane) => [...lane]) : [[]];
      while (lanes.length <= laneIdx) lanes.push([]);
      lanes[laneIdx] = [...lanes[laneIdx], ...clips];
      return lanes;
    });
    setSelectedTimelineClip({ type: 'hostOverlay', id: clips[0].id });
    setTimelineActive((prev) => ({ ...prev, hostOverlay: true }));
    queuePersistEditorAfterMediaChange();
  }, [playback.currentTime, exportAspectRatio, totalDuration, isReelsShortsEditor, overlayTracks, queuePersistEditorAfterMediaChange]);

  const handleSelectHostOverlayClip = useCallback((clipId) => {
    if (!clipId) return;
    setSelectedTimelineClip({ type: 'hostOverlay', id: clipId });
  }, []);

  const handleHostOverlayLayerChange = useCallback((clipId, layer) => {
    setOverlayTracks((prev) => patchHostOverlayClip(prev, clipId, { layer }));
    queuePersistEditorAfterMediaChange();
  }, [queuePersistEditorAfterMediaChange]);

  /** Every lane key the timeline currently renders, in top-to-bottom order. */
  const allTimelineLaneKeys = useMemo(() => {
    const keys = [];
    const push = (kind, count) => {
      for (let i = 0; i < count; i += 1) keys.push(timelineLaneKey(kind, i));
    };
    const imageAndVideoLanes = (videoTracks || []).length;
    push('image', imageAndVideoLanes);
    keys.push(timelineLaneKey('animation', 0));
    push('video', imageAndVideoLanes);
    const audioLanes = (audioTracks || []).length;
    push('audio', audioLanes);
    push('subtitle', audioLanes);
    push('vfx', (effectTracks || []).length);
    push('overlay', (isReelsShortsEditor
      ? padReelsOverlayTracks(overlayTracks)
      : (overlayTracks || [[]])).length);
    push('music', (musicTracks || []).length);
    return keys;
  }, [videoTracks, audioTracks, effectTracks, overlayTracks, musicTracks, isReelsShortsEditor]);

  const allTimelineLanesCollapsed = areAllTimelineLanesCollapsed(
    collapsedTimelineLanes,
    allTimelineLaneKeys,
  );

  const toggleAllTimelineLanesCollapsed = useCallback(() => {
    setCollapsedTimelineLanes((prev) => toggleAllTimelineLanes(prev, allTimelineLaneKeys));
  }, [allTimelineLaneKeys]);

  /**
   * Lanes open minimized so the whole stack fits on screen. Tracked per key so lanes that
   * appear later (a new audio lane, the reels overlay lanes) also start minimized without
   * re-collapsing anything the user has since opened.
   */
  const autoMinimizedLaneKeysRef = useRef(new Set());
  useEffect(() => {
    autoMinimizedLaneKeysRef.current = new Set();
  }, [editorId]);
  useEffect(() => {
    if (loading) return;
    const fresh = allTimelineLaneKeys.filter((key) => !autoMinimizedLaneKeysRef.current.has(key));
    if (!fresh.length) return;
    fresh.forEach((key) => autoMinimizedLaneKeysRef.current.add(key));
    setCollapsedTimelineLanes((prev) => collapseTimelineLanes(prev, fresh));
  }, [loading, allTimelineLaneKeys]);

  const flashOverlayClipInTimeline = useCallback((clipId) => {
    setHighlightedOverlayClipId(clipId || null);
    if (highlightOverlayTimerRef.current) clearTimeout(highlightOverlayTimerRef.current);
    highlightOverlayTimerRef.current = setTimeout(() => setHighlightedOverlayClipId(null), 1800);
  }, []);

  useEffect(() => () => {
    if (highlightOverlayTimerRef.current) clearTimeout(highlightOverlayTimerRef.current);
  }, []);

  /** Preview → timeline: select the clip and scroll its lane placement into view. */
  const revealHostOverlayClipInTimeline = useCallback((clip) => {
    if (!clip?.id) return;
    handleSelectHostOverlayClip(clip.id);
    const laneIdx = laneIndexOfClip(overlayTracks, clip.id);
    if (laneIdx >= 0) {
      const laneKey = timelineLaneKey('overlay', laneIdx);
      setCollapsedTimelineLanes((prev) => (
        isTimelineLaneCollapsed(prev, laneKey) ? toggleTimelineLaneCollapsed(prev, laneKey) : prev
      ));
      overlayTrackRefs.current.get(laneIdx)?.scrollIntoView?.({ block: 'nearest', inline: 'nearest' });
    }
    const scroller = timelineRef.current;
    if (scroller) {
      const left = timelineRevealScrollLeft({
        startTime: clip.startTime,
        duration: clip.duration,
        pxPerSec,
        viewportWidth: scroller.clientWidth,
        labelWidth: TIMELINE_LABEL_WIDTH_PX,
        scrollLeft: scroller.scrollLeft,
      });
      if (Math.abs(left - scroller.scrollLeft) > 1) {
        if (scroller.scrollTo) scroller.scrollTo({ left, behavior: 'smooth' });
        else scroller.scrollLeft = left;
      }
    }
    flashOverlayClipInTimeline(clip.id);
  }, [overlayTracks, pxPerSec, handleSelectHostOverlayClip, flashOverlayClipInTimeline]);

  /** Timeline → preview: park the playhead inside the clip so its placement is on screen. */
  const revealHostOverlayClipInPreview = useCallback((clip) => {
    if (!clip?.id) return;
    handleSelectHostOverlayClip(clip.id);
    if (!timelineActive.hostOverlay) {
      setTimelineActive((prev) => ({ ...prev, hostOverlay: true }));
    }
    playback.seek(clipRevealSeekTime(clip));
  }, [handleSelectHostOverlayClip, playback.seek, timelineActive.hostOverlay]);

  const hostOverlayAutoSelectDoneRef = useRef(false);
  useEffect(() => {
    hostOverlayAutoSelectDoneRef.current = false;
  }, [editorId]);

  useEffect(() => {
    if (loading) return;
    const flat = (overlayTracks || []).flat();
    if (!flat.length) {
      hostOverlayAutoSelectDoneRef.current = false;
      return;
    }
    if (selectedTimelineClip?.type === 'hostOverlay') return;
    if (hostOverlayAutoSelectDoneRef.current) return;
    hostOverlayAutoSelectDoneRef.current = true;
    const nearest = hostOverlaysForPreview(overlayTracks, visualCurrentTime, {
      isPlaying: false,
      trackActive: timelineActive.hostOverlay,
    })[0];
    if (nearest?.id) {
      setSelectedTimelineClip({ type: 'hostOverlay', id: nearest.id });
    }
  }, [
    loading,
    editorId,
    overlayTracks,
    selectedTimelineClip?.type,
    visualCurrentTime,
    timelineActive.hostOverlay,
  ]);

  useEffect(() => {
    if (loading) return;
    setOverlayTracks((prev) => {
      const flat = (prev || []).flat();
      if (!flat.length) return prev;
      const next = loadHostOverlayTracksFromEditor({ overlayTracks: prev }, exportAspectRatio);
      const nextFlat = (next || []).flat();
      if (flat.length !== nextFlat.length) return next;
      const changed = nextFlat.some((clip, idx) => {
        const prevClip = flat[idx];
        return !prevClip
          || clip.id !== prevClip.id
          || clip.imageUrl !== prevClip.imageUrl
          || clip.image_s3_url !== prevClip.image_s3_url;
      });
      return changed ? next : prev;
    });
  }, [loading, exportAspectRatio]);

  const installWindowDragHandlers = (handleMove, handleUp) => {
    document.body.style.userSelect = 'none';
    document.body.style.cursor = 'grabbing';
    window.addEventListener('mousemove', handleMove);
    window.addEventListener('mouseup', handleUp);
  };

  const clearWindowDragHandlers = (handleMove, handleUp) => {
    document.body.style.userSelect = '';
    document.body.style.cursor = '';
    window.removeEventListener('mousemove', handleMove);
    window.removeEventListener('mouseup', handleUp);
  };

  const beginClipDrag = (event, clip, trackIdx, kind = 'video') => {
    if (event.button !== 0) return;
    const sourceTrackNode = trackRefs.current.get(mediaTrackRefKey(kind, trackIdx));
    if (!sourceTrackNode) return;

    event.preventDefault();
    recordEditorHistory();
    setSelectedTimelineClip({ type: kind, id: clip.id });

    const sourceRect = sourceTrackNode.getBoundingClientRect();
    const clipLeftPx = clip.startTime * pxPerSec;
    const dragOffsetPx = Math.max(0, event.clientX - (sourceRect.left + clipLeftPx));

    dragStateRef.current = {
      clipId: clip.sourceClipId || clip.id,
      sourceTrackIdx: trackIdx,
      kind,
      dragOffsetPx,
      sourceTrackNode,
    };
    setDraggingClipId(clip.sourceClipId || clip.id);
    let rafId = null;
    let latestEvent = null;

    const applyMove = () => {
      const moveEvent = latestEvent;
      rafId = null;
      if (!moveEvent) return;
      const dragState = dragStateRef.current;
      if (!dragState) return;

      let targetTrackIdx = dragState.sourceTrackIdx;
      trackRefs.current.forEach((node, key) => {
        if (!key.startsWith(`${dragState.kind}:`)) return;
        const idx = Number(key.split(':')[1]);
        const rect = node.getBoundingClientRect();
        if (moveEvent.clientY >= rect.top && moveEvent.clientY <= rect.bottom) {
          targetTrackIdx = idx;
        }
      });

      const targetTrackNode = trackRefs.current.get(mediaTrackRefKey(dragState.kind, targetTrackIdx)) || dragState.sourceTrackNode;
      const targetRect = targetTrackNode.getBoundingClientRect();
      const newStart = Math.max(0, (moveEvent.clientX - targetRect.left - dragState.dragOffsetPx) / pxPerSec);

      setVideoTracks(prev => {
        const next = prev.map(track => [...track]);
        let draggedClip = null;

        for (let i = 0; i < next.length; i += 1) {
          const existing = next[i].find(c => c.id === dragState.clipId);
          if (existing) {
            draggedClip = existing;
            next[i] = next[i].filter(c => c.id !== dragState.clipId);
            break;
          }
        }

        if (!draggedClip) return prev;
        next[targetTrackIdx] = normalizeTrackNoOverlap([
          ...next[targetTrackIdx],
          { ...draggedClip, startTime: newStart },
        ]);
        return next;
      });
    };

    const handleMove = (moveEvent) => {
      latestEvent = moveEvent;
      if (rafId == null) rafId = requestAnimationFrame(applyMove);
    };

    const handleUp = () => {
      dragStateRef.current = null;
      setDraggingClipId(null);
      if (rafId != null) cancelAnimationFrame(rafId);
      clearWindowDragHandlers(handleMove, handleUp);
    };

    installWindowDragHandlers(handleMove, handleUp);
  };

  const beginClipResize = (event, clip, trackIdx, edge) => {
    if (event.button !== 0) return;
    event.stopPropagation();
    event.preventDefault();
    recordEditorHistory();
    setSelectedTimelineClip({ type: 'video', id: clip.id });

    const clipId = clip.sourceClipId || clip.id;
    const maxSourceSec = clip.videoUrl ? clipVideoMediaCapSeconds(clip) : (clip.duration ?? 5);
    const o = {
      startTime: clip.startTime,
      trimStart: clip.trimStart ?? 0,
      trimEnd: clip.trimEnd ?? clip.duration ?? 5,
      sourceDur: maxSourceSec,
      playbackDuration: clip.playbackDuration ?? clip.duration ?? 5,
      isVideo: Boolean(clip.videoUrl),
      originalEnd: clipEndTime(clip),
    };
    const origX = event.clientX;
    const videoResizeBaseline = videoTracks.map(track => track.map(c => ({ ...c })));
    const audioResizeBaseline = audioTracks.map(track => track.map(c => ({ ...c })));
    const musicResizeBaseline = musicTracks.map(track => track.map(c => ({ ...c })));
    setDraggingClipId(clipId);
    let rafId = null;
    let latestEvent = null;

    const applyMove = () => {
      const moveEvent = latestEvent;
      rafId = null;
      if (!moveEvent) return;
      const dSec = (moveEvent.clientX - origX) / pxPerSec;
      let updatedClip;
      if (!o.isVideo) {
        if (edge === 'left') {
          const nextDuration = Math.max(MIN_CLIP_PLAYBACK_SEC, o.playbackDuration - dSec);
          const maxStart = o.startTime + o.playbackDuration - MIN_CLIP_PLAYBACK_SEC;
          const nextStart = Math.max(0, Math.min(o.startTime + dSec, maxStart));
          updatedClip = {
            ...clip,
            startTime: nextStart,
            duration: nextDuration,
            playbackDuration: nextDuration,
            trimStart: 0,
            trimEnd: nextDuration,
          };
        } else {
          const nextDuration = Math.max(MIN_CLIP_PLAYBACK_SEC, o.playbackDuration + dSec);
          updatedClip = {
            ...clip,
            duration: nextDuration,
            playbackDuration: nextDuration,
            trimStart: 0,
            trimEnd: nextDuration,
          };
        }
      } else if (edge === 'left') {
        let newTrimStart = o.trimStart + dSec;
        newTrimStart = Math.max(0, Math.min(newTrimStart, o.trimEnd - MIN_CLIP_PLAYBACK_SEC));
        const deltaT = newTrimStart - o.trimStart;
        const newStart = o.startTime + deltaT;
        const newPlayback = o.trimEnd - newTrimStart;
        updatedClip = {
          ...clip,
          startTime: newStart,
          trimStart: newTrimStart,
          playbackDuration: Math.max(MIN_CLIP_PLAYBACK_SEC, newPlayback),
        };
      } else {
        let newTrimEnd = o.trimEnd + dSec;
        newTrimEnd = Math.max(
          o.trimStart + MIN_CLIP_PLAYBACK_SEC,
          Math.min(newTrimEnd, o.sourceDur),
        );
        updatedClip = {
          ...clip,
          trimEnd: newTrimEnd,
          playbackDuration: Math.max(MIN_CLIP_PLAYBACK_SEC, newTrimEnd - o.trimStart),
        };
      }

      updatedClip = { ...updatedClip, id: clipId };
      if (clip.sourceClipId) {
        const sourceClip = videoResizeBaseline[trackIdx]?.find(c => c.id === clipId);
        updatedClip = { ...sourceClip, ...updatedClip, videoUrl: sourceClip?.videoUrl || updatedClip.videoUrl };
      }
      if (o.isVideo) {
        updatedClip = clampVideoClipTrimToMediaCap(updatedClip);
        const mediaCap = clipVideoMediaCapSeconds(updatedClip);
        if (mediaCap > 0) {
          updatedClip = {
            ...updatedClip,
            videoMediaDurationSeconds: Math.max(
              Number(updatedClip.videoMediaDurationSeconds) || 0,
              mediaCap,
            ),
            videoSourceDurationMax: Math.max(
              Number(updatedClip.videoSourceDurationMax) || 0,
              mediaCap,
            ),
          };
        }
        const pairedAudio = findPairedAudioClipForVideoOnActiveLanes(
          updatedClip,
          audioResizeBaseline.flat(),
          timelineActive,
        );
        updatedClip = syncVideoClipTimelineSlot(updatedClip, pairedAudio, masterAudioPlaybackSpeedRef.current);
        if (isPitchTimelineClip(updatedClip, pairedAudio)) {
          updatedClip = syncPitchClipTimelineMetadata(updatedClip);
        }
      }
      const delta = clipEndTime(updatedClip) - o.originalEnd;
      setVideoTracks(rippleResizeTracks(videoResizeBaseline, trackIdx, clipId, o.originalEnd, updatedClip));
      setAudioTracks(audioResizeBaseline.map(track => shiftTrackAfter(track, o.originalEnd, delta)));
      setMusicTracks(musicResizeBaseline.map(track => shiftTrackAfter(track, o.originalEnd, delta)));
    };

    const handleMove = (moveEvent) => {
      latestEvent = moveEvent;
      if (rafId == null) rafId = requestAnimationFrame(applyMove);
    };

    const handleUp = () => {
      setDraggingClipId(null);
      if (rafId != null) cancelAnimationFrame(rafId);
      clearWindowDragHandlers(handleMove, handleUp);
    };

    installWindowDragHandlers(handleMove, handleUp);
  };

  const beginAudioClipDrag = (event, clip, trackIdx) => {
    if (event.button !== 0) return;
    const sourceTrackNode = audioTrackRefs.current.get(trackIdx);
    if (!sourceTrackNode) return;
    event.preventDefault();
    recordEditorHistory();
    setSelectedTimelineClip({ type: 'audio', id: clip.id });

    const sourceRect = sourceTrackNode.getBoundingClientRect();
    const dragOffsetPx = Math.max(0, event.clientX - (sourceRect.left + clip.startTime * pxPerSec));
    dragStateRef.current = { clipId: clip.id, sourceTrackIdx: trackIdx, dragOffsetPx, sourceTrackNode, type: 'audio' };
    setDraggingClipId(clip.id);
    let rafId = null;
    let latestEvent = null;

    const applyMove = () => {
      const moveEvent = latestEvent;
      rafId = null;
      if (!moveEvent) return;
      const dragState = dragStateRef.current;
      if (!dragState) return;

      let targetTrackIdx = dragState.sourceTrackIdx;
      audioTrackRefs.current.forEach((node, idx) => {
        const rect = node.getBoundingClientRect();
        if (moveEvent.clientY >= rect.top && moveEvent.clientY <= rect.bottom) {
          targetTrackIdx = idx;
        }
      });

      const targetTrackNode = audioTrackRefs.current.get(targetTrackIdx) || dragState.sourceTrackNode;
      const targetRect = targetTrackNode.getBoundingClientRect();
      const newStart = Math.max(0, (moveEvent.clientX - targetRect.left - dragState.dragOffsetPx) / pxPerSec);

      setAudioTracks(prev => {
        const next = prev.map(track => [...track]);
        let draggedClip = null;
        for (let i = 0; i < next.length; i += 1) {
          const existing = next[i].find(c => c.id === dragState.clipId);
          if (existing) {
            draggedClip = existing;
            next[i] = next[i].filter(c => c.id !== dragState.clipId);
            break;
          }
        }
        if (!draggedClip) return prev;
        next[targetTrackIdx] = normalizeTrackNoOverlap([
          ...next[targetTrackIdx],
          { ...draggedClip, startTime: newStart },
        ]);
        return next;
      });
    };

    const handleMove = (moveEvent) => {
      latestEvent = moveEvent;
      if (rafId == null) rafId = requestAnimationFrame(applyMove);
    };
    const handleUp = () => {
      dragStateRef.current = null;
      setDraggingClipId(null);
      if (rafId != null) cancelAnimationFrame(rafId);
      clearWindowDragHandlers(handleMove, handleUp);
    };

    installWindowDragHandlers(handleMove, handleUp);
  };

  const beginAudioClipResize = (event, clip, trackIdx, edge) => {
    if (event.button !== 0) return;
    event.stopPropagation();
    event.preventDefault();
    recordEditorHistory();
    setSelectedTimelineClip({ type: 'audio', id: clip.id });

    const clipId = clip.id;
    const original = {
      startTime: clip.startTime || 0,
      duration: clip.duration || 1,
      sourceDuration: clip.sourceDuration || clip.duration || 1,
      originalEnd: clipEndTime(clip),
    };
    const origX = event.clientX;
    const audioResizeBaseline = audioTracks.map(track => track.map(c => ({ ...c })));
    const videoResizeBaseline = videoTracks.map(track => track.map(c => ({ ...c })));
    const musicResizeBaseline = musicTracks.map(track => track.map(c => ({ ...c })));
    setDraggingClipId(clipId);
    let rafId = null;
    let latestEvent = null;

    const applyMove = () => {
      const moveEvent = latestEvent;
      rafId = null;
      if (!moveEvent) return;
      const dSec = (moveEvent.clientX - origX) / pxPerSec;
      let updatedClip;
      if (edge === 'left') {
        const minStart = Math.max(0, original.startTime + original.duration - original.sourceDuration);
        const maxStart = original.startTime + original.duration - MIN_CLIP_PLAYBACK_SEC;
        const nextStart = Math.max(minStart, Math.min(original.startTime + dSec, maxStart));
        const nextDuration = Math.max(
          MIN_CLIP_PLAYBACK_SEC,
          Math.min(original.sourceDuration, original.startTime + original.duration - nextStart),
        );
        updatedClip = { ...clip, startTime: nextStart, duration: nextDuration, sourceDuration: original.sourceDuration };
      } else {
        updatedClip = {
          ...clip,
          duration: Math.max(
            MIN_CLIP_PLAYBACK_SEC,
            Math.min(original.sourceDuration, original.duration + dSec),
          ),
          sourceDuration: original.sourceDuration,
        };
      }

      const delta = clipEndTime(updatedClip) - original.originalEnd;
      setAudioTracks(rippleResizeTracks(audioResizeBaseline, trackIdx, clipId, original.originalEnd, updatedClip));
      setVideoTracks(videoResizeBaseline.map(track => shiftTrackAfter(track, original.originalEnd, delta)));
      setMusicTracks(musicResizeBaseline.map(track => shiftTrackAfter(track, original.originalEnd, delta)));
    };

    const handleMove = (moveEvent) => {
      latestEvent = moveEvent;
      if (rafId == null) rafId = requestAnimationFrame(applyMove);
    };
    const handleUp = () => {
      setDraggingClipId(null);
      if (rafId != null) cancelAnimationFrame(rafId);
      clearWindowDragHandlers(handleMove, handleUp);
    };

    installWindowDragHandlers(handleMove, handleUp);
  };

  const beginSubtitleClipDrag = (event, clip, trackIdx = 0) => {
    if (event.button !== 0) return;
    if (event.target?.closest('button') || event.target?.closest('[data-subtitle-resize]')) return;
    const sourceTrackNode = subtitleTrackRefs.current.get(trackIdx);
    if (!sourceTrackNode) return;
    event.preventDefault();
    event.stopPropagation();
    recordEditorHistory();
    setSelectedTimelineClip({ type: 'subtitle', id: clip.id });

    const introDur = bookendTimeline.introDur || 0;
    const sourceRect = sourceTrackNode.getBoundingClientRect();
    const clipLeftPx = ((Number(clip.startTime) || 0) + introDur) * pxPerSec;
    const dragOffsetPx = Math.max(0, event.clientX - (sourceRect.left + clipLeftPx));

    dragStateRef.current = {
      clipId: clip.id,
      sourceTrackIdx: trackIdx,
      dragOffsetPx,
      sourceTrackNode,
      type: 'subtitle',
      introDur,
    };
    setDraggingClipId(clip.id);
    let rafId = null;
    let latestEvent = null;

    const applyMove = () => {
      const moveEvent = latestEvent;
      rafId = null;
      if (!moveEvent) return;
      const dragState = dragStateRef.current;
      if (!dragState) return;

      const targetRect = dragState.sourceTrackNode.getBoundingClientRect();
      const timelineSec = Math.max(
        0,
        (moveEvent.clientX - targetRect.left - dragState.dragOffsetPx) / pxPerSec,
      );
      const newStart = Math.max(0, timelineSec - (dragState.introDur || 0));

      setSubtitleTracks((prev) => {
        const next = prev.map((track) => [...track]);
        const row = next[trackIdx] || [];
        const idx = row.findIndex((c) => c.id === dragState.clipId);
        if (idx < 0) return prev;
        row[idx] = {
          ...row[idx],
          startTime: newStart,
          manualTiming: true,
        };
        next[trackIdx] = row;
        return next;
      });
    };

    const handleMove = (moveEvent) => {
      latestEvent = moveEvent;
      if (rafId == null) rafId = requestAnimationFrame(applyMove);
    };
    const handleUp = () => {
      dragStateRef.current = null;
      setDraggingClipId(null);
      if (rafId != null) cancelAnimationFrame(rafId);
      clearWindowDragHandlers(handleMove, handleUp);
    };

    installWindowDragHandlers(handleMove, handleUp);
  };

  const beginSubtitleClipResize = (event, clip, trackIdx = 0, edge) => {
    if (event.button !== 0) return;
    event.stopPropagation();
    event.preventDefault();
    recordEditorHistory();
    setSelectedTimelineClip({ type: 'subtitle', id: clip.id });

    const clipId = clip.id;
    const original = {
      startTime: Number(clip.startTime) || 0,
      duration: Math.max(MIN_CLIP_PLAYBACK_SEC, Number(clip.duration) || MIN_CLIP_PLAYBACK_SEC),
    };
    original.originalEnd = original.startTime + original.duration;
    const origX = event.clientX;
    setDraggingClipId(clipId);
    let rafId = null;
    let latestEvent = null;

    const applyMove = () => {
      const moveEvent = latestEvent;
      rafId = null;
      if (!moveEvent) return;
      const dSec = (moveEvent.clientX - origX) / pxPerSec;
      let updatedClip;
      if (edge === 'left') {
        const maxStart = original.startTime + original.duration - MIN_CLIP_PLAYBACK_SEC;
        const nextStart = Math.max(0, Math.min(original.startTime + dSec, maxStart));
        const nextDuration = original.startTime + original.duration - nextStart;
        updatedClip = {
          ...clip,
          startTime: nextStart,
          duration: Math.max(MIN_CLIP_PLAYBACK_SEC, nextDuration),
          manualTiming: true,
        };
      } else {
        updatedClip = {
          ...clip,
          duration: Math.max(MIN_CLIP_PLAYBACK_SEC, original.duration + dSec),
          manualTiming: true,
        };
      }

      setSubtitleTracks((prev) => {
        const next = prev.map((track, idx) => {
          if (idx !== trackIdx) return track;
          return track.map((c) => (c.id === clipId ? updatedClip : c));
        });
        return next;
      });
    };

    const handleMove = (moveEvent) => {
      latestEvent = moveEvent;
      if (rafId == null) rafId = requestAnimationFrame(applyMove);
    };
    const handleUp = () => {
      setDraggingClipId(null);
      if (rafId != null) cancelAnimationFrame(rafId);
      clearWindowDragHandlers(handleMove, handleUp);
    };

    installWindowDragHandlers(handleMove, handleUp);
  };

  const beginMusicClipDrag = (event, clip, trackIdx) => {
    if (event.button !== 0) return;
    if (event.target?.dataset?.timelineResizeEdge) return;
    const sourceTrackNode = musicTrackRefs.current.get(trackIdx);
    if (!sourceTrackNode) return;
    event.preventDefault();
    recordEditorHistory();
    setSelectedTimelineClip({ type: 'music', id: clip.id });

    const sourceRect = sourceTrackNode.getBoundingClientRect();
    const dragOffsetPx = Math.max(0, event.clientX - (sourceRect.left + clip.startTime * pxPerSec));
    dragStateRef.current = {
      clipId: clip.id,
      sourceTrackIdx: trackIdx,
      dragOffsetPx,
      sourceTrackNode,
      type: 'music',
    };
    setDraggingClipId(clip.id);
    let rafId = null;
    let latestEvent = null;

    const applyMove = () => {
      const moveEvent = latestEvent;
      rafId = null;
      if (!moveEvent) return;
      const dragState = dragStateRef.current;
      if (!dragState || dragState.type !== 'music') return;

      let targetTrackIdx = dragState.sourceTrackIdx;
      musicTrackRefs.current.forEach((node, idx) => {
        const rect = node.getBoundingClientRect();
        if (moveEvent.clientY >= rect.top && moveEvent.clientY <= rect.bottom) {
          targetTrackIdx = idx;
        }
      });

      const targetTrackNode = musicTrackRefs.current.get(targetTrackIdx) || dragState.sourceTrackNode;
      const targetRect = targetTrackNode.getBoundingClientRect();
      const newStart = Math.max(0, (moveEvent.clientX - targetRect.left - dragState.dragOffsetPx) / pxPerSec);

      setMusicTracks((prev) => {
        const next = prev.map((track) => [...track]);
        let draggedClip = null;
        for (let i = 0; i < next.length; i += 1) {
          const existing = next[i].find((c) => c.id === dragState.clipId);
          if (existing) {
            draggedClip = existing;
            next[i] = next[i].filter((c) => c.id !== dragState.clipId);
            break;
          }
        }
        if (!draggedClip) return prev;
        next[targetTrackIdx] = normalizeTrackNoOverlap([
          ...next[targetTrackIdx],
          { ...draggedClip, startTime: newStart },
        ]);
        return next;
      });
    };

    const handleMove = (moveEvent) => {
      latestEvent = moveEvent;
      if (rafId == null) rafId = requestAnimationFrame(applyMove);
    };
    const handleUp = () => {
      dragStateRef.current = null;
      setDraggingClipId(null);
      if (rafId != null) cancelAnimationFrame(rafId);
      clearWindowDragHandlers(handleMove, handleUp);
    };

    installWindowDragHandlers(handleMove, handleUp);
  };

  const beginMusicClipResize = (event, clip, trackIdx, edge) => {
    if (event.button !== 0) return;
    event.stopPropagation();
    event.preventDefault();
    recordEditorHistory();
    setSelectedTimelineClip({ type: 'music', id: clip.id });

    const clipId = clip.id;
    const maxSource = isStoryBackgroundMusicClip(clip)
      ? storyBackgroundMusicMaxDuration(clip)
      : (clip.sourceDuration || clip.duration || 1);
    const original = {
      startTime: clip.startTime || 0,
      duration: clip.duration || 1,
      sourceDuration: maxSource,
      originalEnd: clipEndTime(clip),
    };
    const origX = event.clientX;
    const musicResizeBaseline = musicTracks.map((track) => track.map((c) => ({ ...c })));
    const videoResizeBaseline = videoTracks.map((track) => track.map((c) => ({ ...c })));
    const audioResizeBaseline = audioTracks.map((track) => track.map((c) => ({ ...c })));
    setDraggingClipId(clipId);
    let rafId = null;
    let latestEvent = null;

    const applyMove = () => {
      const moveEvent = latestEvent;
      rafId = null;
      if (!moveEvent) return;
      const dSec = (moveEvent.clientX - origX) / pxPerSec;
      let updatedClip;
      if (edge === 'left') {
        const minStart = Math.max(0, original.startTime + original.duration - original.sourceDuration);
        const maxStart = original.startTime + original.duration - MIN_CLIP_PLAYBACK_SEC;
        const nextStart = Math.max(minStart, Math.min(original.startTime + dSec, maxStart));
        const nextDuration = Math.max(
          MIN_CLIP_PLAYBACK_SEC,
          Math.min(original.sourceDuration, original.startTime + original.duration - nextStart),
        );
        updatedClip = { ...clip, startTime: nextStart, duration: nextDuration, sourceDuration: original.sourceDuration };
      } else {
        updatedClip = {
          ...clip,
          duration: Math.max(
            MIN_CLIP_PLAYBACK_SEC,
            Math.min(original.sourceDuration, original.duration + dSec),
          ),
          sourceDuration: original.sourceDuration,
        };
      }

      const delta = clipEndTime(updatedClip) - original.originalEnd;
      setMusicTracks(rippleResizeTracks(musicResizeBaseline, trackIdx, clipId, original.originalEnd, updatedClip));
      setVideoTracks(videoResizeBaseline.map((track) => shiftTrackAfter(track, original.originalEnd, delta)));
      setAudioTracks(audioResizeBaseline.map((track) => shiftTrackAfter(track, original.originalEnd, delta)));
    };

    const handleMove = (moveEvent) => {
      latestEvent = moveEvent;
      if (rafId == null) rafId = requestAnimationFrame(applyMove);
    };
    const handleUp = () => {
      setDraggingClipId(null);
      if (rafId != null) cancelAnimationFrame(rafId);
      clearWindowDragHandlers(handleMove, handleUp);
    };

    installWindowDragHandlers(handleMove, handleUp);
  };

  const timeFromTimelineClientX = (clientX) => {
    const el = timelineRef.current;
    if (!el) return 0;
    const r = el.getBoundingClientRect();
    const labelW = TIMELINE_LABEL_WIDTH_PX;
    const x = clientX - r.left + el.scrollLeft - labelW;
    return Math.max(0, x / pxPerSec);
  };

  const beginPlayheadDrag = (e) => {
    if (e.button !== 0) return;
    e.preventDefault();
    e.stopPropagation();
    playback.pause();
    const apply = (clientX) => {
      const t = timeFromTimelineClientX(clientX);
      playback.seek(t);
      setVisualCurrentTime(t);
    };
    apply(e.clientX);
    const onMove = (ev) => apply(ev.clientX);
    const onUp = () => {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
    };
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
  };

  /* ---- Handle media from library ---- */
  const handleMediaFromLibrary = (mediaItem) => {
    recordEditorHistory();
    const dur = mediaItem.duration || 5;
    if (mediaItem.type === 'audio' && mediaItem.source !== 'background_music') {
      const newAudio = {
        id: `${mediaItem.id}-${Date.now()}`,
        name: mediaItem.name,
        sceneId: mediaItem.sceneId,
        url: mediaUrlForPlayback(mediaItem.url),
        startTime: totalDuration,
        duration: dur,
        sourceDuration: dur,
        volume: 0.8,
        muted: false,
      };
      setAudioTracks((prev) => {
        const lanes = prev || [];
        if (!lanes.length) return [[newAudio]];
        return [[...(lanes[0] || []), newAudio], ...lanes.slice(1)];
      });
      toast.success(`Added audio: ${mediaItem.name}`);
      return;
    }

    if (mediaItem.type === 'music' || mediaItem.source === 'background_music') {
      const isStoryBed = mediaItem.source === 'background_music';
      const newMusic = {
        id: isStoryBed ? 'story-background-music' : `${mediaItem.id}-${Date.now()}`,
        name: mediaItem.name,
        url: mediaItem.url,
        ...(isStoryBed ? { source: 'background_music' } : {}),
        ...(isNarrationEditor && projectId && isStoryBed
          ? { waveformUrl: `/narration/projects/${projectId}/story-background-music/waveform` }
          : {}),
        startTime: isStoryBed ? 0 : totalDuration,
        duration: dur,
        sourceDuration: dur,
        volume: mediaItem.volume != null ? clampTimelineClipVolume(Number(mediaItem.volume)) : 0.22,
        muted: false,
      };
      setMusicTracks((prev) => {
        const next = [...prev];
        if (!next.length) return [[newMusic]];
        next[0] = isStoryBed
          ? [newMusic]
          : [...(next[0] || []), newMusic];
        return next;
      });
      if (isStoryBed) setTimelineActive((prev) => ({ ...prev, music: true }));
      toast.success(`Added music: ${mediaItem.name}`);
      return;
    }

    const isVideo = mediaItem.type === 'video';
    const trimEnd = dur;
    const trimStart = isVideo
      ? Math.min(DEFAULT_VIDEO_TRIM_START, Math.max(0, dur - MIN_CLIP_PLAYBACK_SEC))
      : 0;
    const playbackDuration = isVideo
      ? Math.max(MIN_CLIP_PLAYBACK_SEC, trimEnd - trimStart)
      : dur;
    const newClip = {
      id: mediaItem.id,
      name: mediaItem.name,
      sceneId: mediaItem.sceneId || undefined,
      videoUrl: isVideo ? mediaUrlForPlayback(mediaItem.url) : null,
      imageUrl: mediaItem.type === 'image' ? mediaUrlForPlayback(mediaItem.url) : null,
      startTime: totalDuration,
      duration: dur,
      playbackDuration,
      trimStart,
      trimEnd,
    };
    
    // Add to first video track
    setVideoTracks(prev => {
      const next = [...prev];
      if (next.length === 0) next.push([]);
      next[0] = [...next[0], newClip];
      return next;
    });
    
    toast.success(`Added ${mediaItem.name} to timeline`);
  };

  /* ---- Handle drop on timeline ---- */
  const handleTimelineDrop = (e, trackIdx, acceptedKind = 'video') => {
    e.preventDefault();
    
    // Check if it's from media library
    try {
      const mediaData = e.dataTransfer.getData('application/json');
      if (mediaData) {
        const mediaItem = JSON.parse(mediaData);
        if (mediaItem.type === 'audio') {
          toast.info('Drop audio on an audio track');
          return;
        }
        if (mediaItem.type === 'music' || mediaItem.source === 'background_music') {
          toast.info('Drop background music on the music lane');
          return;
        }
        if (acceptedKind === 'image' && mediaItem.type !== 'image') {
          toast.info('Drop images on the image timeline');
          return;
        }
        if (acceptedKind === 'video' && mediaItem.type !== 'video') {
          toast.info('Drop generated videos on the video timeline');
          return;
        }
        const rect = e.currentTarget.getBoundingClientRect();
        const dropX = e.clientX - rect.left;
        const startTime = Math.max(0, dropX / pxPerSec);
        const dur = mediaItem.duration || 5;
        const isVideo = mediaItem.type === 'video';
        const trimEnd = dur;
        const trimStart = isVideo
          ? Math.min(DEFAULT_VIDEO_TRIM_START, Math.max(0, dur - MIN_CLIP_PLAYBACK_SEC))
          : 0;
        const playbackDuration = isVideo
          ? Math.max(MIN_CLIP_PLAYBACK_SEC, trimEnd - trimStart)
          : dur;

        const newClip = {
          id: `${mediaItem.id}-${Date.now()}`,
          name: mediaItem.name,
          sceneId: mediaItem.sceneId || undefined,
          videoUrl: isVideo ? mediaUrlForPlayback(mediaItem.url) : null,
          imageUrl: mediaItem.type === 'image' ? mediaUrlForPlayback(mediaItem.url) : null,
          startTime,
          duration: dur,
          playbackDuration,
          trimStart,
          trimEnd,
        };

        recordEditorHistory();
        setVideoTracks(prev => prev.map((track, i) =>
          i === trackIdx ? [...track, newClip] : track
        ));
        
        toast.success(`Added ${mediaItem.name} to timeline`);
        return;
      }
    } catch (err) {
      // Not from media library, handle as clip reorder
    }
    
    // Clip dragging is handled by pointer-based movement for smoother updates.
  };

  /* ---- Drag & drop audio onto audio track ---- */
  const handleAudioDrop = (e, trackIdx) => {
    e.preventDefault();
    setDragOverTrack(null);
    try {
      const mediaData = e.dataTransfer.getData('application/json');
      if (mediaData) {
        const mediaItem = JSON.parse(mediaData);
        if (mediaItem.type === 'music' || mediaItem.source === 'background_music') {
          toast.info('Drop background music on the music lane');
          return;
        }
        if (mediaItem.type !== 'audio') {
          toast.info('Drop images and videos on a video track');
          return;
        }
        recordEditorHistory();
        const rect = timelineRef.current?.getBoundingClientRect();
        const dropX = rect ? e.clientX - rect.left : 0;
        const startTime = Math.max(0, dropX / pxPerSec);
        const dur = mediaItem.duration || 5;
        const newAudio = {
          id: `${mediaItem.id}-${Date.now()}`,
          name: mediaItem.name,
          sceneId: mediaItem.sceneId,
          url: mediaUrlForPlayback(mediaItem.url),
          startTime,
          duration: dur,
          sourceDuration: dur,
          volume: 0.8,
          muted: false,
        };
        setAudioTracks(prev => prev.map((track, i) => (i === trackIdx ? [...track, newAudio] : track)));
        toast.success(`Added audio: ${mediaItem.name}`);
        return;
      }
    } catch (err) {
      // Fall through to handling dropped local audio files.
    }
    const files = Array.from(e.dataTransfer.files).filter(f => f.type.startsWith('audio/'));
    if (!files.length) { toast.error('Please drop an audio file'); return; }
    const rect = timelineRef.current?.getBoundingClientRect();
    const dropX = rect ? e.clientX - rect.left : 0;
    const startTime = Math.max(0, dropX / pxPerSec);
    files.forEach(file => {
      const url = URL.createObjectURL(file);
      const audio = new Audio(url);
      audio.onloadedmetadata = () => {
        setAudioTracks(prev => prev.map((track, i) => i === trackIdx
          ? [...track, { id: `audio-drop-${Date.now()}`, name: file.name.replace(/\.[^.]+$/, ''), url, startTime, duration: audio.duration, sourceDuration: audio.duration, volume: 0.8, muted: false }]
          : track
        ));
      };
    });
  };

  const handleMusicTrackDrop = (e, trackIdx) => {
    e.preventDefault();
    setDragOverTrack(null);
    try {
      const mediaData = e.dataTransfer.getData('application/json');
      if (!mediaData) return;
      const mediaItem = JSON.parse(mediaData);
      const ok =
        mediaItem.type === 'music'
        || mediaItem.source === 'background_music'
        || mediaItem.kind === 'bg_music'
        || (mediaItem.source === 'inhouse_stock' && mediaItem.type !== 'audio');
      if (!ok) {
        toast.info('Drop background music from the Media Library onto this lane');
        return;
      }
      const rect = timelineRef.current?.getBoundingClientRect();
      const dropX = rect ? e.clientX - rect.left : 0;
      const startTime = Math.max(0, dropX / pxPerSec);
      const dur = mediaItem.duration || 5;
      const isStoryBed = mediaItem.source === 'background_music';
      const newMusic = {
        id: isStoryBed ? 'story-background-music' : `${mediaItem.id}-${Date.now()}`,
        name: mediaItem.name,
        url: mediaItem.url,
        ...(isStoryBed ? { source: 'background_music' } : {}),
        ...(isNarrationEditor && projectId && isStoryBed
          ? { waveformUrl: `/narration/projects/${projectId}/story-background-music/waveform` }
          : {}),
        startTime: isStoryBed ? 0 : startTime,
        duration: dur,
        sourceDuration: dur,
        volume:
          mediaItem.volume != null
            ? clampTimelineClipVolume(Number(mediaItem.volume))
            : 0.22,
        muted: false,
      };
      setMusicTracks((prev) =>
        prev.map((track, i) => (i === trackIdx
          ? (isStoryBed
            ? [newMusic, ...track.filter((c) => c.id !== 'story-background-music')]
            : [...track, newMusic])
          : track)),
      );
      toast.success(`Added music: ${mediaItem.name}`);
    } catch (_) {
      /* ignore non-library drops */
    }
  };

  /* ---- Handle uploaded audio file ---- */
  const handleAudioFileUpload = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const url = URL.createObjectURL(file);
    const audio = new Audio(url);
    audio.onloadedmetadata = () => {
      setAudioTracks((prev) => {
        const lanes = prev || [];
        const newTrack = { id: `audio-upload-${Date.now()}`, name: file.name.replace(/\.[^.]+$/, ''), url, startTime: 0, duration: audio.duration, sourceDuration: audio.duration, volume: 0.8, muted: false };
        if (lanes.length === 0) return [[newTrack]];
        return [(lanes[0] || []).concat(newTrack), ...lanes.slice(1)];
      });
      toast.success(`Added audio: ${file.name}`);
    };
    e.target.value = '';
  };

  /** Audio timeline for server export (when audio lane active; trim aligned with editor). */
  const buildAudioExportPayload = useCallback(() => {
    const sceneById = Object.fromEntries((scenesSnapshot || []).map((s) => [String(s.id), s]));
    const flatVideo = (videoTracks || []).flat().filter((c) => !isBookendTimelineClip(c));
    const mainEnd = timelineEndFromVideoClips(flatVideo) || Number(story?.total_duration) || 5;
    const layout = bookendClipsForMainEnd(videoBookendsRef.current, mainEnd);
    const timelineEnd = layout.totalDuration || mainEnd;
    const introDur = layout.introDur;

    const mapClip = (clip) => {
      if (clip.muted) return null;
      let url = mediaUrlForPlayback(clip.url);
      if ((!url || url.startsWith('blob:')) && clip.sceneId) {
        const sid = String(clip.sceneId).replace(/^audio-/, '').replace(/^seg-audio-/, '').split('-seg-')[0];
        const scene = sceneById[sid];
        const fromScene = scene ? mediaUrlForSegmentField(scene, 'audio_url') : '';
        if (fromScene && !fromScene.startsWith('blob:')) url = fromScene;
      }
      if ((!url || url.startsWith('blob:')) && isStoryBackgroundMusicClip(clip) && story?.music_url) {
        url = mediaUrlForPlayback(story.music_url);
      }
      if (!url || url.startsWith('blob:')) return null;
      const isBgm = isStoryBackgroundMusicClip(clip);
      let timelineSec = Math.max(0.01, clipAudioTimelineDuration(clip, 0, masterAudioPlaybackSpeed));
      if (isBgm) timelineSec = Math.max(timelineSec, timelineEnd);
      const speed = effectiveClipAudioPlaybackSpeed(clip, masterAudioPlaybackSpeed);
      const srcSpan = timelineSec * speed;
      const sd = Number(clip.sourceDuration) || srcSpan;
      const trimStart = Number(clip.trimStart) >= 0
        ? Math.max(0, Number(clip.trimStart))
        : Math.max(0, sd - srcSpan);
      return {
        audio_url: url,
        start_time: isBgm ? 0 : (Number(clip.startTime) || 0) + introDur,
        playback_seconds: timelineSec,
        trim_start: trimStart,
        audio_playback_speed: speed,
        volume: clip.volume != null ? Number(clip.volume) : 0.8,
        muted: false,
        ...(clip.sceneId
          ? {
            scene_id: String(clip.sceneId)
              .replace(/^audio-/, '')
              .replace(/^seg-audio-/, '')
              .split('-seg-')[0],
          }
          : {}),
      };
    };

    const mask = audioLanePlaybackMask(timelineActive, audioTracks.length);
    const voice = audioTracks
      .flatMap((track, idx) => (mask[idx] ? track : []).map(mapClip))
      .filter(Boolean);
    const bgm = musicTracks
      .flatMap((track) => (isMusicLanePlaybackEnabled(timelineActive, track) ? track : []).map(mapClip))
      .filter(Boolean);
    let result = [...voice, ...bgm];

    if (!result.length && (timelineActive.audio !== false || timelineActive.music !== false)) {
      if (timelineActive.audio !== false) {
        if (isNarrationEditor && projectId) {
          const songLane = isSongBasedEditorScenes(scenesSnapshot)
            ? buildSongMasterAudioLaneFromScenes(
              scenesSnapshot,
              mediaUrlForPlayback,
              projectId,
              scenesSnapshot?.[0]?.song_timeline_duration,
            )
            : buildNarrationMixAudioLane(scenesSnapshot, flatVideo, projectId);
          result = songLane.map(mapClip).filter(Boolean);
        } else if (scenesSnapshot?.length) {
          result = buildSegmentAudioLaneClipsForScenes(scenesSnapshot, flatVideo)
            .map(mapClip)
            .filter(Boolean);
        }
      }
      if (timelineActive.music !== false && story?.music_url) {
        const bg = buildMusicTracksFromStoryUrl(story.music_url, timelineEnd, {
          narrationProjectId: projectId,
        });
        result = [...result, ...bg.flat().map(mapClip).filter(Boolean)];
      }
    } else if (timelineActive.music !== false && musicTracksNeedStoryMusic(musicTracks) && story?.music_url) {
      const bg = buildMusicTracksFromStoryUrl(story.music_url, timelineEnd, {
        narrationProjectId: projectId,
      });
      result = [...result, ...bg.flat().map(mapClip).filter(Boolean)];
    }

    const bookendAudio = bookendAudioExportClips(videoBookendsRef.current, mainEnd);
    return [...bookendAudio, ...result];
  }, [
    audioTracks,
    musicTracks,
    timelineActive,
    masterAudioPlaybackSpeed,
    scenesSnapshot,
    story,
    videoTracks,
    isNarrationEditor,
    projectId,
  ]);

  /** Timeline clips for server export (all video rows, active image/video lanes only; trim + URLs the backend can fetch). */
  const buildTimelineExportPayload = useCallback(() => {
    const findPairedAudio = (clip) => findPairedAudioClipForVideoOnActiveLanes(clip, audioTracks, timelineActive);
    const sceneById = Object.fromEntries((scenesSnapshot || []).map((s) => [String(s.id), s]));

    const clipsOnTimeline = (videoTracksRef.current || videoTracks)
      .flatMap((track, trackIndex) =>
        (track || []).map((clip) => ({ clip, trackIndex })),
      )
      .filter(({ clip }) => {
        if (isBookendTimelineClip(clip)) return false;
        const sceneId = normalizeEditorSceneId(clip.parentSceneId || clip.sceneId || clip.id);
        const scene = sceneId ? sceneById[sceneId] : null;
        const sceneMedia = scene ? sceneDocumentMediaPlayback(scene) : null;
        const sceneVideo = sceneMedia?.videoUrl || '';
        const sceneImage = sceneMedia?.imageUrl || '';
        if (
          clip.visualBeat != null
          && sceneVideo
          && timelineActive.video
          && Number(clip.visualBeat) !== 0
        ) {
          return false;
        }
        const hasStill = Boolean(clip.imageUrl || sceneImage);
        // Stills remain exportable even if the Image lane toggle was left off.
        return (clip.videoUrl && timelineActive.video)
          || (sceneVideo && timelineActive.video)
          || (clip.imageUrl && timelineActive.image)
          || (hasStill && !clip.videoUrl && !sceneVideo);
      });
    return clipsOnTimeline
      .sort((a, b) => {
        const d = (Number(a.clip.startTime) || 0) - (Number(b.clip.startTime) || 0);
        if (d !== 0) return d;
        if (a.trackIndex !== b.trackIndex) return a.trackIndex - b.trackIndex;
        return String(a.clip.id).localeCompare(String(b.clip.id));
      })
      .map(({ clip, trackIndex }) => {
        const sceneId = normalizeEditorSceneId(clip.parentSceneId || clip.sceneId || clip.id);
        const scene = sceneId ? sceneById[sceneId] : null;
        const sceneMedia = scene ? sceneDocumentMediaPlayback(scene) : null;
        const sceneVideo = sceneMedia?.videoUrl || '';
        const sceneImage = sceneMedia?.imageUrl || '';
        const useVideo = Boolean((clip.videoUrl || sceneVideo) && timelineActive.video);
        const video_url = useVideo
          ? mediaUrlForPlayback(clip.videoUrl || sceneVideo)
          : null;
        const stillSrc = clip.imageUrl || sceneImage;
        const image_url = stillSrc && (timelineActive.image || !useVideo)
          ? mediaUrlForPlayback(stillSrc)
          : null;
        if (!video_url && !image_url) return null;
        if (video_url?.startsWith('blob:') || image_url?.startsWith('blob:')) return null;
        const dur = Number(clip.duration) || 5;
        const trimStart = Number(clip.trimStart) || 0;
        const trimEndRaw = clip.trimEnd != null ? Number(clip.trimEnd) : dur;
        const trimEnd = useVideo
          ? Math.min(trimEndRaw, clipVideoSourceTrimEndSeconds(clip))
          : trimEndRaw;
        const playback_seconds = Math.max(
          0.01,
          clipVideoExportPlaybackSeconds(clip, findPairedAudio(clip), masterAudioPlaybackSpeed),
        );
        const timeline_seconds = Math.max(
          0.01,
          clipVideoTimelineDuration(clip),
        );
        const mediaCap = useVideo ? clipVideoMediaCapSeconds(clip) : null;
        const transitionOut = normalizeTransitionOut(clip.transitionOut);
        const songMotion = isSongBasedNarrationProject(story) ? songMotionPreviewOpts : null;
        const songAnimEnabled = songMotion?.defaultAnimationEnabled !== false;
        const useAnimation = timelineActive.animation && songAnimEnabled;
        const motionFields = scene ? segmentToClipMotionFields(scene) : {};
        const audioTimeline = clip.audioMotionTimeline || motionFields.audioMotionTimeline;
        let audioMotionTimeOffset = 0;
        if (
          clip.parentSceneId != null
          && clip.multishotPart != null
          && Number(clip.multishotSegmentDuration) > 0
        ) {
          audioMotionTimeOffset = Number(clip.multishotPart) * Number(clip.multishotSegmentDuration);
        }
        const dialogueShots = !video_url && image_url ? dialogueShotsForClip(clip) : null;
        return {
          video_url,
          image_url,
          ...(sceneId ? { scene_id: sceneId } : {}),
          ...(scene?.audio_url && !String(scene.audio_url).startsWith('blob:')
            ? { audio_url: mediaUrlForPlayback(scene.audio_url) }
            : {}),
          trim_start: trimStart,
          trim_end: trimEnd,
          duration_seconds: dur,
          playback_seconds,
          timeline_seconds,
          video_playback_speed: clipVideoPlaybackSpeed(clip),
          video_media_duration_seconds: mediaCap > 0 ? mediaCap : undefined,
          start_time: Number(clip.startTime) || 0,
          track_index: trackIndex,
          transition_out: transitionOut,
          transition_duration: transitionOut === 'cut'
            ? 0
            : Math.max(0.1, Math.min(2, Number(clip.transitionDuration) || 0.5)),
          video_filters: clipFiltersFromClip(clip).map((f) => ({
            filter_id: f.id,
            intensity: f.intensity,
          })),
          motion_preset: useAnimation ? (clip.motionPreset || motionFields.motionPreset || 'none') : 'none',
          motion_strength: Math.max(0.01, Math.min(0.45, Number(clip.motionStrength ?? motionFields.motionStrength) || 0.12)),
          audio_motion_timeline: useAnimation && audioTimeline ? audioTimeline : null,
          audio_motion_time_offset: audioMotionTimeOffset > 0 ? audioMotionTimeOffset : undefined,
          song_animation_speed: songMotion?.animationSpeed ?? undefined,
          ...(dialogueShots ? { dialogue_shots: dialogueShotsExportRows(dialogueShots), motion_preset: 'none' } : {}),
        };
      })
      .filter(Boolean);
  }, [videoTracks, audioTracks, timelineActive.image, timelineActive.video, timelineActive.audio, timelineActive.animation, story, songMotionPreviewOpts, scenesSnapshot, masterAudioPlaybackSpeed, dialogueShotsForClip]);

  /** Caption cues from active subtitle lane(s), paired with matching audio lanes. */
  const buildSubtitleExportPayload = useCallback(() => {
    if (!timelineActive.subtitle) return [];
    const sceneById = Object.fromEntries((scenesSnapshot || []).map((s) => [String(s.id), s]));
    const styleNow = normalizeVideoSubtitleStyle(subtitleStyleRef.current, exportAspectRatio);
    const layerNow = styleNow.layer || subtitleLayerFromLegacyAnchors(
      styleNow.anchorX,
      styleNow.anchorY,
      exportAspectRatio,
    );
    const { width: exportFrameW, height: exportFrameH } = exportTargetSizeForAspect(exportAspectRatio);
    const exportViewportMin = Math.min(exportFrameW, exportFrameH);

    const subMask = subtitleLanePlaybackMask(timelineActive, (subtitleTracks || []).length);
    let cueEntries = [];
    (subtitleTracks || []).forEach((track, laneIndex) => {
      if (!subMask[laneIndex]) return;
      (track || []).forEach((c) => {
        if (c && !c.muted) cueEntries.push({ cue: c, laneIndex });
      });
    });

    const mapCueEntriesToExport = (entries) => entries
      .map(({ cue: c, laneIndex }) => {
        const audioLane = (audioTracks || [])[laneIndex] || [];
        const ac = audioLane.find((a) => a.id === c.audioClipId);
        const sid = normalizeEditorSceneId(c.sceneId || ac?.sceneId);
        const scene = sid ? sceneById[sid] : null;
        const videoClip = findVideoClipForScene(videoTracks, sid, c.videoClipId);
        const text = resolveSubtitleExportText(c, ac, scene, videoClip).normalize('NFC');
        if (!text) return null;
        const layout = computeSubtitleLayerTextLayout({
          text,
          layer: layerNow,
          style: styleNow,
          frameW: exportFrameW,
          frameH: exportFrameH,
          aspectRatio: exportAspectRatio,
          viewportMin: exportViewportMin,
        });
        const startTime = Number(c.startTime) || Number(videoClip?.startTime) || 0;
        const isSongMasterAudio = ac?.id === 'song-master-audio' || ac?.sourceType === 'song_master';
        const playbackSeconds = videoClip
          ? (isSongMasterAudio
            ? Math.max(0.01, Number(c.duration) || clipVideoTimelineDuration(videoClip))
            : Math.max(0.01, clipVideoExportPlaybackSeconds(videoClip, ac, masterAudioPlaybackSpeed)))
          : Math.max(0.01, Number(c.duration) || 0.01);
        const cue = {
          text,
          lines: layout.lines,
          start_time: startTime,
          playback_seconds: playbackSeconds,
          muted: false,
        };
        if (!styleNow.spokenWordWindow) return [cue];
        const timed = alignSpokenWords(
          text,
          scene?.word_timestamps || videoClip?.wordTimestamps,
          Math.max(0.01, Number(c.duration) || playbackSeconds),
        );
        const slices = expandSpokenWordCues(timed, styleNow.spokenWordLimit);
        if (!slices.length) return [cue];
        return slices.map((slice) => {
          const shown = String(slice.text || '').toLocaleUpperCase();
          return {
            text: shown,
            lines: [shown],
            highlight_index: slice.highlightIndex,
            highlight_color: styleNow.spokenWordHighlightColor,
            start_time: startTime + slice.startOffset,
            playback_seconds: slice.duration,
            muted: false,
          };
        });
      })
      .flat()
      .filter(Boolean);

    let exports = mapCueEntriesToExport(cueEntries);
    if (!exports.length) {
      const fallbackEntries = subtitleExportEntriesFromVideoLane(videoTracks, scenesSnapshot);
      exports = mapCueEntriesToExport(
        fallbackEntries.map(({ cue }) => ({ cue, laneIndex: 0 })),
      );
    }

    return exports;
  }, [
    subtitleTracks,
    videoTracks,
    timelineActive.subtitle,
    audioTracks,
    scenesSnapshot,
    exportAspectRatio,
    masterAudioPlaybackSpeed,
  ]);

  const exportableTimelineClipCount = useMemo(
    () => buildTimelineExportPayload().length,
    [buildTimelineExportPayload],
  );

  /* ---- Export ---- */
  /** Export request body for the current timeline (shared by server and on-device export). */
  const buildServerExportBody = () => {
    const clips = buildTimelineExportPayload();
    if (!clips.length) return null;
    const audio_clips = buildAudioExportPayload();
      const subtitle_clips = buildSubtitleExportPayload();
      const vfx_effects = timelineActive.effects
        ? buildVfxExportPayload(effectTracksRef.current)
        : [];
    const body = {
        clips,
        audio_clips,
        aspect_ratio: exportAspectRatio,
        export_quality: exportQuality,
        export_framing: exportFramingToApiPayload(exportFramingRef.current),
        editor_animation_speed: editorAnimationSpeedRef.current,
        ...(vfx_effects.length ? { vfx_effects } : {}),
      };
      const bookendApplied = applyBookendsToExportPayload({
        clips: body.clips,
        subtitleClips: subtitle_clips,
        bookends: normalizeVideoBookends(videoBookendsRef.current, {
          defaultTitle: story?.title || '',
          aspectRatio: exportAspectRatio,
        }),
        defaultTitle: story?.title || '',
        aspectRatio: exportAspectRatio,
      });
      body.clips = bookendApplied.clips;
      const allHostClips = (overlayTracksRef.current || []).flat()
        .filter((clip) => !clip.muted && (clip.imageUrl || clip.image_s3_url));
      const overlay_clips = buildHostOverlayExportPayload(overlayTracksRef.current, {
        aspectRatio: exportAspectRatio,
        introDuration: bookendApplied.introDuration || 0,
      });
      if (overlay_clips.length) {
        body.overlay_clips = overlay_clips;
      } else if (allHostClips.length) {
        toast.warning(
          'Host photos could not be included — use uploaded images (not browser-only previews).',
        );
      }
      if (subtitle_clips.length) {
        body.subtitle_clips = bookendApplied.subtitleClips;
        const styleNow = normalizeVideoSubtitleStyle(subtitleStyleRef.current, exportAspectRatio);
        const layerNow = styleNow.layer || subtitleLayerFromLegacyAnchors(
          styleNow.anchorX,
          styleNow.anchorY,
          exportAspectRatio,
        );
        const exportAnchors = subtitleLayerToExportAnchors(layerNow);
        body.subtitle_style = subtitleStyleToExportPayload(styleNow, layerNow, exportAnchors);
        body.subtitle_timeline = buildSubtitleTimelineDocument({
          aspectRatio: exportAspectRatio,
          style: styleNow,
          layer: layerNow,
          cues: bookendApplied.subtitleClips,
        });
      } else if (timelineActive.subtitle) {
        toast.warning('Subtitles are on but no caption text was found to burn into the export.');
      }
      if (isPitchRecordingStory(story) && pitchVoicePolish?.enabled) {
        body.pitch_voice_polish = { ...pitchVoicePolish, enabled: true };
      }
    return body;
  };

  const handleExportFull = async () => {
    let body = buildServerExportBody();
    if (!body) {
      toast.error('Add at least one timeline clip with a video or image (blob-only previews cannot be exported).');
      return;
    }
    setExportingFull(true);
    setExportProgress(0);
    setExportStatusMessage('Preparing export…');
    setExportedUrl(null);
    try {
      const exportPath = isNarrationEditor
        ? `/narration/projects/${projectId}/export-video`
        : `/stories/${storyId}/export-video`;
      if (exportPayloadHasLocalMedia(body)) {
        toast.info('Uploading local media to cloud before export…');
        body = await stageExportPayloadForCloud(body, {
          narrationProjectId: isNarrationEditor ? projectId : null,
          storyId: isNarrationEditor ? null : storyId,
          onProgress: (msg) => toast.info(msg),
        });
      }
      if (!(body.audio_clips || []).length) {
        toast.warning('No exportable narration or music audio was found — the video may export silent.');
      }
      const { data } = await api.post(exportPath, body);
      setExportDialogOpen(false);
      toast.info('Compiling video on server... (you can close this tab)');
      const storageKey = exportJobStorageKey({ storyId, projectId });
      exportJobStorageKeyRef.current = storageKey;
      saveActiveExportJob(storageKey, data.job_id);
      if (exportPollStopRef.current) exportPollStopRef.current();
      exportPollStopRef.current = pollExportJob(api, data.job_id, {
        onProgress: handleExportJobUpdate,
      });
    } catch (err) {
      setExportingFull(false);
      toast.error(err.response?.data?.detail || err.message || 'Export failed');
    }
  };

  const handleExportLocal = async () => {
    if (exportingLocal) return;
    if (!localExportSupported()) {
      toast.error('This browser cannot render video on-device. Use Chrome or Edge, or use Export.');
      return;
    }
    const body = buildServerExportBody();
    if (!body) {
      toast.error('Add at least one timeline clip with a video or image.');
      return;
    }
      const baseName = (story?.title || 'timeline-export')
        .replace(/[^\w\-]+/g, '_')
        .slice(0, 48) || 'timeline-export';
    let fileHandle = null;
    try {
      fileHandle = await pickLocalExportFile(baseName);
    } catch (err) {
      if (err?.name === 'AbortError') return;
    }

    const controller = new AbortController();
    localExportAbortRef.current = controller;
    const toastId = 'local-timeline-export';
    const cancelAction = { label: 'Cancel', onClick: () => controller.abort() };
    setExportingLocal(true);
    setExportDialogOpen(false);
    toast.loading('Preparing on-device export…', { id: toastId, action: cancelAction, duration: Infinity });
    try {
      const { warnings } = await exportTimelineLocally({
        body,
        filename: baseName,
        fileHandle,
        signal: controller.signal,
        onProgress: ({ message, progress }) => {
          toast.loading(`${message} · ${Math.round(progress * 100)}%`, {
            id: toastId,
            action: cancelAction,
            duration: Infinity,
          });
        },
      });
      toast.success('Video saved on this device.', { id: toastId, action: undefined, duration: 5000 });
      (warnings || []).forEach((w) => toast.warning(w));
    } catch (err) {
      if (err?.name === 'AbortError') {
        toast.info('On-device export cancelled.', { id: toastId, action: undefined, duration: 4000 });
      } else {
        toast.error(err?.message || 'Local export failed', { id: toastId, action: undefined, duration: 8000 });
      }
    } finally {
      if (localExportAbortRef.current === controller) localExportAbortRef.current = null;
      setExportingLocal(false);
    }
  };

  const handleExportPremiere = async () => {
    const clips = buildTimelineExportPayload();
    const audio_clips = buildAudioExportPayload();
    setExportingPremiere(true);
    try {
      const exportPath = isNarrationEditor
        ? `/narration/projects/${projectId}/export-premiere`
        : `/stories/${storyId}/export-premiere`;
      const body = {
        aspect_ratio: exportAspectRatio,
        ...(clips.length ? { clips, audio_clips } : {}),
      };
      const { data } = await api.post(exportPath, body);
      setExportDialogOpen(false);
      toast.info('Building Premiere Pro package… (you can close this tab)');
      let premierePollStop = null;
      premierePollStop = pollExportJob(api, data.job_id, {
        onProgress: (job) => {
          if (job.status === 'completed' && job.result_url) {
            if (premierePollStop) premierePollStop();
            const a = document.createElement('a');
            a.href = job.result_url;
            const slug = (story?.title || 'premiere').replace(/[^\w\-]+/g, '_').slice(0, 40);
            a.download = `${slug}-premiere.zip`;
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);
            toast.success('Premiere package ready! Unzip, then use File → Import in Premiere Pro.');
            setExportingPremiere(false);
          } else if (job.status === 'failed') {
            if (premierePollStop) premierePollStop();
            toast.error(job.error_message || 'Premiere export failed');
            setExportingPremiere(false);
          } else if (job.status === 'cancelled') {
            if (premierePollStop) premierePollStop();
            setExportingPremiere(false);
          }
        },
      });
    } catch (err) {
      setExportingPremiere(false);
      toast.error(err.response?.data?.detail || err.message || 'Premiere export failed');
    }
  };

  const openSubtitleLaneEditor = useCallback((clip) => {
    setSubtitleDraft({ id: clip.id, text: clip.text || '', muted: Boolean(clip.muted) });
    setSubtitleDialogOpen(true);
  }, []);

  const saveSubtitleDraft = useCallback(() => {
    const { id, text, muted } = subtitleDraft;
    if (!id) return;
    setSubtitleTracks((prev) => {
      const rows = prev.length ? [...prev] : [[]];
      const row0 = [...(rows[0] || [])];
      const idx = row0.findIndex((c) => c.id === id);
      if (idx < 0) return prev;
      row0[idx] = { ...row0[idx], text, muted, manualEdit: true };
      rows[0] = row0;
      return rows;
    });
    setSubtitleDialogOpen(false);
  }, [subtitleDraft]);

  const toggleSubtitleClipMuted = useCallback((clipId, trackIdx = 0) => {
    setSubtitleTracks((prev) => {
      const rows = prev.length ? [...prev] : [[]];
      const lane = [...(rows[trackIdx] || [])];
      const idx = lane.findIndex((c) => c.id === clipId);
      if (idx < 0) return prev;
      lane[idx] = { ...lane[idx], muted: !lane[idx].muted };
      rows[trackIdx] = lane;
      return rows;
    });
  }, []);

  const toggleVideoClipEmbeddedAudioMute = useCallback((trackIdx, clipId) => {
    setVideoTracks((prev) => prev.map((track, idx) => {
      if (idx !== trackIdx) return track;
      return track.map((c) => (c.id === clipId ? { ...c, muted: !(c.muted === true) } : c));
    }));
  }, []);

  const handleDownloadIndividual = () => {
    const allClips = videoTracks
      .flatMap((track) => track)
      .filter((c) => c.videoUrl && timelineActive.video)
      .slice()
      .sort((a, b) => {
        const d = (Number(a.startTime) || 0) - (Number(b.startTime) || 0);
        if (d !== 0) return d;
        return String(a.id).localeCompare(String(b.id));
      });
    if (!allClips.length) {
      toast.error('No video clips to download');
      return;
    }
    downloadIndividual(allClips, story?.title || 'scene');
  };

  const handleSaveProjectLocal = async () => {
    if (!editorId) return;
    try {
      let docStory = story;
      let docScenes = scenesSnapshot;
      let docVideoTracks = videoTracksRef.current;
      let docAudioTracks = audioTracksRef.current;
      let docMusicTracks = musicTracksRef.current;
      let docSubtitleTracks = subtitleTracksRef.current;
      let docSubtitleStyle = subtitleStyleRef.current;

      if (isNarrationEditor && projectId && pendingLanguageCommit) {
        await api.post(`/narration/projects/${projectId}/commit-script-language-switch`, {
          language: pendingLanguageCommit.language,
          segments: pendingLanguageCommit.segments.map((s) => ({
            id: s.id,
            text: s.text,
            visual: s.visual ?? '',
            phoneme_notes: s.phoneme_notes ?? '',
          })),
          regenerate_audio: pendingLanguageCommit.regenerate_audio,
        });
        setPendingLanguageCommit(null);

        const { data: narrationProject } = await api.get(`/narration/projects/${projectId}`, {
          params: { _editorFresh: Date.now() },
        });
        const state = narrationProjectToEditorState(narrationProject, editorId);
        const sceneSource = state.scenes;
        const sceneById = Object.fromEntries(sceneSource.map((s) => [String(s.id), s]));

        docStory = state.story;
        docScenes = sceneSource;
        docVideoTracks = videoTracksRef.current.map((track) =>
          track.map((c) => mergeTimelineClipWithLatestScene(
            c,
            sceneById[String(c.sceneId)],
            { freezeMedia: false },
          ) ?? c),
        );
        docAudioTracks = audioTracksRef.current.map((track) =>
          track
            .map((ac) => mergeAudioLaneClipWithLatestScene(ac, sceneById, { freezeMedia: false }))
            .filter((ac) => ac.url),
        );
        docMusicTracks = musicTracksRef.current;
        docSubtitleTracks = subtitleTracksRef.current;
        docSubtitleStyle = subtitleStyleRef.current;

        setStory(docStory);
        setScenesSnapshot(docScenes);
        setVideoTracks(docVideoTracks);
        setAudioTracks(docAudioTracks);
        afterLoadProjectSeekRef.current = true;
      }

      const doc = buildVideoProjectDocument({
        storyId: editorId,
        story: docStory,
        scenes: docScenes,
        videoTracks: docVideoTracks,
        audioTracks: docAudioTracks,
        musicTracks: docMusicTracks,
        subtitleTracks: docSubtitleTracks,
        subtitleStyle: docSubtitleStyle,
        timelineZoom,
        timelineActive,
        volume,
        masterAudioPlaybackSpeed,
        isMuted,
        videoMasterMuted,
        exportAspectRatio,
        exportQuality,
        exportFraming,
        exportClipVideoToSubtitle,
        videoBookends: videoBookendsRef.current,
        audioTrackMeta: audioTrackMetaRef.current,
      });

      let cloudSynced = false;
      let cloudErrorDetail = '';
      const cloudPayload = cloudSnapshotPayloadFromDoc(doc);
      if (cloudPayload && (isNarrationEditor ? projectId : storyId)) {
        try {
          await patchVideoEditorSnapshotToCloud({
            isNarration: isNarrationEditor,
            projectId,
            storyId,
            editor: cloudPayload.editor,
            scenes: cloudPayload.scenes,
            story: cloudPayload.story,
            recordVersion: false,
          });
          editorDirtyRef.current = false;
          cloudSynced = true;
          setCloudSaveStatus('saved');
        } catch (syncErr) {
          console.warn('Cloud editor snapshot sync failed', syncErr);
          setCloudSaveStatus('error');
          const detail = syncErr?.response?.data?.detail;
          cloudErrorDetail =
            typeof detail === 'string'
              ? detail
              : Array.isArray(detail)
                ? detail.map((d) => d.msg || JSON.stringify(d)).join('; ')
                : syncErr?.message || 'Request failed';
        }
      }

      const folderName = story?.title || editorId;
      const result = await saveProjectToLinkedOrNewFolder(editorId, doc, folderName);
      const savedLangLabel = formatVideoProjectLanguageLabel(
        doc?.story?.language ? String(doc.story.language) : '',
      );
      const langToast =
        savedLangLabel || (doc?.story?.language ? String(doc.story.language) : '')
          ? ` · ${savedLangLabel || doc.story.language}`
          : '';

      const cloudSuffix = cloudSynced
        ? ' Timeline saved to your Twin Tales account.'
        : cloudErrorDetail
          ? ` Cloud sync failed: ${cloudErrorDetail}. Fix login / REACT_APP_BACKEND_URL / network — other devices will not see updates until sync succeeds.`
          : '';

      if (result.mode === 'download') {
        toast[cloudSynced ? 'success' : cloudErrorDetail ? 'warning' : 'info'](
          `Downloaded project JSON.${cloudSuffix || ''} Use Chrome or Edge and Save again to link a folder for local copies.`,
        );
      } else {
        setLinkedProjectFolder(result.folderName || folderName);
        if (result.versionIndex != null) {
          toast[cloudErrorDetail ? 'warning' : 'success'](
            `Saved project${langToast} → “${result.folderName}/project.json”.${cloudSuffix}`,
          );
        } else {
          toast[cloudErrorDetail ? 'warning' : 'success'](`Saved project to “${result.folderName}”.${cloudSuffix}`);
        }
      }
    } catch (e) {
      if (e?.name === 'AbortError') return;
      toast.error(e?.message || 'Failed to save project');
    }
  };

  const onOpenProjectFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    try {
      const text = await file.text();
      const parsed = parseVideoProjectDocument(text);
      if (!parsed.ok) {
        toast.error(parsed.error);
        return;
      }
      const { doc } = parsed;
      if (doc.storyId !== editorId) {
        setPendingVideoProjectNavigation(doc);
        if (doc.storyId?.startsWith('narration-')) {
          navigate(`/narration/${doc.storyId.replace(/^narration-/, '')}/video-editor`);
        } else {
          navigate(`/stories/${doc.storyId}/video-editor`);
        }
        toast.info('Switching to this project’s story…');
        return;
      }
      applyProjectDocument(doc);
      skipDirtyMarkRef.current = true;
      editorDirtyRef.current = false;
      toast.success('Project loaded from file');
    } catch {
      toast.error('Could not read project file');
    }
  };

  /** Full timeline clips for script UI and clip list. Playback omits clips without visible media when toggles are on — editing must still see every segment. */
  const allVideoClips = (videoTracks || [])
    .flat()
    .slice()
    .sort((a, b) => (Number(a.startTime) || 0) - (Number(b.startTime) || 0));

  const exportPreviewMedia = useMemo(() => {
    if (currentClip?.videoUrl) {
      return { url: currentClip.videoUrl, type: 'video' };
    }
    if (currentClip?.imageUrl) {
      return { url: currentClip.imageUrl, type: 'image' };
    }
    const flat = (videoTracks || []).flat();
    const withVideo = flat.find((c) => c.videoUrl);
    if (withVideo) return { url: withVideo.videoUrl, type: 'video' };
    const withImage = flat.find((c) => c.imageUrl);
    if (withImage) return { url: withImage.imageUrl, type: 'image' };
    return null;
  }, [currentClip, videoTracks]);

  const selectedExportQuality = useMemo(
    () => EXPORT_QUALITY_OPTIONS.find((o) => o.value === exportQuality) || EXPORT_QUALITY_OPTIONS[0],
    [exportQuality],
  );

  const activeClip = currentClip || allVideoClips[0] || null;
  const introDurForSubtitles = bookendTimeline.introDur || 0;
  const spokenScriptClip = (() => {
    if (isPlayheadBeforeIntroBookend(introDurForSubtitles, visualCurrentTime)) {
      return currentClip?.isBookend ? currentClip : null;
    }
    return allVideoClips.find((clip) =>
      timelineTimeMatchesClipRange(
        visualCurrentTime,
        (Number(clip.startTime) || 0) + introDurForSubtitles,
        clipVideoTimelineDuration(clip),
      ),
    ) || (currentClip?.isBookend ? null : currentClip) || activeClip;
  })();
  const spokenScriptClipRef = useRef(spokenScriptClip);
  spokenScriptClipRef.current = spokenScriptClip;
  const adaptScriptTargetClip = (() => {
    if (selectedTimelineClip?.type === 'video') {
      const picked = allVideoClips.find((c) => c.id === selectedTimelineClip.id);
      if (picked?.videoUrl) return picked;
    }
    if (spokenScriptClip?.videoUrl) return spokenScriptClip;
    if (activeClip?.videoUrl) return activeClip;
    return allVideoClips.find((c) => c.videoUrl && (c.narrationText || c.sceneText || '').trim()) || null;
  })();
  const rollingAdaptTargetClip = adaptScriptTargetClip || spokenScriptClip || activeClip;
  const canAdaptScriptToVideoDuration = Boolean(
    isNarrationEditor
      && projectId
      && adaptScriptTargetClip?.sceneId
      && adaptScriptTargetClip?.videoUrl
      && (adaptScriptTargetClip.narrationText || adaptScriptTargetClip.sceneText || '').trim(),
  );
  const playheadOverlayLeftPx = Math.max(
    TIMELINE_LABEL_WIDTH_PX,
    TIMELINE_LABEL_WIDTH_PX + (visualCurrentTime * pxPerSec) - timelineScrollLeft,
  );
  const pendingVideoEntriesToolbar = pendingVideoClipEntries();
  const regenerateVideoEntriesToolbar = regenerateVideoClipEntries();
  const batchTimelineVideoTargets = pendingVideoEntriesToolbar.length
    ? pendingVideoEntriesToolbar
    : regenerateVideoEntriesToolbar;
  const batchTimelineVideoMode = pendingVideoEntriesToolbar.length ? 'generate' : 'regenerate';
  const batchTimelineVideoCount = batchTimelineVideoTargets.length;
  const pendingAudioCount = pendingAudioClipEntries().length;
  const allAudioCount = allAudioClipEntries().length;
  const batchProviderOptions = isNarrationEditor ? NARRATION_VIDEO_PROVIDER_OPTIONS : VIDEO_PROVIDER_OPTIONS;

  const collectEligibleExtendTargets = useCallback(() => {
    if (isPitchRecordingStory(story)) return [];
    if (!timelineActive.audio) return [];
    return videoTracksRef.current.flat()
      .filter((clip) => clip.videoUrl && clip.sceneId)
      .map((clip) => {
        const pairedAudio = findPairedAudioClipForVideoOnActiveLanes(
          clip,
          audioTracksRef.current,
          timelineActive,
        );
        const extendGap = getVideoExtendGapForClip(clip, pairedAudio, masterAudioPlaybackSpeedRef.current);
        return extendGap ? { clip, extendGap } : null;
      })
      .filter(Boolean)
      .sort((a, b) => (Number(a.clip.startTime) || 0) - (Number(b.clip.startTime) || 0));
  }, [timelineActive, story]);

  const eligibleExtendTargets = useMemo(() => {
    if (isPitchRecordingStory(story)) return [];
    if (!timelineActive.audio) return [];
    return videoTracks.flat()
      .filter((clip) => clip.videoUrl && clip.sceneId)
      .map((clip) => {
        const pairedAudio = findPairedAudioClipForVideoOnActiveLanes(clip, audioTracks, timelineActive);
        const extendGap = getVideoExtendGapForClip(clip, pairedAudio, masterAudioPlaybackSpeedRef.current);
        return extendGap ? { clip, extendGap } : null;
      })
      .filter(Boolean)
      .sort((a, b) => (Number(a.clip.startTime) || 0) - (Number(b.clip.startTime) || 0));
  }, [videoTracks, audioTracks, timelineActive, story, masterAudioPlaybackSpeed]);

  const eligibleExtendCount = eligibleExtendTargets.length;

  const openExtendVideoDialog = useCallback((clip, trackIdx) => {
    if (!clip?.sceneId) return;
    const pairedAudio = findPairedAudioClipForVideoOnActiveLanes(clip, audioTracks, timelineActive);
    const extendGap = getVideoExtendGapForClip(clip, pairedAudio, masterAudioPlaybackSpeed);
    if (!extendGap) return;
    const scene = (scenesSnapshot || []).find((s) => String(s.id) === String(clip.sceneId));
    const defaultPrompt = (
      clip.narrationText
      || clip.sceneText
      || scenePrimaryScriptText(scene)
      || ''
    ).trim();
    const defaultProv = isNarrationEditor
      ? (story?.input?.video_provider || 'minimax')
      : (story?.default_scene_video_provider || 'minimax');
    const extendable = new Set(['minimax', 'ltx', 'veo3', 'runway', 'seedance', 'seedance_2_5', 'chatgpt_astra', 'fable_5_1', 'inhouse', 'narration_slideshow']);
    setExtendVideoProvider(extendable.has(defaultProv) ? defaultProv : 'minimax');
    setExtendVideoPrompt(defaultPrompt);
    setExtendVideoDialog({ clip, trackIdx, extendGap });
  }, [audioTracks, isNarrationEditor, masterAudioPlaybackSpeed, scenesSnapshot, story?.default_scene_video_provider, story?.input?.video_provider, timelineActive]);

  useEffect(() => {
    if (!extendVideoDialog) {
      setExtendVideoFramePreview(null);
      setExtendVideoFrameCaptureError(null);
      setExtendVideoFrameCapturing(false);
      return undefined;
    }
    const { clip, extendGap } = extendVideoDialog;
    if (!clip?.videoUrl || !extendGap) {
      setExtendVideoFramePreview(null);
      setExtendVideoFrameCaptureError(null);
      setExtendVideoFrameCapturing(false);
      return undefined;
    }

    let cancelled = false;
    const trimEnd = Number(extendGap.trimEndSource) || 0;
    const trimStart = Number(extendGap.trimStart) || 0;
    const seekTime = Math.max(trimStart + 0.02, trimEnd - 0.08);

    setExtendVideoFrameCapturing(true);
    setExtendVideoFrameCaptureError(null);
    setExtendVideoFramePreview(null);

    grabVideoFrameDataUrl(clip.videoUrl, seekTime).then((dataUrl) => {
      if (cancelled) return;
      setExtendVideoFrameCapturing(false);
      if (dataUrl) {
        setExtendVideoFramePreview(dataUrl);
        setExtendVideoFrameCaptureError(null);
        return;
      }
      const scene = (scenesSnapshot || []).find((s) => String(s.id) === String(clip.sceneId));
      const imageFallback = mediaUrlForPlayback(clip.imageUrl)
        || sceneDocumentMediaPlayback(scene).imageUrl
        || null;
      if (imageFallback) {
        setExtendVideoFramePreview(imageFallback);
        setExtendVideoFrameCaptureError(null);
        return;
      }
      setExtendVideoFramePreview(null);
      setExtendVideoFrameCaptureError(
        'Could not load a preview for this clip. You can still regenerate — the server uses the last frame of the trimmed video.',
      );
    }).catch(() => {
      if (cancelled) return;
      setExtendVideoFrameCapturing(false);
      const scene = (scenesSnapshot || []).find((s) => String(s.id) === String(clip.sceneId));
      const imageFallback = mediaUrlForPlayback(clip.imageUrl)
        || sceneDocumentMediaPlayback(scene).imageUrl
        || null;
      if (imageFallback) {
        setExtendVideoFramePreview(imageFallback);
        setExtendVideoFrameCaptureError(null);
        return;
      }
      setExtendVideoFramePreview(null);
      setExtendVideoFrameCaptureError(
        'Could not load a preview for this clip. You can still regenerate — the server uses the last frame of the trimmed video.',
      );
    });

    return () => {
      cancelled = true;
    };
  }, [extendVideoDialog, scenesSnapshot]);

  const handleExtendVideoFromLastFrame = async () => {
    if (!extendVideoDialog?.clip?.sceneId) return;
    const { clip } = extendVideoDialog;
    const pairedAudio = findPairedAudioClipForVideoOnActiveLanes(clip, audioTracks, timelineActive);
    const extendGap = getVideoExtendGapForClip(clip, pairedAudio, masterAudioPlaybackSpeed) || extendVideoDialog.extendGap;
    if (!extendGap) {
      toast.error('No active audio clip to match for this segment');
      return;
    }
    setExtendVideoBusy(true);
    try {
      await runExtendVideoForClip(clip, extendGap, {
        provider: extendVideoProvider,
        prompt: extendVideoPrompt.trim() || undefined,
      });
      setExtendVideoDialog(null);
      toast.success('Video extended to match audio length');
    } catch (err) {
      toast.error(err.response?.data?.detail || err.message || 'Could not extend video');
    } finally {
      setExtendVideoBusy(false);
    }
  };

  const runExtendVideoForClip = async (clip, extendGap, { provider, prompt } = {}) => {
    if (!clip?.sceneId || !extendGap) {
      throw new Error('Clip is not eligible for extend');
    }
    const payload = {
      provider: provider || 'minimax',
      target_duration_seconds: extendGap.audioSec,
      trim_start_seconds: extendGap.trimStart,
      trim_end_seconds: extendGap.trimEndSource,
      playback_speed: extendGap.playbackSpeed,
      prompt: prompt || undefined,
    };
    const url = isNarrationEditor && projectId
      ? `/narration/projects/${projectId}/segments/${clipSceneSourceId(clip)}/extend-video-from-last-frame`
      : `/scenes/${clipSceneSourceId(clip)}/extend-video-from-last-frame`;
    const { data } = await api.post(url, payload);
    if (!data?.job_id) throw new Error('No job id returned');
    toast.info('Extending video from last frame…');
    await waitForRegenerationJob(data.job_id);
    if (isNarrationEditor && projectId) {
      const segment = await loadNarrationSegment(clipSceneSourceId(clip), { skipVideoTrackSync: true });
      await applyExtendedVideoToTimeline(clipSceneSourceId(clip), segment, extendGap);
      return segment;
    }
    const scene = await loadStoryScene(clipSceneSourceId(clip), { skipVideoTrackSync: true });
    await applyExtendedVideoToTimeline(clipSceneSourceId(clip), scene, extendGap);
    return scene;
  };

  const handleExtendAllEligibleVideos = async () => {
    const provider = EXTEND_VIDEO_PROVIDER_IDS.has(batchVideoProvider)
      ? batchVideoProvider
      : 'minimax';
    setExtendingAllVideos(true);
    let completed = 0;
    try {
      while (true) {
        const targets = collectEligibleExtendTargets();
        if (!targets.length) break;
        const { clip, extendGap } = targets[0];
        setExtendAllProgress({
          current: completed,
          total: completed + targets.length,
          name: clip.name || clip.sceneId,
        });
        const scene = (scenesSnapshot || []).find((s) => String(s.id) === String(clip.sceneId));
        const defaultPrompt = (
          clip.narrationText
          || clip.sceneText
          || scenePrimaryScriptText(scene)
          || ''
        ).trim();
        toast.info(`Extending ${clip.name || 'segment'}…`);
        await runExtendVideoForClip(clip, extendGap, { provider, prompt: defaultPrompt || undefined });
        completed += 1;
      }
      if (completed > 0) {
        toast.success(`Extended ${completed} video${completed === 1 ? '' : 's'} to match audio`);
      } else {
        toast.info('No segments need extending');
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || err.message || 'Bulk extend failed');
    } finally {
      setExtendingAllVideos(false);
      setExtendAllProgress({ current: 0, total: 0, name: '' });
    }
  };

  const activeSubtitleCue = useMemo(() => {
    if (!timelineActive.subtitle) return null;
    const t = visualCurrentTime;
    const introDur = bookendTimeline.introDur || 0;
    const activeMatch = findActiveSubtitleCueOnActiveLanes(subtitleTracks, timelineActive, t, introDur);
    if (activeMatch?.cue) return activeMatch.cue;
    if (isPlayheadBeforeIntroBookend(introDur, t)) return null;
    const clip = spokenScriptClip;
    if (!clip || clip.isBookend) return null;
    const sid = normalizeEditorSceneId(clip.sceneId || clip.id);
    if (!sid) return null;
    const scene = (scenesSnapshot || []).find((s) => String(s.id) === sid);
    const text = resolveSubtitleDisplayText(null, null, scene, clip);
    if (!text) return null;
    return {
      id: `subtitle-preview-${clip.id}`,
      sceneId: sid,
      videoClipId: clip.id,
      startTime: (Number(clip.startTime) || 0) + introDur,
      duration: clipVideoTimelineDuration(clip),
      text,
      muted: false,
    };
  }, [timelineActive.subtitle, visualCurrentTime, subtitleTracks, spokenScriptClip, scenesSnapshot, bookendTimeline.introDur]);

  /** True when scrubbed playhead is inside a cue on Audio 1. */
  const subtitleLookEnabled = timelineActive.subtitleLook !== false;
  const subtitleLookMinimized = Boolean(timelineActive.subtitleLookMinimized);

  const activeSubtitleText = useMemo(() => {
    if (!activeSubtitleCue || activeSubtitleCue.muted) return '';
    const activeMatch = findActiveSubtitleCueOnActiveLanes(
      subtitleTracks,
      timelineActive,
      visualCurrentTime,
      bookendTimeline.introDur || 0,
    );
    const laneIndex = activeMatch?.laneIndex ?? 0;
    const audioLane = (audioTracks || [])[laneIndex] || [];
    const ac = audioLane.find((a) => a.id === activeSubtitleCue.audioClipId);
    const sid = normalizeEditorSceneId(activeSubtitleCue.sceneId || ac?.sceneId);
    const scene = sid ? (scenesSnapshot || []).find((s) => String(s.id) === sid) : null;
    const videoClip = findVideoClipForScene(
      videoTracks,
      sid,
      activeSubtitleCue.videoClipId,
    ) || spokenScriptClip;
    return resolveSubtitleDisplayText(activeSubtitleCue, ac, scene, videoClip);
  }, [activeSubtitleCue, audioTracks, scenesSnapshot, videoTracks, spokenScriptClip, subtitleTracks, timelineActive, visualCurrentTime, bookendTimeline.introDur]);

  const subtitleOverlayPreviewText = useMemo(() => {
    if (activeSubtitleText) return activeSubtitleText;
    const introDur = bookendTimeline.introDur || 0;
    if (isPlayheadBeforeIntroBookend(introDur, visualCurrentTime)) return '';
    const clip = spokenScriptClip || (currentClip?.isBookend ? null : currentClip) || allVideoClips[0] || null;
    if (clip && !clip.isBookend) {
      const sid = normalizeEditorSceneId(clip.sceneId || clip.id);
      const scene = sid ? (scenesSnapshot || []).find((s) => String(s.id) === sid) : null;
      const fromDisplay = resolveSubtitleDisplayText(null, null, scene, clip);
      if (fromDisplay) return fromDisplay;
    }
    const activeMatch = findActiveSubtitleCueOnActiveLanes(
      subtitleTracks,
      timelineActive,
      visualCurrentTime,
      introDur,
    );
    if (activeMatch?.cue) {
      const laneIndex = activeMatch.laneIndex;
      const ac = (audioTracks || [])[laneIndex]?.find((a) => a.id === activeMatch.cue.audioClipId);
      const sid = normalizeEditorSceneId(activeMatch.cue.sceneId || ac?.sceneId);
      const scene = sid ? (scenesSnapshot || []).find((s) => String(s.id) === sid) : null;
      const vc = findVideoClipForScene(videoTracks, sid, activeMatch.cue.videoClipId);
      const fromCue = resolveSubtitleDisplayText(activeMatch.cue, ac, scene, vc);
      if (fromCue) return fromCue;
    }
    return '';
  }, [
    activeSubtitleText,
    spokenScriptClip,
    currentClip,
    allVideoClips,
    scenesSnapshot,
    subtitleTracks,
    audioTracks,
    videoTracks,
    timelineActive,
    visualCurrentTime,
    bookendTimeline.introDur,
  ]);

  const subtitleSpokenPreview = useMemo(() => {
    if (!subtitleStyle?.spokenWordWindow || !String(subtitleOverlayPreviewText || '').trim()) {
      return { tokens: null, text: subtitleOverlayPreviewText };
    }
    const cueStart = Number(activeSubtitleCue?.startTime) || 0;
    const clock = Math.max(0, visualCurrentTime - cueStart);
    const sid = normalizeEditorSceneId(activeSubtitleCue?.sceneId);
    const scene = sid ? (scenesSnapshot || []).find((s) => String(s.id) === sid) : null;
    const timed = alignSpokenWords(
      subtitleOverlayPreviewText,
      scene?.word_timestamps,
      Math.max(0.2, Number(activeSubtitleCue?.duration) || 3),
    );
    const window = spokenWordWindowAt(timed, clock, subtitleStyle.spokenWordLimit);
    if (!window.tokens.length) return { tokens: null, text: subtitleOverlayPreviewText };
    return { tokens: window.tokens, text: window.text };
  }, [
    subtitleStyle?.spokenWordWindow,
    subtitleStyle?.spokenWordLimit,
    subtitleOverlayPreviewText,
    activeSubtitleCue,
    visualCurrentTime,
    scenesSnapshot,
  ]);

  const resolvedSubtitleLayer = useMemo(() => {
    if (subtitleStyle?.layer) return subtitleStyle.layer;
    return subtitleLayerFromLegacyAnchors(
      subtitleStyle.anchorX,
      subtitleStyle.anchorY,
      exportAspectRatio,
    );
  }, [
    subtitleStyle.layer,
    subtitleStyle.anchorX,
    subtitleStyle.anchorY,
    exportAspectRatio,
  ]);

  const subtitlePreviewLayout = useMemo(() => {
    const fw = previewContentRect.width || previewStageSize.w;
    const fh = previewContentRect.height || previewStageSize.h;
    return computeSubtitleLayerTextLayout({
      text: subtitleOverlayPreviewText,
      layer: resolvedSubtitleLayer,
      style: subtitleStyle,
      frameW: fw,
      frameH: fh,
      aspectRatio: exportAspectRatio,
      viewportMin,
    });
  }, [
    previewContentRect.width,
    previewContentRect.height,
    previewStageSize.w,
    previewStageSize.h,
    resolvedSubtitleLayer,
    subtitleStyle.fontSize,
    subtitleStyle.outlineWidth,
    subtitleStyle.color,
    subtitleStyle.bold,
    subtitleStyle.outlineColor,
    exportAspectRatio,
    viewportMin,
    subtitleOverlayPreviewText,
  ]);

  const hasAnySubtitleWithText = useMemo(() => {
    if (!timelineActive.subtitle) return false;
    const rowHas = (subtitleTracks || []).some((track) =>
      (track || []).some((c) => !c.muted && String(c.text || '').trim()),
    );
    if (rowHas) return true;
    if ((scenesSnapshot || []).some((s) => String(sceneSubtitleScriptText(s) || '').trim())) {
      return true;
    }
    return allVideoClips.some(
      (c) => String(c.narrationText || c.sceneText || '').trim(),
    );
  }, [subtitleTracks, timelineActive.subtitle, scenesSnapshot, allVideoClips]);

  const exportFramingShowsSubtitles = Boolean(
    timelineActive.subtitle
    && (hasAnySubtitleWithText || String(subtitleOverlayPreviewText || '').trim()),
  );

  const handleExportClipVideoToSubtitleChange = useCallback(
    (checked) => {
      setExportClipVideoToSubtitle(checked);
    },
    [],
  );

  const subtitlePreviewIsActiveMoment = Boolean(
    timelineActive.subtitle && String(subtitleOverlayPreviewText || '').trim(),
  );

  const previewMediaClip = currentClip || spokenScriptClip || allVideoClips[0] || null;

  const showSubtitlePreviewOverlay = Boolean(
    timelineActive.subtitle
    && (hasAnySubtitleWithText || String(subtitleOverlayPreviewText || '').trim())
    && (previewMediaClip?.videoUrl || previewMediaClip?.imageUrl),
  );

  const replacementSnippetScene = useMemo(() => {
    if (isNarrationEditor || !replacementDialog?.sourceId) return null;
    return (scenesSnapshot || []).find((s) => String(s.id) === String(replacementDialog.sourceId)) || null;
  }, [isNarrationEditor, replacementDialog?.sourceId, scenesSnapshot]);

  const [subtitleSnapToGrid, setSubtitleSnapToGrid] = useState(false);

  const handleSubtitleLayerChange = useCallback((layer) => {
    const anchors = subtitleLayerToExportAnchors(layer);
    setSubtitleStyle((s) => ({
      ...s,
      layer,
      anchorX: anchors.anchorX,
      anchorY: anchors.anchorY,
    }));
  }, []);

  const openSubtitleSettings = useCallback(() => {
    setSubtitleSettingsOpen(true);
    setTimelineActive((prev) => ({
      ...prev,
      subtitle: true,
      subtitleLook: true,
      subtitleLookMinimized: false,
    }));
  }, []);

  const handleSubtitleLookChange = useCallback((next) => {
    setSubtitleStyle(normalizeVideoSubtitleStyle(next, exportAspectRatio));
  }, [exportAspectRatio]);

  const handleActiveSubtitleTextChange = useCallback((text) => {
    const cueId = activeSubtitleCue?.id;
    if (!cueId) return;
    setSubtitleTracks((rows) => {
      const next = JSON.parse(JSON.stringify(rows || [[]]));
      const row = (next || [])[0] || [];
      const idx = row.findIndex((c) => c.id === cueId);
      if (idx < 0) return rows;
      row[idx] = { ...row[idx], text: String(text || '').trim(), manualEdit: true };
      next[0] = row;
      return next;
    });
  }, [activeSubtitleCue?.id]);

  const narrationLockedAspectRatio = useMemo(() => {
    if (!isNarrationEditor || !story) return null;
    return resolveNarrationProjectAspectRatio(story);
  }, [isNarrationEditor, story]);

  useEffect(() => {
    if (!narrationLockedAspectRatio) return;
    setExportAspectRatio(narrationLockedAspectRatio);
  }, [narrationLockedAspectRatio]);

  const exportAspectRatioPrevRef = useRef(exportAspectRatio);
  useEffect(() => {
    if (exportAspectRatioPrevRef.current === exportAspectRatio) return;
    exportAspectRatioPrevRef.current = exportAspectRatio;
    setSubtitleStyle((s) => {
      const next = normalizeVideoSubtitleStyle(s, exportAspectRatio);
      return {
        ...next,
        layer: stretchSubtitleLayerToAspectMax(next.layer, exportAspectRatio),
      };
    });
    setExportFraming((prev) => centerExportFraming(prev));
  }, [exportAspectRatio]);

  useEffect(() => {
    if (!exportDialogOpen) return;
    setExportFraming((prev) => ensureExportFramingCentered(prev));
  }, [exportDialogOpen]);

  useEffect(() => {
    setTranscriptionDraft(activeClip?.narrationText || '');
  }, [activeClip?.id, activeClip?.narrationText]);

  useEffect(() => {
    if (!spokenScriptClip?.id) return;
    const node = scriptSectionRefs.current.get(spokenScriptClip.id);
    node?.scrollIntoView({ behavior: 'smooth', block: 'center' });
  }, [spokenScriptClip?.id]);

  useEffect(() => {
    if (playback.isPlaying) return undefined;
    const t = playback.getTransportTime?.() ?? playback.currentTime;
    visualCurrentTimeRef.current = t;
    setVisualCurrentTime(t);
    return undefined;
  }, [playback.isPlaying, playback.getTransportTime, playback.currentTime]);

  const saveNewsPackToServer = async () => {
    if (!isNewsVideoEditor || !projectId) return;
    setSavingNewsPack(true);
    try {
      await api.patch(`/narration/projects/${projectId}/news-pack`, newsPackDraft);
      setStory((s) => (s ? { ...s, news_pack: { ...newsPackDraft } } : s));
      toast.success('News overlays saved');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not save news pack');
    } finally {
      setSavingNewsPack(false);
    }
  };

  const applyNarrationStoryBgMusicToTimeline = useCallback((narrProject) => {
    const musicUrl = narrProject?.music_url;
    if (!musicUrl) return;
    const clipEnd = timelineEndFromVideoClips(videoTracksRef.current.flat());
    const dur =
      Number(narrProject?.music_target_duration) > 0
        ? Number(narrProject.music_target_duration)
        : clipEnd || Number(narrProject?.total_duration) || 5;
    setMusicTracks(buildMusicTracksFromStoryUrl(musicUrl, dur, { narrationProjectId: projectId }));
    setTimelineActive((prev) => ({ ...prev, music: true }));
    setStory((prev) => ({
      ...(prev || {}),
      music_url: musicUrl,
      music_mood: narrProject?.music_mood ?? prev?.music_mood,
      music_prompt: narrProject?.music_prompt ?? prev?.music_prompt,
      music_history: narrProject?.music_history || prev?.music_history || [],
      music_target_duration: narrProject?.music_target_duration ?? prev?.music_target_duration,
    }));
  }, [projectId]);

  /** Keep timeline BGM clip URL in sync with story (popup uses story.music_url; lane can hold a stale port). */
  useEffect(() => {
    const href = story?.music_url;
    if (!href) return;
    const resolved = mediaUrlForPlayback(href);
    setMusicTracks((prev) => {
      let changed = false;
      const next = prev.map((track) => track.map((clip) => {
        if (clip?.id !== 'story-background-music' && clip?.source !== 'background_music') return clip;
        if (clip.url === resolved) return clip;
        changed = true;
        return { ...clip, url: resolved, source: 'background_music', muted: false };
      }));
      return changed ? next : prev;
    });
  }, [story?.music_url]);

  const canGenerateBgMusic = Boolean(
    (isNarrationEditor && projectId) || (!isNarrationEditor && storyId),
  );

  const openBgMusicDialog = useCallback((clip) => {
    if (!canGenerateBgMusic) return;
    if (clip?.id && clip.id !== 'story-background-music') return;
    setBgMusicPrompt(story?.music_prompt || '');
    setBgMusicProvider(story?.music_provider || DEFAULT_BG_MUSIC_PROVIDER);
    setBgMusicDialogOpen(true);
  }, [canGenerateBgMusic, story?.music_prompt, story?.music_provider]);

  const applyBgMusicFromNarrationProject = useCallback((narrProject, promptFallback = '') => {
    if (!narrProject?.music_url) return false;
    applyNarrationStoryBgMusicToTimeline(narrProject);
    setBgMusicPrompt(narrProject?.music_prompt || promptFallback || '');
    setStory((prev) => ({
      ...(prev || {}),
      music_url: narrProject.music_url,
      music_mood: narrProject.music_mood,
      music_prompt: narrProject.music_prompt,
      music_provider: narrProject.music_provider,
      music_history: narrProject.music_history || [],
      music_target_duration: narrProject.music_target_duration,
    }));
    return true;
  }, [applyNarrationStoryBgMusicToTimeline]);

  const applyBgMusicFromStoryDoc = useCallback((storyDoc, promptFallback = '') => {
    const musicUrl = storyDoc?.music_url;
    if (!musicUrl) return false;
    const clipEnd = timelineEndFromVideoClips(videoTracksRef.current.flat());
    const dur =
      Number(storyDoc?.music_target_duration) > 0
        ? Number(storyDoc.music_target_duration)
        : clipEnd || Number(storyDoc?.total_duration) || 5;
    setMusicTracks(buildMusicTracksFromStoryUrl(musicUrl, dur));
    setTimelineActive((prev) => ({ ...prev, music: true }));
    setBgMusicPrompt(storyDoc?.music_prompt || promptFallback || '');
    setStory((prev) => ({
      ...(prev || {}),
      music_url: musicUrl,
      music_mood: storyDoc?.music_mood ?? prev?.music_mood,
      music_prompt: storyDoc?.music_prompt ?? prev?.music_prompt ?? promptFallback,
      music_provider: storyDoc?.music_provider ?? prev?.music_provider,
      music_history: storyDoc?.music_history || prev?.music_history || [],
      music_target_duration: storyDoc?.music_target_duration ?? prev?.music_target_duration ?? dur,
    }));
    return true;
  }, []);

  const pollBgMusicJobInBackground = useCallback(async (jobId, promptFallback = '') => {
    try {
      await waitForRegenerationJob(jobId);
      if (isNarrationEditor && projectId) {
        const { data: narrProject } = await api.get(`/narration/projects/${projectId}`);
        if (applyBgMusicFromNarrationProject(narrProject, promptFallback)) {
          toast.success('Background music ready — applied to the Music lane');
        } else {
          toast.error('Background music job finished but no track was saved');
        }
      } else if (storyId) {
        const { data: storyDoc } = await api.get(`/stories/${storyId}`);
        if (applyBgMusicFromStoryDoc(storyDoc, promptFallback)) {
          toast.success('Background music ready — applied to the Music lane');
        } else {
          toast.error('Background music job finished but no track was saved');
        }
      }
    } catch (err) {
      toast.error(err.message || 'Background music generation failed');
    } finally {
      setGeneratingBgMusic(false);
    }
  }, [
    applyBgMusicFromNarrationProject,
    applyBgMusicFromStoryDoc,
    isNarrationEditor,
    projectId,
    storyId,
  ]);

  const bgMusicVersionOptions = useMemo(() => {
    const current = story?.music_url;
    const history = Array.isArray(story?.music_history) ? story.music_history : [];
    const byUrl = new Map();
    history.forEach((entry) => {
      if (entry?.url) byUrl.set(entry.url, { ...entry, active: false });
    });
    if (current) {
      byUrl.set(current, { ...(byUrl.get(current) || {}), url: current, active: true, source: 'current' });
    }
    return [...byUrl.values()].sort(
      (a, b) => new Date(b.created_at || 0) - new Date(a.created_at || 0),
    );
  }, [story?.music_url, story?.music_history]);

  const saveClipScriptToServer = async (clip, rawText, { invalidateAudio = false } = {}) => {
    const text = (rawText || '').trim();
    if (!text) {
      toast.error('Script text cannot be empty');
      return false;
    }
    const segmentId = clipSceneSourceId(clip);
    if (!segmentId) {
      toast.error('This clip is not linked to a scene');
      return false;
    }
    try {
      const estimateDur = clipSourcePlaybackDuration(clip);
      const wordTs = estimateWordTimestamps(text, estimateDur);

      if (isNarrationEditor && projectId) {
        await api.patch(`/narration/projects/${projectId}/segments/${segmentId}`, {
          text,
          visual: text,
        });
        const matchesSavedClip = (c) =>
          c.id === clip.id || clipMatchesSceneSource(c, segmentId);
        setVideoTracks((prev) =>
          prev.map((track) =>
            track.map((c) =>
              matchesSavedClip(c)
                ? {
                  ...c,
                  narrationText: text,
                  sceneText: text,
                  wordTimestamps: wordTs,
                  ...(invalidateAudio ? { audioUrl: null } : {}),
                }
                : c,
            ),
          ),
        );
        const nextAudioTracks = invalidateAudio
          ? audioTracksRef.current.map((track) =>
            track.map((ac) => {
              const sid = clipSceneSourceId(ac) || String(ac.id || '').replace(/^audio-/, '');
              if (sid !== segmentId) return ac;
              return { ...ac, url: null };
            }),
          )
          : audioTracksRef.current;
        if (invalidateAudio) {
          setAudioTracks(nextAudioTracks);
        }
        const nextNarrationScenes = scenesSnapshot.map((s) =>
          (String(s.id) === segmentId
            ? {
              ...s,
              text,
              authored_script: text,
              narration_text: text,
              scene_text: text,
              transcription_text: text,
              word_timestamps: wordTs,
              ...(invalidateAudio ? { audio_url: null, audio_duration: null } : {}),
            }
            : s),
        );
        setScenesSnapshot(nextNarrationScenes);
        setSubtitleTracks(rebuildSubtitlesAfterSceneScriptSave(
          nextAudioTracks,
          nextNarrationScenes,
          segmentId,
          text,
          clip,
        ));
        toast.success(
          invalidateAudio
            ? 'Segment script saved. Subtitles updated — regenerate audio if needed.'
            : 'Transcript saved',
        );
        return true;
      }

      await api.put(`/scenes/${clip.sceneId}`, { narration_text: text });
      const matchesSavedStoryClip = (c) =>
        c.id === clip.id || (clip.sceneId != null && c.sceneId === clip.sceneId);
      setVideoTracks((prev) =>
        prev.map((track) =>
          track.map((c) =>
            matchesSavedStoryClip(c)
              ? { ...c, narrationText: text, sceneText: text, wordTimestamps: wordTs }
              : c,
          ),
        ),
      );
      const nextStoryScenes = scenesSnapshot.map((s) =>
        (s.id === clip.sceneId
          ? {
            ...s,
            narration_text: text,
            transcription_text: text,
            word_timestamps: wordTs,
          }
          : s),
      );
      setScenesSnapshot(nextStoryScenes);
      setSubtitleTracks(rebuildSubtitlesAfterSceneScriptSave(
        audioTracksRef.current,
        nextStoryScenes,
        clip.sceneId,
        text,
        clip,
      ));
      toast.success(invalidateAudio ? 'Script saved' : 'Transcript saved');
      return true;
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save script');
      return false;
    }
  };

  const handleSaveTranscription = async () => {
    if (!activeClip?.sceneId) {
      toast.error('Select a scene clip first');
      return;
    }
    try {
      setSavingTranscription(true);
      await saveClipScriptToServer(activeClip, transcriptionDraft, { invalidateAudio: false });
    } finally {
      setSavingTranscription(false);
    }
  };

  const cancelRollingScriptEdit = () => {
    setRollingScriptEditClipId(null);
    setRollingScriptDraft('');
  };

  const handleAdaptScriptToVideoDuration = async () => {
    if (!isNarrationEditor || !projectId) return;
    const clip = adaptScriptTargetClip || rollingAdaptTargetClip;
    if (!clip?.sceneId) {
      toast.error('Select a video clip on the timeline (click it or move the playhead over it).');
      return;
    }
    if (!clip.videoUrl) {
      toast.error('This segment needs a generated video URL before matching duration.');
      return;
    }
    const scriptSrc = (clip.narrationText || clip.sceneText || '').trim();
    if (!scriptSrc) {
      toast.error('No script text on this segment to adapt.');
      return;
    }
    setMatchingScriptToVideoDuration(true);
    try {
      const rollingAudio = defaultReplacementOverrides(story, isNarrationEditor);
      const payload = {
        source_text: scriptSrc,
        persist_and_regenerate_audio: adaptScriptThenRegenerateAudio,
        use_timeline_target_seconds: false,
        tts_provider: rollingRegenerateTtsProvider || rollingAudio.tts_provider,
        voice_style: rollingRegenerateVoiceStyle || rollingAudio.voice_style,
        language: rollingAudio.language,
        narrator_elevenlabs_voice_id: rollingAudio.narrator_elevenlabs_voice_id || undefined,
        narrator_murf_voice_id: rollingAudio.narrator_murf_voice_id || undefined,
        narrator_murf_locale: rollingAudio.narrator_murf_locale || undefined,
        narrator_minimax_voice_id: rollingAudio.narrator_minimax_voice_id || undefined,
      };
      const { data } = await api.post(
        `/narration/projects/${projectId}/segments/${clipSceneSourceId(clip)}/adapt-script-to-video-duration`,
        payload,
      );
      if (adaptScriptThenRegenerateAudio && data?.audio_url != null) {
        applyRegeneratedNarrationSegment('audio', data);
        setRollingScriptEditClipId(null);
        setRollingScriptDraft('');
        toast.success(
          data.target_seconds_used != null
            ? `Script adapted (~${Number(data.target_seconds_used).toFixed(1)}s video). Audio regenerated.`
            : 'Script adapted and audio regenerated.',
        );
      } else {
        const next = (data.text || '').trim();
        if (!next) throw new Error('Empty adaptation');
        setRollingScriptEditClipId(clip.id);
        setRollingScriptDraft(next);
        toast.success(
          data.target_seconds_used != null
            ? `Draft updated for ~${Number(data.target_seconds_used).toFixed(1)}s video — review and save.`
            : 'Draft updated — review and save.',
        );
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || err.message || 'Could not adapt script');
    } finally {
      setMatchingScriptToVideoDuration(false);
    }
  };

  const handleWorkspaceSwitchScriptLanguage = async () => {
    if (!isNarrationEditor || !projectId || !workspaceScriptLangTarget) return;
    if (workspaceScriptLangTarget === narrationProjectLanguage) {
      toast.error('Pick a different language than the project’s current script language.');
      return;
    }
    setWorkspaceSwitchingLanguage(true);
    try {
      const { data } = await api.post(`/narration/projects/${projectId}/switch-script-language`, {
        language: workspaceScriptLangTarget,
        regenerate_audio: false,
        constrain_to_video_duration: workspaceSwitchFitVideoDuration,
        persist: false,
      });
      if (data?.persisted === false && Array.isArray(data.segments)) {
        setPendingLanguageCommit({
          language: data.language,
          segments: data.segments,
          regenerate_audio: workspaceSwitchRegenerateAudio,
        });
        toast.success(
          workspaceSwitchRegenerateAudio
            ? 'Translation ready (unsaved). Rolling Script and playback stay on the saved version until Save project; then the script commits and narration audio can regenerate.'
            : 'Translation ready (unsaved). Rolling Script and playback stay on the saved version until Save project.',
        );
      } else {
        await loadStory();
        toast.success('Script language updated.');
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || err.message || 'Could not switch script language');
    } finally {
      setWorkspaceSwitchingLanguage(false);
    }
  };

  const regenerateNarrationAudioForSegmentClip = async (clip, scriptOverride) => {
    if (!isNarrationEditor || !projectId) return false;
    const sourceId = clipSceneSourceId(clip);
    if (!sourceId) return false;
    const script = (scriptOverride ?? clip.narrationText ?? clip.sceneText ?? '').trim();
    if (!script) {
      toast.error('No script text for this segment');
      return false;
    }
    const defs = defaultReplacementOverrides(story, isNarrationEditor);
    setRegeneratingRollingAudio(true);
    try {
      const { data: updatedSegment } = await api.post(
        `/narration/projects/${projectId}/segments/${sourceId}/regenerate-audio`,
        buildNarrationAudioApiPayload(
          {
            ...defs,
            tts_provider: rollingRegenerateTtsProvider || defs.tts_provider,
            voice_style: rollingRegenerateVoiceStyle || defs.voice_style,
          },
          { prompt: script },
        ),
      );
      applyRegeneratedNarrationSegment('audio', updatedSegment);
      if (replacementDialog?.sourceId === sourceId) {
        await refreshReplacementOptions('audio', sourceId);
      }
      toast.success('Narration audio regenerated for this segment');
      return true;
    } catch (err) {
      toast.error(err.response?.data?.detail || err.message || 'Could not regenerate narration audio');
      return false;
    } finally {
      setRegeneratingRollingAudio(false);
    }
  };

  const regenerateStorySceneAudioForClip = async (clip, scriptOverride) => {
    if (isNarrationEditor || !storyId || !clip?.sceneId) return false;
    const script = (scriptOverride ?? clip.narrationText ?? clip.sceneText ?? '').trim();
    if (!script) return false;
    try {
      const defaults = defaultReplacementOverrides(story, false);
      const defs = {
        ...defaults,
        voice_style: rollingRegenerateVoiceStyle || defaults.voice_style,
        tts_provider: rollingRegenerateTtsProvider || defaults.tts_provider,
      };
      const scene = (scenesSnapshotRef.current || []).find(
        (row) => String(row.id) === String(clip.sceneId),
      ) || null;
      const payload = storySceneAudioGenerationPayload(defs, story, scene, script);
      const { data } = await api.post(
        `/stories/${storyId}/scenes/${clip.sceneId}/generate-audio`,
        payload,
      );
      await waitForRegenerationJob(data.job_id);
      await loadStoryScene(clip.sceneId);
      return true;
    } catch (err) {
      toast.error(err.response?.data?.detail || err.message || 'Could not regenerate scene audio');
      return false;
    }
  };

  const syncAudioVideoByRules = async () => {
    const sceneClips = videoTracks
      .flat()
      .filter((c) => c.sceneId)
      .sort((a, b) => (Number(a.startTime) || 0) - (Number(b.startTime) || 0));
    if (!sceneClips.length) {
      toast.info('No scene clips to sync.');
      return;
    }
    setSyncingTimingRules(true);
    let trimmed = 0;
    let adapted = 0;
    let skipped = 0;
    try {
      const referenceLane = resolveMagicEditReferenceAudioLane({
        audioTracks: audioTracksRef.current,
        audioTrackMeta: audioTrackMetaRef.current,
        timelineActive,
      }).laneIndex ?? 0;
      for (const clip of sceneClips) {
        const sourceId = String(clip.sceneId || '');
        const audioSec = sceneAudioDurationFromTimeline(
          sourceId,
          clip.startTime,
          audioTracksRef.current,
          referenceLane,
        );
        const videoSec = Math.max(0.01, Number(clipVideoTimelineDuration(clip)) || 0.01);
        if (!(audioSec > 0)) {
          skipped += 1;
          continue;
        }

        // Rule 1: if audio is shorter, trim visible video snippet to audio length.
        if (audioSec + 0.05 < videoSec) {
          const { clip: next, changed } = trimVideoClipToMatchAudioDuration(clip, audioSec);
          if (changed) {
            setVideoTracks((prev) =>
              prev.map((track) => track.map((c) => (c.id === clip.id ? next : c))),
            );
            trimmed += 1;
          } else {
            skipped += 1;
          }
          continue;
        }

        // Rule 2: if video is shorter, shorten script then regenerate scene audio and map back.
        if (audioSec > videoSec + 0.05) {
          const sourceScript = (clip.narrationText || clip.sceneText || '').trim();
          if (!sourceScript) {
            skipped += 1;
            continue;
          }
          const shorter = shortenScriptToTargetDuration(sourceScript, audioSec, videoSec);
          if (!shorter) {
            skipped += 1;
            continue;
          }
          const saveOk = await saveClipScriptToServer(clip, shorter);
          if (!saveOk) {
            skipped += 1;
            continue;
          }
          const refreshedClip = videoTracksRef.current.flat().find((c) => c.id === clip.id) || { ...clip, narrationText: shorter, sceneText: shorter };
          const regenOk = await regenerateStorySceneAudioForClip(refreshedClip, shorter);
          if (!regenOk) {
            skipped += 1;
            continue;
          }
          adapted += 1;
          continue;
        }
      }
      toast.success(`Sync done: ${trimmed} trimmed, ${adapted} adapted${skipped ? `, ${skipped} skipped` : ''}.`);
    } finally {
      setSyncingTimingRules(false);
    }
  };

  const regenerateAllNarrationSegmentsAudio = async () => {
    if (!isNarrationEditor || !projectId) return;
    const clips = videoTracks
      .flat()
      .filter((c) => c.sceneId && (c.narrationText || c.sceneText || '').trim());
    const seen = new Set();
    const ordered = clips
      .filter((c) => {
        const sid = String(c.sceneId);
        if (seen.has(sid)) return false;
        seen.add(sid);
        return true;
      })
      .sort((a, b) => (Number(a.startTime) || 0) - (Number(b.startTime) || 0));
    if (!ordered.length) {
      toast.info('No segments with script text to regenerate.');
      return;
    }
    const defs = defaultReplacementOverrides(story, isNarrationEditor);
    setRegeneratingRollingAudio(true);
    try {
      for (let i = 0; i < ordered.length; i += 1) {
        const clip = ordered[i];
        const script = (clip.narrationText || clip.sceneText || '').trim();
        const segmentId = clipSceneSourceId(clip);
        const { data: updatedSegment } = await api.post(
          `/narration/projects/${projectId}/segments/${segmentId}/regenerate-audio`,
          {
            prompt: script,
            tts_provider: rollingRegenerateTtsProvider || defs.tts_provider,
            voice_style: rollingRegenerateVoiceStyle || defs.voice_style,
            language: defs.language,
            narrator_elevenlabs_voice_id: defs.narrator_elevenlabs_voice_id || undefined,
            narrator_murf_voice_id: defs.narrator_murf_voice_id || undefined,
            narrator_murf_locale: defs.narrator_murf_locale || undefined,
            narrator_minimax_voice_id: defs.narrator_minimax_voice_id || undefined,
          },
        );
        applyRegeneratedNarrationSegment('audio', updatedSegment);
      }
      toast.success(`Regenerated narration audio for ${ordered.length} segment${ordered.length === 1 ? '' : 's'}.`);
    } catch (err) {
      toast.error(err.response?.data?.detail || err.message || 'Could not regenerate all narration audio');
    } finally {
      setRegeneratingRollingAudio(false);
    }
  };

  const regenerateAllStoryScenesAudio = async () => {
    if (isNarrationEditor || !storyId) return;
    const clips = videoTracks
      .flat()
      .filter((c) => c.sceneId && (c.narrationText || c.sceneText || '').trim());
    const seen = new Set();
    const ordered = clips
      .filter((c) => {
        const sid = String(c.sceneId);
        if (seen.has(sid)) return false;
        seen.add(sid);
        return true;
      })
      .sort((a, b) => (Number(a.startTime) || 0) - (Number(b.startTime) || 0));
    if (!ordered.length) {
      toast.info('No scenes with script text to regenerate.');
      return;
    }
    setRegeneratingRollingAudio(true);
    try {
      for (const clip of ordered) {
        await regenerateStorySceneAudioForClip(clip);
      }
      toast.success(`Generated audio for ${ordered.length} scene${ordered.length === 1 ? '' : 's'}.`);
    } catch {
      /* regenerateStorySceneAudioForClip toasts errors */
    } finally {
      setRegeneratingRollingAudio(false);
    }
  };

  const handleSaveRollingScript = async () => {
    if (!rollingScriptEditClipId) return;
    const clip = allVideoClips.find((c) => c.id === rollingScriptEditClipId);
    if (!clip?.sceneId) {
      toast.error('Could not find that clip');
      return;
    }
    const savedText = rollingScriptDraft.trim();
    try {
      setSavingRollingScript(true);
      const ok = await saveClipScriptToServer(clip, rollingScriptDraft, { invalidateAudio: true });
      if (ok) {
        if (regenerateAudioAfterRollingScriptSave) {
          if (isNarrationEditor && projectId) {
            await regenerateNarrationAudioForSegmentClip(
              { ...clip, narrationText: savedText, sceneText: savedText },
              savedText,
            );
          } else if (storyId) {
            await regenerateStorySceneAudioForClip(
              { ...clip, narrationText: savedText, sceneText: savedText },
              savedText,
            );
          }
        }
        setRollingScriptEditClipId(null);
        setRollingScriptDraft('');
        if (activeClip?.id === clip.id) setTranscriptionDraft(savedText);
      }
    } finally {
      setSavingRollingScript(false);
    }
  };

  const handleRetryNarrationPipeline = async () => {
    if (!projectId) return;
    setRetryingNarration(true);
    try {
      const { data } = await api.post(`/narration/projects/${projectId}/resume`);
      toast.success('Narration pipeline restarted');
      navigate('/tasks', {
        state: {
          resumedNarrationProjectId: projectId,
          resumedNarrationJobId: data.job_id,
        },
      });
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not restart narration pipeline');
    } finally {
      setRetryingNarration(false);
    }
  };

  const handleRetryStoryGeneration = async () => {
    if (!storyId) return;
    setRetryingNarration(true);
    try {
      await api.post(`/stories/${storyId}/resume`);
      toast.success('Story generation restarted — new character looks will be used');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not restart story generation');
    } finally {
      setRetryingNarration(false);
    }
  };

  const handleGenerateNarrationBackgroundMusic = async (opts = {}) => {
    if (!canGenerateBgMusic) return;
    const promptOverride = opts.musicPrompt;
    const trimmed =
      promptOverride != null ? String(promptOverride).trim() : String(bgMusicPrompt || '').trim();
    const provider = opts.provider || bgMusicProvider || DEFAULT_BG_MUSIC_PROVIDER;
    const waitForFinish = Boolean(opts.wait);
    setGeneratingBgMusic(true);
    try {
      const body = { provider };
      if (trimmed) body.music_prompt = trimmed;
      const endpoint = isNarrationEditor && projectId
        ? `/narration/projects/${projectId}/generate-background-music`
        : `/stories/${storyId}/generate-music`;
      const { data } = await api.post(endpoint, body);
      if (!data?.job_id) {
        throw new Error('Could not queue background music');
      }
      setBgMusicDialogOpen(false);
      if (data.already_queued) {
        toast.info('Background music is already queued — check Tasks for progress');
      } else {
        toast.success('Background music queued — check Tasks for progress');
      }
      if (waitForFinish) {
        await pollBgMusicJobInBackground(data.job_id, trimmed);
      } else {
        pollBgMusicJobInBackground(data.job_id, trimmed);
      }
    } catch (err) {
      const detail = err.response?.data?.detail;
      const msg =
        typeof detail === 'string'
          ? detail
          : Array.isArray(detail)
            ? detail.map((e) => e.msg || JSON.stringify(e)).join('; ')
            : err.message || 'Could not queue background music';
      toast.error(msg);
      setGeneratingBgMusic(false);
    }
  };

  const handleSelectBgMusicVersion = async (url) => {
    if (!url) return;
    setSelectingBgMusicUrl(url);
    try {
      if (isNarrationEditor && projectId) {
        const { data: narrProject } = await api.post(
          `/narration/projects/${projectId}/background-music/select`,
          { music_url: url },
        );
        applyNarrationStoryBgMusicToTimeline(narrProject);
      } else if (storyId) {
        applyBgMusicFromStoryDoc({
          ...(story || {}),
          music_url: url,
        });
      } else {
        return;
      }
      toast.success('Background music version applied');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not switch background music version');
    } finally {
      setSelectingBgMusicUrl(null);
    }
  };

  const handleSplitSelectedClipAtPlayhead = () => {
    recordEditorHistory();
    const curTime = visualCurrentTime || playback.currentTime;
    let targetClip = null;
    let targetTrackIdx = 0;
    let targetType = 'video';

    if (selectedTimelineClip) {
      targetClip = selectedTimelineClip;
      targetTrackIdx = selectedTimelineClip.trackIdx || 0;
      targetType = selectedTimelineClip.type || 'video';
    } else {
      // Find clip currently under playhead in video tracks
      videoTracks.forEach((tr, tIdx) => {
        tr.forEach((c) => {
          if (curTime > c.startTime + 0.1 && curTime < c.startTime + c.duration - 0.1) {
            targetClip = c;
            targetTrackIdx = tIdx;
            targetType = 'video';
          }
        });
      });
    }

    if (!targetClip) {
      toast.info('Position playhead inside a clip to split it, or select a clip.');
      return;
    }

    if (targetType === 'video' || targetType === 'image') {
      setVideoTracks((prev) => {
        const next = JSON.parse(JSON.stringify(prev));
        const tr = next[targetTrackIdx] || [];
        const cIdx = tr.findIndex((c) => c.id === targetClip.id);
        if (cIdx < 0) return prev;
        const clip = tr[cIdx];
        if (curTime <= clip.startTime + 0.2 || curTime >= clip.startTime + clip.duration - 0.2) {
          toast.info('Position playhead inside the clip boundaries to split.');
          return prev;
        }
        const firstDur = curTime - clip.startTime;
        const secDur = clip.duration - firstDur;
        const firstClip = {
          ...clip,
          duration: firstDur,
          playbackDuration: firstDur,
          trimEnd: (clip.trimStart || 0) + firstDur,
        };
        const secondClip = {
          ...clip,
          id: `${clip.id}-part2-${Date.now()}`,
          name: `${clip.name} (Pt 2)`,
          startTime: curTime,
          duration: secDur,
          playbackDuration: secDur,
          trimStart: (clip.trimStart || 0) + firstDur,
          trimEnd: clip.trimEnd || (clip.trimStart || 0) + clip.duration,
        };
        tr.splice(cIdx, 1, firstClip, secondClip);
        toast.success('Clip split into 2 parts.');
        return next;
      });
    } else if (targetType === 'audio') {
      setAudioTracks((prev) => {
        const next = JSON.parse(JSON.stringify(prev));
        const tr = next[targetTrackIdx] || [];
        const cIdx = tr.findIndex((c) => c.id === targetClip.id);
        if (cIdx < 0) return prev;
        const clip = tr[cIdx];
        if (curTime <= clip.startTime + 0.2 || curTime >= clip.startTime + clip.duration - 0.2) {
          toast.info('Position playhead inside the audio clip to split.');
          return prev;
        }
        const firstDur = curTime - clip.startTime;
        const secDur = clip.duration - firstDur;
        const firstClip = { ...clip, duration: firstDur };
        const secondClip = {
          ...clip,
          id: `${clip.id}-part2-${Date.now()}`,
          name: `${clip.name} (Pt 2)`,
          startTime: curTime,
          duration: secDur,
        };
        tr.splice(cIdx, 1, firstClip, secondClip);
        toast.success('Audio clip split.');
        return next;
      });
    }
  };

  const handleDeleteSelectedClip = () => {
    if (!selectedTimelineClip && selectedTimelineClips.size === 0) {
      toast.info('Select a clip on the timeline first to delete it.');
      return;
    }
    recordEditorHistory();
    if (selectedTimelineClip) {
      const { type, trackIdx = 0, id } = selectedTimelineClip;
      if (type === 'video' || type === 'image') {
        setVideoTracks((prev) => prev.map((tr, i) => (i === trackIdx ? tr.filter((c) => c.id !== id) : tr)));
        setSelectedTimelineClip(null);
        toast.success('Video clip deleted.');
      } else if (type === 'audio') {
        setAudioTracks((prev) => prev.map((tr, i) => (i === trackIdx ? tr.filter((c) => c.id !== id) : tr)));
        setSelectedTimelineClip(null);
        toast.success('Audio clip deleted.');
      } else if (type === 'music') {
        setMusicTracks((prev) => prev.map((tr, i) => (i === trackIdx ? tr.filter((c) => c.id !== id) : tr)));
        setSelectedTimelineClip(null);
        toast.success('Music clip deleted.');
      }
    }
  };

  let timelineSpeedSelection = null;
  if (selectedTimelineClip?.type === 'audio') {
    const clip = audioTracks.flat().find((c) => c.id === selectedTimelineClip.id);
    if (clip) timelineSpeedSelection = { kind: 'audio', clip };
  } else if (selectedTimelineClip?.type === 'video' || selectedTimelineClip?.type === 'image') {
    const sid = selectedTimelineClip.id;
    const rawId = String(sid).replace(/__image$/, '');
    for (const tr of videoTracks) {
      const found = tr.find((c) => c.id === rawId || c.id === sid || `${c.id}__image` === String(sid));
      if (found) {
        timelineSpeedSelection = { kind: 'video', clip: found };
        break;
      }
    }
  }

  let timelineVolumeSelection = null;
  if (selectedTimelineClip?.type === 'audio') {
    const clip = audioTracks.flat().find((c) => c.id === selectedTimelineClip.id);
    if (clip) timelineVolumeSelection = { lane: 'audio', clip };
  } else if (selectedTimelineClip?.type === 'music') {
    const clip = musicTracks.flat().find((c) => c.id === selectedTimelineClip.id);
    if (clip) timelineVolumeSelection = { lane: 'music', clip };
  }

  const timelineClipVolumeValue = timelineVolumeSelection
    ? clampTimelineClipVolume(
        timelineVolumeSelection.clip.volume != null
          ? timelineVolumeSelection.clip.volume
          : timelineVolumeSelection.lane === 'music'
            ? 0.22
            : 0.8,
      )
    : 0.8;

  const handleSnapshotFrame = () => {
    try {
      const video = playback.videoRef?.current;
      if (video) {
        const canvas = document.createElement('canvas');
        canvas.width = video.videoWidth || 1280;
        canvas.height = video.videoHeight || 720;
        const ctx = canvas.getContext('2d');
        ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
        const url = canvas.toDataURL('image/png');
        const a = document.createElement('a');
        a.href = url;
        a.download = `snapshot_${Math.round(playback.currentTime)}s.png`;
        a.click();
        toast.success('Snapshot downloaded');
      } else if (previewImageRef?.current) {
        const img = previewImageRef.current;
        const canvas = document.createElement('canvas');
        canvas.width = img.naturalWidth || 1280;
        canvas.height = img.naturalHeight || 720;
        const ctx = canvas.getContext('2d');
        ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
        const url = canvas.toDataURL('image/png');
        const a = document.createElement('a');
        a.href = url;
        a.download = `snapshot_${Math.round(playback.currentTime)}s.png`;
        a.click();
        toast.success('Snapshot downloaded');
      } else {
        toast.info('Snapshot captured');
      }
    } catch {
      toast.info('Snapshot captured');
    }
  };

  const togglePreviewFitMode = () => {
    setPreviewFitMode((prev) => (prev === 'cover' ? 'contain' : 'cover'));
    toast.info(previewFitMode === 'cover' ? 'Fit aspect ratio' : 'Fill full stage in place');
  };

  const handleExportVideo = async () => {
    if (isReelsShortsEditor && projectId) {
      try {
        setReelsExporting(true);
        await flushCloudEditorSave();
        toast.info('Queuing final MP4 from this layout…');
        await api.post(`/reels-shorts/projects/${projectId}/render`, {
          include_broll: reelsIncludeBrollRef.current !== false,
          include_subtitles: timelineActiveSubtitleRef.current !== false
            && reelsIncludeSubtitlesRef.current !== false,
        });
        toast.success('Final video started — stay here or open the REEL&SHORTS page for progress');
        navigate(`/reels-shorts/create?project=${projectId}`);
      } catch (err) {
        toast.error(formatApiError(err, 'Could not export the final reel'));
      } finally {
        setReelsExporting(false);
      }
    } else if (storyId || (isNarrationEditor && projectId)) {
      // The dialog's export sends the live timeline (animation, VFX, trims, filters,
      // subtitles, overlays) rather than whatever was last saved server-side.
      if (exportingFull) {
        toast.info(exportStatusMessage || 'Export already in progress');
        return;
      }
      if (exportableTimelineClipCount === 0) {
        toast.error('Turn on the image or video timeline and use hosted scene media — local-only previews cannot be exported.');
        return;
      }
      setExportDialogOpen(true);
    } else {
      toast.error('Open a story or narration project to export.');
    }
  };

  const leftSidebarItems = [
    {
      id: 'script',
      name: 'Script & Scenes',
      desc: 'View & edit scene narration script and details.',
      icon: MapIcon,
      actionName: 'Open Script',
      action: () => {
        if (currentClip) {
          setEditingClip({ clip: currentClip });
        } else {
          toast.info('Select a clip to edit script details');
        }
      },
    },
    {
      id: 'media',
      name: 'Media Library',
      desc: 'Browse project media and in-house stock BG music / SFX.',
      icon: FolderMinus,
      actionName: 'Open Library',
      action: () => setShowMediaLibrary(true),
    },
    {
      id: 'project',
      name: 'Project File',
      desc: 'Import or open an existing saved project JSON file.',
      icon: Folder,
      actionName: 'Open Project',
      action: () => openProjectInputRef.current?.click(),
    },
    {
      id: 'export',
      name: 'Export Video',
      desc: isReelsShortsEditor
        ? 'Save this layout and queue the 9:16 MP4.'
        : 'Compile all timeline tracks and render final MP4.',
      icon: Download,
      actionName: isReelsShortsEditor ? 'Export MP4' : 'Start Export',
      action: handleExportVideo,
    },
    {
      id: 'play',
      name: 'Play Preview',
      desc: 'Play or pause the sequential video and audio playback.',
      icon: PlayCircle,
      actionName: playback.isPlaying ? 'Pause Preview' : 'Play Preview',
      action: playback.togglePlay,
    },
    {
      id: 'camera',
      name: 'Capture Frame',
      desc: 'Take a snapshot PNG of the current player video frame.',
      icon: Camera,
      actionName: 'Save Snapshot',
      action: handleSnapshotFrame,
    },
    {
      id: 'director',
      name: 'Scenes Director',
      desc: 'Navigate back to the Scene Grid workspace.',
      icon: Film,
      actionName: 'Go to Scenes',
      action: async () => {
        if (!(await confirmLeaveEditor())) return;
        navigate(
          isReelsShortsEditor
            ? `/reels-shorts/create?project=${projectId}`
            : isNarrationEditor
            ? (isNewsRoute ? '/news/create' : `/narration/${projectId}/edit`)
            : `/stories/${storyId}/edit`,
        );
      },
    },
  ];

  const rightSidebarItems = [
    {
      id: 'vfx',
      name: 'Visual VFX',
      desc: 'Load atmospheric VFX layers and video effects.',
      icon: Film,
      actionName: 'Load VFX Demo',
      action: loadDemoVfxTimeline,
    },
    {
      id: 'magic',
      name: 'AI Magic Edit',
      desc: 'Auto-generate video animations for pending scenes.',
      icon: TwinneeLogo,
      actionName: 'Generate Videos',
      action: generatePendingVideos,
    },
    {
      id: 'audio',
      name: 'Narration Voice',
      desc: 'Generate or regenerate speech audio for scenes.',
      icon: Activity,
      actionName: 'Generate Voice',
      action: () => (isNarrationEditor ? regenerateAllNarrationSegmentsAudio() : regenerateAllStoryScenesAudio()),
    },
    {
      id: 'subtitles',
      name: 'Subtitles & Text',
      desc: 'Open subtitle size, color, and spoken-word highlight.',
      icon: FileText,
      actionName: 'Subtitle settings',
      action: openSubtitleSettings,
    },
    {
      id: 'music',
      name: 'Background Music',
      desc: 'Upload or generate background music soundtrack.',
      icon: Music,
      actionName: 'Upload Music',
      action: () => audioInputRef.current?.click(),
    },
    {
      id: 'addVideo',
      name: 'Add Video Track',
      desc: 'Add a new V-track lane for overlays.',
      icon: PlusSquare,
      actionName: 'Add V-Track',
      action: addVideoTrack,
    },
    {
      id: 'addAudio',
      name: 'Add Audio Track',
      desc: 'Add a new A-track lane for SFX or secondary audio.',
      icon: PlusSquare,
      actionName: 'Add A-Track',
      action: addAudioTrack,
    },
  ];

  return (
    <div className="flex flex-col w-full h-screen bg-[#070b0e] text-slate-300 font-sans select-none overflow-hidden relative" data-testid="video-editor-page">
      {/* ── Ambient SVG Gradient Glows ── */}
      <div
        className="pointer-events-none fixed top-0 left-0 w-[750px] lg:w-[1000px] z-0 select-none"
        style={{ opacity: 0.60, transform: 'translate(-15%, -15%)' }}
        dangerouslySetInnerHTML={{ __html: leftTopArrowGradient }}
      />
      <div
        className="pointer-events-none fixed bottom-0 right-0 w-[700px] lg:w-[950px] z-0 select-none"
        style={{ opacity: 0.60, transform: 'translate(15%, 15%)' }}
        dangerouslySetInnerHTML={{ __html: rightBottomArrowGradient }}
      />

      {engineSfxState.mode === 'updating' ? (
        <div
          className="absolute top-14 left-1/2 z-40 -translate-x-1/2 rounded-md border border-amber-400/40 bg-amber-500/15 px-3 py-1 text-xs font-medium text-amber-100"
          data-testid="engine-sfx-updating"
        >
          updating sound
        </div>
      ) : null}
      {engineSfxState.mode === 'failed' ? (
        <div
          className="absolute top-14 left-1/2 z-40 -translate-x-1/2 rounded-md border border-red-400/40 bg-red-500/15 px-3 py-1 text-xs font-medium text-red-100"
          data-testid="engine-sfx-failed"
        >
          sound mix failed
        </div>
      ) : null}
      <EditorTopNav
        active="editor"
        mode={isReelsShortsEditor ? 'reels_shorts' : isNarrationEditor ? 'narration' : 'story'}
        projectId={projectId}
        storyId={storyId}
        title={story?.title || ''}
        loading={loading}
        onBeforeNavigate={async () => {
          if (!(await confirmLeaveEditor())) return false;
          return true;
        }}
        onBack={async () => {
              if (!(await confirmLeaveEditor())) return;
              navigate(
            isReelsShortsEditor && projectId
              ? `/reels-shorts/create?project=${projectId}`
              : '/dashboard',
          );
        }}
        actions={(
          <>
            <CreateAgentBackButton
              projectId={projectId || storyId}
              size="sm"
              label={<span className="hidden sm:inline">Back to agent</span>}
              iconClassName="w-3.5 h-3.5 text-[#4de1c1]"
              className="h-8 px-3 text-xs gap-1.5 rounded-lg border-white/10 bg-white/5 hover:bg-white/10 text-white font-medium shrink-0"
            />
          <Button
            size="sm"
            variant="outline"
            onClick={handleSaveProjectLocal}
            disabled={!editorId}
            className="h-8 px-3 text-xs gap-1.5 rounded-lg border-white/10 bg-white/5 hover:bg-white/10 text-white font-medium shrink-0"
            data-testid="save-project-local-btn"
            title="Save project to a local file"
          >
            <FolderInput className="w-3.5 h-3.5 text-[#4de1c1]" />
              <span className="hidden sm:inline">Save File</span>
          </Button>

          {exportedUrl ? (
            <a
              href={exportedUrl}
              download={`${story?.title || 'story'}.mp4`}
              className="flex items-center gap-1.5 h-8 px-3 rounded-lg text-xs font-bold shrink-0 whitespace-nowrap bg-[#4de1c1] text-black shadow-md hover:brightness-110 transition-all"
              data-testid="download-compiled-btn"
            >
              <Download className="w-3.5 h-3.5" />
                <span className="hidden sm:inline">DOWNLOAD MP4</span>
            </a>
          ) : (
            <Button
              size="sm"
              onClick={handleExportVideo}
                disabled={Boolean(isReelsShortsEditor && reelsExporting)}
              className="h-8 px-3.5 rounded-lg bg-[#4de1c1] text-black font-bold text-xs gap-1.5 hover:brightness-110"
              data-testid="export-full-btn"
                title={exportingFull ? (exportStatusMessage || 'Export in progress') : 'Export the current timeline'}
            >
                {(isReelsShortsEditor && reelsExporting) || exportingFull ? (
                  <Loader2 className="w-3.5 h-3.5 animate-spin" />
                ) : (
              <Download className="w-3.5 h-3.5" />
                )}
                <span className="hidden sm:inline">{isReelsShortsEditor ? 'EXPORT MP4' : 'EXPORT'}</span>
            </Button>
          )}
          </>
        )}
      />

      {/* Main Editor Container — vertical split: preview ↔ timeline */}
      <div className="flex-1 min-h-0 w-full bg-[#0f1418]/90 overflow-hidden flex flex-col relative z-10">
        <PanelGroup
          direction="vertical"
          autoSaveId="video-editor-main-vsplit"
          className="flex flex-1 min-h-0 min-w-0"
        >
          <Panel defaultSize={56} minSize={22} className="flex min-h-0 min-w-0 flex-col overflow-hidden">
        {/* Top Section: Sidebar Left + Video Player + Sidebar Right */}
        <div className="flex flex-1 min-h-0 relative overflow-hidden h-full">
          
          {/* Left Toolbar */}
          <div
            className={`${
              leftExpanded ? 'w-52 px-3.5' : 'w-14 items-center px-0'
            } bg-[#11171d] border-r border-[#1e272e] flex flex-col py-3 text-[#4de1c1] shrink-0 transition-all duration-200 z-20 select-none h-full`}
          >
            {/* Toggle Button */}
            <div className={`w-full flex ${leftExpanded ? 'justify-end pr-1 mb-1' : 'justify-center mb-1'}`}>
              <button
                type="button"
                onClick={() => setLeftExpanded(!leftExpanded)}
                className="p-1 rounded text-slate-400 hover:text-white hover:bg-white/5 transition"
                title={leftExpanded ? 'Collapse Sidebar' : 'Expand Sidebar'}
                data-testid="left-sidebar-toggle-btn"
              >
                {leftExpanded ? <ChevronLeft size={16} /> : <ChevronRight size={16} />}
              </button>
            </div>

            {/* 7 Icons evenly spaced across full height */}
            <div className="w-full flex-1 flex flex-col justify-evenly py-1">
              {leftSidebarItems.map((item) => {
                const Icon = item.icon;
                return (
                  <button
                    key={item.id}
                    type="button"
                    onClick={item.action}
                    className={`w-full flex items-center text-[#4de1c1] hover:text-white transition group py-1.5 ${
                      leftExpanded ? 'px-2.5 rounded text-left hover:bg-white/5' : 'justify-center'
                    }`}
                    title={item.name}
                  >
                    <Icon size={19} strokeWidth={1.75} className="shrink-0" />
                    {leftExpanded && (
                      <span className="ml-4 text-xs font-semibold text-slate-200 group-hover:text-white whitespace-nowrap truncate tracking-wider">
                        {item.name}
                      </span>
                    )}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Video Preview Section — controls sit below the stage so they never cover the picture */}
          <div className="flex-1 min-h-0 h-full flex flex-col overflow-hidden bg-black">
          <div
            className="relative flex-1 bg-black flex items-center justify-center overflow-hidden min-h-0"
            ref={previewStageRef}
            data-testid="video-preview-stage"
          >
            {loading && !currentClip ? (
              <div
                className="absolute inset-0 z-10 flex flex-col items-center justify-center gap-2 pointer-events-none"
                data-testid="video-editor-loading"
                role="status"
                aria-live="polite"
              >
                <Loader2 className="w-8 h-8 animate-spin text-amber-400" />
                <p className="text-xs text-slate-400">Loading timelines…</p>
              </div>
            ) : null}
            {/* Motion layer wraps media + VFX so Ken Burns / filters move with effects. */}
            <div
              ref={previewMotionLayerRef}
              className="absolute inset-0 overflow-hidden"
              data-testid="preview-motion-layer"
            >
              <div
                className="absolute inset-0"
                style={showDualTransitionOverlay ? { opacity: 0, visibility: 'hidden' } : undefined}
                data-testid="preview-main-media"
              >
              {currentClip?.videoUrl ? (
                talkHeadScreen ? (
                  <div
                    className="absolute overflow-hidden bg-black"
                    style={{
                      left: talkHeadScreen.left,
                      top: talkHeadScreen.top,
                      width: talkHeadScreen.width,
                      height: talkHeadScreen.height,
                    }}
                    data-testid="talking-head-pane"
                  >
                    <video
                      key={`${currentClip.id}:${mediaUrlForPlayback(currentClip.videoUrl)}`}
                      ref={(el) => {
                        playback.videoRef.current = el;
                        previewMediaRef.current = el;
                      }}
                      src={mediaUrlForPlayback(currentClip.videoUrl)}
                      className="max-w-none"
                      style={talkHeadVideoStyle}
                      playsInline
                      preload="auto"
                    />
                  </div>
                ) : (
                <video
                  key={`${currentClip.id}:${mediaUrlForPlayback(currentClip.videoUrl)}`}
                  ref={(el) => {
                    playback.videoRef.current = el;
                    previewMediaRef.current = el;
                  }}
                  src={mediaUrlForPlayback(currentClip.videoUrl)}
                  className={`w-full h-full ${previewFitMode === 'cover' ? 'object-cover' : 'object-contain'} transition-all`}
                  playsInline
                  preload="auto"
                />
                )
              ) : currentClip?.imageUrl ? (
                <img
                  key={`${currentClip.id}:${mediaUrlForPlayback(currentClip.imageUrl)}`}
                  ref={(el) => {
                    previewImageRef.current = el;
                    previewMediaRef.current = el;
                  }}
                  src={mediaUrlForPlayback(currentClip.imageUrl)}
                  alt="Scene preview"
                  className={`w-full h-full ${previewFitMode === 'cover' ? 'object-cover' : 'object-contain'} transition-all`}
                  onError={(e) => {
                    e.currentTarget.src = 'https://images.unsplash.com/photo-1543466835-00a7907e9de1?auto=format&fit=crop&w=1200&q=80';
                  }}
                />
              ) : (
                <img
                  src="https://images.unsplash.com/photo-1543466835-00a7907e9de1?auto=format&fit=crop&w=1200&q=80"
                  alt="Scene preview"
                  className={`w-full h-full ${previewFitMode === 'cover' ? 'object-cover' : 'object-contain'} transition-all`}
                />
              )}
              </div>

              {activeTransitionBlend ? (
                <DualTransitionPreview
                  blend={activeTransitionBlend}
                  showOutgoing={!outgoingDrivesMainVideo}
                  fitMode={previewFitMode}
                  outgoingVideoRef={outgoingTransitionVideoRef}
                  outgoingImageRef={outgoingTransitionImageRef}
                  incomingVideoRef={incomingTransitionVideoRef}
                  incomingImageRef={incomingTransitionImageRef}
                />
              ) : null}

              {timelineActive.effects && flatEffectClips.length > 0 && !activeTransitionBlend ? (
                <VfxPreviewOverlay
                  mediaRef={previewMediaRef}
                  stageRef={previewStageRef}
                  stageWidth={previewStageSize.w}
                  stageHeight={previewStageSize.h}
                  contentRect={previewContentRect}
                  aspectRatio={exportAspectRatio}
                  currentTime={visualCurrentTime}
                  currentTimeRef={visualCurrentTimeRef}
                  effects={flatEffectClips}
                  enabled={Boolean(timelineActive.effects)}
                />
              ) : null}
            </div>

            {timelineActive.hostOverlay && slidingCardCarouselClip?.carouselCards?.length ? (
              <div
                className="absolute pointer-events-none"
                style={{
                  left: previewContentRect.left,
                  top: previewContentRect.top,
                  width: previewContentRect.width,
                  height: previewContentRect.height,
                }}
                data-testid="sliding-card-carousel-overlay"
              >
                <CardCarousel
                  cards={slidingCardCarouselClip.carouselCards}
                  currentTime={visualCurrentTime}
                  slideDuration={700}
                  position="bottom"
                />
              </div>
            ) : null}

            {timelineActive.hostOverlay ? (
              <>
                {backgroundOverlayPreviewClips.map((clip) => (
                  <HostOverlayPreview
                    key={`bg-ov-${clip.id}`}
                    stageRef={previewStageRef}
                    stageWidth={previewStageSize.w}
                    stageHeight={previewStageSize.h}
                    contentRect={previewContentRect}
                    aspectRatio={exportAspectRatio}
                    clip={clip}
                    isSelected={selectedHostOverlayClip?.id === clip.id}
                    interactive={selectedHostOverlayClip?.id === clip.id}
                    timelineTime={visualCurrentTime}
                    isPlaying={Boolean(playback.isPlaying)}
                    onSelect={() => handleSelectHostOverlayClip(clip.id)}
                    onLayerChange={(layer) => handleHostOverlayLayerChange(clip.id, layer)}
                    onOpacityChange={(opacity) => {
                      setOverlayTracks((prev) => patchHostOverlayClip(prev, clip.id, { opacity }));
                    }}
                    onRevealInTimeline={revealHostOverlayClipInTimeline}
                    onOpenDetails={(ov) => {
                      revealHostOverlayClipInTimeline(ov);
                      setOverlayDetailsClipId(ov.id);
                    }}
                  />
                ))}
                {foregroundHostOverlayPreviewClips.map((clip) => (
                  <HostOverlayPreview
                    key={`fg-ov-${clip.id}`}
                    stageRef={previewStageRef}
                    stageWidth={previewStageSize.w}
                    stageHeight={previewStageSize.h}
                    contentRect={previewContentRect}
                    aspectRatio={exportAspectRatio}
                    clip={clip}
                    isSelected={selectedHostOverlayClip?.id === clip.id}
                    interactive
                    timelineTime={visualCurrentTime}
                    isPlaying={Boolean(playback.isPlaying)}
                    onSelect={() => handleSelectHostOverlayClip(clip.id)}
                    onLayerChange={(layer) => handleHostOverlayLayerChange(clip.id, layer)}
                    onOpacityChange={(opacity) => {
                      setOverlayTracks((prev) => patchHostOverlayClip(prev, clip.id, { opacity }));
                    }}
                    onRevealInTimeline={revealHostOverlayClipInTimeline}
                    onOpenDetails={(ov) => {
                      revealHostOverlayClipInTimeline(ov);
                      setOverlayDetailsClipId(ov.id);
                    }}
                  />
                ))}
              </>
            ) : null}

            {showSubtitlePreviewOverlay && subtitleLookEnabled && !subtitleLookMinimized ? (
              <SubtitlePreviewOverlay
                stageRef={previewStageRef}
                stageWidth={previewStageSize.w}
                stageHeight={previewStageSize.h}
                contentRect={previewContentRect}
                aspectRatio={exportAspectRatio}
                viewportMin={viewportMin}
                subtitleStyle={subtitleStyle}
                layer={resolvedSubtitleLayer}
                text={subtitleSpokenPreview.text}
                editText={subtitleStyle?.spokenWordWindow ? subtitleOverlayPreviewText : null}
                tokens={subtitleSpokenPreview.tokens}
                highlightColor={subtitleStyle.spokenWordHighlightColor}
                isActive={subtitlePreviewIsActiveMoment}
                interactive={!playback.isPlaying}
                snapToGrid={subtitleSnapToGrid}
                onLayerChange={handleSubtitleLayerChange}
                onTextChange={handleActiveSubtitleTextChange}
              />
            ) : null}

          </div>
            {/* Video controls — reserved strip under the preview, not over the frame */}
            <div
              className="shrink-0 mx-3 mb-2 mt-1 h-11 bg-[#161c22] rounded-lg px-5 flex items-center justify-between text-xs text-slate-300 border border-white/5 select-none shadow-xl"
              data-testid="preview-transport-bar"
            >
              
              {/* Playback controls */}
              <div className="flex items-center space-x-4">
                <button type="button" onClick={() => playback.seek(Math.max(0, playback.currentTime - 5))} className="hover:text-teal-400 transition" title="Skip backward 5s"><SkipBack size={16} /></button>
                <button type="button" onClick={playback.togglePlay} className="hover:text-teal-400 transition" title={playback.isPlaying ? 'Pause' : 'Play'}>
                  {playback.isPlaying ? <Pause size={16} fill="currentColor" /> : <Play size={16} fill="currentColor" />}
                </button>
                <button type="button" onClick={() => playback.seek(Math.min(totalDuration, playback.currentTime + 5))} className="hover:text-teal-400 transition" title="Skip forward 5s"><SkipForward size={16} /></button>
              </div>

              {/* Time Indicators */}
              <div className="flex items-center space-x-3 font-mono text-xs tracking-wider text-slate-300 tabular-nums">
                <span>{formatTime(visualCurrentTime)}</span>
                <span className="text-slate-500">{formatTime(totalDuration)}</span>
              </div>

              {/* Volume & Scale Controls */}
              <div className="flex items-center space-x-4">
                <button
                  type="button"
                  onClick={() => (subtitleSettingsOpen ? setSubtitleSettingsOpen(false) : openSubtitleSettings())}
                  className={`hover:text-teal-400 transition ${subtitleSettingsOpen ? 'text-teal-300' : ''}`}
                  title={subtitleSettingsOpen ? 'Close subtitle settings' : 'Subtitle settings'}
                  data-testid="preview-subtitle-settings-btn"
                  aria-expanded={subtitleSettingsOpen}
                >
                  <Captions size={16} />
                </button>
                <button type="button" onClick={() => setIsMuted((m) => !m)} className="hover:text-teal-400 transition" title={isMuted ? 'Unmute' : 'Mute'}>
                  <Volume2 size={16} className={isMuted ? 'text-red-400' : ''} />
                </button>
                <span
                  onClick={() => {
                    const nextSpeed = masterAudioPlaybackSpeed === 1 ? 1.5 : masterAudioPlaybackSpeed === 1.5 ? 2 : 1;
                    setMasterAudioPlaybackSpeed(nextSpeed);
                  }}
                  className="text-xs px-2 py-0.5 border border-slate-600 rounded text-slate-400 cursor-pointer hover:border-teal-400 hover:text-teal-300 transition"
                  title="Toggle Speed"
                >
                  {masterAudioPlaybackSpeed || 1.0}x
                </span>
                <button
                  type="button"
                  onClick={togglePreviewFitMode}
                  className="hover:text-teal-400 transition"
                  title={previewFitMode === 'cover' ? 'Fit aspect ratio' : 'Fill full stage in place'}
                >
                  <Maximize size={16} />
                </button>
              </div>
            </div>
            {subtitleSettingsOpen ? (
              <div
                className="shrink-0 mx-3 mb-2 max-h-56 overflow-y-auto rounded-lg border border-white/10 bg-[#161c22] px-3 py-2.5"
                data-testid="subtitle-settings-panel"
              >
                <div className="mb-2 flex items-center justify-between gap-2">
                  <span className="text-[11px] font-semibold uppercase tracking-wide text-slate-300">
                    Subtitle settings
                  </span>
                  <button
                    type="button"
                    className="text-[11px] text-slate-400 hover:text-white"
                    onClick={() => setSubtitleSettingsOpen(false)}
                    data-testid="subtitle-settings-close"
                  >
                    Close
                  </button>
                </div>
                <label className="mb-2 flex items-center gap-2 text-[11px] text-slate-300 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={timelineActive.subtitle !== false}
                    onChange={(ev) => {
                      const next = ev.target.checked;
                      setTimelineActive((prev) => ({
                        ...prev,
                        subtitle: next,
                        subtitleLook: next ? true : prev.subtitleLook,
                        subtitleLookMinimized: next ? false : prev.subtitleLookMinimized,
                      }));
                    }}
                    data-testid="editor-subtitle-visible-toggle"
                  />
                  Show subtitles on the preview
                </label>
                <SubtitleLookControls
                  style={subtitleStyle}
                  onChange={handleSubtitleLookChange}
                  previewFontPx={subtitlePreviewLayout?.fontPx}
                  showSnapToGrid
                  snapToGrid={subtitleSnapToGrid}
                  onSnapToGridChange={setSubtitleSnapToGrid}
                  testIdPrefix="editor-subtitle"
                />
              </div>
            ) : null}
          </div>

          {/* Right Toolbar */}
          <div
            className={`${
              rightExpanded ? 'w-52 px-3.5' : 'w-14 items-center px-0'
            } bg-[#11171d] border-l border-[#1e272e] flex flex-col py-3 text-[#4de1c1] shrink-0 transition-all duration-200 z-20 select-none h-full`}
          >
            {/* Toggle Button */}
            <div className={`w-full flex ${rightExpanded ? 'justify-start pl-1 mb-1' : 'justify-center mb-1'}`}>
              <button
                type="button"
                onClick={() => setRightExpanded(!rightExpanded)}
                className="p-1 rounded text-slate-400 hover:text-white hover:bg-white/5 transition"
                title={rightExpanded ? 'Collapse Sidebar' : 'Expand Sidebar'}
                data-testid="right-sidebar-toggle-btn"
              >
                {rightExpanded ? <ChevronRight size={16} /> : <ChevronLeft size={16} />}
              </button>
            </div>

            {/* 7 Icons evenly spaced across full height */}
            <div className="w-full flex-1 flex flex-col justify-evenly py-1">
              {rightSidebarItems.map((item) => {
                const Icon = item.icon;
                return (
                  <button
                    key={item.id}
                    type="button"
                    onClick={item.action}
                    className={`w-full flex items-center text-[#4de1c1] hover:text-white transition group py-1.5 ${
                      rightExpanded ? 'px-2.5 rounded text-left hover:bg-white/5' : 'justify-center'
                    }`}
                    title={item.name}
                  >
                    <Icon size={19} strokeWidth={1.75} className="shrink-0" />
                    {rightExpanded && (
                      <span className="ml-4 text-xs font-semibold text-slate-200 group-hover:text-white whitespace-nowrap truncate tracking-wider">
                        {item.name}
                      </span>
                    )}
                  </button>
                );
              })}
            </div>
          </div>
        </div>
          </Panel>

          <PanelResizeHandle
            className="h-1.5 shrink-0 cursor-row-resize border-y border-[#1b232a] bg-[#11171d] outline-none transition-colors hover:bg-[#4de1c1]/25 active:bg-[#4de1c1]/40"
            data-testid="editor-vsplit-handle"
            title="Drag to resize preview and timeline"
          />

          <Panel
            defaultSize={44}
            minSize={18}
            className="flex min-h-0 min-w-0 flex-col overflow-hidden"
            data-testid="timeline-panel"
            data-journey-ignore=""
          >
        <div className="flex h-full min-h-0 min-w-0 overflow-hidden" data-journey-ignore="">
          {showMediaLibrary ? (
            <div
              className="w-72 max-w-[40%] shrink-0 border-r flex flex-col min-h-0 bg-[#11171d]"
              style={{ borderColor: 'var(--glass-border)' }}
              data-testid="editor-media-library-panel"
            >
              <MediaLibrary
                initialItems={generatedMediaLibraryItems}
                onMediaSelect={handleMediaFromLibrary}
                onClose={() => setShowMediaLibrary(false)}
              />
            </div>
          ) : null}
          <div className="flex flex-1 min-h-0 min-w-0 flex-col overflow-hidden">
          {isReelsShortsEditor && selectedHostOverlayClip ? (
            <div
              className="shrink-0 max-h-[42%] overflow-y-auto px-4 py-2 border-b"
              style={{ borderColor: 'var(--glass-border)', background: 'var(--bg-secondary)' }}
            >
              <ReelsLayoutInspector
                clip={selectedHostOverlayClip}
                onChange={(patch) => {
                  recordEditorHistory();
                  setOverlayTracks((prev) => patchHostOverlayClip(prev, selectedHostOverlayClip.id, patch));
                  queuePersistEditorAfterMediaChange();
                }}
              />
            </div>
          ) : null}
          {isCardOverlayClip(selectedHostOverlayClip) ? (
            <div className="shrink-0 px-4 py-1.5 border-b" style={{ borderColor: 'var(--glass-border)', background: 'var(--bg-secondary)' }}>
              <CardCarouselInspector
                clip={selectedHostOverlayClip}
                tracks={overlayTracks}
                onParamsChange={(patch) => {
                  recordEditorHistory();
                  setOverlayTracks((prev) => applyParamsToGroup(
                    prev,
                    selectedHostOverlayClip.carouselGroupId,
                    patch,
                    exportAspectRatio,
                  ));
                }}
                onClipChange={(patch) => {
                  setOverlayTracks((prev) => patchCarouselClip(prev, selectedHostOverlayClip.id, patch));
                }}
                onAiCreate={(text) => handleAddCardCarousel(text)}
                onAiEdit={(params) => {
                  recordEditorHistory();
                  setOverlayTracks((prev) => applyParamsToGroup(
                    prev,
                    selectedHostOverlayClip.carouselGroupId,
                    params,
                    exportAspectRatio,
                  ));
                }}
              />
            </div>
          ) : null}
          {/* Timeline toolbar */}
          <div
            className="flex items-center gap-3 px-4 py-2 border-b shrink-0 min-w-0"
            style={{ background: 'var(--bg-secondary)', borderColor: 'var(--glass-border)' }}
            data-tour="timeline-toolbar"
          >
            <span className="text-xs font-semibold shrink-0" style={{ color: 'var(--text-tertiary)' }}>TIMELINE</span>
            {loading ? (
              <span
                className="inline-flex items-center gap-1.5 text-[11px] text-slate-400"
                data-testid="timeline-lanes-loading"
              >
                <Loader2 className="w-3.5 h-3.5 animate-spin text-amber-400" />
                Loading timelines…
              </span>
            ) : null}
            <div className="flex items-center gap-0.5 shrink-0">
              <button
                type="button"
                onClick={undoEditorAction}
                disabled={!canUndoEditor}
                className="flex h-7 w-7 items-center justify-center rounded-lg transition-opacity disabled:opacity-35"
                style={{ color: 'var(--text-secondary)' }}
                title="Undo (Ctrl+Z)"
                data-testid="video-editor-undo-btn"
              >
                <Undo2 className="w-3.5 h-3.5" />
            </button>
              <button
                type="button"
                onClick={redoEditorAction}
                disabled={!canRedoEditor}
                className="flex h-7 w-7 items-center justify-center rounded-lg transition-opacity disabled:opacity-35"
                style={{ color: 'var(--text-secondary)' }}
                title="Redo (Ctrl+Shift+Z)"
                data-testid="video-editor-redo-btn"
              >
                <Redo2 className="w-3.5 h-3.5" />
            </button>
            </div>
            <div className="flex min-w-0 flex-1 items-center gap-2 overflow-x-auto overflow-y-hidden pb-0.5">
            <div className="flex items-center gap-2 flex-nowrap shrink-0 min-w-max">
              <Select value={batchVideoProvider} onValueChange={setBatchVideoProvider} disabled={generatingPendingVideos || generatingPendingAudios}>
                <SelectTrigger className="h-8 w-44 text-xs" data-testid="pending-video-provider-select">
                  <SelectValue placeholder="Video provider" />
                </SelectTrigger>
                <SelectContent>
                  {batchProviderOptions.map(option => (
                    <SelectItem key={option.value} value={option.value}>{option.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Select value={batchCameraMotion} onValueChange={setBatchCameraMotion} disabled={generatingPendingVideos || generatingPendingAudios}>
                <SelectTrigger className="h-8 w-28 text-xs" data-testid="pending-video-motion-select">
                  <SelectValue placeholder="Motion" />
                </SelectTrigger>
                <SelectContent>
                  {CAMERA_MOTION_OPTIONS.map(option => (
                    <SelectItem key={option.value} value={option.value}>{option.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <button
                onClick={generatePendingVideos}
                disabled={
                  generatingPendingVideos
                  || generatingPendingAudios
                  || batchTimelineVideoCount === 0
                  || (isNarrationEditor ? !projectId : !storyId)
                }
                className="flex items-center gap-1.5 px-3 py-1.5 rounded-xl text-xs font-medium transition-all disabled:opacity-60 disabled:cursor-not-allowed"
                style={{
                  background: 'var(--primary-light)',
                  color: 'var(--primary)',
                  border: '1px solid var(--primary)',
                }}
                title={
                  batchTimelineVideoCount === 0
                    ? 'No segment videos on the timeline yet (generate), or none to regenerate'
                    : batchTimelineVideoMode === 'generate'
                      ? `Generate ${batchTimelineVideoCount} videos (${batchVideoProvider}) one by one`
                      : `Regenerate all ${batchTimelineVideoCount} videos with ${batchVideoProvider}${
                        batchCameraMotion && batchCameraMotion !== 'auto' ? ` · ${batchCameraMotion}` : ''
                      }`
                }
                data-testid="generate-pending-videos-btn">
                {generatingPendingVideos ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Film className="w-3.5 h-3.5" />}
                {generatingPendingVideos
                  ? `${batchTimelineVideoMode === 'regenerate' ? 'Regenerating' : 'Generating'} ${pendingVideoProgress.current}/${pendingVideoProgress.total}`
                  : batchTimelineVideoMode === 'regenerate'
                    ? `Regenerate videos (${batchTimelineVideoCount})`
                    : `Generate videos (${batchTimelineVideoCount})`}
              </button>
              {!isPitchRecordingStory(story) ? (
              <button
                onClick={handleExtendAllEligibleVideos}
                disabled={
                  extendingAllVideos
                  || extendVideoBusy
                  || generatingPendingVideos
                  || generatingPendingAudios
                  || eligibleExtendCount === 0
                  || !timelineActive.audio
                  || (isNarrationEditor ? !projectId : !storyId)
                }
                className="flex items-center gap-1.5 px-3 py-1.5 rounded-xl text-xs font-medium transition-all disabled:opacity-60 disabled:cursor-not-allowed"
                style={{
                  background: '#ef444420',
                  color: '#f87171',
                  border: '1px solid #ef444460',
                }}
                title={
                  eligibleExtendCount === 0
                    ? 'No timeline videos shorter than their narration audio'
                    : `Extend ${eligibleExtendCount} video${eligibleExtendCount === 1 ? '' : 's'} to match audio (uses video provider above)`
                }
                data-testid="extend-all-videos-btn">
                {extendingAllVideos ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <ChevronRight className="w-3.5 h-3.5" />}
                {extendingAllVideos
                  ? `Extending ${extendAllProgress.current}/${Math.max(extendAllProgress.total, extendAllProgress.current + 1)}`
                  : `Extend all (${eligibleExtendCount})`}
              </button>
              ) : null}
              <button
                onClick={() => setBulkAudioDialogOpen(true)}
                disabled={
                  generatingPendingVideos
                  || generatingPendingAudios
                  || allAudioCount === 0
                  || (!projectId && !storyId)
                }
                className="flex items-center gap-1.5 px-3 py-1.5 rounded-xl text-xs font-medium transition-all disabled:opacity-60 disabled:cursor-not-allowed"
                style={{
                  background: '#6366f120',
                  color: '#818cf8',
                  border: '1px solid #6366f160',
                }}
                title={
                  allAudioCount
                    ? `Generate narration audio — ${pendingAudioCount} pending, ${allAudioCount} total with script`
                    : 'No timeline segments with script text'
                }
                data-testid="generate-bulk-audios-btn">
                {generatingPendingAudios ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Volume2 className="w-3.5 h-3.5" />}
                {generatingPendingAudios
                  ? `Audio ${pendingAudioProgress.current}/${pendingAudioProgress.total}`
                  : `Generate audio${pendingAudioCount ? ` (${pendingAudioCount} pending)` : ''}`}
              </button>

            <button
              onClick={() => setShowMediaLibrary(!showMediaLibrary)}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-xl text-xs font-medium transition-all"
              style={{
                background: showMediaLibrary ? 'var(--primary-light)' : 'var(--bg-tertiary)',
                color: showMediaLibrary ? 'var(--primary)' : 'var(--text-secondary)',
                border: `1px solid ${showMediaLibrary ? 'var(--primary)' : 'var(--glass-border)'}`,
              }}
              data-testid="toggle-media-library-btn">
              {showMediaLibrary ? <ChevronRight className="w-3.5 h-3.5" /> : <ChevronLeft className="w-3.5 h-3.5" />}
              Media Library
            </button>

            <button
              onClick={() => audioInputRef.current?.click()}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-xl text-xs font-medium transition-all"
              style={{ background: '#ec489920', color: '#ec4899', border: '1px solid #ec489940' }}
              data-testid="add-audio-btn">
              <Music className="w-3.5 h-3.5" /> Add Audio
            </button>
            <input ref={audioInputRef} type="file" accept="audio/*" className="hidden"
              onChange={handleAudioFileUpload} />

            <button onClick={addVideoTrack}
              className="flex items-center gap-1 px-3 py-1.5 rounded-xl text-xs font-medium"
              style={{ background: 'var(--bg-tertiary)', color: 'var(--text-secondary)', border: '1px solid var(--glass-border)' }}
              data-testid="add-video-track-btn">
              <Plus className="w-3 h-3" /> Video Track
            </button>
            <button onClick={addAudioTrack}
              className="flex items-center gap-1 px-3 py-1.5 rounded-xl text-xs font-medium"
              style={{ background: 'var(--bg-tertiary)', color: 'var(--text-secondary)', border: '1px solid var(--glass-border)' }}
              data-testid="add-audio-track-btn">
              <Plus className="w-3 h-3" /> Audio Track
            </button>
            <VideoEditorTimelineBookendsControl
              bookends={videoBookends}
              onChange={handleVideoBookendsChange}
              onPersist={persistVideoBookends}
              defaultTitle={story?.title || ''}
              aspectRatio={exportAspectRatio}
              narrationProjectId={isNarrationEditor ? projectId : null}
              storyId={isNarrationEditor ? null : storyId}
            />
            <button
              type="button"
              onClick={loadDemoVfxTimeline}
              className="flex items-center gap-1 px-3 py-1.5 rounded-xl text-xs font-medium"
              style={{ background: '#a855f720', color: '#a855f7', border: '1px solid #a855f760' }}
              title="Load core VFX demo (5 effects)"
              data-testid="load-vfx-demo-btn"
            >
              <TwinneeLogo className="w-3 h-3" /> VFX demo
            </button>
            <button
              type="button"
              onClick={loadEnvironmentalVfxDemo}
              className="flex items-center gap-1 px-3 py-1.5 rounded-xl text-xs font-medium"
              style={{ background: '#fbbf2420', color: '#d97706', border: '1px solid #fbbf2460' }}
              title="Load full environmental pack demo (15 effects)"
              data-testid="load-environmental-vfx-demo-btn"
            >
              <TwinneeLogo className="w-3 h-3" /> Env VFX
            </button>
            <button
              type="button"
              onClick={refreshPackedSceneTimeline}
              className="flex items-center gap-1 px-3 py-1.5 rounded-xl text-xs font-medium"
              style={{ background: 'var(--primary-light)', color: 'var(--primary)', border: '1px solid var(--primary)' }}
              title="Rebuild timeline: each scene gets image, video, audio, and subtitles in one block. Scenes are placed next to each other; block length is the longest audio or video in that scene. Colors alternate per scene."
              data-testid="refresh-packed-scene-timeline-btn"
            >
              <RefreshCw className="w-3 h-3" /> Refresh scene timeline
            </button>
            {storyId && !isNarrationEditor ? (
              <button
                type="button"
                onClick={handleRetryStoryGeneration}
                disabled={retryingNarration}
                className="flex items-center gap-1.5 px-3 py-1.5 rounded-xl text-xs font-medium transition-all disabled:opacity-60 disabled:cursor-not-allowed"
                style={{ background: 'var(--bg-tertiary)', color: 'var(--text-secondary)', border: '1px solid var(--glass-border)' }}
                data-testid="retry-story-generation-btn"
                title="Restart story generation from the start so new character portraits are used"
              >
                {retryingNarration ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />}
                Retry Generation
              </button>
            ) : null}
            {canGenerateBgMusic ? (
              <button
                type="button"
                onClick={() => openBgMusicDialog()}
                disabled={generatingBgMusic}
                className="flex items-center gap-1.5 px-3 py-1.5 rounded-xl text-xs font-medium transition-all disabled:opacity-60 disabled:cursor-not-allowed"
                style={{ background: 'var(--bg-tertiary)', color: 'var(--text-secondary)', border: '1px solid var(--glass-border)' }}
                data-testid="generate-narration-bg-music-btn"
                title="Queue instrumental background music (choose provider and edit prompt)"
              >
                {generatingBgMusic ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <TwinneeLogo className="w-3.5 h-3.5" />}
                Generate BG music
              </button>
            ) : null}
            {isNarrationEditor ? (
              <div className="flex items-center gap-1.5 pl-1 border-l" style={{ borderColor: 'var(--glass-border)' }}>
                <button
                  type="button"
                  onClick={handleRetryNarrationPipeline}
                  disabled={retryingNarration || !projectId}
                  className="flex items-center gap-1.5 px-3 py-1.5 rounded-xl text-xs font-medium transition-all disabled:opacity-60 disabled:cursor-not-allowed"
                  style={{ background: 'var(--bg-tertiary)', color: 'var(--text-secondary)', border: '1px solid var(--glass-border)' }}
                  data-testid="retry-narration-pipeline-btn"
                  title="Restart the narration pipeline if generation was interrupted or stuck"
                >
                  {retryingNarration ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />}
                  Retry Generation
                </button>
                <Select
                  value={audioTranslationLangTarget}
                  onValueChange={setAudioTranslationLangTarget}
                  disabled={translatingAudioLane}
                >
                  <SelectTrigger className="h-8 w-32 text-xs" data-testid="audio-translation-lang-select">
                    <SelectValue placeholder="Language" />
                  </SelectTrigger>
                  <SelectContent>
                    {AUDIO_TRANSLATION_LANGUAGES.map((o) => (
                      <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <button
                  type="button"
                  onClick={addTranslationAudioLane}
                  disabled={translatingAudioLane || !projectId}
                  className="flex items-center gap-1.5 px-3 py-1.5 rounded-xl text-xs font-medium transition-all disabled:opacity-60"
                  style={{ background: '#0ea5e920', color: '#38bdf8', border: '1px solid #0ea5e960' }}
                  title="Translate narration and add a new audio lane. Original audio stays on Audio 1."
                  data-testid="add-translation-audio-lane-btn"
                >
                  {translatingAudioLane ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Languages className="w-3.5 h-3.5" />}
                  {translatingAudioLane ? 'Translating…' : 'Translate audio lane'}
                </button>
          </div>
            ) : null}
            <button
              type="button"
              onClick={removeAllTimelineGaps}
              className="flex items-center gap-1 px-3 py-1.5 rounded-xl text-xs font-medium"
              style={{ background: 'var(--bg-tertiary)', color: 'var(--text-secondary)', border: '1px solid var(--glass-border)' }}
              title="Pack clips on every lane with no blank space between them. Each lane keeps its first clip’s start time."
              data-testid="remove-all-timeline-gaps-btn"
            >
              <AlignJustify className="w-3 h-3" /> Remove all gaps
            </button>
            <button
              type="button"
              onClick={clipAllSceneVideoToMatchAudio}
              disabled={clippingVideoToMatchAudio}
              className="flex items-center gap-1 px-3 py-1.5 rounded-xl text-xs font-medium transition-all disabled:opacity-60"
              style={{ background: 'var(--bg-tertiary)', color: 'var(--text-secondary)', border: '1px solid var(--glass-border)' }}
              title={
                isSongBasedEditorScenes(scenesSnapshot)
                  ? 'Snap each stanza image/video to its aligned song timestamp and rebuild lyric subtitles on the master track.'
                  : 'Match each segment image/video to the active narration audio lane (or pick a lane if several are active), then pack segments and rebuild subtitles.'
              }
              data-testid="clip-video-to-match-audio-btn"
            >
              {clippingVideoToMatchAudio ? (
                <Loader2 className="w-3 h-3 animate-spin" />
              ) : (
                <Scissors className="w-3 h-3" />
              )}
              Magic edit
            </button>

            {isSongBasedEditorScenes(scenesSnapshot) && selectedSongMultishotClips.length > 0 ? (
              <>
                <button
                  type="button"
                  onClick={showSelectedMultishotsOnTimeline}
                  disabled={multishotTimelineToggling}
                  className="flex items-center gap-1 px-3 py-1.5 rounded-xl text-xs font-medium transition-all disabled:opacity-60"
                  style={{ background: 'var(--bg-tertiary)', color: 'var(--text-secondary)', border: '1px solid var(--glass-border)' }}
                  title="Include selected multishot images on the timeline again"
                  data-testid="show-multishot-on-timeline-btn"
                >
                  {multishotTimelineToggling ? (
                    <Loader2 className="w-3 h-3 animate-spin" />
                  ) : (
                    <ImageIcon className="w-3 h-3" />
                  )}
                  Show on timeline ({selectedSongMultishotClips.length})
            </button>
                <button
                  type="button"
                  onClick={hideSelectedMultishotsFromTimeline}
                  disabled={multishotTimelineToggling}
                  className="flex items-center gap-1 px-3 py-1.5 rounded-xl text-xs font-medium transition-all disabled:opacity-60"
                  style={{ background: 'var(--bg-tertiary)', color: 'var(--text-secondary)', border: '1px solid var(--glass-border)' }}
                  title="Hide selected multishot images from the timeline without deleting generated files"
                  data-testid="hide-multishot-from-timeline-btn"
                >
                  {multishotTimelineToggling ? (
                    <Loader2 className="w-3 h-3 animate-spin" />
                  ) : (
                    <ImageIcon className="w-3 h-3" />
                  )}
                  Hide from timeline ({selectedSongMultishotClips.length})
                </button>
              </>
            ) : null}

            {(audioTracks.some((t) => t.length) || musicTracks.some((t) => t.length)) ? (
              <div
                className="flex items-center gap-2 px-2.5 py-1.5 rounded-xl"
                style={{ background: 'var(--bg-tertiary)', border: '1px solid var(--glass-border)' }}
                data-testid="master-audio-speed-control"
              >
                <span className="text-[10px] font-semibold uppercase tracking-wide shrink-0" style={{ color: 'var(--text-tertiary)' }}>
                  All audio speed
                </span>
              <Slider
                  value={[playbackSpeedToSliderPercent(masterAudioPlaybackSpeed)]}
                  min={playbackSpeedToSliderPercent(MIN_PLAYBACK_SPEED)}
                  max={playbackSpeedToSliderPercent(MAX_PLAYBACK_SPEED)}
                step={5}
                  className="w-[130px]"
                  data-testid="master-audio-speed-slider"
                  onValueChange={([pct]) => setMasterAudioPlaybackSpeed(playbackSpeedFromSliderPercent(pct))}
              />
                <span className="text-[11px] w-11 shrink-0 tabular-nums text-right" style={{ color: 'var(--text-secondary)' }}>
                  {clampPlaybackSpeed(masterAudioPlaybackSpeed).toFixed(2)}×
                </span>
            </div>
            ) : null}

            <div className="flex items-center gap-1">
              <button
                type="button"
                onClick={toggleAllTimelineLanesCollapsed}
                className="p-1 rounded border"
                style={{
                  color: allTimelineLanesCollapsed ? 'var(--text-primary)' : 'var(--text-secondary)',
                  borderColor: 'var(--glass-border)',
                  background: allTimelineLanesCollapsed ? 'var(--bg-tertiary)' : 'transparent',
                }}
                title={allTimelineLanesCollapsed
                  ? 'Expand every timeline lane'
                  : 'Minimize every timeline lane'}
                aria-label={allTimelineLanesCollapsed
                  ? 'Expand every timeline lane'
                  : 'Minimize every timeline lane'}
                aria-pressed={allTimelineLanesCollapsed}
                data-testid="toggle-all-timeline-lanes-btn"
              >
                {allTimelineLanesCollapsed
                  ? <ChevronsUpDown className="w-3.5 h-3.5" />
                  : <ChevronsDownUp className="w-3.5 h-3.5" />}
              </button>
              <button onClick={() => setTimelineZoom(z => Math.max(MIN_TIMELINE_ZOOM, z - 0.2))}
                className="p-1 rounded" style={{ color: 'var(--text-secondary)' }}>
                <ZoomOut className="w-3.5 h-3.5" />
              </button>
              <span className="text-xs w-10 text-center" style={{ color: 'var(--text-secondary)' }}>
                {Math.round(timelineZoom * 100)}%
              </span>
              <button onClick={() => setTimelineZoom(z => Math.min(MAX_TIMELINE_ZOOM, z + 0.2))}
                className="p-1 rounded" style={{ color: 'var(--text-secondary)' }}>
                <ZoomIn className="w-3.5 h-3.5" />
            </button>
          </div>
            </div>
          </div>
        </div>

          {timelineSpeedSelection || timelineVolumeSelection ? (
            <div className="flex items-center flex-wrap gap-x-4 gap-y-2 px-4 py-1.5 border-b shrink-0 text-xs"
              style={{ background: 'var(--bg-primary)', borderColor: 'var(--glass-border)' }}>
              {timelineSpeedSelection ? (
                <>
                  <span className="font-semibold uppercase tracking-wide" style={{ color: 'var(--text-tertiary)' }}>
                    {timelineSpeedSelection.kind === 'audio' ? 'Audio clip' : 'Video clip'} speed
                  </span>
                  <Select
                    value={String(
                      timelineSpeedSelection.kind === 'audio'
                        ? clipAudioPlaybackSpeed(timelineSpeedSelection.clip)
                        : clipVideoPlaybackSpeed(timelineSpeedSelection.clip),
                    )}
                    onValueChange={(v) => applyTimelineClipSpeed(
                      timelineSpeedSelection.kind === 'audio' ? 'audio' : 'video',
                      timelineSpeedSelection.clip.id,
                      Number(v),
                    )}
                  >
                    <SelectTrigger className="h-7 w-[92px] text-xs" data-testid="timeline-clip-speed-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {PLAYBACK_SPEED_PRESETS.map((sp) => (
                        <SelectItem key={sp} value={String(sp)}>{sp}×</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <span className="text-[11px] max-w-md" style={{ color: 'var(--text-tertiary)' }}>
                    Higher speed shortens the clip on the timeline and plays media faster in preview.
                  </span>
                </>
              ) : null}
              {timelineVolumeSelection ? (
                <div className="flex items-center flex-wrap gap-2 min-w-0">
                  <span className="font-semibold uppercase tracking-wide shrink-0" style={{ color: 'var(--text-tertiary)' }}>
                    {timelineVolumeSelection.lane === 'music' ? 'Music' : 'Audio'} clip volume
                  </span>
                  <Button
                    type="button"
                    variant="outline"
                    size="icon"
                    className="h-7 w-7 shrink-0 rounded-lg"
                    style={{ borderColor: 'var(--glass-border)', color: 'var(--text-secondary)' }}
                    data-testid="timeline-clip-volume-down"
                    title="Lower volume"
                    onClick={() => applyTimelineClipVolume(
                      timelineVolumeSelection.lane,
                      timelineVolumeSelection.clip.id,
                      timelineClipVolumeValue - TIMELINE_CLIP_VOLUME_STEP,
                    )}
                  >
                    <Minus className="w-3.5 h-3.5" />
                  </Button>
                  <Slider
                    value={[Math.round(timelineClipVolumeValue * 100)]}
                    min={0}
                    max={100}
                    step={1}
                    className="w-[140px]"
                    data-testid="timeline-clip-volume-slider"
                    onValueChange={([pct]) => applyTimelineClipVolume(
                      timelineVolumeSelection.lane,
                      timelineVolumeSelection.clip.id,
                      pct / 100,
                    )}
                  />
                  <Button
                    type="button"
                    variant="outline"
                    size="icon"
                    className="h-7 w-7 shrink-0 rounded-lg"
                    style={{ borderColor: 'var(--glass-border)', color: 'var(--text-secondary)' }}
                    data-testid="timeline-clip-volume-up"
                    title="Raise volume"
                    onClick={() => applyTimelineClipVolume(
                      timelineVolumeSelection.lane,
                      timelineVolumeSelection.clip.id,
                      timelineClipVolumeValue + TIMELINE_CLIP_VOLUME_STEP,
                    )}
                  >
                    <Plus className="w-3.5 h-3.5" />
                  </Button>
                  <span className="text-[11px] w-10 shrink-0 tabular-nums"
                    style={{ color: 'var(--text-secondary)' }}>
                    {Math.round(timelineClipVolumeValue * 100)}%
                  </span>
                </div>
              ) : null}
            </div>
          ) : null}

          {/* Scrollable timeline — sticky labels (z-100); playhead clamped to clip area */}
          <div className="relative flex-1 min-h-0 overflow-hidden">
          <div
            className="absolute inset-0 overflow-x-auto overflow-y-auto"
            ref={timelineRef}
            onScroll={(e) => setTimelineScrollLeft(e.currentTarget.scrollLeft)}
          >
            <div className="relative" style={{ minWidth: `${totalDuration * pxPerSec + TIMELINE_LABEL_WIDTH_PX + 200}px` }}>

              {/* Time ruler */}
              <div className="sticky top-0 z-10 h-8 flex border-b"
                style={{ background: 'var(--bg-secondary)', borderColor: 'var(--glass-border)' }}>
                <div
                  className={`${TIMELINE_RULER_CORNER_CLASS} flex flex-col justify-center gap-1 px-2`}
                  style={{ ...TIMELINE_LABEL_PANEL_STYLE, zIndex: TIMELINE_LABEL_Z_INDEX + 10 }}
                >
                  <VideoEditorTimelineBookendsControl
                    compact
                    bookends={videoBookends}
                    onChange={handleVideoBookendsChange}
                    onPersist={persistVideoBookends}
                    defaultTitle={story?.title || ''}
                    aspectRatio={exportAspectRatio}
                    narrationProjectId={isNarrationEditor ? projectId : null}
                    storyId={isNarrationEditor ? null : storyId}
                  />
                </div>
                <div
                  className="relative flex-1 cursor-pointer"
                  onMouseDown={beginPlayheadDrag}
                  role="presentation">
                  {visibleTimelineTimeMarks.map((i) => (
                    <div key={i} className="absolute top-0 h-full flex flex-col justify-end pb-1 pointer-events-none"
                      style={{ left: i * pxPerSec }}>
                      <div className="w-px h-3" style={{ background: 'var(--glass-border)' }} />
                      <span className="text-[9px] ml-1" style={{ color: 'var(--text-tertiary)' }}>
                        {formatTime(i)}
                      </span>
                    </div>
                  ))}
            </div>
          </div>

              {/* Image Timeline */}
          {videoTracks.map((track, trackIdx) => {
                const imageLaneSource = track
                  .map((clip) => {
                    const imageUrl = clipImageUrlForTimelineDisplay(clip, sceneByIdMap, scenesSnapshot);
                    if (!imageUrl) return null;
                    const withImage = { ...clip, imageUrl };
                    return withImage.videoUrl
                      ? { ...withImage, id: `${clip.id}__image`, sourceClipId: clip.id, videoUrl: null }
                      : withImage;
                  })
                  .filter(Boolean);
                const imageTrack = mergeLaneClipsWithBookends(
                  imageLaneSource,
                  trackIdx === 0 ? bookendTimeline.clips.image : [],
                  bookendTimeline.introDur,
                );
                const imageLaneKey = timelineLaneKey('image', trackIdx);
                const imageLaneCollapsed = isTimelineLaneCollapsed(collapsedTimelineLanes, imageLaneKey);
            return (
                  <div key={`itrack-${trackIdx}`} className="flex border-b"
                    style={{
                      borderColor: 'var(--glass-border)',
                      opacity: timelineActive.image ? 1 : 0.45,
                      ...timelineLaneRowSizeStyle(imageLaneCollapsed, 72),
                    }}>
                    <div
                      className={`${TIMELINE_LABEL_STICKY_CLASS} flex flex-col justify-center gap-1 px-2 ${imageLaneCollapsed ? 'py-0 overflow-hidden' : 'py-1.5'}`}
                      style={TIMELINE_LABEL_PANEL_STYLE}>
                      <div className="flex items-center justify-between gap-1 min-w-0">
                        <div className="flex items-center gap-1 min-w-0">
                          <TimelineLaneMinimizeButton
                            collapsed={imageLaneCollapsed}
                            onToggle={() => toggleTimelineLane(imageLaneKey)}
                            label={`Image ${trackIdx + 1} track`}
                            testId={`timeline-lane-minimize-image-${trackIdx}`}
                          />
                          <ImageIcon className="w-3 h-3 shrink-0" style={{ color: '#f59e0b' }} />
                          <span
                            className="text-[10px] font-medium truncate"
                            style={{ color: 'var(--text-secondary)' }}
                            title={`Image ${trackIdx + 1}`}
                          >
                            Image {trackIdx + 1}
                          </span>
                        </div>
                        {trackIdx === 0 ? (
                          <button
                            type="button"
                            onClick={() => setTimelineActive(prev => ({ ...prev, image: !prev.image }))}
                            className="text-[10px] rounded px-1.5 py-0.5 border shrink-0"
                            style={{ borderColor: 'var(--glass-border)', color: timelineActive.image ? '#f59e0b' : 'var(--text-tertiary)' }}>
                            {timelineActive.image ? 'Active' : 'Off'}
                          </button>
                        ) : null}
                      </div>
                </div>

                    <div className="relative flex-1 z-0"
                      ref={(node) => setTrackRef('image', trackIdx, node)}
                      style={{
                        background: trackIdx % 2 === 0 ? 'var(--bg-primary)' : 'var(--bg-tertiary)',
                        ...timelineLaneClipAreaStyle(imageLaneCollapsed),
                      }}
                      onDragOver={(e) => e.preventDefault()}
                      onDrop={(e) => handleTimelineDrop(e, trackIdx, 'image')}>
                      {visibleTimelineClips(trackTimelineGaps(imageTrack)).map(gap => {
                        const isSelected = selectedTimelineClip?.type === 'gap'
                          && selectedTimelineClip.trackType === 'image'
                          && selectedTimelineClip.trackIdx === trackIdx
                          && selectedTimelineClip.id === gap.id;
                      return (
                        <div
                            key={gap.id}
                            className="absolute top-2 bottom-2 rounded-lg select-none group flex items-center justify-center"
                            style={{
                              left: gap.startTime * pxPerSec,
                              width: Math.max(24, gap.duration * pxPerSec - 2),
                              background: isSelected ? 'rgba(245, 158, 11, 0.18)' : 'rgba(148, 163, 184, 0.10)',
                              border: `1px dashed ${isSelected ? '#f59e0b' : 'rgba(148, 163, 184, 0.35)'}`,
                              boxShadow: isSelected ? '0 0 0 2px rgba(245, 158, 11, 0.18)' : 'none',
                              zIndex: isSelected ? 20 : 2,
                              cursor: 'pointer',
                            }}
                            onClick={(e) => {
                              e.stopPropagation();
                              setSelectedTimelineClip({ type: 'gap', trackType: 'image', trackIdx, id: gap.id });
                            }}
                            title={`Blank space: ${formatTime(gap.duration)}`}
                            data-testid={`image-gap-${trackIdx}-${gap.id}`}>
                            <div
                              className="absolute right-0 top-0 bottom-0 w-3 z-20 cursor-ew-resize bg-white/10 opacity-0 group-hover:opacity-100 hover:bg-white/30 transition-opacity"
                              onMouseDown={(e) => beginGapResize(e, gap, 'video', trackIdx)}
                              title="Resize blank space"
                            />
                            <div className="flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] opacity-0 group-hover:opacity-100 transition-opacity"
                              style={{ background: '#0006', color: '#fff' }}>
                              Gap {formatTime(gap.duration)}
                              <button
                                className="p-0.5 rounded hover:bg-white/20 transition-colors"
                                onMouseDown={(e) => e.stopPropagation()}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  closeTimelineGap(gap);
                                }}
                                title="Delete blank space">
                                <Trash2 className="w-2.5 h-2.5 text-white" />
                              </button>
                            </div>
                          </div>
                        );
                      })}
                      {(() => {
                        const imageClipsForGates = visibleTimelineClips(imageTrack, {
                          getDuration: (c) => {
                            const sourceVideo = c.sourceClipId
                              ? track.find((row) => row.id === c.sourceClipId)
                              : null;
                            return imageLaneClipDrawDuration(c, sourceVideo || c);
                          },
                        }).filter((c) => !c.isBookend);
                        const sortedImage = [...imageClipsForGates].sort(
                          (a, b) => (a.startTime || 0) - (b.startTime || 0),
                        );
                        return sortedImage.slice(0, -1).map((clip, gateIdx) => {
                          const next = sortedImage[gateIdx + 1];
                          const sourceVideo = clip.sourceClipId
                            ? track.find((row) => row.id === clip.sourceClipId)
                            : track.find((row) => row.id === clip.id) || clip;
                          const imageDrawSec = imageLaneClipDrawDuration(clip, sourceVideo || clip);
                          const leftPx = (clip.startTime + imageDrawSec) * pxPerSec;
                          const editId = clip.sourceClipId || String(clip.id).replace(/__image$/, '');
                          return (
                            <TimelineTransitionGate
                              key={`img-tr-${clip.id}-${next.id}`}
                              leftPx={leftPx}
                              transitionOut={sourceVideo?.transitionOut}
                              testId={`image-transition-gate-${clip.id}`}
                              onClick={() => openTransitionPicker({
                                clipId: editId,
                                trackIdx,
                                fromClip: clip,
                                toClip: next,
                                transitionOut: sourceVideo?.transitionOut,
                                transitionDuration: sourceVideo?.transitionDuration,
                              })}
                            />
                          );
                        });
                      })()}
                      {visibleTimelineClips(imageTrack, {
                        getDuration: (c) => {
                          const sourceVideo = c.sourceClipId
                            ? track.find((row) => row.id === c.sourceClipId)
                            : null;
                          return imageLaneClipDrawDuration(c, sourceVideo || c);
                        },
                      }).map(clip => {
                        const selectionId = clip.sourceClipId || String(clip.id).replace(/__image$/, '');
                        const isSelected = isClipInTimelineSelection({ type: 'image', id: selectionId }, selectedTimelineClips)
                          || isClipInTimelineSelection({ type: 'image', id: clip.id }, selectedTimelineClips);
                        const sourceVideo = clip.sourceClipId
                          ? track.find((c) => c.id === clip.sourceClipId)
                          : null;
                        const imageDrawSec = imageLaneClipDrawDuration(clip, sourceVideo || clip);
                        const scenePal = clip.isBookend ? null : sceneTimelineColors(timelineClipSceneIndex(clip));
                        const bookendStyle = clip.isBookend ? bookendTimelineClipStyle(clip, isSelected) : null;
                        return (
                          <motion.div key={clip.id}
                            className="absolute top-1 bottom-1 rounded-xl overflow-hidden flex items-center select-none group"
                            style={{
                              left: clip.startTime * pxPerSec,
                              width: Math.max(40, imageDrawSec * pxPerSec - 2),
                              ...(bookendStyle || {
                                background: scenePal?.image ?? '#f59e0b',
                                opacity: 0.9,
                                boxShadow: isSelected ? '0 0 0 2px white, 0 0 0 4px #f59e0b' : 'none',
                              }),
                              cursor: clip.isBookend ? 'pointer' : (draggingClipId === (clip.sourceClipId || clip.id) ? 'grabbing' : 'grab'),
                              zIndex: isSelected || draggingClipId === (clip.sourceClipId || clip.id) ? 28 : 5,
                            }}
                            onMouseDown={(e) => { if (!clip.isBookend) beginClipDrag(e, clip, trackIdx, 'image'); }}
                            onClick={(e) => {
                              if (clip.isBookend) {
                                setSelectedTimelineClip({ type: 'bookend', id: clip.id, role: clip.bookendRole });
                                playback.seek(clip.startTime);
                                return;
                              }
                              selectTimelineClip(
                                { type: 'image', id: selectionId },
                                { additive: e.metaKey || e.ctrlKey },
                              );
                            playback.seek(clip.startTime);
                          }}
                            onDoubleClick={(e) => {
                              e.stopPropagation();
                              if (clip.isBookend) return;
                              openReplacementDialog(clip, trackIdx, 'image');
                            }}
                            data-testid={`image-timeline-clip-${clip.id}`}>
                            <div
                              className="absolute left-0 top-0 bottom-0 w-3 z-20 cursor-ew-resize bg-white/10 opacity-0 group-hover:opacity-100 hover:bg-white/30 transition-opacity"
                              onMouseDown={(e) => beginClipResize(e, clip, trackIdx, 'left')}
                              title="Resize image start"
                            />
                            <div
                              className="absolute right-0 top-0 bottom-0 w-3 z-20 cursor-ew-resize bg-white/10 opacity-0 group-hover:opacity-100 hover:bg-white/30 transition-opacity"
                              onMouseDown={(e) => beginClipResize(e, clip, trackIdx, 'right')}
                              title="Resize image duration"
                            />
                            {clip.imageUrl && (
                              <div className="absolute inset-0">
                                <img src={mediaUrlForPlayback(clip.imageUrl)} alt="" className="w-full h-full object-cover opacity-30" />
                              </div>
                            )}
                            <div className="relative z-10 px-2 flex items-center justify-between w-full pointer-events-none">
                              <span className="text-[10px] font-medium text-white truncate">
                                {clip.isBookend ? `${clip.bookendRole === 'outro' ? 'Outro' : 'Intro'} bookend` : clip.name}
                              </span>
                              <div className="flex gap-0.5 shrink-0 ml-1 pointer-events-auto">
                                {!clip.sourceClipId && !clip.isBookend && (
                                  <button className="p-0.5 rounded hover:bg-white/20 transition-colors"
                                    onClick={(e) => { e.stopPropagation(); setEditingClip({ clip, trackIdx }); }}
                                    data-testid={`edit-image-clip-btn-${clip.id}`}>
                                    <Scissors className="w-2.5 h-2.5 text-white" />
                                  </button>
                                )}
                                <button className="p-0.5 rounded hover:bg-white/20 transition-colors"
                                  onClick={(e) => { e.stopPropagation(); if (!clip.isBookend) removeClipFromTrack(trackIdx, clip.sourceClipId || clip.id); }}
                                  data-testid={`remove-image-clip-btn-${clip.id}`}>
                                  <Trash2 className="w-2.5 h-2.5 text-white" />
                                </button>
                        </div>
                            </div>
                          </motion.div>
                        );
                      })}
                      {imageTrack.length === 0 && (
                        <div className="absolute inset-0 flex items-center justify-center text-xs"
                          style={{ color: 'var(--text-tertiary)' }}>
                          Drag images here
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}

              {/* Animation timeline — Ken Burns / camera moves on image clips */}
              <AnimationTimelineTrack
                clips={animationLaneClips}
                pxPerSec={pxPerSec}
                timelineActive={timelineActive.animation}
                selectedClipId={
                  selectedTimelineClip?.type === 'image' || selectedTimelineClip?.type === 'video'
                    ? selectedTimelineClip.id
                    : null
                }
                editorAnimationSpeed={editorAnimationSpeed}
                onEditorAnimationSpeedChange={handleEditorAnimationSpeedChange}
                isSongBased={isSongEditorProject}
                songBeatSyncSpeed={songMotionPreviewOpts.animationSpeed}
                onSongBeatSyncSpeedChange={handleSongBeatSyncSpeedChange}
                songBeatSyncEnabled={songMotionPreviewOpts.defaultAnimationEnabled !== false}
                onToggleActive={() => setTimelineActive((prev) => ({ ...prev, animation: !prev.animation }))}
                onSelectClip={(clip) => {
                  setSelectedTimelineClip({ type: 'image', id: clip.id });
                  playback.seek(clip.startTime);
                }}
                onPresetChange={patchClipMotion}
                onApplyToAll={applyMotionToAllImages}
                onApplyRandomStory={() => applyRandomMotionToImageClips({ storyOnly: true })}
                onApplyRandomAll={() => applyRandomMotionToImageClips({ storyOnly: false })}
                collapsed={isTimelineLaneCollapsed(collapsedTimelineLanes, timelineLaneKey('animation', 0))}
                onToggleCollapsed={() => toggleTimelineLane(timelineLaneKey('animation', 0))}
                viewportStartSec={timelineViewportRange.startSec}
                viewportEndSec={timelineViewportRange.endSec}
              />

              {/* Video Timeline */}
              {videoTracks.map((track, trackIdx) => {
                const videoLaneSource = track.filter((clip) => clip.videoUrl && !clip.isBookend);
                const videoTrack = mergeLaneClipsWithBookends(
                  videoLaneSource,
                  trackIdx === 0 ? bookendTimeline.clips.video : [],
                  bookendTimeline.introDur,
                );
                const videoLaneKey = timelineLaneKey('video', trackIdx);
                const videoLaneCollapsed = isTimelineLaneCollapsed(collapsedTimelineLanes, videoLaneKey);
                return (
                <div key={`vtrack-${trackIdx}`} className="flex border-b"
                  style={{
                    borderColor: 'var(--glass-border)',
                    opacity: timelineActive.video ? 1 : 0.45,
                    ...timelineLaneRowSizeStyle(videoLaneCollapsed, 72),
                  }}>
                  {/* Track label */}
                  <div
                    className={`${TIMELINE_LABEL_STICKY_CLASS} flex flex-col justify-center gap-1 px-2 ${videoLaneCollapsed ? 'py-0 overflow-hidden' : 'py-1.5'}`}
                    style={TIMELINE_LABEL_PANEL_STYLE}>
                    <div className="flex items-center justify-between gap-1 min-w-0">
                      <div className="flex items-center gap-1 min-w-0">
                        <TimelineLaneMinimizeButton
                          collapsed={videoLaneCollapsed}
                          onToggle={() => toggleTimelineLane(videoLaneKey)}
                          label={`Video ${trackIdx + 1} track`}
                          testId={`timeline-lane-minimize-video-${trackIdx}`}
                        />
                        <Film className="w-3 h-3 shrink-0" style={{ color: 'var(--primary)' }} />
                        <span
                          className="text-[10px] font-medium truncate"
                          style={{ color: 'var(--text-secondary)' }}
                          title={`Video ${trackIdx + 1}`}
                        >
                          Video {trackIdx + 1}
                        </span>
                      </div>
                      {trackIdx === 0 ? (
                        <button
                          type="button"
                          onClick={() => setTimelineActive((prev) => ({ ...prev, video: !prev.video }))}
                          className="text-[10px] rounded px-1.5 py-0.5 border shrink-0"
                          style={{
                            borderColor: 'var(--glass-border)',
                            color: timelineActive.video ? 'var(--primary)' : 'var(--text-tertiary)',
                          }}
                        >
                          {timelineActive.video ? 'Active' : 'Off'}
                        </button>
                      ) : (
                        <button
                          type="button"
                          onClick={() => {
                            recordEditorHistory();
                            setVideoTracks((p) => p.filter((_, i) => i !== trackIdx));
                          }}
                          className="shrink-0 opacity-50 hover:opacity-100 transition-opacity"
                          title="Remove video track"
                        >
                          <Trash2 className="w-3 h-3" style={{ color: '#ef4444' }} />
                        </button>
                      )}
                    </div>
                    {trackIdx === 0 && !videoLaneCollapsed ? (
                      <button
                        type="button"
                        onClick={() => setVideoMasterMuted((m) => !m)}
                        className="self-start text-[10px] rounded px-1.5 py-0.5 border inline-flex items-center gap-0.5"
                        style={{
                          borderColor: videoMasterMuted ? 'var(--primary)' : 'var(--glass-border)',
                          color: videoMasterMuted ? 'var(--primary)' : 'var(--text-tertiary)',
                        }}
                        title={videoMasterMuted ? 'Unmute all video clips' : 'Mute all video clips'}
                        data-testid="video-timeline-master-mute-btn"
                      >
                        {videoMasterMuted ? <VolumeX className="w-3 h-3" /> : <Volume2 className="w-3 h-3" />}
                        <span>{videoMasterMuted ? 'Unmute' : 'Mute'}</span>
                      </button>
                    ) : null}
                  </div>

                  {/* Track clips */}
                  <div className="relative flex-1 z-0"
                    ref={(node) => setTrackRef('video', trackIdx, node)}
                    style={{
                      background: trackIdx % 2 === 0 ? 'var(--bg-primary)' : 'var(--bg-tertiary)',
                      ...timelineLaneClipAreaStyle(videoLaneCollapsed),
                    }}
                    onDragOver={(e) => e.preventDefault()}
                    onDrop={(e) => handleTimelineDrop(e, trackIdx, 'video')}>
                    {visibleTimelineClips(trackTimelineGaps(videoTrack)).map(gap => {
                      const isSelected = selectedTimelineClip?.type === 'gap'
                        && selectedTimelineClip.trackType === 'video'
                        && selectedTimelineClip.trackIdx === trackIdx
                        && selectedTimelineClip.id === gap.id;
                      return (
                        <div
                          key={gap.id}
                          className="absolute top-2 bottom-2 rounded-lg select-none group flex items-center justify-center"
                          style={{
                            left: gap.startTime * pxPerSec,
                            width: Math.max(24, gap.duration * pxPerSec - 2),
                            background: isSelected ? 'rgba(148, 163, 184, 0.22)' : 'rgba(148, 163, 184, 0.10)',
                            border: `1px dashed ${isSelected ? 'var(--primary)' : 'rgba(148, 163, 184, 0.35)'}`,
                            boxShadow: isSelected ? '0 0 0 2px var(--primary-light)' : 'none',
                            zIndex: isSelected ? 20 : 2,
                            cursor: 'pointer',
                          }}
                          onClick={(e) => {
                            e.stopPropagation();
                            setSelectedTimelineClip({ type: 'gap', trackType: 'video', trackIdx, id: gap.id });
                          }}
                          title={`Blank space: ${formatTime(gap.duration)}`}
                          data-testid={`video-gap-${trackIdx}-${gap.id}`}>
                          <div
                            className="absolute right-0 top-0 bottom-0 w-3 z-20 cursor-ew-resize bg-white/10 opacity-0 group-hover:opacity-100 hover:bg-white/30 transition-opacity"
                            onMouseDown={(e) => beginGapResize(e, gap, 'video', trackIdx)}
                            title="Resize blank space"
                          />
                          <div className="flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] opacity-0 group-hover:opacity-100 transition-opacity"
                            style={{ background: '#0006', color: '#fff' }}>
                            Gap {formatTime(gap.duration)}
                            <button
                              className="p-0.5 rounded hover:bg-white/20 transition-colors"
                              onMouseDown={(e) => e.stopPropagation()}
                              onClick={(e) => {
                                e.stopPropagation();
                                closeTimelineGap(gap);
                              }}
                              title="Delete blank space">
                              <Trash2 className="w-2.5 h-2.5 text-white" />
                            </button>
                          </div>
                        </div>
                      );
                    })}
                    {(() => {
                      const videoClipsForGates = visibleTimelineClips(videoTrack, {
                        getDuration: (c) => Math.max(
                          clipVideoSnippetDrawDuration(c),
                          Number(c.timelineSlotDuration) || 0,
                          clipVideoTimelineDuration(c),
                        ),
                      }).filter((c) => !c.isBookend);
                      const sortedVideo = [...videoClipsForGates].sort(
                        (a, b) => (a.startTime || 0) - (b.startTime || 0),
                      );
                      return sortedVideo.slice(0, -1).map((clip, gateIdx) => {
                        const next = sortedVideo[gateIdx + 1];
                        const videoDrawSec = clipVideoSnippetDrawDuration(clip);
                        const leftPx = (clip.startTime + videoDrawSec) * pxPerSec;
                        return (
                          <TimelineTransitionGate
                            key={`vid-tr-${clip.id}-${next.id}`}
                            leftPx={leftPx}
                            transitionOut={clip.transitionOut}
                            testId={`video-transition-gate-${clip.id}`}
                            onClick={() => openTransitionPicker({
                              clipId: clip.id,
                              trackIdx,
                              fromClip: clip,
                              toClip: next,
                              transitionOut: clip.transitionOut,
                              transitionDuration: clip.transitionDuration,
                            })}
                          />
                        );
                      });
                    })()}
                    {visibleTimelineClips(videoTrack, {
                      getDuration: (c) => Math.max(
                        clipVideoSnippetDrawDuration(c),
                        Number(c.timelineSlotDuration) || 0,
                        clipVideoTimelineDuration(c),
                      ),
                    }).map(clip => {
                      const isSelected = isClipInTimelineSelection({ type: 'video', id: clip.id }, selectedTimelineClips);
                      const pairedAudio = findPairedAudioClipForVideoOnActiveLanes(clip, audioTracks, timelineActive);
                      const extendGap = timelineActive.audio && !isPitchTimelineClip(clip, pairedAudio)
                        ? getVideoExtendGapForClip(clip, pairedAudio, masterAudioPlaybackSpeed)
                        : null;
                      const videoDrawSec = clipVideoSnippetDrawDuration(clip);
                      const slotSec = isPitchTimelineClip(clip, pairedAudio)
                        ? videoDrawSec
                        : Number(clip.timelineSlotDuration) > 0
                          ? Number(clip.timelineSlotDuration)
                          : extendGap
                            ? extendGap.slotTimelineSec
                            : Math.max(videoDrawSec, clipVideoTimelineDuration(clip));
                      const scenePal = clip.isBookend ? null : sceneTimelineColors(timelineClipSceneIndex(clip));
                      const bookendStyle = clip.isBookend ? bookendTimelineClipStyle(clip, isSelected) : null;
                      const slotW = Math.max(40, slotSec * pxPerSec - 2);
                      const videoW = Math.max(40, videoDrawSec * pxPerSec - 2);
                      const gapW = extendGap ? Math.max(28, extendGap.gapSec * pxPerSec - 2) : 0;
                      return (
                      <motion.div
                        key={`${clip.id}:${mediaUrlForPlayback(clip.videoUrl || clip.imageUrl || '')}`}
                        className="absolute top-1 bottom-1"
                        style={{
                          left: clip.startTime * pxPerSec,
                          width: slotW,
                          zIndex: isSelected || draggingClipId === clip.id ? 28 : 4,
                        }}
                      >
                      <motion.div
                        className="absolute top-0 bottom-0 left-0 rounded-xl overflow-hidden flex items-center select-none group"
                        style={{
                          width: videoW,
                          ...(bookendStyle || {
                            background: scenePal?.video ?? 'var(--primary)',
                            opacity: 0.9,
                            boxShadow: isSelected
                              ? `0 0 0 2px white, 0 0 0 4px ${scenePal?.video ?? 'var(--primary)'}`
                              : 'none',
                          }),
                          cursor: clip.isBookend ? 'pointer' : (draggingClipId === clip.id ? 'grabbing' : 'grab'),
                          zIndex: 2,
                        }}
                        onMouseDown={(e) => { if (!clip.isBookend) beginClipDrag(e, clip, trackIdx, 'video'); }}
                        onClick={(e) => {
                          if (clip.isBookend) {
                            setSelectedTimelineClip({ type: 'bookend', id: clip.id, role: clip.bookendRole });
                            playback.seek(clip.startTime);
                            return;
                          }
                          selectTimelineClip(
                            { type: 'video', id: clip.id },
                            { additive: e.metaKey || e.ctrlKey },
                          );
                          playback.seek(clip.startTime);
                        }}
                        onDoubleClick={(e) => {
                          e.stopPropagation();
                          if (clip.isBookend) return;
                          openReplacementDialog(clip, trackIdx, 'video');
                        }}
                        data-testid={`timeline-clip-${clip.id}`}>
                        <>
                          <div
                            className="absolute left-0 top-0 bottom-0 w-3 z-20 cursor-ew-resize bg-white/10 opacity-0 group-hover:opacity-100 hover:bg-white/30 transition-opacity"
                            onMouseDown={(e) => beginClipResize(e, clip, trackIdx, 'left')}
                            title={clip.videoUrl
                              ? 'Drag to reveal earlier footage (up to start of file)'
                              : 'Resize image start'}
                          />
                          <div
                            className="absolute right-0 top-0 bottom-0 w-3 z-20 cursor-ew-resize bg-white/10 opacity-0 group-hover:opacity-100 hover:bg-white/30 transition-opacity"
                            onMouseDown={(e) => beginClipResize(e, clip, trackIdx, 'right')}
                            title={clip.videoUrl
                              ? `Drag to extend trim (up to ${clipVideoMediaCapSeconds(clip).toFixed(1)}s source)`
                              : 'Trim end (visible video)'}
                          />
                        </>
                        {/* Thumbnail bg — video takes precedence over still image */}
                        {clip.videoUrl ? (
                          <div className="absolute inset-0">
                            <video
                              key={mediaUrlForPlayback(clip.videoUrl)}
                              src={mediaUrlForPlayback(clip.videoUrl)}
                              className="w-full h-full object-cover opacity-30 pointer-events-none"
                              muted
                              playsInline
                              preload="metadata"
                              onLoadedData={(e) => {
                                const el = e.currentTarget;
                                const seek = Math.max(0, Number(clip.trimStart) || 0);
                                if (Number.isFinite(seek) && el.duration > seek) {
                                  el.currentTime = seek;
                                }
                              }}
                            />
                          </div>
                        ) : clip.imageUrl ? (
                          <div className="absolute inset-0">
                            <img
                              key={mediaUrlForPlayback(clip.imageUrl)}
                              src={mediaUrlForPlayback(clip.imageUrl)}
                              alt=""
                              className="w-full h-full object-cover opacity-30"
                            />
                          </div>
                        ) : null}
                        {(clipFiltersFromClip(clip).length > 0
                          || normalizeTransitionOut(clip.transitionOut) !== 'cut') ? (
                          <div className="absolute right-4 top-1 z-30 flex flex-col items-end gap-0.5 pointer-events-none">
                            {clipFiltersFromClip(clip).length > 0 ? (
                              <div
                                className="flex items-center gap-0.5 rounded px-1 py-0.5 text-[8px] font-semibold uppercase tracking-wide"
                                style={{ background: 'rgba(0,0,0,0.55)', color: '#93c5fd' }}
                                title={`Filters: ${clipFiltersFromClip(clip).map((f) => filterShortLabel(f.id)).join(', ')}`}
                              >
                                <SlidersHorizontal className="w-2.5 h-2.5" />
                                <span>{clipFiltersFromClip(clip).length}</span>
                              </div>
                            ) : null}
                            {normalizeTransitionOut(clip.transitionOut) !== 'cut' ? (
                              <div
                                className="flex items-center gap-0.5 rounded px-1 py-0.5 text-[8px] font-semibold uppercase tracking-wide"
                                style={{ background: 'rgba(0,0,0,0.55)', color: '#fde68a' }}
                                title={`Transition: ${transitionShortLabel(clip.transitionOut) || clip.transitionOut}`}
                              >
                                <TwinneeLogo className="w-2.5 h-2.5" />
                                <span className="max-w-[52px] truncate">{transitionShortLabel(clip.transitionOut)}</span>
                              </div>
                            ) : null}
                          </div>
                        ) : null}
                        <div className="relative z-10 px-1 flex items-center justify-between w-full gap-0.5">
                          <span className="text-[10px] font-medium text-white truncate pointer-events-none flex-1 min-w-0 px-1">{clip.name}</span>
                          <div className="flex items-center gap-0.5 shrink-0 pointer-events-auto">
                            {clip.videoUrl ? (
                              <TimelineClipVerticalVolume
                                volume={clip.volume != null ? Number(clip.volume) : DEFAULT_VIDEO_EMBEDDED_AUDIO_VOLUME}
                                muted={Boolean(clip.muted)}
                                accent="#fff"
                                testId={`video-clip-volume-${clip.id}`}
                                onVolumeChange={(vol) => applyTimelineClipVolume('video', clip.id, vol)}
                                onMuteToggle={() => toggleVideoClipEmbeddedAudioMute(trackIdx, clip.id)}
                              />
                            ) : null}
                            <button className="p-0.5 rounded hover:bg-white/20 transition-colors"
                              onClick={(e) => { e.stopPropagation(); setEditingClip({ clip, trackIdx }); }}
                              data-testid={`edit-clip-btn-${clip.id}`}>
                              <Scissors className="w-2.5 h-2.5 text-white" />
                            </button>
                            <button className="p-0.5 rounded hover:bg-white/20 transition-colors"
                              onClick={(e) => { e.stopPropagation(); removeClipFromTrack(trackIdx, clip.id); }}
                              data-testid={`remove-clip-btn-${clip.id}`}>
                              <Trash2 className="w-2.5 h-2.5 text-white" />
                            </button>
                          </div>
                        </div>
                      </motion.div>
                        {extendGap ? (
                          <button
                            type="button"
                            className="absolute top-0 bottom-0 flex items-center justify-center gap-0.5 rounded-r-xl border-2 border-red-500/80 select-none transition-opacity hover:opacity-100"
                            style={{
                              left: videoW,
                              width: gapW,
                              background: 'linear-gradient(90deg, rgba(239,68,68,0.45), rgba(220,38,38,0.65))',
                              color: '#fff',
                              cursor: 'pointer',
                              zIndex: 3,
                              opacity: 0.92,
                            }}
                            title={`Extend video by ${extendGap.gapSec.toFixed(1)}s from last frame`}
                            data-testid={`timeline-extend-video-${clip.id}`}
                            onMouseDown={(e) => e.stopPropagation()}
                            onClick={(e) => {
                              e.stopPropagation();
                              openExtendVideoDialog(clip, trackIdx);
                            }}
                          >
                            <span className="text-[9px] font-bold uppercase tracking-wide px-1">Extend</span>
                            <ChevronRight className="w-4 h-4 shrink-0" strokeWidth={3} />
                          </button>
                        ) : null}
                      </motion.div>
                      );
                    })}
                    {videoTrack.length === 0 && (
                      <div className="absolute inset-0 flex items-center justify-center text-xs"
                        style={{ color: 'var(--text-tertiary)' }}>
                        Drag generated videos here
                    </div>
                  )}
                </div>
              </div>
            );
          })}

          {/* Audio Tracks */}
          {audioTracks.map((track, trackIdx) => {
                const laneMask = audioLanePlaybackMask(timelineActive, audioTracks.length);
                const laneActive = laneMask[trackIdx];
                const laneMeta = audioTrackMeta[trackIdx] || {};
                const laneLabel = laneMeta.label
                  || (laneMeta.languageCode ? audioTranslationLanguageLabel(laneMeta.languageCode) : `Audio ${trackIdx + 1}`);
                const audioLaneClips = trackIdx === 0
                  ? mergeLaneClipsWithBookends(track, bookendTimeline.clips.audio, bookendTimeline.introDur)
                  : (track || []).map((c) => offsetTimelineClipForBookendIntro(c, bookendTimeline.introDur));
                const subMask = subtitleLanePlaybackMask(timelineActive, audioTracks.length);
                const subLaneActive = subMask[trackIdx];
                const subLaneLabel = subtitleLaneLabel(trackIdx, audioTrackMeta);
                const subRow = (subtitleTracks || [])[trackIdx] || [];
                const audioLaneKey = timelineLaneKey('audio', trackIdx);
                const audioLaneCollapsed = isTimelineLaneCollapsed(collapsedTimelineLanes, audioLaneKey);
                const subtitleLaneKey = timelineLaneKey('subtitle', trackIdx);
                const subtitleLaneCollapsed = isTimelineLaneCollapsed(collapsedTimelineLanes, subtitleLaneKey);
            return (
                <Fragment key={`audio-sub-block-${trackIdx}`}>
                <div className="flex border-b"
                  style={{
                    borderColor: 'var(--glass-border)',
                    opacity: laneActive ? 1 : 0.45,
                    ...timelineLaneRowSizeStyle(audioLaneCollapsed, 56),
                  }}
                  onDragOver={(e) => { e.preventDefault(); setDragOverTrack({ type: 'audio', index: trackIdx }); }}
                  onDragLeave={() => setDragOverTrack(null)}
                  onDrop={(e) => handleAudioDrop(e, trackIdx)}>
                  {/* Track label */}
                  <div
                    className={`${TIMELINE_LABEL_STICKY_CLASS} flex items-center justify-between px-2 gap-1 ${audioLaneCollapsed ? 'py-0 overflow-hidden' : 'py-1.5'}`}
                    style={TIMELINE_LABEL_PANEL_STYLE}>
                    <div className="flex items-center gap-1 min-w-0">
                      <TimelineLaneMinimizeButton
                        collapsed={audioLaneCollapsed}
                        onToggle={() => toggleTimelineLane(audioLaneKey)}
                        label={`${laneLabel} track`}
                        testId={`timeline-lane-minimize-audio-${trackIdx}`}
                      />
                      <Music className="w-3 h-3 shrink-0" style={{ color: laneMeta.isTranslation ? '#38bdf8' : '#ec4899' }} />
                      <span className="text-[10px] font-medium truncate" style={{ color: 'var(--text-secondary)' }} title={laneLabel}>
                        {laneLabel}
                      </span>
                    </div>
                    <div className="flex items-center gap-0.5 shrink-0">
                      <button
                        type="button"
                        onClick={() => toggleAudioLaneActive(trackIdx)}
                        className="text-[10px] rounded px-1.5 py-0.5 border"
                        style={{ borderColor: 'var(--glass-border)', color: laneActive ? (laneMeta.isTranslation ? '#38bdf8' : '#ec4899') : 'var(--text-tertiary)' }}
                        data-testid={`audio-lane-active-${trackIdx}`}
                      >
                        {laneActive ? 'Active' : 'Off'}
                      </button>
                      {trackIdx > 0 ? (
                        <button type="button" onClick={() => removeAudioTrackAt(trackIdx)} title="Remove audio lane">
                          <Trash2 className="w-3 h-3" style={{ color: '#ef4444', opacity: 0.5 }} />
                        </button>
                      ) : null}
                    </div>
                </div>

                  {/* Audio clips */}
                  <div className="relative flex-1 z-0"
                    ref={(node) => setAudioTrackRef(trackIdx, node)}
                    style={{
                      background: dragOverTrack?.type === 'audio' && dragOverTrack.index === trackIdx
                        ? '#ec489910' : 'var(--bg-primary)',
                      ...timelineLaneClipAreaStyle(audioLaneCollapsed),
                    }}>
                    {visibleTimelineClips(trackTimelineGaps(audioLaneClips)).map(gap => {
                      const isSelected = selectedTimelineClip?.type === 'gap'
                        && selectedTimelineClip.trackType === 'audio'
                        && selectedTimelineClip.trackIdx === trackIdx
                        && selectedTimelineClip.id === gap.id;
                      return (
                        <div
                          key={gap.id}
                          className="absolute top-2 bottom-2 rounded-lg select-none group flex items-center justify-center"
                          style={{
                            left: gap.startTime * pxPerSec,
                            width: Math.max(24, gap.duration * pxPerSec - 2),
                            background: isSelected ? 'rgba(236, 72, 153, 0.16)' : 'rgba(148, 163, 184, 0.10)',
                            border: `1px dashed ${isSelected ? '#ec4899' : 'rgba(148, 163, 184, 0.35)'}`,
                            boxShadow: isSelected ? '0 0 0 2px rgba(236, 72, 153, 0.18)' : 'none',
                            zIndex: isSelected ? 20 : 2,
                            cursor: 'pointer',
                          }}
                          onClick={(e) => {
                            e.stopPropagation();
                            setSelectedTimelineClip({ type: 'gap', trackType: 'audio', trackIdx, id: gap.id });
                          }}
                          title={`Blank space: ${formatTime(gap.duration)}`}
                          data-testid={`audio-gap-${trackIdx}-${gap.id}`}>
                          <div
                            className="absolute right-0 top-0 bottom-0 w-3 z-20 cursor-ew-resize bg-white/10 opacity-0 group-hover:opacity-100 hover:bg-white/30 transition-opacity"
                            onMouseDown={(e) => beginGapResize(e, gap, 'audio', trackIdx)}
                            title="Resize blank space"
                          />
                          <div className="flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] opacity-0 group-hover:opacity-100 transition-opacity"
                            style={{ background: '#0006', color: '#fff' }}>
                            Gap {formatTime(gap.duration)}
                            <button
                              className="p-0.5 rounded hover:bg-white/20 transition-colors"
                              onMouseDown={(e) => e.stopPropagation()}
                              onClick={(e) => {
                                e.stopPropagation();
                                closeTimelineGap(gap);
                              }}
                              title="Delete blank space">
                              <Trash2 className="w-2.5 h-2.5 text-white" />
                            </button>
                          </div>
                        </div>
                      );
                    })}
                    {visibleTimelineClips(audioLaneClips, {
                      getDuration: (c) => clipAudioTimelineDuration(c, 0, masterAudioPlaybackSpeed),
                    }).map(clip => {
                      const isSelected = selectedTimelineClip?.type === 'audio' && selectedTimelineClip.id === clip.id;
                      const scenePal = clip.isBookend ? null : sceneTimelineColors(timelineClipSceneIndex(clip));
                      const bookendStyle = clip.isBookend ? bookendTimelineClipStyle(clip, isSelected) : null;
                      return (
                      <div key={clip.id}
                        className="absolute top-1 bottom-1 rounded-xl flex items-center px-2 select-none group"
                        style={{
                          left: clip.startTime * pxPerSec,
                          width: Math.max(40, clipAudioTimelineDuration(clip, 0, masterAudioPlaybackSpeed) * pxPerSec - 2),
                          ...(bookendStyle || {
                            background: scenePal ? `${scenePal.audio}30` : '#ec489930',
                            border: scenePal ? `1px solid ${scenePal.audio}60` : '1px solid #ec489960',
                            boxShadow: isSelected
                              ? `0 0 0 2px white, 0 0 0 4px ${scenePal?.audio ?? '#ec4899'}`
                              : 'none',
                          }),
                          cursor: clip.isBookend ? 'pointer' : (draggingClipId === clip.id ? 'grabbing' : 'grab'),
                          zIndex: isSelected || draggingClipId === clip.id ? 28 : 5,
                        }}
                        onMouseDown={(e) => { if (!clip.isBookend) beginAudioClipDrag(e, clip, trackIdx); }}
                        onClick={() => setSelectedTimelineClip({ type: clip.isBookend ? 'bookend' : 'audio', id: clip.id, role: clip.bookendRole })}
                        onDoubleClick={(e) => {
                          e.stopPropagation();
                          if (clip.isBookend) return;
                          openReplacementDialog(clip, trackIdx, 'audio');
                        }}
                        data-testid={`audio-clip-${clip.id}`}>
                        <div
                          className="absolute left-0 top-0 bottom-0 w-3 z-20 cursor-ew-resize bg-white/10 opacity-0 group-hover:opacity-100 hover:bg-white/30 transition-opacity"
                          onMouseDown={(e) => beginAudioClipResize(e, clip, trackIdx, 'left')}
                          title="Resize audio start"
                        />
                        <div
                          className="absolute right-0 top-0 bottom-0 w-3 z-20 cursor-ew-resize bg-white/10 opacity-0 group-hover:opacity-100 hover:bg-white/30 transition-opacity"
                          onMouseDown={(e) => beginAudioClipResize(e, clip, trackIdx, 'right')}
                          title="Resize audio duration"
                        />
                        <Music className="w-3 h-3 shrink-0 mr-1" style={{ color: scenePal?.audio ?? '#ec4899' }} />
                        <span className="text-[10px] truncate font-medium flex-1 min-w-0" style={{ color: scenePal?.audio ?? '#ec4899' }}>{clip.name}</span>
                        <AudioWaveform url={clip.url} waveformUrl={clip.waveformUrl} />
                        <TimelineClipVerticalVolume
                          volume={clip.volume != null ? Number(clip.volume) : 0.8}
                          muted={Boolean(clip.muted)}
                          accent="#ec4899"
                          testId={`audio-clip-volume-${clip.id}`}
                          onVolumeChange={(vol) => applyTimelineClipVolume('audio', clip.id, vol)}
                          onMuteToggle={() => {
                            setAudioTracks((prev) => prev.map((tr, idx) => (idx !== trackIdx ? tr : tr.map((c) => (
                              c.id === clip.id ? { ...c, muted: !(c.muted === true) } : c
                            )))));
                          }}
                        />
                        <button className="shrink-0 p-0.5 opacity-60 hover:opacity-100"
                          onMouseDown={(e) => e.stopPropagation()}
                          onClick={(e) => { e.stopPropagation(); removeClipFromTrack(trackIdx, clip.id, 'audio'); }}>
                          <Trash2 className="w-2.5 h-2.5" style={{ color: '#ef4444' }} />
                        </button>
                      </div>
                      );
                    })}
                    {track.length === 0 && (
                      <div className="absolute inset-0 flex items-center justify-center text-xs gap-2"
                        style={{ color: 'var(--text-tertiary)', borderStyle: 'dashed', borderWidth: 1, borderColor: 'var(--glass-border)', margin: 4, borderRadius: 8 }}>
                        <Upload className="w-3 h-3" /> Drop audio file here or click "Add Audio"
                      </div>
                    )}
                  </div>
                </div>

                {/* Subtitles paired with this audio lane */}
                <div className="flex border-b"
                  style={{
                    borderColor: 'var(--glass-border)',
                    opacity: timelineActive.subtitle && subLaneActive ? 1 : 0.45,
                    ...timelineLaneRowSizeStyle(subtitleLaneCollapsed, 44),
                  }}>
                  <div
                    className={`${TIMELINE_LABEL_STICKY_CLASS} flex items-center justify-between px-2 gap-1 ${subtitleLaneCollapsed ? 'py-0 overflow-hidden' : 'py-1.5'}`}
                    style={TIMELINE_LABEL_PANEL_STYLE}>
                    <div className="flex items-center gap-1 min-w-0">
                      <TimelineLaneMinimizeButton
                        collapsed={subtitleLaneCollapsed}
                        onToggle={() => toggleTimelineLane(subtitleLaneKey)}
                        label={`${subLaneLabel} track`}
                        testId={`timeline-lane-minimize-subtitle-${trackIdx}`}
                      />
                      <Captions className="w-3 h-3 shrink-0" style={{ color: laneMeta.isTranslation ? '#2dd4bf' : '#14b8a6' }} />
                      <span className="text-[10px] font-medium truncate" style={{ color: 'var(--text-secondary)' }} title={subLaneLabel}>
                        {subLaneLabel}
                      </span>
                    </div>
                    <button
                      type="button"
                      onClick={() => toggleSubtitleLaneActive(trackIdx)}
                      className="text-[10px] rounded px-1.5 py-0.5 border shrink-0"
                      style={{
                        borderColor: 'var(--glass-border)',
                        color: subLaneActive && timelineActive.subtitle ? '#14b8a6' : 'var(--text-tertiary)',
                      }}
                      data-testid={`subtitle-lane-active-${trackIdx}`}
                    >
                      {subLaneActive && timelineActive.subtitle ? 'Active' : 'Off'}
                    </button>
                  </div>
                  <div
                    className="relative flex-1 z-0"
                    style={{ background: 'var(--bg-primary)', ...timelineLaneClipAreaStyle(subtitleLaneCollapsed) }}
                    ref={(el) => {
                      if (el) subtitleTrackRefs.current.set(trackIdx, el);
                      else subtitleTrackRefs.current.delete(trackIdx);
                    }}
                  >
                    {visibleTimelineClips(subRow, {
                      timeOffsetSec: bookendTimeline.introDur,
                    }).map((clip) => {
                      const scenePal = sceneTimelineColors(timelineClipSceneIndex(clip));
                      const isSelected = selectedTimelineClip?.type === 'subtitle'
                        && selectedTimelineClip.trackIdx === trackIdx
                        && selectedTimelineClip.id === clip.id;
                    return (
                      <div
                        key={clip.id}
                        className="absolute top-1 bottom-1 rounded-lg flex items-center px-2 select-none group"
                        style={{
                          left: (Number(clip.startTime) + bookendTimeline.introDur) * pxPerSec,
                          width: Math.max(40, (Number(clip.duration) || 0.01) * pxPerSec - 2),
                          background: clip.muted
                            ? 'rgba(148, 163, 184, 0.2)'
                            : (scenePal ? `${scenePal.subtitle}28` : '#14b8a628'),
                          border: scenePal ? `1px solid ${scenePal.subtitle}55` : '1px solid #14b8a655',
                          boxShadow: isSelected || draggingClipId === clip.id
                            ? `0 0 0 2px white, 0 0 0 4px ${scenePal?.subtitle ?? '#14b8a6'}`
                            : 'none',
                          cursor: draggingClipId === clip.id ? 'grabbing' : 'grab',
                          zIndex: isSelected || draggingClipId === clip.id ? 28 : 4,
                        }}
                        onMouseDown={(e) => beginSubtitleClipDrag(e, clip, trackIdx)}
                        onClick={() => {
                          setSelectedTimelineClip({ type: 'subtitle', id: clip.id, trackIdx });
                          playback.seek((Number(clip.startTime) || 0) + bookendTimeline.introDur);
                        }}
                        onDoubleClick={(e) => {
                          e.stopPropagation();
                          openSubtitleLaneEditor(clip);
                        }}
                        title="Drag to move · handles to resize · double-click to edit text"
                      >
                        <div
                          data-subtitle-resize="left"
                          className="absolute left-0 top-0 bottom-0 w-3 z-20 cursor-ew-resize bg-white/10 opacity-0 group-hover:opacity-100 hover:bg-white/30 transition-opacity"
                          onMouseDown={(e) => beginSubtitleClipResize(e, clip, trackIdx, 'left')}
                          title="Resize subtitle start"
                        />
                        <div
                          data-subtitle-resize="right"
                          className="absolute right-0 top-0 bottom-0 w-3 z-20 cursor-ew-resize bg-white/10 opacity-0 group-hover:opacity-100 hover:bg-white/30 transition-opacity"
                          onMouseDown={(e) => beginSubtitleClipResize(e, clip, trackIdx, 'right')}
                          title="Resize subtitle duration"
                        />
                        <Captions className="w-3 h-3 shrink-0 mr-1" style={{ color: scenePal?.subtitle ?? '#14b8a6' }} />
                        <span className="text-[10px] truncate font-medium flex-1" style={{ color: scenePal?.subtitle ?? '#0d9488' }}>
                          {clip.muted ? '(off) ' : ''}{String(clip.text || '').trim() || '—'}
                        </span>
                        <button
                          type="button"
                          className="shrink-0 p-0.5 rounded hover:bg-white/10"
                          style={{ color: 'var(--text-secondary)' }}
                          title={clip.muted ? 'Include in export' : 'Skip in export'}
                          onMouseDown={(e) => e.stopPropagation()}
                          onClick={(e) => {
                            e.stopPropagation();
                            toggleSubtitleClipMuted(clip.id, trackIdx);
                          }}
                        >
                          {clip.muted ? <VolumeX className="w-3 h-3" /> : <Volume2 className="w-3 h-3 opacity-40" />}
                        </button>
                      </div>
                      );
                    })}
                    {!subRow.length && (
                      <div className="absolute inset-0 flex items-center justify-center text-xs px-3 text-center"
                        style={{ color: 'var(--text-tertiary)' }}>
                        {trackIdx === 0
                          ? 'Subtitles follow narration timing on this audio lane'
                          : 'Translated subtitles appear when translation audio is added'}
                      </div>
                    )}
                  </div>
                </div>
                </Fragment>
                );
              })}

              {/* Sound FX, then background music. SFX sits with the audio lanes, not under empty music rows. */}
              {displayedMusicLanes.map(({ track, trackIdx }) => {
                const musicLaneClips = stretchBackgroundMusicForBookends([track], bookendTimeline.totalDuration)[0] || track;
                const musicLaneKey = timelineLaneKey('music', trackIdx);
                const musicLaneCollapsed = isTimelineLaneCollapsed(collapsedTimelineLanes, musicLaneKey);
                const sfxLane = isSceneSfxLane(track) || isEngineSfxStemLane(track);
                const laneActive = isMusicLanePlaybackEnabled(timelineActive, track);
                const laneAccent = sfxLane ? '#2DD4BF' : '#a855f7';
                return (
                <div
                  key={`mtrack-${trackIdx}`}
                  className="flex border-b"
                  data-testid={sfxLane ? `timeline-sfx-lane-${trackIdx}` : `timeline-music-lane-${trackIdx}`}
                  style={{
                    borderColor: 'var(--glass-border)',
                    opacity: laneActive ? 1 : 0.45,
                    ...timelineLaneRowSizeStyle(musicLaneCollapsed, 48),
                  }}
                  onDragOver={(e) => {
                    e.preventDefault();
                    setDragOverTrack({ type: 'music', index: trackIdx });
                  }}
                  onDragLeave={() => setDragOverTrack(null)}
                  onDrop={(e) => handleMusicTrackDrop(e, trackIdx)}
                >
                  <div
                    className={`${TIMELINE_LABEL_STICKY_CLASS} flex items-center justify-between px-2 gap-1 ${musicLaneCollapsed ? 'py-0 overflow-hidden' : 'py-1.5'}`}
                    style={TIMELINE_LABEL_PANEL_STYLE}
                  >
                    <div className="flex items-center gap-1 min-w-0">
                      <TimelineLaneMinimizeButton
                        collapsed={musicLaneCollapsed}
                        onToggle={() => toggleTimelineLane(musicLaneKey)}
                        label={sfxLane ? 'Sound FX track' : 'Music track'}
                        testId={`timeline-lane-minimize-music-${trackIdx}`}
                      />
                      {sfxLane ? (
                        <Waves className="w-3 h-3 shrink-0" style={{ color: laneAccent }} />
                      ) : (
                        <Music className="w-3 h-3 shrink-0" style={{ color: laneAccent }} />
                      )}
                      <span className="text-[10px] font-medium truncate" style={{ color: 'var(--text-secondary)' }}>
                        {sfxLane ? 'Sound FX' : 'Music'}
                      </span>
                    </div>
                    <button
                      type="button"
                      onClick={() => setTimelineActive((prev) => (
                        sfxLane
                          ? { ...prev, sfx: prev.sfx === false }
                          : { ...prev, music: prev.music === false }
                      ))}
                      className="text-[10px] rounded px-1.5 py-0.5 border"
                      style={{
                        borderColor: 'var(--glass-border)',
                        color: laneActive ? laneAccent : 'var(--text-tertiary)',
                      }}
                      data-testid={sfxLane ? `sfx-lane-active-${trackIdx}` : `music-lane-active-${trackIdx}`}
                    >
                      {laneActive ? 'Active' : 'Off'}
                    </button>
                  </div>
                  <div
                    className="relative flex-1"
                    ref={(node) => setMusicTrackRef(trackIdx, node)}
                    style={{
                      background:
                        dragOverTrack?.type === 'music' && dragOverTrack.index === trackIdx
                          ? '#a855f718'
                          : 'var(--bg-primary)',
                      ...timelineLaneClipAreaStyle(musicLaneCollapsed),
                    }}
                  >
                    {visibleTimelineClips(musicLaneClips, {
                      getDuration: (c) => clipStoryBackgroundMusicDrawDuration(
                        c,
                        Number(audioFileDurations[c.id]) || 0,
                        masterAudioPlaybackSpeed,
                      ),
                    }).map((clip) => {
                      const isSelected = selectedTimelineClip?.type === 'music' && selectedTimelineClip.id === clip.id;
                      const musicFileDur = Number(audioFileDurations[clip.id]) || 0;
                      const musicDrawSec = clipStoryBackgroundMusicDrawDuration(clip, musicFileDur, masterAudioPlaybackSpeed);
                      return (
                        <div
                          key={clip.id}
                          className="absolute top-1 bottom-1 rounded-xl flex items-center px-2 select-none group"
                          style={{
                            left: clip.startTime * pxPerSec,
                            width: Math.max(40, musicDrawSec * pxPerSec - 2),
                            background: sfxLane ? '#2DD4BF26' : '#a855f726',
                            border: sfxLane ? '1px solid #2DD4BF55' : '1px solid #a855f755',
                            cursor: draggingClipId === clip.id ? 'grabbing' : 'grab',
                            boxShadow: isSelected ? '0 0 0 2px white, 0 0 0 4px #a855f7' : 'none',
                            zIndex: isSelected || draggingClipId === clip.id ? 28 : 5,
                          }}
                          onMouseDown={(e) => {
                            if (e.target?.dataset?.timelineResizeEdge) return;
                            beginMusicClipDrag(e, clip, trackIdx);
                          }}
                          onClick={() => setSelectedTimelineClip({ type: 'music', id: clip.id })}
                          onDoubleClick={(e) => {
                            e.stopPropagation();
                            if (clip.id === 'story-background-music') openBgMusicDialog(clip);
                          }}
                          data-testid={`music-clip-${clip.id}`}
                          title={clip.id === 'story-background-music' ? 'Double-click to regenerate background music' : undefined}
                        >
                          <div
                            data-timeline-resize-edge="left"
                            className={`absolute left-0 top-0 bottom-0 w-3.5 z-30 cursor-ew-resize transition-opacity ${
                              isSelected
                                ? 'bg-white/35 opacity-100'
                                : 'bg-white/10 opacity-0 group-hover:opacity-100 hover:bg-white/30'
                            }`}
                            onMouseDown={(e) => beginMusicClipResize(e, clip, trackIdx, 'left')}
                            title="Trim music start"
                          />
                          <div
                            data-timeline-resize-edge="right"
                            className={`absolute right-0 top-0 bottom-0 w-3.5 z-30 cursor-ew-resize transition-opacity ${
                              isSelected
                                ? 'bg-white/35 opacity-100'
                                : 'bg-white/10 opacity-0 group-hover:opacity-100 hover:bg-white/30'
                            }`}
                            onMouseDown={(e) => beginMusicClipResize(e, clip, trackIdx, 'right')}
                            title="Trim music end"
                          />
                          <Music className="w-3 h-3 shrink-0 mr-1.5 pointer-events-none" style={{ color: laneAccent }} />
                          <span className="text-[10px] truncate font-medium" style={{ color: laneAccent }}>{clip.name}</span>
                          <AudioWaveform url={clip.url} waveformUrl={clip.waveformUrl} />
                          <button
                            type="button"
                            className="ml-auto shrink-0 p-0.5 opacity-60 hover:opacity-100"
                            onMouseDown={(e) => e.stopPropagation()}
                            onClick={(e) => {
                              e.stopPropagation();
                              removeClipFromTrack(trackIdx, clip.id, 'music');
                            }}
                            title="Remove from timeline"
                          >
                            <Trash2 className="w-2.5 h-2.5" style={{ color: '#ef4444' }} />
                          </button>
                      </div>
                    );
                  })}
                    {track.length === 0 && (
                      <div
                        className="absolute inset-0 flex items-center justify-center text-[11px] px-3 text-center"
                        style={{
                          color: 'var(--text-tertiary)',
                          borderStyle: 'dashed',
                          borderWidth: 1,
                          borderColor: 'var(--glass-border)',
                          margin: 4,
                          borderRadius: 8,
                        }}
                      >
                        Background music — drag from Media Library (“Background music”) or generate from the toolbar.
                      </div>
                    )}
                </div>
              </div>
            );
          })}
              {/* VFX Effects Track */}
              {(effectTracks || []).map((track, trackIdx) => (
                <EffectsTimelineTrack
                  key={`efx-${trackIdx}`}
                  track={track}
                  trackIdx={trackIdx}
                  pxPerSec={pxPerSec}
                  timelineActive={timelineActive.effects}
                  selectedClipId={selectedTimelineClip?.type === 'effect' ? selectedTimelineClip.id : null}
                  draggingClipId={draggingClipId}
                  onToggleActive={() => setTimelineActive((prev) => ({ ...prev, effects: !prev.effects }))}
                  onSelectClip={(clip) => {
                    setSelectedTimelineClip({ type: 'effect', id: clip.id });
                          playback.seek(clip.startTime);
                        }}
                  onBeginDrag={(e, clip, idx) => beginEffectClipDrag({
                    event: e,
                    clip,
                    trackIdx: idx,
                    pxPerSec,
                    effectTrackRefs,
                    setEffectTracks,
                    setSelectedTimelineClip,
                    setDraggingClipId,
                    dragStateRef,
                    clearWindowDragHandlers,
                    installWindowDragHandlers,
                  })}
                  onBeginResize={(e, clip, idx, edge) => beginEffectClipResize({
                    event: e,
                    clip,
                    trackIdx: idx,
                    edge,
                    pxPerSec,
                    setEffectTracks,
                    dragStateRef,
                    clearWindowDragHandlers,
                    installWindowDragHandlers,
                  })}
                  onRemoveClip={handleRemoveVfxClip}
                  onClearAll={handleClearAllVfx}
                  onDuplicateClip={(clip) => {
                    recordEditorHistory();
                    setEffectTracks((prev) => normalizeEffectTracks(duplicateEffectClip(prev, clip)));
                    queuePersistEditorAfterMediaChange();
                  }}
                  onAddEffect={handleAddVfxEffect}
                  setTrackRef={setEffectTrackRef}
                  collapsed={isTimelineLaneCollapsed(collapsedTimelineLanes, timelineLaneKey('vfx', trackIdx))}
                  onToggleCollapsed={() => toggleTimelineLane(timelineLaneKey('vfx', trackIdx))}
                  viewportStartSec={timelineViewportRange.startSec}
                  viewportEndSec={timelineViewportRange.endSec}
                />
              ))}

              {/* Host / narrator photo overlay track (REEL&SHORTS: B-ROLL + Artifacts) */}
              {(isReelsShortsEditor ? padReelsOverlayTracks(overlayTracks) : (overlayTracks || [[]])).map((track, trackIdx) => {
                const reelsLane = isReelsShortsEditor ? reelsOverlayLaneMeta(trackIdx) : null;
                return (
                <HostOverlayTimelineTrack
                  key={`host-overlay-${trackIdx}`}
                  track={track}
                  trackIdx={trackIdx}
                  pxPerSec={pxPerSec}
                  timelineActive={timelineActive.hostOverlay}
                  selectedClipId={selectedTimelineClip?.type === 'hostOverlay' ? selectedTimelineClip.id : null}
                  highlightedClipId={highlightedOverlayClipId}
                  draggingClipId={draggingClipId}
                  narrationProjectId={isNarrationEditor ? projectId : null}
                  storyId={isNarrationEditor || isReelsShortsEditor ? null : storyId}
                  label={reelsLane?.label}
                  emptyHint={reelsLane?.emptyHint}
                  accent={reelsLane?.accent}
                  testId={reelsLane?.testId}
                  showHostTools={!isReelsShortsEditor}
                  icon={isReelsShortsEditor ? (trackIdx === 0 ? Layers : trackIdx === 1 ? TwinneeLogo : Type) : undefined}
                  collapsed={isTimelineLaneCollapsed(collapsedTimelineLanes, timelineLaneKey('overlay', trackIdx))}
                  onToggleCollapsed={() => toggleTimelineLane(timelineLaneKey('overlay', trackIdx))}
                  viewportStartSec={timelineViewportRange.startSec}
                  viewportEndSec={timelineViewportRange.endSec}
                  onClipDoubleClick={(clip) => {
                    revealHostOverlayClipInPreview(clip);
                    setOverlayDetailsClipId(clip.id);
                  }}
                  onToggleActive={() => setTimelineActive((prev) => ({ ...prev, hostOverlay: !prev.hostOverlay }))}
                  onSelectClip={(clip) => {
                    setSelectedTimelineClip({ type: 'hostOverlay', id: clip.id });
                    playback.seek(clip.startTime);
                  }}
                  onBeginDrag={(e, clip, idx) => beginHostOverlayClipDrag({
                    event: e,
                    clip,
                    trackIdx: idx,
                    pxPerSec,
                    overlayTrackRefs,
                    setOverlayTracks,
                    setSelectedTimelineClip,
                    setDraggingClipId,
                    dragStateRef,
                    clearWindowDragHandlers,
                    installWindowDragHandlers,
                  })}
                  onBeginResize={(e, clip, idx, edge) => beginHostOverlayClipResize({
                    event: e,
                    clip,
                    trackIdx: idx,
                    edge,
                    pxPerSec,
                    setOverlayTracks,
                    clearWindowDragHandlers,
                    installWindowDragHandlers,
                  })}
                  onRemoveClip={(id) => {
                    recordEditorHistory();
                    setOverlayTracks((prev) => removeHostOverlayClip(prev, id));
                  }}
                  onDuplicateClip={(clip) => {
                    recordEditorHistory();
                    setOverlayTracks((prev) => duplicateHostOverlayClip(prev, clip));
                  }}
                  onAddClip={(url, name) => handleAddHostOverlayClip(url, name)}
                  onAddBackgroundTemplate={handleAddBackgroundTemplate}
                  onAddCarousel={() => handleAddCardCarousel()}
                  setTrackRef={setOverlayTrackRef}
                />
                );
              })}


              {/* Export progress bar (full width) */}
              {exportingFull && (
                <div className="px-4 py-3 border-t" style={{ borderColor: 'var(--glass-border)' }}>
                  <div className="flex items-center gap-3">
                    <Loader2 className="w-4 h-4 animate-spin shrink-0" style={{ color: 'var(--primary)' }} />
                    <span className="text-xs min-w-0 truncate" style={{ color: 'var(--text-secondary)' }}>
                      {exportProgress > 0
                        ? `${exportProgress}%${exportStatusMessage ? ` · ${exportStatusMessage}` : ''}`
                        : (exportStatusMessage || 'Queued…')}
                    </span>
                    <div className="flex-1 h-1.5 rounded-full overflow-hidden" style={{ background: 'var(--bg-tertiary)' }}>
                      <div className="h-full rounded-full transition-all" style={{ width: `${exportProgress}%`, background: 'var(--primary)' }} />
          </div>
                  </div>
                </div>
              )}
            </div>
          </div>
            {/* Playhead — clamped so it never crosses the label gutter */}
            <div className="absolute inset-0 pointer-events-none z-[90]" aria-hidden>
              <div
                className="absolute w-0.5"
            style={{
                  top: 11,
                  bottom: 0,
                  left: playheadOverlayLeftPx,
                  transform: 'translateX(-50%)',
                  background: 'hsl(var(--primary))',
                }}
              />
              <div
                data-playhead-knob
                className="absolute cursor-grab active:cursor-grabbing pointer-events-auto touch-none drop-shadow-md"
                style={{
                  top: 0,
                  left: playheadOverlayLeftPx,
                  transform: 'translateX(-50%)',
                }}
                onMouseDown={beginPlayheadDrag}
                title="Drag playhead"
              >
                <svg width="14" height="11" viewBox="0 0 14 11" aria-hidden="true">
                  <path
                    d="M7 11 L0 0 L14 0 Z"
                    fill="hsl(var(--primary))"
                    stroke="hsl(var(--background))"
                    strokeWidth="1.25"
                    strokeLinejoin="round"
                  />
                </svg>
              </div>
              <div
                className="absolute cursor-grab active:cursor-grabbing touch-none pointer-events-auto"
                style={{
                  top: 32,
                  bottom: 0,
                  left: playheadOverlayLeftPx,
                  transform: 'translateX(-50%)',
                  width: 14,
                }}
                onMouseDown={beginPlayheadDrag}
                role="presentation"
              />
            </div>
          </div>

          {/* VFX inspector — docked below timeline tracks */}
          {selectedEffectClip ? (
            <div
              className="shrink-0 border-t max-h-52 overflow-y-auto"
              style={{ borderColor: 'var(--glass-border)', background: 'var(--bg-secondary)' }}
              data-testid="vfx-inspector-section"
            >
              <EffectInspectorPanel
                clip={selectedEffectClip}
                currentTime={visualCurrentTime}
                onUpdate={(patch) => handleUpdateEffectClip(selectedEffectClip.id, patch)}
                onClose={() => setSelectedTimelineClip((prev) => (
                  prev?.type === 'effect' ? null : prev
                ))}
              />
            </div>
          ) : null}
          </div>
        </div>
          </Panel>
        </PanelGroup>
      </div>


      <input
        ref={openProjectInputRef}
        type="file"
        accept=".json,application/json"
        className="hidden"
        onChange={onOpenProjectFile}
      />
      <input
        ref={audioInputRef}
        type="file"
        accept="audio/*"
        className="hidden"
        onChange={handleAudioFileUpload}
      />

      <OverlayClipDetailsDialog
        clip={overlayDetailsClip}
        aspectRatio={exportAspectRatio}
        open={Boolean(overlayDetailsClip)}
        onOpenChange={(open) => { if (!open) setOverlayDetailsClipId(null); }}
        onPromptChange={(prompt) => {
          if (!overlayDetailsClip) return;
          recordEditorHistory();
          setOverlayTracks((prev) => patchHostOverlayClip(prev, overlayDetailsClip.id, {
            generationPrompt: prompt,
          }));
          queuePersistEditorAfterMediaChange();
        }}
        onDisplayTextChange={(text) => {
          if (!overlayDetailsClip) return;
          recordEditorHistory();
          setOverlayTracks((prev) => patchHostOverlayClip(
            prev,
            overlayDetailsClip.id,
            overlayClipDisplayTextPatch(overlayDetailsClip, text),
          ));
          queuePersistEditorAfterMediaChange();
        }}
        onAnimationStyleChange={(style) => {
          if (!overlayDetailsClip) return;
          recordEditorHistory();
          setOverlayTracks((prev) => patchHostOverlayClip(prev, overlayDetailsClip.id, {
            animationStyle: style,
          }));
          queuePersistEditorAfterMediaChange();
        }}
        defaultVisualStyle={reelsCutRef.current?.broll_visual_style || ''}
        onVisualStyleChange={(style) => {
          if (!overlayDetailsClip) return;
          recordEditorHistory();
          setOverlayTracks((prev) => patchHostOverlayClip(prev, overlayDetailsClip.id, {
            visualStyle: style,
          }));
          queuePersistEditorAfterMediaChange();
        }}
        onHasBackgroundChange={(next) => {
          if (!overlayDetailsClip) return;
          recordEditorHistory();
          setOverlayTracks((prev) => patchHostOverlayClip(prev, overlayDetailsClip.id, {
            hasBackground: next,
          }));
          queuePersistEditorAfterMediaChange();
        }}
        onGenerate={
          isReelsShortsEditor && projectId && overlayDetailsClip?.suggestionId
            ? (opts) => generateOverlayArtifact(overlayDetailsClip, opts)
            : null
        }
        onLocateInPreview={
          overlayDetailsClip ? () => revealHostOverlayClipInPreview(overlayDetailsClip) : null
        }
        onOpacityChange={(opacity) => {
          if (!overlayDetailsClip) return;
          recordEditorHistory();
          setOverlayTracks((prev) => patchHostOverlayClip(prev, overlayDetailsClip.id, { opacity }));
          queuePersistEditorAfterMediaChange();
        }}
      />

      <Dialog open={subtitleDialogOpen} onOpenChange={setSubtitleDialogOpen}>
        <DialogContent
          className="max-w-lg"
          style={{ background: 'var(--bg-primary)', borderColor: 'var(--glass-border)' }}
        >
          <DialogHeader>
            <DialogTitle style={{ color: 'var(--text-primary)' }}>Subtitle text</DialogTitle>
            <DialogDescription style={{ color: 'var(--text-secondary)' }}>
              Shown when the Subs lane is active. Timing matches the paired audio clip.
            </DialogDescription>
          </DialogHeader>
          <textarea
            className="w-full min-h-[120px] rounded-lg border p-3 text-sm"
            style={{
              background: 'var(--bg-secondary)',
              borderColor: 'var(--glass-border)',
              color: 'var(--text-primary)',
            }}
            value={subtitleDraft.text}
            onChange={(e) => setSubtitleDraft((d) => ({ ...d, text: e.target.value }))}
            placeholder="Caption for this audio segment…"
          />
          <label className="flex items-center gap-2 text-xs cursor-pointer" style={{ color: 'var(--text-secondary)' }}>
            <input
              type="checkbox"
              checked={subtitleDraft.muted}
              onChange={(e) => setSubtitleDraft((d) => ({ ...d, muted: e.target.checked }))}
            />
            Skip this cue in exported video
          </label>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setSubtitleDialogOpen(false)}
              style={{ borderColor: 'var(--glass-border)', color: 'var(--text-secondary)' }}
            >
              Cancel
            </Button>
            <Button
              onClick={saveSubtitleDraft}
              style={{ background: '#14b8a6', color: '#fff' }}
            >
              Save
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={bulkAudioDialogOpen} onOpenChange={(open) => {
        if (!generatingPendingAudios) setBulkAudioDialogOpen(open);
      }}>
        <DialogContent
          className="max-w-md"
          style={{ background: 'var(--bg-primary)', borderColor: 'var(--glass-border)' }}
        >
          <DialogHeader>
            <DialogTitle style={{ color: 'var(--text-primary)' }}>Generate narration audio</DialogTitle>
            <DialogDescription style={{ color: 'var(--text-secondary)' }}>
              Choose audio provider and voice, then pick which segments to generate.
            </DialogDescription>
          </DialogHeader>
          <NarrationAudioSettingsPanel
            settings={bulkAudioSettings}
            onChange={onBulkAudioSettingsChange}
            onChooseVoice={() => setBulkAudioVoicePickerOpen(true)}
            disabled={generatingPendingAudios}
            testId="timeline-bulk-audio-settings"
          />
          <motion.div className="grid gap-3 py-2">
            <button
              type="button"
              disabled={pendingAudioCount === 0}
              onClick={() => handleBulkAudioChoice('pending')}
              className="rounded-xl border-2 p-4 text-left transition-all disabled:opacity-50 disabled:cursor-not-allowed hover:border-primary/60"
              style={{
                borderColor: 'var(--glass-border)',
                background: 'var(--bg-secondary)',
                color: 'var(--text-primary)',
              }}
              data-testid="bulk-audio-pending-btn"
            >
              <span className="block text-sm font-semibold">Pending only</span>
              <span className="block text-xs mt-1" style={{ color: 'var(--text-tertiary)' }}>
                {pendingAudioCount
                  ? `${pendingAudioCount} segment${pendingAudioCount === 1 ? '' : 's'} missing audio on the timeline`
                  : 'All segments already have audio'}
              </span>
            </button>
            <button
              type="button"
              disabled={allAudioCount === 0}
              onClick={() => handleBulkAudioChoice('all')}
              className="rounded-xl border-2 p-4 text-left transition-all disabled:opacity-50 disabled:cursor-not-allowed hover:border-primary/60"
              style={{
                borderColor: 'var(--glass-border)',
                background: 'var(--bg-secondary)',
                color: 'var(--text-primary)',
              }}
              data-testid="bulk-audio-all-btn"
            >
              <span className="block text-sm font-semibold">All scenes</span>
              <span className="block text-xs mt-1" style={{ color: 'var(--text-tertiary)' }}>
                {allAudioCount
                  ? `Regenerate audio for all ${allAudioCount} segment${allAudioCount === 1 ? '' : 's'} with script (timeline order)`
                  : 'No segments with script text'}
              </span>
            </button>
          </motion.div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setBulkAudioDialogOpen(false)}
              disabled={generatingPendingAudios}
              className="rounded-lg"
            >
              Cancel
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <NarrationVoicePickerDialog
        open={bulkAudioVoicePickerOpen}
        onOpenChange={setBulkAudioVoicePickerOpen}
        provider={bulkAudioSettings.tts_provider}
        language={bulkAudioSettings.language}
        onSelectVoice={selectBulkAudioVoice}
      />

      <NarrationVoicePickerDialog
        open={replacementAudioVoicePickerOpen}
        onOpenChange={setReplacementAudioVoicePickerOpen}
        provider={replacementAudioSettings.tts_provider}
        language={replacementAudioSettings.language}
        onSelectVoice={selectReplacementAudioVoice}
      />

      <Dialog open={exportDialogOpen} onOpenChange={setExportDialogOpen}>
        <DialogContent
          className="max-w-xl max-h-[min(92vh,720px)] overflow-y-auto"
          style={{ background: 'var(--bg-primary)', borderColor: 'var(--glass-border)' }}
        >
          <DialogHeader>
            <DialogTitle style={{ color: 'var(--text-primary)' }}>Export video</DialogTitle>
            <DialogDescription style={{ color: 'var(--text-secondary)' }}>
              Choose aspect ratio and export quality, drag and zoom the crop preview, and position subtitles as they will appear in the export.
            </DialogDescription>
          </DialogHeader>

          {isNarrationEditor ? (
            <div
              className="rounded-lg border p-3 py-2.5"
              style={{ borderColor: 'var(--glass-border)', background: 'var(--bg-secondary)' }}
              data-testid="export-aspect-ratio-locked-narration"
            >
              <span className="block text-sm font-semibold" style={{ color: 'var(--text-primary)' }}>
                Aspect ratio: {narrationAspectRatioLabel(exportAspectRatio)}
              </span>
              <span className="block text-xs mt-1" style={{ color: 'var(--text-tertiary)' }}>
                Locked from your narration project setup — preview and export use this shape.
              </span>
            </div>
          ) : (
            <div className="grid grid-cols-2 gap-2 py-1">
              {EXPORT_ASPECT_RATIO_OPTIONS.map((option) => {
                const selected = exportAspectRatio === option.value;
                return (
                  <button
                    key={option.value}
                    type="button"
                    onClick={() => {
                      setExportAspectRatio(option.value);
                      setExportFraming((prev) => {
                        const centered = centerExportFraming(prev);
                        exportFramingRef.current = centered;
                        return centered;
                      });
                    }}
                    className="rounded-lg border p-2.5 text-left transition-all"
                    style={{
                      borderColor: selected ? 'var(--primary)' : 'var(--glass-border)',
                      background: selected ? 'var(--primary-light)' : 'var(--bg-secondary)',
                      color: selected ? 'var(--primary)' : 'var(--text-primary)',
                    }}
                    data-testid={`export-aspect-${option.value.replace(':', '-')}`}
                  >
                    <span className="block text-sm font-semibold">{option.label}</span>
                    <span className="block text-xs mt-1" style={{ color: selected ? 'var(--primary)' : 'var(--text-tertiary)' }}>
                      {option.detail}
                    </span>
                  </button>
                );
              })}
            </div>
          )}

          <div className="py-2 space-y-1.5 border-t" style={{ borderColor: 'var(--glass-border)' }}>
            <Label htmlFor="export-quality-select" className="text-xs font-semibold uppercase tracking-wide" style={{ color: 'var(--text-tertiary)' }}>
              Export quality
            </Label>
            <Select value={exportQuality} onValueChange={(v) => setExportQuality(normalizeExportQuality(v))}>
              <SelectTrigger
                id="export-quality-select"
                className="w-full"
                style={{ borderColor: 'var(--glass-border)', background: 'var(--bg-secondary)', color: 'var(--text-primary)' }}
                data-testid="export-quality-select"
              >
                <SelectValue>{selectedExportQuality.label}</SelectValue>
              </SelectTrigger>
              <SelectContent style={{ background: 'var(--bg-primary)', borderColor: 'var(--glass-border)' }}>
                {EXPORT_QUALITY_OPTIONS.map((option) => (
                  <SelectItem
                    key={option.value}
                    value={option.value}
                    textValue={option.label}
                    data-testid={`export-quality-${option.value}`}
                  >
                    <div className="flex flex-col gap-0.5 py-0.5">
                      <span className="font-medium">{option.label}</span>
                      <span className="text-xs" style={{ color: 'var(--text-tertiary)' }}>
                        {option.detail}
                      </span>
                    </div>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <p className="text-[11px]" style={{ color: 'var(--text-tertiary)' }}>
              {selectedExportQuality.detail}
            </p>
          </div>

          <div className="py-1 space-y-1.5 border-t" style={{ borderColor: 'var(--glass-border)' }}>
            <div className="flex items-center justify-between gap-2 pt-1">
              <span className="text-xs font-semibold uppercase tracking-wide" style={{ color: 'var(--text-tertiary)' }}>
                Crop preview
              </span>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                className="h-7 text-xs"
                onClick={() => {
                  const reset = resetExportFraming();
                  exportFramingRef.current = reset;
                  setExportFraming(reset);
                }}
                data-testid="export-framing-reset-btn"
              >
                Reset framing
              </Button>
            </div>
            <ExportFramingPreview
              aspectRatio={exportAspectRatio}
              previewSize="large"
              framing={exportFraming}
              onChange={(next) => {
                const norm = normalizeVideoExportFraming(next);
                exportFramingRef.current = norm;
                setExportFraming(norm);
              }}
              mediaUrl={exportPreviewMedia?.url}
              mediaType={exportPreviewMedia?.type || 'video'}
              subtitleStyle={exportFramingShowsSubtitles ? subtitleStyle : null}
              subtitleLayer={exportFramingShowsSubtitles ? resolvedSubtitleLayer : null}
              subtitleText={subtitleSpokenPreview.text}
              subtitleTokens={subtitleSpokenPreview.tokens}
              subtitleHighlightColor={subtitleStyle.spokenWordHighlightColor}
              clipVideoToSubtitle={exportClipVideoToSubtitle}
              onSubtitleLayerChange={handleSubtitleLayerChange}
            />
            {exportFramingShowsSubtitles ? (
              <div
                className="flex items-start gap-3 rounded-lg border px-3 py-2.5"
                style={{ borderColor: 'var(--glass-border)', background: 'var(--bg-secondary)' }}
              >
                <Switch
                  id="export-clip-video-to-subtitle"
                  checked={exportClipVideoToSubtitle}
                  onCheckedChange={handleExportClipVideoToSubtitleChange}
                  data-testid="export-clip-video-to-subtitle-toggle"
                />
                <Label
                  htmlFor="export-clip-video-to-subtitle"
                  className="text-xs leading-snug cursor-pointer"
                  style={{ color: 'var(--text-secondary)' }}
                >
                  <span className="font-semibold block" style={{ color: 'var(--text-primary)' }}>
                    Clip video to subtitle
                  </span>
                  Lock captions to the video — when you drag or zoom the crop, subtitles move with the picture (same as clubbing them together).
                </Label>
              </div>
            ) : (
              <p className="text-[11px]" style={{ color: 'var(--text-tertiary)' }}>
                Enable subtitles on the timeline to preview caption placement in this crop.
              </p>
            )}
          </div>

          {isPitchRecordingStory(story) ? (
            <div
              className="py-2 space-y-2.5 border-t"
              style={{ borderColor: 'var(--glass-border)' }}
              data-testid="pitch-voice-polish-export"
            >
              <div
                className="flex items-start gap-3 rounded-lg border px-3 py-2.5"
                style={{ borderColor: 'var(--glass-border)', background: 'var(--bg-secondary)' }}
              >
                <Switch
                  id="pitch-voice-polish-enabled"
                  checked={Boolean(pitchVoicePolish.enabled)}
                  onCheckedChange={(checked) =>
                    setPitchVoicePolish((prev) => ({ ...prev, enabled: Boolean(checked) }))
                  }
                  data-testid="pitch-voice-polish-enabled"
                />
                <Label
                  htmlFor="pitch-voice-polish-enabled"
                  className="text-xs leading-snug cursor-pointer"
                  style={{ color: 'var(--text-secondary)' }}
                >
                  <span className="font-semibold block" style={{ color: 'var(--text-primary)' }}>
                    Clean pitch voice
                  </span>
                  Enhance speech, cut umm/uh, long breaths, and low-energy gaps, then mux cleaned audio into the video.
                </Label>
              </div>
              {pitchVoicePolish.enabled ? (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 pl-1">
                  {[
                    { key: 'clean_voice', label: 'Clean voice', detail: 'Noise reduce & clarify' },
                    { key: 'remove_fillers', label: 'Remove umm / uh', detail: 'Spoken fillers' },
                    { key: 'remove_long_breaths', label: 'Long breaths', detail: 'Cut long inhales' },
                    { key: 'remove_low_energy', label: 'Low-energy sections', detail: 'Quiet dead air' },
                    { key: 'remove_redundant', label: 'Redundant statements', detail: 'Heuristic trim' },
                    { key: 'remove_repeated', label: 'Repeated sentences', detail: 'Drop immediate repeats' },
                    { key: 'remove_mistakes', label: 'Mistakes', detail: 'False starts / corrections' },
                    { key: 'remove_unnecessary_intros', label: 'Unnecessary intros', detail: 'Hi / hello / um lead-ins' },
                    { key: 'mux_av', label: 'Mux video + audio', detail: 'Bake cleaned VO into MP4' },
                  ].map((opt) => (
                    <label
                      key={opt.key}
                      className="flex items-start gap-2 rounded-md border px-2.5 py-2 cursor-pointer"
                      style={{ borderColor: 'var(--glass-border)', background: 'var(--bg-primary)' }}
                      data-testid={`pitch-voice-polish-${opt.key}`}
                    >
                      <input
                        type="checkbox"
                        className="mt-0.5"
                        checked={Boolean(pitchVoicePolish[opt.key])}
                        onChange={(e) =>
                          setPitchVoicePolish((prev) => ({
                            ...prev,
                            [opt.key]: e.target.checked,
                          }))
                        }
                      />
                      <span className="min-w-0">
                        <span className="block text-xs font-medium" style={{ color: 'var(--text-primary)' }}>
                          {opt.label}
                        </span>
                        <span className="block text-[11px]" style={{ color: 'var(--text-tertiary)' }}>
                          {opt.detail}
                        </span>
                      </span>
                    </label>
                  ))}
                </div>
              ) : null}
            </div>
          ) : null}

          <DialogFooter className="flex-col sm:flex-row gap-2 sm:justify-end flex-wrap">
            <Button
              variant="outline"
              onClick={() => setExportDialogOpen(false)}
              disabled={exportingFull || exportingLocal || exportingPremiere}
              style={{ borderColor: 'var(--glass-border)', color: 'var(--text-secondary)' }}
            >
              Cancel
            </Button>
            <Button
              variant="outline"
              onClick={handleExportLocal}
              disabled={exportingFull || exportingLocal || exportingPremiere || exportableTimelineClipCount === 0}
              className="gap-2"
              style={{ borderColor: 'var(--glass-border)', color: 'var(--text-secondary)' }}
              data-testid="export-video-local-btn"
              title="Render the full timeline on this device (no upload, no server job) — animation, VFX, filters, transitions, framing, host overlays and subtitles included. Keep this tab open until it finishes."
            >
              {exportingLocal ? <Loader2 className="w-4 h-4 animate-spin" /> : <Film className="w-4 h-4" />}
              Export locally
            </Button>
            <Button
              variant="outline"
              onClick={handleExportPremiere}
              disabled={exportingFull || exportingLocal || exportingPremiere}
              className="gap-2"
              style={{ borderColor: 'var(--glass-border)', color: 'var(--text-secondary)' }}
              data-testid="export-premiere-btn"
              title="Download an editable Premiere Pro / FCPXML package (one clip per scene). Unzip, then File → Import in Premiere."
            >
              {exportingPremiere ? <Loader2 className="w-4 h-4 animate-spin" /> : <FolderInput className="w-4 h-4" />}
              {exportingPremiere ? 'Building…' : 'Premiere Pro package'}
            </Button>
            <Button
              onClick={handleExportFull}
              disabled={exportingFull || exportingLocal || exportingPremiere}
              className="gap-2"
              style={{ background: 'var(--primary)', color: 'var(--text-primary)' }}
              data-testid="confirm-export-video-btn"
              title="Upload local files to S3 when needed, then compile on the server with audio and subtitles"
            >
              {exportingFull ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />}
              Export {exportAspectRatio} · {selectedExportQuality.label}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <YouTubePublishDialog
        open={youtubePublishOpen}
        onOpenChange={setYoutubePublishOpen}
        oauthReturnPath={location.pathname}
        defaultVideoUrl={preferredYoutubeVideoUrl}
        defaultTitle={story?.title || ''}
        scriptContext={youtubePublishScriptContext}
        tone={story?.tone || ''}
        visualStyle={story?.visual_style || ''}
        storyLanguage={story?.language || story?.input?.language || ''}
      />

      <InstagramPublishDialog
        open={instagramPublishOpen}
        onOpenChange={setInstagramPublishOpen}
        oauthReturnPath={location.pathname}
        defaultVideoUrl={preferredYoutubeVideoUrl}
        defaultTitle={story?.title || ''}
        scriptContext={youtubePublishScriptContext}
        tone={story?.tone || ''}
        visualStyle={story?.visual_style || ''}
      />

      <Dialog open={Boolean(magicEditLanePicker)} onOpenChange={(open) => {
        if (!open) setMagicEditLanePicker(null);
      }}>
        <DialogContent
          className="max-w-md"
          style={{ background: 'var(--bg-primary)', borderColor: 'var(--glass-border)' }}
        >
          <DialogHeader>
            <DialogTitle style={{ color: 'var(--text-primary)' }}>Choose audio timeline</DialogTitle>
            <DialogDescription style={{ color: 'var(--text-secondary)' }}>
              Multiple audio lanes are active. Pick which lane Magic Edit should use to set each segment&apos;s
              image/video length and subtitle timing.
            </DialogDescription>
          </DialogHeader>
          <div className="flex flex-col gap-2 pt-2">
            {(magicEditLanePicker?.choices || []).map((choice) => (
              <Button
                key={choice.laneIndex}
                type="button"
                variant="outline"
                className="justify-start h-auto py-3"
                data-testid={`magic-edit-audio-lane-${choice.laneIndex}`}
                onClick={() => {
                  setMagicEditLanePicker(null);
                  runMagicEditWithReferenceLane(choice.laneIndex);
                }}
              >
                {choice.label}
              </Button>
            ))}
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={Boolean(extendVideoDialog)} onOpenChange={(open) => {
        if (!open) setExtendVideoDialog(null);
      }}>
        <DialogContent
          className="max-w-2xl max-h-[90vh] overflow-y-auto"
          style={{ background: 'var(--bg-primary)', borderColor: 'var(--glass-border)' }}
        >
          <DialogHeader>
            <DialogTitle style={{ color: 'var(--text-primary)' }}>Extend video from last frame</DialogTitle>
            <DialogDescription style={{ color: 'var(--text-secondary)' }}>
              Last frame is taken from the end of this timeline snippet (after trim). Generate about{' '}
              {extendVideoDialog?.extendGap?.gapSec != null
                ? `${extendVideoDialog.extendGap.gapSec.toFixed(1)}s`
                : ''}{' '}
              more video to match the active narration audio lane.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div
              className="rounded-xl border overflow-hidden flex items-center justify-center min-h-[180px] max-h-[50vh] relative"
              style={{ background: 'var(--bg-secondary)', borderColor: 'var(--glass-border)' }}
            >
              {extendVideoFrameCapturing ? (
                <div className="flex flex-col items-center gap-2 py-12 text-sm" style={{ color: 'var(--text-secondary)' }}>
                  <Loader2 className="w-8 h-8 animate-spin" />
                  Extracting last frame from clip…
                </div>
              ) : extendVideoFramePreview ? (
                <img
                  src={extendVideoFramePreview}
                  alt="Last frame of trimmed clip used as reference for extension"
                  className="max-w-full max-h-[50vh] w-auto h-auto object-contain block"
                  data-testid="extend-video-last-frame-preview"
                />
              ) : (
                <div className="text-center text-sm px-4 py-10" style={{ color: 'var(--text-secondary)' }}>
                  {extendVideoFrameCaptureError || 'No preview'}
                </div>
              )}
            </div>
            <div>
              <Label className="text-xs" style={{ color: 'var(--text-secondary)' }}>Video provider</Label>
              <Select value={extendVideoProvider} onValueChange={setExtendVideoProvider}>
                <SelectTrigger className="h-9 mt-1 rounded-lg" data-testid="extend-video-provider-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="z-[120]">
                  {batchProviderOptions.map((option) => (
                    <SelectItem key={option.value} value={option.value}>{option.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs" style={{ color: 'var(--text-secondary)' }}>Motion prompt</Label>
              <Textarea
                value={extendVideoPrompt}
                onChange={(e) => setExtendVideoPrompt(e.target.value)}
                className="mt-1 min-h-24 rounded-lg text-sm"
                style={{ background: 'var(--bg-secondary)', borderColor: 'var(--glass-border)', color: 'var(--text-primary)' }}
                data-testid="extend-video-prompt-input"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setExtendVideoDialog(null)} disabled={extendVideoBusy}>
              Cancel
            </Button>
            <Button
              onClick={handleExtendVideoFromLastFrame}
              disabled={extendVideoBusy}
              style={{ background: 'var(--primary)', color: 'var(--text-primary)' }}
              data-testid="confirm-extend-video-btn"
            >
              {extendVideoBusy ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
              Regenerate video
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={bgMusicDialogOpen} onOpenChange={setBgMusicDialogOpen}>
        <DialogContent
          className="max-w-2xl max-h-[88vh] overflow-hidden flex flex-col"
          style={{ background: 'var(--bg-primary)', borderColor: 'var(--glass-border)' }}
        >
          <DialogHeader>
            <DialogTitle style={{ color: 'var(--text-primary)' }}>Background music</DialogTitle>
            <DialogDescription style={{ color: 'var(--text-secondary)' }}>
              Choose a provider, edit the instrumental prompt, then queue generation on the worker.
              When the job completes, the track is applied to the Music lane automatically.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 min-h-0 overflow-auto pr-1">
            <div className="space-y-2">
              <label className="text-xs font-medium" style={{ color: 'var(--text-secondary)' }}>Provider</label>
              <Select value={bgMusicProvider} onValueChange={setBgMusicProvider}>
                <SelectTrigger className="rounded-xl" style={{ borderColor: 'var(--glass-border)' }}>
                  <SelectValue placeholder="Provider" />
                </SelectTrigger>
                <SelectContent>
                  {BG_MUSIC_GENERATION_OPTIONS.map((p) => (
                    <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <label className="text-xs font-medium" style={{ color: 'var(--text-secondary)' }}>Music prompt</label>
              <Textarea
                value={bgMusicPrompt}
                onChange={(e) => setBgMusicPrompt(e.target.value)}
                rows={6}
                placeholder="Describe genre, tempo, mood, and instrumentation (instrumental only). Leave empty to infer mood from your narration script…"
                className="rounded-xl text-sm resize-y min-h-[120px]"
                style={{ borderColor: 'var(--glass-border)', background: 'var(--bg-secondary)' }}
              />
              {story?.music_mood && (
                <p className="text-[10px]" style={{ color: 'var(--text-tertiary)' }}>
                  Mood: {story.music_mood}
                  {story?.music_target_duration
                    ? ` · Target ${Math.round(Number(story.music_target_duration))}s (matches narration length)`
                    : ''}
                </p>
              )}
            </div>

            {story?.music_url && (
              <div className="rounded-xl border p-3 space-y-2"
                style={{ borderColor: 'var(--glass-border)', background: 'var(--bg-secondary)' }}>
                <span className="text-xs font-semibold" style={{ color: 'var(--text-primary)' }}>Current track</span>
                <audio src={mediaUrlForPlayback(story.music_url)} controls className="w-full h-9" />
              </div>
            )}

            <div className="space-y-2">
              <span className="text-xs font-semibold" style={{ color: 'var(--text-primary)' }}>Previous versions</span>
              {bgMusicVersionOptions.length === 0 ? (
                <p className="text-xs rounded-lg border p-3" style={{ borderColor: 'var(--glass-border)', color: 'var(--text-tertiary)' }}>
                  No prior versions yet. Regenerating will archive the current track here.
                </p>
              ) : (
                <div className="space-y-2 max-h-48 overflow-auto">
                  {bgMusicVersionOptions.map((option, index) => (
                    <div
                      key={`${option.url}-${index}`}
                      className="rounded-lg border p-3 space-y-2"
                      style={{
                        borderColor: option.active ? '#a855f7' : 'var(--glass-border)',
                        background: 'var(--bg-secondary)',
                      }}
                    >
                      <div className="flex items-center justify-between gap-2">
                        <span className="text-xs font-medium" style={{ color: 'var(--text-primary)' }}>
                          {option.active ? 'Active' : (option.source || 'Generated')}
                        </span>
                        {option.created_at && (
                          <span className="text-[10px]" style={{ color: 'var(--text-tertiary)' }}>
                            {new Date(option.created_at).toLocaleString()}
                          </span>
                        )}
                      </div>
                      {option.music_prompt && (
                        <p className="text-[10px] line-clamp-2" style={{ color: 'var(--text-tertiary)' }}>
                          {option.music_prompt}
                        </p>
                      )}
                      <audio src={mediaUrlForPlayback(option.url)} controls className="w-full h-8" />
                      <Button
                        size="sm"
                        variant={option.active ? 'outline' : 'default'}
                        disabled={option.active || selectingBgMusicUrl === option.url}
                        onClick={() => handleSelectBgMusicVersion(option.url)}
                        className="w-full rounded-lg"
                        style={option.active
                          ? { borderColor: 'var(--glass-border)', color: 'var(--text-secondary)' }
                          : { background: '#a855f7', color: 'white' }}
                      >
                        {selectingBgMusicUrl === option.url ? (
                          <Loader2 className="w-3 h-3 animate-spin mr-1" />
                        ) : null}
                        {option.active ? 'Selected' : 'Use this version'}
                      </Button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          <DialogFooter className="gap-2 sm:gap-0">
            <Button variant="outline" onClick={() => setBgMusicDialogOpen(false)} className="rounded-xl">
              Close
            </Button>
            <Button
              onClick={() => handleGenerateNarrationBackgroundMusic({ provider: bgMusicProvider, musicPrompt: bgMusicPrompt })}
              disabled={generatingBgMusic || !canGenerateBgMusic}
              className="rounded-xl gap-2"
              style={{ background: '#a855f7', color: 'white' }}
              data-testid="queue-bg-music-btn"
            >
              {generatingBgMusic ? <Loader2 className="w-4 h-4 animate-spin" /> : <TwinneeLogo className="w-4 h-4" />}
              Queue background music
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={Boolean(replacementDialog)} onOpenChange={(open) => {
        if (!open) {
          setReplacementDialog(null);
          setReplacementPanelTab('upload');
          setReplacementOptions([]);
        }
      }}>
        <DialogContent
          className="max-w-3xl max-h-[88vh] overflow-hidden flex flex-col"
          style={{ background: 'var(--bg-primary)', borderColor: 'var(--glass-border)' }}
        >
          <DialogHeader>
            <DialogTitle style={{ color: 'var(--text-primary)' }}>
              Replace {replacementDialog?.kind || 'media'} snippet
            </DialogTitle>
            <DialogDescription style={{ color: 'var(--text-secondary)' }}>
              The gallery reflects the Video / Image / Audio tab you choose on the right. Upload replaces from a file only.
            </DialogDescription>
          </DialogHeader>

          <div className="grid md:grid-cols-[1fr_280px] gap-4 min-h-0">
            <div className="min-h-0 overflow-auto pr-1">
              {replacementPanelTab === 'upload' ? (
                <div className="rounded-xl border p-8 text-center flex flex-col items-center gap-3"
                  style={{ borderColor: 'var(--glass-border)', background: 'var(--bg-secondary)' }}>
                  <Upload className="w-10 h-10" style={{ color: 'var(--text-tertiary)' }} />
                  <p className="text-sm font-semibold" style={{ color: 'var(--text-primary)' }}>Snippet history lives under each media tab</p>
                  <p className="text-xs max-w-[20rem]" style={{ color: 'var(--text-tertiary)' }}>
                    Use <strong className="text-[var(--text-secondary)]">Video</strong>, <strong className="text-[var(--text-secondary)]">Image</strong>, or <strong className="text-[var(--text-secondary)]">Audio</strong> here to browse versions; this panel shows uploads only.
                  </p>
                </div>
              ) : replacementLoading ? (
                <div className="flex items-center justify-center gap-2 py-12 text-sm" style={{ color: 'var(--text-secondary)' }}>
                  <Loader2 className="w-4 h-4 animate-spin" /> Loading generated snippets…
                </div>
              ) : replacementOptions.length === 0 ? (
                <div className="rounded-xl border p-6 text-center text-sm"
                  style={{ borderColor: 'var(--glass-border)', color: 'var(--text-secondary)' }}>
                  No {replacementPanelTab} snippets yet. Try regenerating on the right or pick another tab.
                </div>
              ) : (
                <div className="grid sm:grid-cols-2 gap-3">
                  {replacementOptions.map((option, index) => (
                    <div
                      key={`${option.url}-${index}`}
                      className="rounded-xl overflow-hidden border"
                      style={{
                        borderColor: option.active ? 'var(--primary)' : 'var(--glass-border)',
                        background: 'var(--bg-secondary)',
                      }}
                    >
                      <div className="aspect-video flex items-center justify-center" style={{ background: 'var(--bg-tertiary)' }}>
                        {replacementPanelTab === 'image' ? (
                          <img src={mediaUrlForPlayback(option.url)} alt="" className="w-full h-full object-cover" />
                        ) : replacementPanelTab === 'video' ? (
                          <video src={mediaUrlForPlayback(option.url)} controls className="w-full h-full object-contain bg-black" />
                        ) : (
                          <div className="w-full px-3">
                            <Music className="w-8 h-8 mx-auto mb-3" style={{ color: '#ec4899' }} />
                            <audio src={mediaUrlForPlayback(option.url)} controls className="w-full h-9" />
                          </div>
                        )}
                      </div>
                      <div className="p-3 space-y-2">
                        <div className="flex items-center justify-between gap-2">
                          <span className="text-xs font-semibold capitalize" style={{ color: 'var(--text-primary)' }}>
                            {option.active ? 'Current' : option.source || 'Generated'}
                          </span>
                          {option.created_at && (
                            <span className="text-[10px]" style={{ color: 'var(--text-tertiary)' }}>
                              {new Date(option.created_at).toLocaleString()}
                            </span>
                          )}
                        </div>
                        {replacementPanelTab === 'video' && option.source_image_url && (
                          <div className="flex items-center gap-2 rounded-lg border p-2"
                            style={{ borderColor: 'var(--glass-border)', background: 'var(--bg-primary)' }}>
                            <img
                              src={mediaUrlForPlayback(option.source_image_url)}
                              alt=""
                              className="w-12 h-8 rounded object-cover shrink-0"
                            />
                            <span className="text-[10px] leading-tight" style={{ color: 'var(--text-tertiary)' }}>
                              Generated from this image
                            </span>
                          </div>
                        )}
                        <Button
                          size="sm"
                          variant={option.active ? 'outline' : 'default'}
                          disabled={option.active}
                          onClick={() => replaceTimelineSnippet(option)}
                          className="w-full rounded-lg"
                          style={option.active
                            ? { borderColor: 'var(--glass-border)', color: 'var(--text-secondary)' }
                            : { background: 'var(--primary)', color: 'var(--text-primary)' }}
                        >
                          {option.active ? 'Selected' : 'Use this snippet'}
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div className="rounded-xl border p-3 overflow-auto"
              style={{ borderColor: 'var(--glass-border)', background: 'var(--bg-secondary)' }}>
              <input
                ref={replacementUploadInputRef}
                type="file"
                className="hidden"
                accept={
                  replacementDialog?.kind === 'image'
                    ? 'image/jpeg,image/png,image/webp,image/gif'
                    : replacementDialog?.kind === 'video'
                      ? 'video/mp4,video/quicktime,video/webm,.m4v'
                      : 'audio/mpeg,.mp3,audio/wav,.wav,.m4a,audio/mp4,.aac'
                }
                onChange={handleReplacementLocalUpload}
              />
              <Tabs value={replacementPanelTab} onValueChange={(v) => void handleReplacementTabChange(v)} className="w-full">
                <TabsList className="grid grid-cols-4 w-full mb-3">
                  <TabsTrigger value="upload">Upload</TabsTrigger>
                  <TabsTrigger value="video">Video</TabsTrigger>
                  <TabsTrigger value="image">Image</TabsTrigger>
                  <TabsTrigger value="audio">Audio</TabsTrigger>
                </TabsList>

                <TabsContent value="upload" className="space-y-3">
                  <p className="text-xs font-semibold" style={{ color: 'var(--text-primary)' }}>
                    Upload from device
                  </p>
                  <p className="text-[11px]" style={{ color: 'var(--text-tertiary)' }}>
                    Replaces this snippet&apos;s current {replacementDialog?.kind || 'media'} with your file (stored on your account).
                  </p>
                  <Button
                    type="button"
                    variant="outline"
                    className="w-full gap-2 rounded-lg"
                    disabled={replacementUploading || replacementLoading}
                    style={{ borderColor: 'var(--glass-border)', color: 'var(--text-primary)' }}
                    onClick={() => replacementUploadInputRef.current?.click()}
                  >
                    {replacementUploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
                    Choose file…
                  </Button>
                </TabsContent>

                <TabsContent value="video" className="space-y-3">
                  <div>
                    <p className="text-xs font-semibold mb-1" style={{ color: 'var(--text-primary)' }}>Regenerate video</p>
                    <p className="text-[11px] mb-2" style={{ color: 'var(--text-tertiary)' }}>
                      Uses this prompt and the selected video provider for this generation only.
                    </p>
                    <textarea
                      value={replacementDrafts.video || ''}
                      onChange={(event) => setReplacementDrafts(prev => ({ ...prev, video: event.target.value }))}
                      className="w-full min-h-24 rounded-lg border p-2 text-sm resize-none"
                      style={{ background: 'var(--bg-primary)', borderColor: 'var(--glass-border)', color: 'var(--text-primary)' }}
                      data-testid="replacement-video-prompt-input"
                    />
                  </div>
                  <div className="grid gap-2">
                    <Select value={replacementOverrides.video_provider} onValueChange={(value) => updateReplacementOverride('video_provider', value)}>
                      <SelectTrigger className="h-9 rounded-lg text-xs" data-testid="replacement-video-provider-select"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {(isNarrationEditor ? NARRATION_VIDEO_PROVIDER_OPTIONS : VIDEO_PROVIDER_OPTIONS).map(option => (
                          <SelectItem key={option.value} value={option.value}>{option.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <Select value={replacementOverrides.camera_motion} onValueChange={(value) => updateReplacementOverride('camera_motion', value)}>
                      <SelectTrigger className="h-9 rounded-lg text-xs" data-testid="replacement-video-camera-select"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {CAMERA_MOTION_OPTIONS.map(option => (
                          <SelectItem key={option.value} value={option.value}>{option.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <Button
                    onClick={() => regenerateReplacementSnippet('video')}
                    disabled={Boolean(regeneratingReplacement) || replacementLoading}
                    className="w-full rounded-lg gap-2"
                    style={{ background: 'var(--primary)', color: 'var(--text-primary)' }}
                    data-testid="regenerate-video-snippet-btn"
                  >
                    {regeneratingReplacement === 'video' ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
                    Regenerate video
                  </Button>
                  {!isNarrationEditor && !isPitchRecordingStory(story) && (
                    <div className="space-y-2 pt-1">
                      <label className="flex items-start gap-2 cursor-pointer text-left">
                        <input
                          type="checkbox"
                          className="mt-0.5 rounded border shrink-0"
                          style={{ borderColor: 'var(--glass-border)' }}
                          checked={Boolean(replacementOverrides.extend_scene_video_to_audio_duration)}
                          onChange={(e) => updateReplacementOverride('extend_scene_video_to_audio_duration', e.target.checked)}
                          data-testid="replacement-video-extend-audio-checkbox"
                        />
                        <span className="text-[11px] leading-snug" style={{ color: 'var(--text-secondary)' }}>
                          Extend scene to match audio length (pads or trims the clip after generation so the timeline matches scene dialogue audio).
                        </span>
                      </label>
                      <label
                        className={`flex items-start gap-2 text-left ${(!replacementSnippetScene?.audio_url || !replacementSnippetScene?.image_url) ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
                        title={(!replacementSnippetScene?.audio_url || !replacementSnippetScene?.image_url) ? 'Scene needs generated image and scene audio first.' : undefined}
                      >
                        <input
                          type="checkbox"
                          className="mt-0.5 rounded border shrink-0"
                          style={{ borderColor: 'var(--glass-border)' }}
                          disabled={!replacementSnippetScene?.audio_url || !replacementSnippetScene?.image_url}
                          checked={Boolean(replacementOverrides.lip_sync_scene_video_with_audio)}
                          onChange={(e) => updateReplacementOverride('lip_sync_scene_video_with_audio', e.target.checked)}
                          data-testid="replacement-video-lipsync-checkbox"
                        />
                        <span className="text-[11px] leading-snug" style={{ color: 'var(--text-secondary)' }}>
                          Lip sync with audio (Seedance-style generation, then mux scene audio into the clip). Uses Runway/Seedance pipeline.
                        </span>
                      </label>
                    </div>
                  )}
                </TabsContent>

                <TabsContent value="image" className="space-y-3">
                  <div>
                    <p className="text-xs font-semibold mb-1" style={{ color: 'var(--text-primary)' }}>Regenerate image</p>
                    <p className="text-[11px] mb-2" style={{ color: 'var(--text-tertiary)' }}>
                      Uses this image prompt and provider without saving them as scene defaults.
                    </p>
                    <textarea
                      value={replacementDrafts.image || ''}
                      onChange={(event) => setReplacementDrafts(prev => ({ ...prev, image: event.target.value }))}
                      className="w-full min-h-24 rounded-lg border p-2 text-sm resize-none"
                      style={{ background: 'var(--bg-primary)', borderColor: 'var(--glass-border)', color: 'var(--text-primary)' }}
                      data-testid="replacement-image-prompt-input"
                    />
                  </div>
                  <Select value={replacementOverrides.image_provider} onValueChange={(value) => updateReplacementOverride('image_provider', value)}>
                    <SelectTrigger className="h-9 rounded-lg text-xs" data-testid="replacement-image-provider-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {IMAGE_PROVIDER_OPTIONS.map(option => (
                        <SelectItem key={option.value} value={option.value}>{option.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <Button
                    onClick={() => regenerateReplacementSnippet('image')}
                    disabled={Boolean(regeneratingReplacement) || replacementLoading || !replacementDrafts.image?.trim()}
                    className="w-full rounded-lg gap-2"
                    style={{ background: 'var(--primary)', color: 'var(--text-primary)' }}
                    data-testid="regenerate-image-snippet-btn"
                  >
                    {regeneratingReplacement === 'image' ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
                    Regenerate image
                  </Button>
                </TabsContent>

                <TabsContent value="audio" className="space-y-3">
                  <div>
                    <p className="text-xs font-semibold mb-1" style={{ color: 'var(--text-primary)' }}>Regenerate audio</p>
                    <p className="text-[11px] mb-2" style={{ color: 'var(--text-tertiary)' }}>
                      Uses this script, TTS provider, voice, and language for this generation only.
                    </p>
                    <textarea
                      value={replacementDrafts.audio || ''}
                      onChange={(event) => setReplacementDrafts(prev => ({ ...prev, audio: event.target.value }))}
                      className="w-full min-h-24 rounded-lg border p-2 text-sm resize-none"
                      style={{ background: 'var(--bg-primary)', borderColor: 'var(--glass-border)', color: 'var(--text-primary)' }}
                      data-testid="replacement-audio-script-input"
                    />
                  </div>
                  <NarrationAudioSettingsPanel
                    settings={replacementAudioSettings}
                    onChange={onReplacementAudioSettingsChange}
                    onChooseVoice={() => setReplacementAudioVoicePickerOpen(true)}
                    disabled={Boolean(regeneratingReplacement) || replacementLoading}
                    testId="replacement-audio-settings"
                  />
                  <Button
                    onClick={() => regenerateReplacementSnippet('audio')}
                    disabled={Boolean(regeneratingReplacement) || replacementLoading || !replacementDrafts.audio?.trim()}
                    className="w-full rounded-lg gap-2"
                    style={{ background: 'var(--primary)', color: 'var(--text-primary)' }}
                    data-testid="regenerate-audio-snippet-btn"
                  >
                    {regeneratingReplacement === 'audio' ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
                    Regenerate audio
                  </Button>
                </TabsContent>
              </Tabs>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {directorDialogOpen ? (
        <MovieDirectorPanel
          layout="docked"
          story={
            isNarrationEditor && projectId
              ? { ...story, id: projectId, story_type: 'narration' }
              : story
          }
          scenes={scenesSnapshot}
          editorMode={isNarrationEditor ? 'narration' : 'story'}
          onClose={() => setDirectorDialogOpen(false)}
          onHide={() => setDirectorDialogOpen(false)}
          dockedBackdrop
        />
      ) : null}

      {/* Clip editor dialog */}

      {editingClip && (
        <VideoClipEditor
          isOpen
          onClose={() => setEditingClip(null)}
          clip={editingClip.clip}
          onSave={saveEditedClip}
          onClipPatch={patchEditedClip}
        />
      )}

      <TransitionPickerDialog
        open={Boolean(transitionPicker)}
        onClose={() => setTransitionPicker(null)}
        fromClip={transitionPicker?.fromClip}
        toClip={transitionPicker?.toClip}
        initialTransitionOut={transitionPicker?.transitionOut}
        initialDuration={transitionPicker?.transitionDuration}
        onApply={applyTransitionFromPicker}
      />
    </div>
  );
};
